<template>
  <div class="chain-builder">
    <!-- Landing Page -->
    <div v-if="showLandingPage && !showHistoryView && !showReportView" class="landing-page">
      <div class="landing-header">
        <div class="landing-title-section">
          <h1 class="landing-title">Prompt Chains</h1>
          <p class="landing-subtitle">Create and manage multi-step prompt workflows</p>
        </div>
        <v-btn
          v-if="chainScope === 'template'"
          color="#50B3CB"
          dark
          large
          @click="createNewChain"
        >
          <v-icon left>mdi-plus</v-icon>
          Create New Chain
        </v-btn>
        <v-btn
          v-if="chainScope === 'system'"
          color="teal"
          dark
          large
          @click="createNewSystemChain"
        >
          <v-icon left>mdi-plus</v-icon>
          Create System Chain
        </v-btn>
      </div>

      <div class="landing-content">
        <div class="d-flex align-center mb-4">
          <v-btn-toggle v-model="chainScope" mandatory dense class="mr-4">
            <v-btn value="template" small>
              <v-icon left small>mdi-file-document-outline</v-icon>
              Template
            </v-btn>
            <v-btn value="system" small>
              <v-icon left small>mdi-cog-outline</v-icon>
              System
            </v-btn>
          </v-btn-toggle>
          <v-text-field
            v-model="chainSearchQuery"
            placeholder="Search chains..."
            outlined
            dense
            prepend-inner-icon="mdi-magnify"
            hide-details
            class="search-field"
            style="max-width: 400px;"
          ></v-text-field>
        </div>

        <div class="chains-grid">
          <v-card
            v-for="chainItem in filteredChains"
            :key="chainItem.id"
            class="chain-card"
            outlined
            hover
          >
            <v-card-text>
              <div class="chain-card-header">
                <div class="chain-icon">
                  <v-icon size="32" :color="chainScope === 'system' ? 'grey' : 'blue'">mdi-link-variant</v-icon>
                </div>
                <div class="chain-info">
                  <h3 class="chain-title">{{ chainItem.chain_name }}</h3>
                  <p class="chain-description">{{ chainItem.description }}</p>
                </div>
                <v-chip v-if="chainScope === 'system'" x-small outlined color="grey" class="ml-2">System</v-chip>
              </div>

              <div class="chain-stats">
                <div class="stat-item">
                  <v-icon small color="grey">mdi-format-list-numbered</v-icon>
                  <span>{{ chainItem.step_count }} steps</span>
                </div>
                <div class="stat-item">
                  <v-icon small color="grey">mdi-clock-outline</v-icon>
                  <span>{{ formatDate(chainItem.updated_at) }}</span>
                </div>
                <div class="stat-item">
                  <v-chip
                    x-small
                    :color="chainItem.status === 'active' ? 'success' : 'grey'"
                    text-color="white"
                  >
                    {{ chainItem.status }}
                  </v-chip>
                </div>
              </div>

              <v-divider class="my-3"></v-divider>

              <!-- Template chain actions -->
              <div v-if="chainScope === 'template'" class="chain-actions">
                <v-btn
                  text
                  small
                  color="#50B3CB"
                  @click="editChain(chainItem)"
                >
                  <v-icon left small>mdi-pencil</v-icon>
                  Edit
                </v-btn>
                <v-btn
                  text
                  small
                  color="#5E2872"
                  @click="viewChainHistory(chainItem)"
                >
                  <v-icon left small>mdi-history</v-icon>
                  View History
                </v-btn>
                <v-btn
                  text
                  small
                  color="#FF6B35"
                  @click="viewChainReport(chainItem)"
                >
                  <v-icon left small>mdi-chart-line</v-icon>
                  Report
                </v-btn>
              </div>

              <!-- System chain actions -->
              <div v-else class="chain-actions">
                <v-btn
                  text
                  small
                  color="#50B3CB"
                  @click="editSystemChain(chainItem)"
                >
                  <v-icon left small>mdi-pencil</v-icon>
                  Edit
                </v-btn>
                <v-menu offset-y left>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon small v-bind="attrs" v-on="on">
                      <v-icon small>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>
                  <v-list dense>
                    <v-list-item @click="viewSystemChain(chainItem)">
                      <v-list-item-icon class="mr-3"><v-icon small>mdi-eye</v-icon></v-list-item-icon>
                      <v-list-item-title>View</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="viewChainHistory(chainItem)">
                      <v-list-item-icon class="mr-3"><v-icon small color="#5E2872">mdi-history</v-icon></v-list-item-icon>
                      <v-list-item-title>View History</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="viewChainReport(chainItem)">
                      <v-list-item-icon class="mr-3"><v-icon small color="#FF6B35">mdi-chart-line</v-icon></v-list-item-icon>
                      <v-list-item-title>Report</v-list-item-title>
                    </v-list-item>
                    <v-divider></v-divider>
                    <v-list-item @click="copyChainToTemplate(chainItem)">
                      <v-list-item-icon class="mr-3"><v-icon small color="success">mdi-content-copy</v-icon></v-list-item-icon>
                      <v-list-item-title>Copy to Template</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>
            </v-card-text>
          </v-card>
        </div>

        <div v-if="filteredChains.length === 0" class="empty-state">
          <v-icon size="64" color="grey lighten-1">mdi-link-variant-off</v-icon>
          <h3>No chains found</h3>
          <p v-if="chainScope === 'system'">No system-level chains are available</p>
          <p v-else>Create your first prompt chain to get started</p>
          <v-btn
            v-if="chainScope === 'template'"
            color="#50B3CB"
            dark
            class="mt-4"
            @click="createNewChain"
          >
            <v-icon left>mdi-plus</v-icon>
            Create New Chain
          </v-btn>
          <v-btn
            v-if="chainScope === 'system'"
            color="teal"
            dark
            class="mt-4"
            @click="createNewSystemChain"
          >
            <v-icon left>mdi-plus</v-icon>
            Create System Chain
          </v-btn>
        </div>
      </div>
    </div>

    <!-- History View (moved above builder) -->
    <div v-else-if="showHistoryView" class="history-view">
      <div class="history-header">
        <v-btn
          icon
          small
          @click="closeHistoryView"
          class="mr-3"
        >
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>

        <div style="flex: 1;">
          <h2 class="history-title">
            <v-icon left>mdi-history</v-icon>
            <span v-if="!showExecutionDetails">Execution History</span>
            <span v-else>Execution Details</span>
          </h2>
          <p class="history-subtitle" v-if="selectedChainForHistory">
            {{ selectedChainForHistory.chain_name }}
          </p>
        </div>

        <v-btn
          v-if="!showExecutionDetails"
          color="#FF6B35"
          dark
          @click="viewChainReport(selectedChainForHistory)"
        >
          <v-icon left>mdi-chart-line</v-icon>
          View Report
        </v-btn>
        <v-btn
          v-else
          color="#FF6B35"
          dark
          @click="viewExecutionReport(selectedExecution)"
        >
          <v-icon left>mdi-chart-box-outline</v-icon>
          Execution Report
        </v-btn>
      </div>

      <!-- Execution History List -->
      <div v-if="!showExecutionDetails" class="history-content">
        <div v-if="loadingHistory" class="text-center pa-8">
          <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
          <div class="mt-4">Loading execution history...</div>
        </div>

        <div v-else-if="executionHistory.length === 0" class="text-center pa-8 grey--text">
          <v-icon size="64" color="grey lighten-1" class="mb-3">mdi-history</v-icon>
          <div class="text-h6 mb-2">No Execution History</div>
          <div class="text-body-2">
            This chain has not been executed yet.
          </div>
        </div>

        <div v-else>
          <v-data-table
            :headers=" [
              { text: 'Type', value: 'execution_type', width: '100px' },
              { text: 'Execution ID', value: 'execution_id', width: '120px' },
              { text: 'Status', value: 'execution_status', width: '120px' },
              { text: 'Trigger', value: 'trigger_context', width: '150px' },
              { text: 'Started', value: 'started_at', width: '180px' },
              { text: 'Completed', value: 'completed_at', width: '180px' },
              { text: 'Duration', value: 'duration', width: '100px' },
              { text: 'Steps', value: 'total_steps', width: '80px' },
              { text: 'User Name', value: 'user_name', width: '100px' },
              { text: 'Issues', value: 'issues', width: '80px' },
              { text: 'Actions', value: 'actions', sortable: false, width: '100px' }
            ]"
            :items="executionHistory"
            :items-per-page="10"
            class="elevation-1"
          >
            <template v-slot:[`item.execution_type`]="{ item }">
              <v-chip
                x-small
                :color="getExecutionTypeColor(item.execution_type)"
                text-color="white"
              >
                <v-icon left x-small>{{ getExecutionTypeIcon(item.execution_type) }}</v-icon>
                {{ getExecutionTypeLabel(item.execution_type) }}
              </v-chip>
            </template>          
            <template #execution_status="{ item }">
              <v-chip
                x-small
                :color="getStatusColor(item.execution_status)"
                text-color="white"
              >
                <v-icon left x-small>{{ getStatusIcon(item.execution_status) }}</v-icon>
                {{ item.execution_status }}
              </v-chip>
            </template>

            <template v-slot:[`item.trigger_context`]="{ item }">
              <div v-if="item.trigger_context && typeof item.trigger_context === 'object' && item.trigger_context.trigger_name" class="trigger-context">
                <v-chip
                  x-small
                  color="purple"
                  outlined
                  :title="`Triggered by: ${item.trigger_context.trigger_name}`"
                >
                  <v-icon left x-small>{{ getTriggerIcon(item.trigger_context.trigger_type || 'manual') }}</v-icon>
                  {{ item.trigger_context.trigger_name }}
                </v-chip>
              </div>
              <div v-else-if="item.trigger_context && typeof item.trigger_context === 'string'" class="trigger-context">
                <v-chip
                  x-small
                  color="purple"
                  outlined
                  :title="`Triggered by: ${item.trigger_context}`"
                >
                  <v-icon left x-small>{{ getTriggerIcon('manual') }}</v-icon>
                  {{ item.trigger_context }}
                </v-chip>
              </div>
              <div v-else class="trigger-context">
                <v-chip
                  x-small
                  color="grey"
                  outlined
                  title="Manually executed"
                >
                  <v-icon left x-small>{{ getTriggerIcon('manual') }}</v-icon>
                  Manual
                </v-chip>
              </div>
            </template>

            <template #started_at="{ item }">
              {{ formatDateTime(item.started_at) }}
            </template>

            <template #completed_at="{ item }">
              {{ formatDateTime(item.completed_at) }}
            </template>

            <template #duration="{ item }">
              {{ formatDuration(item.duration) }}
            </template>

            <template #total_steps="{ item }">
              <span v-if="item.completed_steps !== undefined && item.total_steps !== undefined">
                {{ item.completed_steps }}/{{ item.total_steps }}
              </span>
              <span v-else>{{ item.total_steps || 'N/A' }}</span>
            </template>

            <template v-slot:[`item.issues`]="{ item }">
              <div v-if="item.has_empty_inputs" class="issues-indicator">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-chip
                      x-small
                      color="warning"
                      text-color="white"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon x-small left>mdi-alert-circle</v-icon>
                      Empty
                    </v-chip>
                  </template>
                  <span>This execution has empty or null input values</span>
                </v-tooltip>
              </div>
            </template>

            <template v-slot:[`item.actions`]="{ item }">
              <v-btn
                icon
                small
                @click="viewExecutionDetails(item)"
                title="View Details"
              >
                <v-icon small>mdi-eye</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </div>
      </div>

      <!-- Execution Details View -->
      <div v-if="showExecutionDetails && selectedExecution" class="execution-details-content">
        <div class="execution-details-layout">
          <!-- Left Panel - Execution List -->
          <div class="execution-list-panel">
            <div class="panel-header">
              <h3>All Executions</h3>
              <v-btn
                icon
                small
                @click="backToHistoryList"
                title="Back to History"
              >
                <v-icon small>mdi-arrow-left</v-icon>
              </v-btn>
            </div>
            
            <div class="execution-list">
              <div
                v-for="execution in executionHistory"
                :key="execution.execution_id"
                :class="[
                  'execution-item', 
                  { 
                    'active': selectedExecution.execution_id === execution.execution_id,
                    'has-issues': execution.has_empty_inputs,
                    'is-regeneration': execution.execution_type === 'regeneration',
                    'is-error': execution.execution_type === 'error',
                    'is-partial': execution.execution_type === 'partial'
                  }
                ]"
                @click="viewExecutionDetails(execution)"
              >
                <div class="execution-item-header">
                  <div class="execution-id-row">
                  <span class="execution-id">#{{ execution.execution_id }}</span>
                  <v-chip
                    x-small
                    :color="getExecutionTypeColor(execution.execution_type)"
                    text-color="white"
                    class="ml-1"
                  >
                    {{ getExecutionTypeLabel(execution.execution_type) }}
                  </v-chip>
                  </div>
                  <v-chip
                    x-small
                    :color="getStatusColor(execution.execution_status)"
                    text-color="white"
                  >
                    {{ execution.execution_status }}
                  </v-chip>
                </div>
                <div class="execution-item-meta">
                  <div class="execution-time">{{ formatDateTime(execution.started_at) }}</div>
                  <div class="execution-duration">{{ formatDuration(execution.duration) }}</div>
                  <div v-if="execution.trigger_context && typeof execution.trigger_context === 'object' && execution.trigger_context.trigger_name" class="execution-trigger">
                    <v-icon x-small color="purple" class="mr-1">{{ getTriggerIcon(execution.trigger_context.trigger_type || 'manual') }}</v-icon>
                    <span class="trigger-text">{{ execution.trigger_context.trigger_name }}</span>
                  </div>
                  <div v-else-if="execution.trigger_context && typeof execution.trigger_context === 'string'" class="execution-trigger">
                    <v-icon x-small color="purple" class="mr-1">mdi-flash</v-icon>
                    <span class="trigger-text">{{ execution.trigger_context }}</span>
                  </div>
                  <div v-else class="execution-trigger">
                    <v-icon x-small color="grey" class="mr-1">{{ getTriggerIcon('manual') }}</v-icon>
                    <span class="trigger-text">Manual</span>
                  </div>
                  <div v-if="execution.has_empty_inputs" class="execution-warning">
                    <v-icon x-small color="warning" class="mr-1">mdi-alert-triangle</v-icon>
                    <span class="warning-text">Empty inputs detected</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Right Panel - Step Details -->
          <div class="execution-steps-panel">
            <div class="panel-header">
              <div class="panel-header-content">
                <h3>Execution #{{ selectedExecution.execution_id }} Details</h3>
                <div class="execution-meta-chips">
                  <v-chip
                    small
                    :color="getExecutionTypeColor(selectedExecution.execution_type)"
                    text-color="white"
                  >
                    <v-icon left small>{{ getExecutionTypeIcon(selectedExecution.execution_type) }}</v-icon>
                    {{ getExecutionTypeLabel(selectedExecution.execution_type) }}
                  </v-chip>
                  <v-chip
                    small
                    :color="getStatusColor(selectedExecution.execution_status)"
                    text-color="white"
                  >
                    <v-icon left small>{{ getStatusIcon(selectedExecution.execution_status) }}</v-icon>
                    {{ selectedExecution.execution_status }}
                  </v-chip>
                  <v-chip
                    small
                    color="blue"
                    outlined
                    v-if="getTotalTokensUsed()"
                  >
                    <v-icon left small>mdi-flash</v-icon>
                    {{ getTotalTokensUsed() }} tokens
                  </v-chip>
                </div>
              </div>
            </div>

            <div class="execution-summary-detailed">
              <div class="summary-grid">
                <div class="summary-card">
                  <div class="summary-label">Started</div>
                  <div class="summary-value">{{ formatDateTime(selectedExecution.started_at) }}</div>
                </div>
                <div class="summary-card">
                  <div class="summary-label">Duration</div>
                  <div class="summary-value">{{ formatDuration(selectedExecution.duration) }}</div>
                </div>
                <div class="summary-card">
                  <div class="summary-label">Steps Progress</div>
                  <div class="summary-value">{{ selectedExecution.completed_steps || 0 }}/{{ selectedExecution.total_steps }}</div>
                </div>
                <div class="summary-card" v-if="getTotalTokensUsed()">
                  <div class="summary-label">Total Tokens</div>
                  <div class="summary-value">{{ getTotalTokensUsed() }}</div>
                </div>
                <div class="summary-card" v-if="selectedExecution.trigger_context">
                  <div class="summary-label">Triggered By</div>
                  <div class="summary-value trigger-name">
                    <v-icon small color="purple" class="mr-1">{{ getTriggerIcon(selectedExecution.trigger_context?.trigger_type || 'manual') }}</v-icon>
                    {{ 
                      typeof selectedExecution.trigger_context === 'object' && selectedExecution.trigger_context.trigger_name 
                        ? selectedExecution.trigger_context.trigger_name 
                        : typeof selectedExecution.trigger_context === 'string' 
                        ? selectedExecution.trigger_context 
                        : 'Unknown Trigger' 
                    }}
                  </div>
                </div>
                <div class="summary-card" v-else>
                  <div class="summary-label">Execution Type</div>
                  <div class="summary-value trigger-name">
                    <v-icon small color="grey" class="mr-1">{{ getTriggerIcon('manual') }}</v-icon>
                    Manual Execution
                  </div>
                </div>
                <div class="summary-card">
                  <div class="summary-label">User</div>
                  <div class="summary-value">{{ selectedExecution.user_name }}</div>
                </div>
              </div>
              
              <div v-if="selectedExecution.error_message" class="error-summary-detailed">
                <v-icon small color="error" class="error-icon">mdi-alert-circle</v-icon>
                <div class="error-content">
                  <div class="error-title">Execution Error</div>
                  <div class="error-text">{{ selectedExecution.error_message }}</div>
                </div>
              </div>
            </div>

            <!-- Step Details -->
            <div class="steps-container">
              <div v-if="loadingExecutionSteps" class="loading-state">
                <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
                <div class="loading-text">Loading execution steps...</div>
              </div>

              <div v-else-if="executionSteps.length === 0" class="empty-steps-state">
                <v-icon size="48" color="grey lighten-1" class="mb-3">mdi-format-list-numbered</v-icon>
                <div class="empty-title">No Steps Found</div>
                <div class="empty-subtitle">No execution steps were recorded for this execution.</div>
              </div>

              <div v-else class="steps-timeline">
                <div
                  v-for="(step, index) in executionSteps"
                  :key="step.step_execution_id"
                  class="step-timeline-item"
                >
                  <div class="step-timeline-connector" v-if="index > 0"></div>
                  
                  <div :class="['step-detail-card-enhanced', { 'step-skipped': step.step_status === 'skipped' }]">
                    <div class="step-header-enhanced">
                      <div class="step-indicator">
                        <div class="step-number-enhanced">{{ index + 1 }}</div>
                        <div :class="['step-status-indicator', `status-${step.step_status}`]">
                          <v-icon 
                            small 
                            :color="getStepStatusIconColor(step.step_status)"
                          >
                            {{ getStepStatusIcon(step.step_status) }}
                          </v-icon>
                        </div>
                      </div>
                      
                      <div class="step-info-enhanced">
                        <div class="step-title-row">
                          <h4 class="step-name-enhanced">{{ step.step_name || `Step ${step.step_order}` }}</h4>
                          <div class="step-badges">
                            <v-chip
                              x-small
                              :color="getStepStatusColor(step.step_status)"
                              text-color="white"
                              class="status-chip"
                            >
                              {{ getStepStatusLabel(step.step_status) }}
                            </v-chip>
                            <v-chip
                              x-small
                              :color="step.step_type === 'service' ? 'purple' : step.step_type === 'output_formatter' ? 'orange' : 'blue'"
                              outlined
                              class="type-chip"
                            >
                              {{ step.step_type }}
                            </v-chip>
                          </div>
                        </div>
                        
                        <div class="step-metrics">
                          <div class="metric-item" v-if="step.duration && step.step_status !== 'skipped'">
                            <v-icon x-small color="grey">mdi-clock-outline</v-icon>
                            <span>{{ formatDuration(step.duration) }}</span>
                          </div>
                          <div class="metric-item" v-if="step.tokens_used && step.step_status !== 'skipped'">
                            <v-icon x-small color="grey">mdi-flash</v-icon>
                            <span>{{ step.tokens_used }} tokens</span>
                          </div>
                          <div class="metric-item" v-if="step.model_used && step.step_status !== 'skipped'">
                            <v-icon x-small color="grey">mdi-brain</v-icon>
                            <span>{{ step.model_used }}</span>
                          </div>
                          <div class="metric-item" v-if="step.prompt_version && step.step_type === 'prompt' && step.step_status !== 'skipped'">
                            <v-icon x-small color="grey">mdi-file-document-outline</v-icon>
                            <span>v{{ step.prompt_version }}</span>
                            <v-tooltip v-if="step.prompt_version !== step.current_version" top>
                              <template v-slot:activator="{ on, attrs }">
                                <v-icon x-small color="warning" class="ml-1" v-bind="attrs" v-on="on">mdi-history</v-icon>
                              </template>
                              <span>Historical version (current: v{{ step.current_version }})</span>
                            </v-tooltip>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div v-if="step.step_status === 'skipped'" class="step-skipped-info">
                      <v-icon small color="warning" class="mr-2">mdi-skip-forward</v-icon>
                      <span class="skipped-text">This step was skipped during execution</span>
                    </div>
                    <div class="step-timing-detailed">
                      <div class="timing-row">
                        <div class="timing-item-detailed">
                          <span class="timing-label-detailed">Started</span>
                          <span class="timing-value-detailed">{{ formatDateTime(step.started_at) }}</span>
                        </div>
                        <div class="timing-item-detailed" v-if="step.completed_at">
                          <span class="timing-label-detailed">Completed</span>
                          <span class="timing-value-detailed">{{ formatDateTime(step.completed_at) }}</span>
                        </div>
                        <div class="timing-item-detailed" v-if="step.duration">
                          <span class="timing-label-detailed">Duration</span>
                          <span class="timing-value-detailed">{{ formatDuration(step.duration) }}</span>
                        </div>
                      </div>
                    </div>

                    <div v-if="step.error_message" class="step-error-detailed">
                      <v-icon small color="error" class="error-icon">mdi-alert-circle</v-icon>
                      <div class="error-content">
                        <div class="error-title">Step Error</div>
                        <div class="error-message">{{ step.error_message }}</div>
                      </div>
                    </div>

                    <!-- Enhanced Step Data Tabs -->
                    <v-tabs v-model="step.activeTab" class="step-tabs-enhanced">                   
                      <v-tab class="step-tab">
                        <v-icon left small>mdi-import</v-icon>
                        Inputs
                        <v-chip x-small color="blue" outlined class="ml-2" v-if="Object.keys(step.inputs || {}).length">
                          {{ Object.keys(step.inputs || {}).length }}
                        </v-chip>
                      </v-tab>
                      <v-tab class="step-tab">
                        <v-icon left small>mdi-export</v-icon>
                        Outputs
                        <v-chip x-small color="green" outlined class="ml-2" v-if="Object.keys(step.outputs || {}).length">
                          {{ Object.keys(step.outputs || {}).length }}
                        </v-chip>
                      </v-tab>
                      <v-tab class="step-tab" v-if="step.step_type === 'prompt'">
                        <v-icon left small>mdi-message-text</v-icon>
                        Prompt
                      </v-tab>
                      <v-tab class="step-tab" v-if="step.raw_response">
                        <v-icon left small>mdi-code-json</v-icon>
                        Raw Response
                      </v-tab>
                      <v-tab class="step-tab" v-if="step.output_data && step.output_data._validation_attempts">
                        <v-icon left small>mdi-check-circle-outline</v-icon>
                        Validation History
                        <v-chip x-small color="orange" outlined class="ml-2">
                          {{ step.output_data._validation_attempts.length }}
                        </v-chip>
                      </v-tab>
                        <div style="display: inline-flex; align-items:center; float:right" align="right">
                          <v-icon @click="step.toggleViewDetails = !step.toggleViewDetails">{{ !step.toggleViewDetails ? 'mdi-chevron-down' : 'mdi-chevron-up' }}</v-icon>
                          <div class="step-tab">{{ !step.toggleViewDetails ? 'Show' : 'Hide' }} Details</div>
                        </div>   
                      <v-tab-item>
                        <div class="tab-content-enhanced" v-if="step.toggleViewDetails">
                          <div v-if="!step.inputs || Object.keys(step.inputs).length === 0" class="no-data-enhanced">
                            <v-icon color="grey lighten-1" size="32" class="mb-2">mdi-import</v-icon>
                            <div class="no-data-text">No inputs recorded</div>
                          </div>
                          <div v-else class="data-display">
                            <div
                              v-for="(value, key) in step.inputs"
                              :key="key"
                              :class="['data-item', { 'data-item-warning': isEmptyOrNull(value) }]"
                            >
                              <div class="data-header">
                                <div class="data-key-row">
                                  <span class="data-key-enhanced">{{ key }}</span>
                                  <v-icon 
                                    v-if="isEmptyOrNull(value)" 
                                    color="warning" 
                                    size="16" 
                                    class="ml-2"
                                    :title="`Warning: ${getEmptyValueType(value)} input value`"
                                  >
                                    mdi-alert-circle
                                  </v-icon>
                                </div>
                                <v-chip x-small color="blue" outlined>
                                  {{ typeof value }}
                                </v-chip>
                              </div>
                              <div class="data-value-enhanced">
                                <div v-if="isEmptyOrNull(value)" class="empty-value-warning">
                                  <v-icon small color="warning" class="mr-2">mdi-alert-triangle</v-icon>
                                  <span class="empty-value-text">{{ getEmptyValueMessage(value) }}</span>
                                </div>
                                <div v-else-if="typeof value === 'string' && value.length > 200" class="expandable-content">
                                  <div class="content-preview">{{ value.substring(0, 200) }}...</div>
                                  <v-btn
                                    text
                                    x-small
                                    color="primary"
                                    class="expand-btn"
                                    @click="step[`showFullInput_${key}`] = !step[`showFullInput_${key}`]"
                                  >
                                    <v-icon left x-small>{{ step[`showFullInput_${key}`] ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                                    {{ step[`showFullInput_${key}`] ? 'Show Less' : 'Show More' }}
                                  </v-btn>
                                  <div v-if="step[`showFullInput_${key}`]" class="full-content">
                                    <pre class="content-code">{{ value }}</pre>
                                  </div>
                                </div>
                                <div v-else class="simple-content">
                                  <pre class="content-code">{{ value }}</pre>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </v-tab-item>
                      
                      <v-tab-item>
                        <div class="tab-content-enhanced" v-if="step.toggleViewDetails">
                          <div v-if="!step.outputs || Object.keys(step.outputs).length === 0" class="no-data-enhanced">
                            <v-icon color="grey lighten-1" size="32" class="mb-2">mdi-export</v-icon>
                            <div class="no-data-text">No outputs recorded</div>
                          </div>
                          <div v-else class="data-display">
                            <div
                              v-for="(value, key) in step.outputs"
                              :key="key"
                              class="data-item"
                            >
                              <div class="data-header">
                                <span class="data-key-enhanced">{{ key }}</span>
                                <v-chip x-small color="green" outlined>
                                  {{ typeof value }}
                                </v-chip>
                              </div>
                              <div class="data-value-enhanced">
                                <div v-if="typeof value === 'string' && value.length > 200" class="expandable-content">
                                  <div class="content-preview">{{ value.substring(0, 200) }}...</div>
                                  <v-btn
                                    text
                                    x-small
                                    color="primary"
                                    class="expand-btn"
                                    @click="step[`showFullOutput_${key}`] = !step[`showFullOutput_${key}`]"
                                  >
                                    <v-icon left x-small>{{ step[`showFullOutput_${key}`] ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                                    {{ step[`showFullOutput_${key}`] ? 'Show Less' : 'Show More' }}
                                  </v-btn>
                                  <div v-if="step[`showFullOutput_${key}`]" class="full-content">
                                    <pre class="content-code">{{ value }}</pre>
                                  </div>
                                </div>
                                <div v-else class="simple-content">
                                  <template v-if="typeof value === 'object' && value !== null && Object.keys(value).length === 2 && 'reason' in value && 'result' in value">
                                    <div class="object-format">
                                      <div v-if="value.result" style="padding-bottom:10px">
                                        <pre class="content-code">{{ value.result }}</pre>
                                      </div>
                                      <div v-if="value.reason" class="reason-block">
                                        <strong>Reason:</strong>
                                        <pre class="content-code">{{ value.reason }}</pre>
                                      </div>
                                      <div v-if="!value.reason && !value.result">
                                        <pre class="content-code">{{ value }}</pre>
                                      </div>
                                    </div>
                                  </template>
                                  <template v-else>
                                    <pre class="content-code">{{ value }}</pre>
                                  </template>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </v-tab-item>

                      <v-tab-item v-if="step.step_type === 'prompt'">
                        <div class="tab-content-enhanced"  v-if="step.toggleViewDetails">
                          <div class="prompt-display">
                            <!-- Version Info Banner -->
                            <div v-if="step.prompt_version" class="prompt-version-banner" :class="{ 'historical-version': step.prompt_version !== step.current_version }">
                              <div class="version-info">
                                <v-icon small :color="step.prompt_version !== step.current_version ? 'warning' : 'success'" class="mr-2">
                                  {{ step.prompt_version !== step.current_version ? 'mdi-history' : 'mdi-check-circle' }}
                                </v-icon>
                                <span class="version-text">
                                  <strong>Prompt Version {{ step.prompt_version }}</strong>
                                  <span v-if="step.prompt_version !== step.current_version" class="historical-label">
                                    (Historical - Current: v{{ step.current_version }})
                                  </span>
                                  <span v-else class="current-label">(Current)</span>
                                </span>
                              </div>
                              <div v-if="step.model_used" class="model-info">
                                <v-icon x-small class="mr-1">mdi-brain</v-icon>
                                <span>{{ step.model_used }}</span>
                              </div>
                            </div>

                            <div class="prompt-section-enhanced" v-if="step.system_prompt">
                              <div class="prompt-section-header">
                                <v-icon small color="purple" class="mr-2">mdi-account-cog</v-icon>
                                <span class="prompt-section-title">System Prompt</span>
                              </div>
                              <pre class="prompt-content">{{ step.system_prompt }}</pre>
                            </div>
                            <div class="prompt-section-enhanced" v-if="step.user_prompt">
                              <div class="prompt-section-header">
                                <v-icon small color="blue" class="mr-2">mdi-account</v-icon>
                                <span class="prompt-section-title">User Prompt</span>
                              </div>
                              <pre class="prompt-content">{{ step.user_prompt }}</pre>
                            </div>
                          </div>
                        </div>
                      </v-tab-item>

                      <v-tab-item v-if="step.raw_response">
                        <div class="tab-content-enhanced" v-if="step.toggleViewDetails">
                          <div class="raw-response-display">
                            <div class="response-header">
                              <v-icon small color="orange" class="mr-2">mdi-code-json</v-icon>
                              <span class="response-title">Raw API Response</span>
                              <v-chip x-small color="orange" outlined class="ml-2">
                                {{ typeof step.raw_response }}
                              </v-chip>
                            </div>
                            <pre class="response-content">{{ typeof step.raw_response === 'string' ? step.raw_response : JSON.stringify(step.raw_response, null, 2) }}</pre>
                          </div>
                        </div>
                      </v-tab-item>

                      <v-tab-item v-if="step.output_data && step.output_data._validation_attempts">
                        <div class="tab-content-enhanced" v-if="step.toggleViewDetails">
                          <div class="validation-history-display">
                            <div class="validation-history-header mb-3">
                              <v-icon small color="orange" class="mr-2">mdi-check-circle-outline</v-icon>
                              <span class="response-title">Validation Retry History</span>
                              <v-chip x-small color="orange" outlined class="ml-2">
                                {{ step.output_data._validation_attempts.length }} attempt(s)
                              </v-chip>
                            </div>

                            <div class="validation-attempts-list">
                              <div
                                v-for="(attempt, index) in step.output_data._validation_attempts"
                                :key="index"
                                class="validation-attempt-card"
                              >
                                <div class="attempt-header">
                                  <div class="attempt-number">
                                    <v-icon small :color="attempt.validationPassed ? 'success' : 'error'" class="mr-2">
                                      {{ attempt.validationPassed ? 'mdi-check-circle' : 'mdi-alert-circle' }}
                                    </v-icon>
                                    <span class="attempt-label">Attempt #{{ attempt.attempt }}</span>
                                  </div>
                                  <v-chip
                                    x-small
                                    :color="attempt.validationPassed ? 'success' : 'error'"
                                    text-color="white"
                                  >
                                    {{ attempt.validationPassed ? 'Passed' : 'Failed' }}
                                  </v-chip>
                                </div>

                                <div v-if="!attempt.validationPassed && attempt.errors" class="attempt-errors">
                                  <div class="errors-header">
                                    <v-icon small color="error" class="mr-1">mdi-alert-circle</v-icon>
                                    <span class="errors-title">Validation Errors:</span>
                                  </div>
                                  <div class="errors-list">
                                    <div
                                      v-for="(error, errorIndex) in attempt.errors"
                                      :key="errorIndex"
                                      class="error-item"
                                    >
                                      <v-icon x-small color="error" class="mr-2">mdi-circle-small</v-icon>
                                      <span>{{ error }}</span>
                                    </div>
                                  </div>
                                </div>

                                <div v-if="attempt.response" class="attempt-response">
                                  <v-expansion-panels flat>
                                    <v-expansion-panel>
                                      <v-expansion-panel-header class="response-toggle">
                                        <div class="d-flex align-center">
                                          <v-icon small color="grey" class="mr-2">mdi-code-json</v-icon>
                                          <span>View Response</span>
                                        </div>
                                      </v-expansion-panel-header>
                                      <v-expansion-panel-content>
                                        <pre class="response-content-small">{{ typeof attempt.response === 'string' ? attempt.response : JSON.stringify(attempt.response, null, 2) }}</pre>
                                      </v-expansion-panel-content>
                                    </v-expansion-panel>
                                  </v-expansion-panels>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </v-tab-item>
                    </v-tabs>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Report View -->
    <div v-else-if="showReportView" class="report-view">
      <div class="report-header">
        <v-btn
          icon
          small
          @click="closeReportView"
          class="mr-3"
        >
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>

        <div style="flex: 1;">
          <h2 class="report-title">
            <v-icon left>mdi-chart-line</v-icon>
            <span v-if="!selectedExecutionForReport">Chain Performance Report</span>
            <span v-else>Execution Report</span>
          </h2>
          <p class="report-subtitle" v-if="selectedChainForReport">
            {{ selectedChainForReport.chain_name }}
          </p>
          <p class="report-subtitle" v-if="selectedExecutionForReport">
            Execution ID: {{ selectedExecutionForReport.execution_id }}
            <v-chip x-small :color="getStatusColor(selectedExecutionForReport.execution_status)" text-color="white" class="ml-2">
              {{ selectedExecutionForReport.execution_status }}
            </v-chip>
          </p>
        </div>
      </div>

      <!-- Report Content -->
      <div class="report-content">
        <div v-if="loadingReportData" class="text-center pa-8">
          <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
          <div class="mt-4">Loading report data...</div>
        </div>

        <div v-else-if="!reportData" class="text-center pa-8 grey--text">
          <v-icon size="64" color="grey lighten-1" class="mb-3">mdi-chart-line-variant</v-icon>
          <div class="text-h6 mb-2">No Report Data Available</div>
          <div class="text-body-2">
            This chain has not been executed yet or no data is available for reporting.
          </div>
        </div>

        <div v-else class="report-dashboard">
          <!-- Summary Cards -->
          <v-row class="mb-4">
            <!-- Chain-level summary cards -->
            <template v-if="!selectedExecutionForReport">
              <v-col cols="12" md="4">
                <v-card class="report-summary-card">
                  <v-card-text>
                    <div class="summary-card-header">
                      <v-icon color="primary" large>mdi-play-circle</v-icon>
                      <span class="summary-card-label">Total Executions</span>
                    </div>
                    <div class="summary-card-value">{{ reportData.summary?.total_executions || 0 }}</div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" md="4">
                <v-card class="report-summary-card">
                  <v-card-text>
                    <div class="summary-card-header">
                      <v-icon color="success" large>mdi-check-circle</v-icon>
                      <span class="summary-card-label">Success Rate</span>
                    </div>
                    <div class="summary-card-value">
                      {{ reportData.summary?.success_rate !== undefined ? `${reportData.summary.success_rate.toFixed(1)}%` : 'N/A' }}
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" md="4">
                <v-card class="report-summary-card">
                  <v-card-text>
                    <div class="summary-card-header">
                      <v-icon color="orange" large>mdi-refresh</v-icon>
                      <span class="summary-card-label">Avg Validation Retries</span>
                    </div>
                    <div class="summary-card-value">
                      {{ reportData.summary?.avg_validation_attempts !== undefined ? reportData.summary.avg_validation_attempts.toFixed(1) : 'N/A' }}
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </template>

            <!-- Execution-level summary cards -->
            <template v-else-if="reportData.execution_specific">
              <v-col cols="12" md="3">
                <v-card class="report-summary-card">
                  <v-card-text>
                    <div class="summary-card-header">
                      <v-icon color="primary" large>mdi-format-list-checks</v-icon>
                      <span class="summary-card-label">Total Steps</span>
                    </div>
                    <div class="summary-card-value">{{ reportData.execution_specific.total_steps }}</div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" md="3">
                <v-card class="report-summary-card">
                  <v-card-text>
                    <div class="summary-card-header">
                      <v-icon color="success" large>mdi-check-circle</v-icon>
                      <span class="summary-card-label">Completed Steps</span>
                    </div>
                    <div class="summary-card-value">{{ reportData.execution_specific.completed_steps }}</div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" md="3">
                <v-card class="report-summary-card">
                  <v-card-text>
                    <div class="summary-card-header">
                      <v-icon color="orange" large>mdi-refresh</v-icon>
                      <span class="summary-card-label">Avg Validation Attempts</span>
                    </div>
                    <div class="summary-card-value">
                      {{ reportData.execution_specific.avg_validation_attempts?.toFixed(1) || '0.0' }}
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" md="3">
                <v-card class="report-summary-card">
                  <v-card-text>
                    <div class="summary-card-header">
                      <v-icon color="info" large>mdi-clock-outline</v-icon>
                      <span class="summary-card-label">Total Duration</span>
                    </div>
                    <div class="summary-card-value">{{ reportData.execution_specific.total_duration_seconds }}s</div>
                  </v-card-text>
                </v-card>
              </v-col>
            </template>
          </v-row>

          <!-- Execution-Specific Details -->
          <v-card v-if="selectedExecutionForReport && reportData.execution_specific" class="mb-4">
            <v-card-title>
              <v-icon left color="primary">mdi-format-list-checks</v-icon>
              Step-by-Step Breakdown
            </v-card-title>
            <v-card-subtitle>
              Total Duration: {{ reportData.execution_specific.total_duration_seconds }}s |
              Steps: {{ reportData.execution_specific.completed_steps }}/{{ reportData.execution_specific.total_steps }} completed
              <span v-if="reportData.execution_specific.failed_steps > 0" class="error--text">
                ({{ reportData.execution_specific.failed_steps }} failed)
              </span>
            </v-card-subtitle>
            <v-card-text>
              <v-data-table
                :headers="[
                  { text: 'Step Name', value: 'step_name' },
                  { text: 'Version', value: 'prompt_version' },
                  { text: 'Model', value: 'model_used' },
                  { text: 'Status', value: 'status' },
                  { text: 'Duration (s)', value: 'duration_seconds' },
                  { text: 'Validation Attempts', value: 'validation_attempts' }
                ]"
                :items="reportData.execution_specific.step_breakdown || []"
                :items-per-page="20"
                class="elevation-0"
              >
                <template v-slot:[`item.prompt_version`]="{ item }">
                  <v-chip v-if="item.prompt_version" x-small outlined color="primary">
                    v{{ item.prompt_version }}
                  </v-chip>
                  <span v-else class="grey--text">N/A</span>
                </template>

                <template v-slot:[`item.model_used`]="{ item }">
                  <v-chip v-if="item.model_used" x-small outlined color="purple">
                    <v-icon left x-small>mdi-brain</v-icon>
                    {{ item.model_used }}
                  </v-chip>
                  <span v-else class="grey--text">N/A</span>
                </template>

                <template v-slot:[`item.status`]="{ item }">
                  <v-chip
                    x-small
                    :color="getStepStatusColor(item.status)"
                    text-color="white"
                  >
                    <v-icon left x-small>{{ getStepStatusIcon(item.status) }}</v-icon>
                    {{ getStepStatusLabel(item.status) }}
                  </v-chip>
                </template>

                <template v-slot:[`item.duration_seconds`]="{ item }">
                  {{ item.duration_seconds !== null ? item.duration_seconds : 'N/A' }}
                </template>

                <template v-slot:[`item.validation_attempts`]="{ item }">
                  <v-chip
                    v-if="item.validation_attempts > 0"
                    x-small
                    :color="item.validation_attempts <= 1 ? 'success' : item.validation_attempts <= 3 ? 'warning' : 'error'"
                    text-color="white"
                  >
                    {{ item.validation_attempts }}
                  </v-chip>
                  <span v-else class="grey--text">0</span>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>

          <!-- Tabs for different views -->
          <v-card v-if="!selectedExecutionForReport">
            <v-tabs v-model="reportTab" :background-color="$vuetify.theme.dark ? '' : 'grey lighten-4'">
              <v-tab>
                <v-icon left>mdi-chart-areaspline</v-icon>
                Success/Fail Trend
              </v-tab>
              <v-tab>
                <v-icon left>mdi-alert-decagram</v-icon>
                Validation Analysis
              </v-tab>
              <v-tab>
                <v-icon left>mdi-file-document-multiple</v-icon>
                By Prompt Version
              </v-tab>
              <v-tab>
                <v-icon left>mdi-brain</v-icon>
                By Model
              </v-tab>
            </v-tabs>

            <v-tabs-items v-model="reportTab">
              <!-- Success/Fail Trend Tab -->
              <v-tab-item>
                <v-card-text>
                  <h3 class="mb-4">Execution Success/Failure Trend Over Time</h3>
                  <p class="mb-4 grey--text text-body-2">
                    Click on a row with failed executions to see details and error messages.
                  </p>
                  <div v-if="reportData.trend_data && reportData.trend_data.length > 0">
                    <v-data-table
                      :headers="[
                        { text: '', value: 'data-table-expand' },
                        { text: 'Date', value: 'date' },
                        { text: 'Total', value: 'total_executions' },
                        { text: 'Successful', value: 'successful_executions' },
                        { text: 'Failed', value: 'failed_executions' },
                        { text: 'Success Rate', value: 'success_rate' }
                      ]"
                      :items="reportData.trend_data"
                      :items-per-page="10"
                      :expanded.sync="expandedTrendRows"
                      item-key="date"
                      show-expand
                      class="elevation-1"
                    >
                      <template v-slot:[`item.failed_executions`]="{ item }">
                        <span :class="item.failed_executions > 0 ? 'error--text font-weight-bold' : ''">
                          {{ item.failed_executions }}
                        </span>
                      </template>

                      <template v-slot:[`item.success_rate`]="{ item }">
                        <v-chip
                          small
                          :color="item.success_rate >= 80 ? 'success' : item.success_rate >= 50 ? 'warning' : 'error'"
                          text-color="white"
                        >
                          {{ item.success_rate.toFixed(1) }}%
                        </v-chip>
                      </template>

                      <template v-slot:expanded-item="{ headers, item }">
                        <td :colspan="headers.length" class="pa-0" style="max-width: 100%;">
                          <div v-if="loadingTrendDetails[item.date]" class="pa-4 text-center">
                            <v-progress-circular indeterminate color="primary" size="24"></v-progress-circular>
                            <span class="ml-2">Loading failed executions...</span>
                          </div>
                          <div v-else-if="trendDetails[item.date] && trendDetails[item.date].length > 0" class="pa-4 report-expanded-content">
                            <h4 class="mb-3">Failed Executions on {{ item.date }}</h4>
                            <v-simple-table dense>
                              <template v-slot:default>
                                <thead>
                                  <tr>
                                    <th>Execution ID</th>
                                    <th>Started At</th>
                                    <th>User</th>
                                    <th>Error Message</th>
                                    <th>Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr v-for="exec in trendDetails[item.date]" :key="exec.execution_id">
                                    <td>{{ exec.execution_id }}</td>
                                    <td>{{ formatDateTime(exec.started_at) }}</td>
                                    <td>{{ exec.user_name || 'N/A' }}</td>
                                    <td class="error--text" style="max-width: 400px;">
                                      <v-tooltip top max-width="500" v-if="exec.error_message && exec.error_message.length > 80">
                                        <template v-slot:activator="{ on, attrs }">
                                          <span v-bind="attrs" v-on="on" style="cursor: help;">
                                            {{ truncateText(exec.error_message, 80) }}
                                          </span>
                                        </template>
                                        <span>{{ exec.error_message }}</span>
                                      </v-tooltip>
                                      <span v-else>{{ exec.error_message || 'No error message recorded' }}</span>
                                    </td>
                                    <td>
                                      <v-btn x-small text color="primary" @click="viewExecutionDetails({ execution_id: exec.execution_id })">
                                        View Details
                                      </v-btn>
                                    </td>
                                  </tr>
                                </tbody>
                              </template>
                            </v-simple-table>
                          </div>
                          <div v-else class="pa-4 text-center grey--text">
                            No failed executions on this date
                          </div>
                        </td>
                      </template>
                    </v-data-table>
                  </div>
                  <div v-else class="text-center pa-8 grey--text">
                    No trend data available
                  </div>
                </v-card-text>
              </v-tab-item>

              <!-- Validation Analysis Tab -->
              <v-tab-item>
                <v-card-text>
                  <h3 class="mb-4">Validation Retry Analysis</h3>
                  <p class="mb-4 grey--text text-body-2">
                    Track validation retry patterns to identify prompts that are deviating from expected output formats. Click on a row to see validation error details.
                  </p>
                  <div v-if="reportData.validation_analysis && reportData.validation_analysis.length > 0">
                    <v-data-table
                      :headers="[
                        { text: '', value: 'data-table-expand' },
                        { text: 'Step Name', value: 'step_name' },
                        { text: 'Total Executions', value: 'total_executions' },
                        { text: 'Avg Retries', value: 'avg_retries' },
                        { text: 'Max Retries', value: 'max_retries' },
                        { text: 'Failure Rate', value: 'failure_rate' }
                      ]"
                      :items="reportData.validation_analysis"
                      :items-per-page="10"
                      :expanded.sync="expandedValidationRows"
                      item-key="step_name"
                      show-expand
                      class="elevation-1"
                    >
                      <template v-slot:[`item.avg_retries`]="{ item }">
                        <v-chip
                          small
                          :color="item.avg_retries <= 1 ? 'success' : item.avg_retries <= 3 ? 'warning' : 'error'"
                          text-color="white"
                        >
                          {{ item.avg_retries.toFixed(2) }}
                        </v-chip>
                      </template>
                      <template v-slot:[`item.max_retries`]="{ item }">
                        <span :class="item.max_retries > 3 ? 'error--text font-weight-bold' : ''">
                          {{ item.max_retries }}
                        </span>
                      </template>
                      <template v-slot:[`item.failure_rate`]="{ item }">
                        <span :class="item.failure_rate > 20 ? 'error--text font-weight-bold' : ''">
                          {{ item.failure_rate.toFixed(1) }}%
                        </span>
                      </template>

                      <template v-slot:expanded-item="{ headers, item }">
                        <td :colspan="headers.length" class="pa-0" style="max-width: 100%;">
                          <div v-if="loadingValidationDetails[item.step_name]" class="pa-4 text-center">
                            <v-progress-circular indeterminate color="primary" size="24"></v-progress-circular>
                            <span class="ml-2">Loading validation errors...</span>
                          </div>
                          <div v-else-if="validationDetails[item.step_name] && validationDetails[item.step_name].length > 0" class="pa-4 report-expanded-content">
                            <div class="d-flex justify-space-between align-center mb-3" style="flex-wrap: wrap; gap: 12px;">
                              <h4 style="margin: 0;">Validation Errors for "{{ item.step_name }}"</h4>
                              <div class="d-flex align-center" style="gap: 8px; flex-wrap: wrap;">
                                <v-chip-group
                                  v-model="validationFilters[item.step_name]"
                                  active-class="primary--text"
                                  mandatory
                                >
                                  <v-chip small outlined>
                                    <v-icon left small>mdi-format-list-bulleted</v-icon>
                                    All
                                  </v-chip>
                                  <v-chip small outlined>
                                    <v-icon left small>mdi-alert-circle</v-icon>
                                    Only Failures
                                  </v-chip>
                                </v-chip-group>
                                <v-divider vertical class="mx-2"></v-divider>
                                <v-chip-group
                                  v-model="validationGrouping[item.step_name]"
                                  active-class="primary--text"
                                  mandatory
                                >
                                  <v-chip small outlined>
                                    <v-icon left small>mdi-view-list</v-icon>
                                    List
                                  </v-chip>
                                  <v-chip small outlined>
                                    <v-icon left small>mdi-calendar-range</v-icon>
                                    Group by Date
                                  </v-chip>
                                </v-chip-group>
                              </div>
                            </div>

                            <!-- Flat List View -->
                            <div v-if="!validationGrouping[item.step_name] || validationGrouping[item.step_name] === 0" style="width: 100%; overflow-x: auto;">
                              <v-simple-table dense>
                                <template v-slot:default>
                                  <thead>
                                    <tr>
                                      <th>Execution ID</th>
                                      <th>Date</th>
                                      <th>Retry Count</th>
                                      <th>Validation Errors</th>
                                      <th>Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr v-for="detail in getFilteredValidationDetails(item.step_name)" :key="detail.execution_id">
                                      <td>{{ detail.execution_id }}</td>
                                      <td>{{ formatDateTime(detail.execution_date) }}</td>
                                      <td>
                                        <v-chip
                                          x-small
                                          :color="detail.retry_count <= 0 ? 'success' : detail.retry_count <= 2 ? 'warning' : 'error'"
                                          text-color="white"
                                        >
                                          {{ detail.retry_count }}
                                        </v-chip>
                                      </td>
                                      <td style="max-width: 400px;">
                                        <div v-if="detail.validation_errors && detail.validation_errors.length > 0" class="validation-errors" style="max-width: 100%;">
                                          <v-tooltip
                                            v-for="(error, idx) in detail.validation_errors.slice(0, 3)"
                                            :key="idx"
                                            top
                                            max-width="500"
                                          >
                                            <template v-slot:activator="{ on, attrs }">
                                              <v-chip
                                                x-small
                                                color="error"
                                                outlined
                                                class="ma-1"
                                                v-bind="attrs"
                                                v-on="on"
                                                style="max-width: 350px;"
                                              >
                                                <span style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                                                  {{ truncateText(error, 60) }}
                                                </span>
                                              </v-chip>
                                            </template>
                                            <span>{{ error }}</span>
                                          </v-tooltip>
                                          <v-chip
                                            v-if="detail.validation_errors.length > 3"
                                            x-small
                                            outlined
                                            class="ma-1"
                                          >
                                            +{{ detail.validation_errors.length - 3 }} more
                                          </v-chip>
                                        </div>
                                        <span v-else class="grey--text">No specific errors recorded</span>
                                      </td>
                                      <td>
                                        <v-btn x-small text color="primary" @click="viewExecutionDetails({ execution_id: detail.execution_id })">
                                          View Full Details
                                        </v-btn>
                                      </td>
                                    </tr>
                                  </tbody>
                                </template>
                              </v-simple-table>
                            </div>

                            <!-- Grouped by Date View -->
                            <div v-else style="width: 100%; overflow-x: auto;">
                              <div v-for="(group, date) in getGroupedValidationDetails(item.step_name)" :key="date" class="mb-4">
                                <div class="d-flex align-center mb-2 report-date-group-header">
                                  <v-icon small color="primary" class="mr-2">mdi-calendar</v-icon>
                                  <strong class="mr-3">{{ date }}</strong>
                                  <v-chip x-small color="primary" outlined>
                                    {{ group.length }} execution(s)
                                  </v-chip>
                                  <v-spacer></v-spacer>
                                  <v-chip
                                    x-small
                                    :color="group.filter(d => d.retry_count > 0).length > 0 ? 'error' : 'success'"
                                    text-color="white"
                                  >
                                    {{ group.filter(d => d.retry_count > 0).length }} with retries
                                  </v-chip>
                                </div>
                                <v-simple-table dense>
                                  <template v-slot:default>
                                    <thead>
                                      <tr>
                                        <th>Execution ID</th>
                                        <th>Time</th>
                                        <th>Retry Count</th>
                                        <th>Validation Errors</th>
                                        <th>Actions</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      <tr v-for="detail in group" :key="detail.execution_id">
                                        <td>{{ detail.execution_id }}</td>
                                        <td>{{ formatTime(detail.execution_date) }}</td>
                                        <td>
                                          <v-chip
                                            x-small
                                            :color="detail.retry_count <= 0 ? 'success' : detail.retry_count <= 2 ? 'warning' : 'error'"
                                            text-color="white"
                                          >
                                            {{ detail.retry_count }}
                                          </v-chip>
                                        </td>
                                        <td style="max-width: 400px;">
                                          <div v-if="detail.validation_errors && detail.validation_errors.length > 0" class="validation-errors" style="max-width: 100%;">
                                            <v-tooltip
                                              v-for="(error, idx) in detail.validation_errors.slice(0, 3)"
                                              :key="idx"
                                              top
                                              max-width="500"
                                            >
                                              <template v-slot:activator="{ on, attrs }">
                                                <v-chip
                                                  x-small
                                                  color="error"
                                                  outlined
                                                  class="ma-1"
                                                  v-bind="attrs"
                                                  v-on="on"
                                                  style="max-width: 350px;"
                                                >
                                                  <span style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                                                    {{ truncateText(error, 60) }}
                                                  </span>
                                                </v-chip>
                                              </template>
                                              <span>{{ error }}</span>
                                            </v-tooltip>
                                            <v-chip
                                              v-if="detail.validation_errors.length > 3"
                                              x-small
                                              outlined
                                              class="ma-1"
                                            >
                                              +{{ detail.validation_errors.length - 3 }} more
                                            </v-chip>
                                          </div>
                                          <span v-else class="grey--text">No specific errors recorded</span>
                                        </td>
                                        <td>
                                          <v-btn x-small text color="primary" @click="viewExecutionDetails({ execution_id: detail.execution_id })">
                                            View Full Details
                                          </v-btn>
                                        </td>
                                      </tr>
                                    </tbody>
                                  </template>
                                </v-simple-table>
                              </div>
                            </div>

                            <div v-if="getFilteredValidationDetails(item.step_name).length === 0" class="text-center pa-4 grey--text">
                              No items match the current filter
                            </div>
                          </div>
                          <div v-else class="pa-4 text-center grey--text">
                            No validation error details available
                          </div>
                        </td>
                      </template>
                    </v-data-table>
                  </div>
                  <div v-else class="text-center pa-8 grey--text">
                    No validation data available
                  </div>
                </v-card-text>
              </v-tab-item>

              <!-- By Prompt Version Tab -->
              <v-tab-item>
                <v-card-text>
                  <h3 class="mb-4">Performance by Prompt Version</h3>
                  <p class="mb-4 grey--text">
                    Compare execution success rates across different prompt versions to identify improvements or regressions.
                  </p>
                  <div v-if="reportData.by_version && reportData.by_version.length > 0">
                    <v-data-table
                      :headers="[
                        { text: 'Step Name', value: 'step_name' },
                        { text: 'Version', value: 'prompt_version' },
                        { text: 'Executions', value: 'execution_count' },
                        { text: 'Success Rate', value: 'success_rate' },
                        { text: 'Avg Retries', value: 'avg_validation_retries' },
                        { text: 'Avg Duration (s)', value: 'avg_duration' }
                      ]"
                      :items="reportData.by_version"
                      :items-per-page="15"
                      class="elevation-1"
                      @click:row="drilldownPromptVersion"
                    >
                      <template v-slot:[`item.prompt_version`]="{ item }">
                        <v-chip small outlined color="primary">
                          v{{ item.prompt_version }}
                        </v-chip>
                      </template>
                      <template v-slot:[`item.success_rate`]="{ item }">
                        <v-chip
                          small
                          :color="item.success_rate >= 80 ? 'success' : item.success_rate >= 50 ? 'warning' : 'error'"
                          text-color="white"
                        >
                          {{ item.success_rate.toFixed(1) }}%
                        </v-chip>
                      </template>
                      <template v-slot:[`item.avg_validation_retries`]="{ item }">
                        {{ item.avg_validation_retries !== null ? item.avg_validation_retries.toFixed(2) : 'N/A' }}
                      </template>
                      <template v-slot:[`item.avg_duration`]="{ item }">
                        {{ item.avg_duration !== null ? item.avg_duration.toFixed(1) : 'N/A' }}
                      </template>
                    </v-data-table>
                  </div>
                  <div v-else class="text-center pa-8 grey--text">
                    No version data available
                  </div>
                </v-card-text>
              </v-tab-item>

              <!-- By Model Tab -->
              <v-tab-item>
                <v-card-text>
                  <h3 class="mb-4">Performance by AI Model</h3>
                  <p class="mb-4 grey--text">
                    Compare execution performance across different AI models to optimize model selection.
                  </p>
                  <div v-if="reportData.by_model && reportData.by_model.length > 0">
                    <v-data-table
                      :headers="[
                        { text: 'Step Name', value: 'step_name' },
                        { text: 'Model', value: 'model_used' },
                        { text: 'Executions', value: 'execution_count' },
                        { text: 'Success Rate', value: 'success_rate' },
                        { text: 'Avg Retries', value: 'avg_validation_retries' },
                        { text: 'Avg Duration (s)', value: 'avg_duration' }
                      ]"
                      :items="reportData.by_model"
                      :items-per-page="15"
                      class="elevation-1"
                      @click:row="drilldownModel"
                    >
                      <template v-slot:[`item.model_used`]="{ item }">
                        <v-chip small outlined color="purple">
                          <v-icon left x-small>mdi-brain</v-icon>
                          {{ item.model_used }}
                        </v-chip>
                      </template>
                      <template v-slot:[`item.success_rate`]="{ item }">
                        <v-chip
                          small
                          :color="item.success_rate >= 80 ? 'success' : item.success_rate >= 50 ? 'warning' : 'error'"
                          text-color="white"
                        >
                          {{ item.success_rate.toFixed(1) }}%
                        </v-chip>
                      </template>
                      <template v-slot:[`item.avg_validation_retries`]="{ item }">
                        {{ item.avg_validation_retries !== null ? item.avg_validation_retries.toFixed(2) : 'N/A' }}
                      </template>
                      <template v-slot:[`item.avg_duration`]="{ item }">
                        {{ item.avg_duration !== null ? item.avg_duration.toFixed(1) : 'N/A' }}
                      </template>
                    </v-data-table>
                  </div>
                  <div v-else class="text-center pa-8 grey--text">
                    No model data available
                  </div>
                </v-card-text>
              </v-tab-item>
            </v-tabs-items>
          </v-card>
        </div>
      </div>
    </div>

    <!-- Chain Builder -->
    <div v-else class="builder-view">
      <!-- Header -->
      <div class="header-bar">
        <v-btn
          icon
          small
          @click="backToLanding"
          class="mr-3"
        >
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>

        <v-text-field
          v-model="chain.chain_name"
          class="chain-name-input"
          hide-details
          solo
          flat
          :readonly="isViewingSystemChain"
        ></v-text-field>
        <v-chip v-if="isSystemChainMode" small outlined :color="isViewingSystemChain ? 'grey' : 'teal'" class="ml-2">
          <v-icon left small>{{ isViewingSystemChain ? 'mdi-lock' : 'mdi-cog-outline' }}</v-icon>
          {{ isViewingSystemChain ? 'System Chain (View Only)' : 'System Chain' }}
        </v-chip>

        <div class="tab-navigation ml-4 mr-4">
          <v-btn
            text
            :class="['tab-btn', { 'tab-active': activeTab === 'builder' }]"
            @click="activeTab = 'builder'"
          >
            Chain Builder
          </v-btn>
          <v-btn
            text
            :class="['tab-btn', { 'tab-active': activeTab === 'triggers' }]"
            @click="activeTab = 'triggers'"
          >
            <v-icon left small>mdi-flash</v-icon>
            Triggers
            <v-chip v-if="chain.triggers.length > 0" x-small color="purple" text-color="white" class="ml-2">
              {{ chain.triggers.length }}
            </v-chip>
          </v-btn>
          <v-btn
            text
            :class="['tab-btn', { 'tab-active': activeTab === 'schema' }]"
            @click="activeTab = 'schema'"
          >
            <v-icon left small>mdi-code-braces</v-icon>
            Schema
            <v-chip v-if="activeChainInputs.length > 0" x-small color="blue" text-color="white" class="ml-2">
              {{ activeChainInputs.length }}
            </v-chip>
          </v-btn>
        </div>

        <v-spacer></v-spacer>

        <!-- View-only system chain: only show Copy to Template -->
        <template v-if="isViewingSystemChain">
          <v-btn
            color="success"
            dark
            class="mr-2"
            small
            @click="copyChainToTemplate({ prompt_chain_id: chain.prompt_chain_id, chain_name: chain.chain_name })"
          >
            <v-icon left small>mdi-content-copy</v-icon>
            Copy to Template
          </v-btn>
        </template>

        <!-- Editable mode (template chains and new/editable system chains) -->
        <template v-else>
          <v-btn
            v-if="activeTab === 'builder'"
            color="#5E2872"
            dark
            class="mr-2"
            small
            @click="showServiceModal = true"
          >
            <v-icon left small>mdi-cog</v-icon>
            Manage Services
          </v-btn>

          <v-btn
            color="#50B3CB"
            dark
            class="mr-2"
            small
            @click="saveChain"
          >
            <v-icon left small>mdi-content-save</v-icon>
            Save Chain
          </v-btn>

          <v-btn
            v-if="hasApiTrigger"
            color="#10B981"
            dark
            class="mr-2"
            small
            @click="openTestChainDialog"
          >
            <v-icon left small>mdi-play-circle</v-icon>
            Test Chain
          </v-btn>

          <v-btn
            color="#9333EA"
            dark
            class="mr-2"
            small
            @click="exportChain"
          >
            <v-icon left small>mdi-export</v-icon>
            Export
          </v-btn>

          <v-btn
            color="#9333EA"
            dark
            class="mr-2"
            small
            @click="openImportDialog"
          >
            <v-icon left small>mdi-import</v-icon>
            Import
          </v-btn>
        </template>
      </div>

      <!-- Builder Tab -->
      <div v-if="activeTab === 'builder'" class="main-content">
        <!-- Sidebar (hidden in system chain view mode) -->
        <div v-if="!isViewingSystemChain" class="sidebar">
          <div class="sidebar-header">
            <h3 class="sidebar-title">Available Components</h3>
            <v-text-field
              v-model="searchQuery"
              placeholder="Search prompts & services..."
              outlined
              dense
              hide-details
              prepend-inner-icon="mdi-magnify"
            ></v-text-field>
          </div>

          <div class="sidebar-content">
            <v-expansion-panels
              v-model="expandedPanels"
              multiple
              flat
            >
              <v-expansion-panel
                v-for="category in promptCategories"
                :key="category.type"
              >
                <v-expansion-panel-header class="category-header">
                  <div class="d-flex align-center">
                    <span class="category-name">{{ category.name }}</span>
                    <v-chip x-small class="ml-auto">{{ category.items.length }}</v-chip>
                  </div>
                </v-expansion-panel-header>

                <v-expansion-panel-content class="category-content">
                  <template v-for="item in filteredItems(category.items)">
                    <!-- Service Parent (non-draggable header) -->
                    <div
                      v-if="item && item.itemType === 'service_parent'"
                      :key="item.id"
                      class="service-parent-item"
                    >
                      <v-icon small class="mr-2" @click="toggleServiceExpanded(item)">
                        {{ item.isExpanded ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
                      </v-icon>
                      <div class="item-icon service-icon" @click="toggleServiceExpanded(item)">
                        <v-icon small color="purple">mdi-api</v-icon>
                      </div>
                      <div class="item-content" @click="toggleServiceExpanded(item)">
                        <div class="item-name">{{ item.name }}</div>
                        <div class="item-meta">
                          <span>{{ item.endpoint_count }} endpoint{{ item.endpoint_count !== 1 ? 's' : '' }}</span>
                        </div>
                      </div>
                      <v-btn icon x-small @click.stop="editService(item)">
                        <v-icon small>mdi-pencil</v-icon>
                      </v-btn>
                    </div>

                    <!-- Service Endpoint (draggable) -->
                    <div
                      v-if="item && item.itemType === 'service' && item.isEndpoint && isServiceExpanded(item.parentServiceId)"
                      :key="item.id"
                      class="draggable-item service-endpoint-item"
                      draggable="true"
                      @dragstart="onDragStart($event, item)"
                      @dragend="onDragEnd"
                    >
                      <div class="item-icon service-icon">
                        <v-icon small color="purple">mdi-flash</v-icon>
                      </div>

                      <div class="item-content">
                        <div class="item-name">
                          <v-chip x-small class="mr-1" color="purple" text-color="white">{{ item.http_method }}</v-chip>
                          {{ item.name }}
                        </div>
                        <div class="item-meta">
                          <span class="endpoint-path">{{ item.endpoint_path }}</span>
                        </div>
                      </div>

                      <v-icon small color="grey">mdi-drag-vertical</v-icon>
                    </div>

                    <!-- Other items (prompts, formatters) -->
                    <div
                      v-if="item && item.itemType !== 'service_parent' && (!item.isEndpoint || !item.itemType === 'service')"
                      :key="item.id"
                      class="draggable-item"
                      draggable="true"
                      @dragstart="onDragStart($event, item)"
                      @dragend="onDragEnd"
                    >
                      <div :class="['item-icon', item.itemType === 'service' ? 'service-icon' : item.itemType === 'output_formatter' ? 'formatter-icon' : item.itemType === 'chain' ? 'chain-icon' : 'prompt-icon']">
                        <v-icon small :color="item.itemType === 'service' ? 'purple' : item.itemType === 'output_formatter' ? 'orange' : item.itemType === 'chain' ? 'green' : 'blue'">
                          {{ getItemIcon(item) }}
                        </v-icon>
                      </div>

                      <div class="item-content">
                        <div class="item-name">{{ item && item.name }}<v-chip v-if="item.is_system" x-small outlined color="grey" class="ml-1">System</v-chip></div>
                        <div class="item-meta">
                          <span>{{ item && (item.itemType === 'service' ? (item.service_type || 'service') : item.itemType === 'output_formatter' ? 'output_formatter' : (item.prompt_type || 'client_prompts')) }}</span>
                        </div>
                      </div>

                      <v-icon small color="grey">mdi-drag-vertical</v-icon>
                    </div>
                  </template>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </div>

        <template v-if="viewMode === 'canvas'">
        <!-- Canvas -->
        <div
          ref="canvasRef"
          class="canvas"
          @drop="onCanvasDrop"
          @dragover="onCanvasDragOver"
          @mousemove="handleMouseMove"
          @mouseup="cancelConnection"
        >
          <!-- Trigger Nodes (replaces static Start node) -->
          <div
            v-for="(trigger, triggerIndex) in chain.triggers"
            :key="'trigger-' + (trigger.trigger_id || triggerIndex)"
            :data-trigger-id="trigger.trigger_id"
            :class="['trigger-node', 'trigger-type-' + trigger.trigger_type, { 'selected': selectedTrigger?.trigger_id === trigger.trigger_id, 'dragging': isDraggingTrigger && draggedTrigger?.trigger_id === trigger.trigger_id }]"
            :style="{ left: (trigger.x || 50) + 'px', top: (trigger.y || (100 + triggerIndex * 140)) + 'px' }"
            @mousedown="startDragTrigger($event, trigger)"
            @click.stop="selectTrigger(trigger)"
            @dblclick.stop="openTriggerModal(trigger)"
          >
            <div class="trigger-header">
              <v-icon color="white" size="20">{{ getTriggerIcon(trigger.trigger_type) }}</v-icon>
              <span class="trigger-name">{{ trigger.trigger_name || getTriggerTypeLabel(trigger.trigger_type) }}</span>
              <v-btn icon x-small dark @click.stop="openTriggerModal(trigger)" title="Edit Trigger">
                <v-icon x-small>mdi-pencil</v-icon>
              </v-btn>
            </div>
            <div class="trigger-body">
              <!-- API Trigger Summary -->
              <div v-if="trigger.trigger_type === 'api'" class="trigger-summary">
                <div v-if="getApiTriggerConnectionCount(trigger)" class="mappings-preview">
                  <span class="label">Connections:</span>
                  <span class="mapping-count">{{ getApiTriggerConnectionCount(trigger) }} step(s)</span>
                </div>
                <div v-if="trigger.trigger_config?.input_schema" class="schema-preview">
                  <v-icon x-small color="success" class="mr-1">mdi-check-circle</v-icon>
                  <span class="label">Custom schema defined</span>
                </div>
              </div>
              <!-- Chain Output Summary -->
              <div v-else-if="trigger.trigger_type === 'chain_output'" class="trigger-summary">
                <div v-if="trigger.trigger_config?.source_chain_id" class="chain-preview">
                  <span class="label">From:</span>
                  <span class="chain-name">{{ getSourceChainName(trigger.trigger_config.source_chain_id) }}</span>
                </div>
              </div>
              <!-- Excel File Trigger Summary -->
              <div v-else-if="trigger.trigger_type === 'excel_file'" class="trigger-summary">
                <div v-if="trigger.trigger_config?.column_mappings?.length" class="mappings-preview">
                  <span class="label">Mappings:</span>
                  <span class="mapping-count">{{ trigger.trigger_config.column_mappings.length }} column(s)</span>
                </div>
                <div v-if="trigger.trigger_config?.group_by_columns?.length" class="chain-preview">
                  <span class="label">Group by:</span>
                  <span class="chain-name">{{ trigger.trigger_config.group_by_columns.join(' → ') }}</span>
                </div>
              </div>
              <!-- Hierarchy Trigger Summary -->
              <div v-else-if="trigger.trigger_type === 'hierarchy_node_action'" class="trigger-summary">
                <div v-if="trigger.trigger_config?.ht_id" class="hierarchy-preview">
                  <span class="label">Hierarchy:</span>
                  <span class="hierarchy-name">{{ getHierarchyName(trigger.trigger_config.ht_id) }}</span>
                </div>
              </div>
              <!-- Document Trigger Summary -->
              <div v-else-if="trigger.trigger_type === 'document_action'" class="trigger-summary">
                <span class="label">Document Action</span>
              </div>
              <!-- Other trigger types show type label -->
              <div v-else class="trigger-summary">
                <span class="label">{{ getTriggerTypeLabel(trigger.trigger_type) }}</span>
              </div>
            </div>
            <!-- Connection points on all four sides -->
            <div
              class="connection-point left-point"
              @mouseup.prevent.stop="endConnection($event, 'trigger-' + trigger.trigger_id, 'left')"
              @mousedown.prevent.stop="startConnection($event, 'trigger-' + trigger.trigger_id, 'left')"
            ></div>
            <div
              class="connection-point right-point"
              @mouseup.prevent.stop="endConnection($event, 'trigger-' + trigger.trigger_id, 'right')"
              @mousedown.prevent.stop="startConnection($event, 'trigger-' + trigger.trigger_id, 'right')"
            ></div>
            <div
              class="connection-point top-point"
              @mouseup.prevent.stop="endConnection($event, 'trigger-' + trigger.trigger_id, 'top')"
              @mousedown.prevent.stop="startConnection($event, 'trigger-' + trigger.trigger_id, 'top')"
            ></div>
            <div
              class="connection-point bottom-point"
              @mouseup.prevent.stop="endConnection($event, 'trigger-' + trigger.trigger_id, 'bottom')"
              @mousedown.prevent.stop="startConnection($event, 'trigger-' + trigger.trigger_id, 'bottom')"
            ></div>
          </div>

          <!-- Default Start Node (shown when no triggers exist) -->
          <div v-if="!chain.triggers || chain.triggers.length === 0" class="start-node" @dblclick="addDefaultTrigger">
            <v-icon color="white" size="32">mdi-play-circle</v-icon>
            <span class="start-text">Start</span>
            <span class="start-hint">Double-click to add trigger</span>
            <!-- Connection points on all four sides -->
            <div
              class="connection-point left-point"
              @mousedown.prevent.stop="startConnection($event, 'start', 'left')"
            ></div>
            <div
              class="connection-point right-point"
              @mousedown.prevent.stop="startConnection($event, 'start', 'right')"
            ></div>
            <div
              class="connection-point top-point"
              @mousedown.prevent.stop="startConnection($event, 'start', 'top')"
            ></div>
            <div
              class="connection-point bottom-point"
              @mousedown.prevent.stop="startConnection($event, 'start', 'bottom')"
            ></div>
          </div>

          <!-- Steps -->
          <div
            v-for="(step) in chain.steps"
            :key="step.id"
            :data-step-id="step.id"
            :class="['step-node', {
              'selected': selectedStep?.id === step.id,
              'dragging': isDraggingStep && draggedStep?.id === step.id,
              'snap-target': snapTarget === step.id,
              'inactive': !step.active
            }]"
            :style="{ left: step.x + 'px', top: step.y + 'px' }"
            @mousedown="startDragStep($event, step)"
          >
            <div :class="['step-header', step.step_type === 'service' ? 'service-header' : step.step_type === 'output_formatter' ? 'formatter-header' : step.step_type === 'chain' ? 'chain-header' : step.step_type === 'manual_selection' ? 'selection-header' : step.step_type === 'group_processor' ? 'group-processor-header' : 'prompt-header']">
              <div class="step-number" v-if="hasIncomingConnections(step.id)">{{ step.step_order === 999 ? '?' : step.step_order }}</div>
              <v-text-field
                v-model="step.step_name"
                dense
                hide-details
                dark
                solo
                flat
                class="step-name-input"
                @click.stop
                @mousedown.stop
              ></v-text-field>
              <v-btn
                v-if="step.step_type === 'prompt'"
                icon
                x-small
                dark
                @click.stop="openTestPromptDialog(step)"
                title="Test Prompt"
              >
                <v-icon small color="white">mdi-play-circle-outline</v-icon>
              </v-btn>
              <v-btn
                icon
                x-small
                dark
                @click.stop="duplicateStep(step)"
              >
                <v-icon small color="white">mdi-content-copy</v-icon>
              </v-btn>
              <v-switch
                v-if="!isViewingSystemChain"
                v-model="step.active"
                :true-value="true"
                :false-value="false"
                dense
                hide-details
                class="step-active-switch"
                @change="toggleStepActive(step)"
                @click.stop
                @mousedown.stop
                :title="step.active ? 'Disable step' : 'Enable step'"
              ></v-switch>
              <v-btn
                v-if="!isViewingSystemChain"
                icon
                x-small
                dark
                @click.stop="deleteStep(step)"
              >
                <v-icon small color="white">mdi-delete</v-icon>
              </v-btn>
            </div>

            <div class="step-body">
              <div class="step-type">
                <v-icon small>{{ getItemIcon(step.item || step) }}</v-icon>
                <span>{{ step.step_type === 'service' ? 'Service Call' : (step.item ? step.item.prompt_type : step.prompt_type || 'client_prompts') }}</span>
                {{ step.item ? step.item.tmpl_ai_prompt_id : step.tmpl_ai_prompt_id }}
                <v-chip
                  v-if="step.step_type === 'service' && (step.item?.http_method || step.config?.service_config?.http_method)"
                  x-small
                  color="purple"
                  text-color="white"
                  class="ml-auto"
                >
                  {{ step.item?.http_method || step.config?.service_config?.http_method || 'POST' }}
                </v-chip>
              </div>

              <div v-if="step.step_type === 'service'" class="step-endpoint">
                {{ step.item?.name || step.item?.endpoint_name || 'Service' }}
              </div>

              <div v-if="step.dependencies && step.dependencies.length > 0" class="step-dependencies">
                <div class="dependency-label">Inputs from:</div>
                <div class="dependency-chips">
                  <v-chip
                    v-for="dep in step.dependencies"
                    :key="dep"
                    x-small
                    color="blue"
                    text-color="white"
                  >
                    Step {{ getStepOrderByDependency(dep) }}
                  </v-chip>
                </div>
              </div>

              <div v-if="step.outputs && step.outputs.length > 0" class="step-outputs">
                <div class="output-label">Outputs:</div>
                <div class="output-chips">
                  <v-chip
                    v-for="output in step.outputs"
                    :key="output"
                    x-small
                    color="green"
                    text-color="white"
                  >
                    {{ output }}
                  </v-chip>
                </div>
              </div>

              <div v-if="step.continue_on_error" class="error-continue">
                <v-icon small color="orange">mdi-alert-circle</v-icon>
                <span>Continue on error</span>
              </div>
              <div v-if="step.is_post_process" class="error-continue">
                <v-icon small color="blue">mdi-clock-fast</v-icon>
                <span>Post-process</span>
              </div>
            </div>

            <!-- Connection points on all four sides -->
            <div
              :class="['connection-point left-point',
                       step.step_type === 'service' ? 'connection-service' :
                       step.step_type === 'output_formatter' ? 'connection-formatter' :
                       step.step_type === 'chain' ? 'connection-chain' :
                       'connection-prompt']"
              @mouseup.prevent.stop="endConnection($event, step.id, 'left')"
              @mousedown.prevent.stop="startConnection($event, step.id, 'left')"
            ></div>
            <div
              :class="['connection-point right-point',
                       step.step_type === 'service' ? 'connection-service' :
                       step.step_type === 'output_formatter' ? 'connection-formatter' :
                       step.step_type === 'chain' ? 'connection-chain' :
                       'connection-prompt']"
              @mouseup.prevent.stop="endConnection($event, step.id, 'right')"
              @mousedown.prevent.stop="startConnection($event, step.id, 'right')"
            ></div>
            <div
              :class="['connection-point top-point',
                       step.step_type === 'service' ? 'connection-service' :
                       step.step_type === 'output_formatter' ? 'connection-formatter' :
                       step.step_type === 'chain' ? 'connection-chain' :
                       'connection-prompt']"
              @mouseup.prevent.stop="endConnection($event, step.id, 'top')"
              @mousedown.prevent.stop="startConnection($event, step.id, 'top')"
            ></div>
            <div
              :class="['connection-point bottom-point',
                       step.step_type === 'service' ? 'connection-service' :
                       step.step_type === 'output_formatter' ? 'connection-formatter' :
                       step.step_type === 'chain' ? 'connection-chain' :
                       'connection-prompt']"
              @mouseup.prevent.stop="endConnection($event, step.id, 'bottom')"
              @mousedown.prevent.stop="startConnection($event, step.id, 'bottom')"
            ></div>
          </div>

          <!-- Connections SVG -->
          <svg class="connections-svg">
            <defs>
              <!-- Arrow marker that auto-rotates based on path direction -->
              <marker id="arrowhead" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto">
                <polygon points="0 0, 10 3, 0 6" fill="#64748b" />
              </marker>
              <marker id="arrowhead-hover" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto">
                <polygon points="0 0, 10 3, 0 6" fill="#F44336" />
              </marker>
              <marker id="arrowhead-drawing" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto">
                <polygon points="0 0, 10 3, 0 6" fill="#50B3CB" />
              </marker>
            </defs>
            <g v-for="conn in connectionPaths" :key="conn.id">
              <!-- Invisible wider path for easier hovering -->
              <path
                :d="conn.path"
                stroke="transparent"
                stroke-width="20"
                fill="none"
                class="connection-hover-area"
                @mouseenter="hoveredConnection = conn.id"
                @mouseleave="hoveredConnection = null"
              />
              <!-- Visible connection line -->
              <path
                :d="conn.path"
                :stroke="hoveredConnection === conn.id ? '#F44336' : '#64748b'"
                :stroke-width="hoveredConnection === conn.id ? 3 : 2"
                fill="none"
                :marker-end="hoveredConnection === conn.id ? 'url(#arrowhead-hover)' : 'url(#arrowhead)'"
                class="connection-line"
                style="pointer-events: none;"
              />
            </g>
            <path
              v-if="isDrawingConnection && connectionStart"
              :d="createPath(getNodePosition(connectionStart.id || connectionStart, connectionStart.point || 'right'), mousePos)"
              stroke="#50B3CB"
              stroke-width="2"
              fill="none"
              stroke-dasharray="5,5"
              marker-end="url(#arrowhead-drawing)"
            />
          </svg>

          <!-- Connection Action Buttons -->
          <div
            v-for="conn in connectionPaths"
            :key="'btn-' + conn.id"
            v-show="hoveredConnection === conn.id"
            :style="conn.midpointStyle"
            class="connection-actions"
            @mouseenter="hoveredConnection = conn.id"
            @mouseleave="hoveredConnection = null"
          >
            <!-- Edit Mapping Button (only for trigger connections) -->
            <div
              v-if="isTriggerConnection(conn)"
              class="connection-action-btn connection-edit-btn"
              @click="openConnectionMappingDialog(conn)"
              :title="conn.mapping_config?.mappings?.length > 0 ? `${conn.mapping_config.mappings.length} mapping(s) configured` : 'Configure mapping'"
            >
              <v-icon size="16" color="white">mdi-map-marker-path</v-icon>
              <span v-if="conn.mapping_config?.mappings?.length > 0" class="mapping-count">
                {{ conn.mapping_config.mappings.length }}
              </span>
            </div>
            <!-- Delete Button -->
            <div
              class="connection-action-btn connection-delete-btn"
              @click="deleteConnection(conn.id)"
            >
              <v-icon size="16" color="white">mdi-close</v-icon>
            </div>
          </div>

          <!-- Drag Overlay -->
          <div v-if="isDraggingItem" class="drag-overlay">
            <v-icon size="64" color="blue">mdi-plus</v-icon>
            <p class="drag-text">Drop here to add to chain</p>
          </div>
        </div>
        </template>

        <!-- Properties Panel -->
        <div v-if="selectedStep" class="properties-panel">
          <div class="properties-header">
            <h3>{{ selectedStep.step_type === 'service' ? 'Service Configuration' : 'Step Configuration' }}</h3>
            <v-btn icon small @click="selectedStep = null">
              <v-icon small>mdi-close</v-icon>
            </v-btn>
          </div>

          <div class="properties-content">
            <div class="property-group">
              <label class="property-label">Step Name</label>
              <v-text-field
                v-model="selectedStep.step_name"
                outlined
                dense
                hide-details
              ></v-text-field>
            </div>

            <div class="property-group">
              <v-switch
                v-model="selectedStep.is_final_step"
                label="Mark as Final Step"
                hint="Final steps are always executed during partial regeneration"
                persistent-hint
                dense
                class="mt-0"
              ></v-switch>
              <v-switch
                v-model="selectedStep.is_post_process"
                label="Post-Process Step"
                hint="Runs after chain completes — does not affect chain result"
                persistent-hint
                dense
                class="mt-2"
              ></v-switch>
            </div>

            <v-expansion-panels v-model="configPanels" multiple>
              <!-- Step Details Panel -->
              <v-expansion-panel>
                <v-expansion-panel-header>
                  <div class="panel-header">
                    <v-icon left small>{{ selectedStep.step_type === 'service' ? 'mdi-flash' : 'mdi-file-document' }}</v-icon>
                    Step Details
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <!-- Prompt Selection Dropdown (only for prompt steps) -->
                  <div v-if="selectedStep.step_type === 'prompt'" class="property-group">
                    <label class="property-label">Prompt Template</label>
                    <v-select
                      :value="selectedStep.tmpl_ai_prompt_id"
                      :items="availablePrompts"
                      item-text="name"
                      item-value="tmpl_ai_prompt_id"
                      outlined
                      dense
                      hide-details
                      @change="changeStepPrompt($event)"
                    >
                      <template v-slot:selection="{ item }">
                        <div class="d-flex align-center">
                          <v-icon :color="getPromptTypeColor(item.prompt_type)" small class="mr-2">
                            {{ getItemIcon(item) }}
                          </v-icon>
                          <span>{{ item.name }}</span>
                        </div>
                      </template>
                      <template v-slot:item="{ item }">
                        <div class="d-flex align-center" style="width: 100%;">
                          <v-icon :color="getPromptTypeColor(item.prompt_type)" small class="mr-3">
                            {{ getItemIcon(item) }}
                          </v-icon>
                          <div class="flex-grow-1">
                            <div class="font-weight-medium">{{ item.name }}</div>
                            <div class="caption text--secondary">{{ item.prompt_type }}</div>
                          </div>
                        </div>
                      </template>
                    </v-select>
                    <div class="caption grey--text mt-1">
                      Select a different prompt template. Inputs will be recalculated based on the new prompt's requirements.
                    </div>
                  </div>

                  <!-- Output Formatter Configuration -->
                  <div v-if="selectedStep.step_type === 'output_formatter'" class="formatter-details">
                    <div class="formatter-info-box">
                      <div class="info-item">
                        <span class="info-label">Format Type:</span>
                        <div class="info-value">{{ selectedStep.config.formatter_config?.output_format || 'json' }}</div>
                      </div>
                    </div>

                    <div class="property-group">
                      <label class="property-label">Output Format</label>
                      <v-select
                        :value="selectedStep.config.formatter_config?.output_format || 'json'"
                        :items="[
                          {text: 'JSON', value: 'json'}, 
                          {text: 'Text', value: 'text'},
                          {text: 'Document', value: 'document'}
                        ]"
                        outlined
                        dense
                        hide-details
                        @change="updateFormatterConfig('output_format', $event)"
                      ></v-select>
                    </div>

                    <!-- Document Formatter Configuration -->
                    <div v-if="selectedStep.config.formatter_config?.output_format === 'document'" class="property-group">
                      <label class="property-label">Document Mappings</label>
                      <div class="document-mappings-summary">
                        <div v-if="(selectedStep.config.formatter_config?.document_mappings || []).length === 0" class="no-mappings">
                          No mappings configured
                        </div>
                        <div v-else class="mappings-count">
                          {{ selectedStep.config.formatter_config.document_mappings.length }} mapping(s) configured
                          <span v-if="selectedStep.config.formatter_config.document_mappings.some(m => m.is_active === false)">
                            ({{ selectedStep.config.formatter_config.document_mappings.filter(m => m.is_active !== false).length }} active,
                            {{ selectedStep.config.formatter_config.document_mappings.filter(m => m.is_active === false).length }} inactive)
                          </span>
                        </div>
                        <v-btn
                          color="#5E2872"
                          dark
                          small
                          @click="openDocumentFormatterDialog"
                        >
                          <v-icon left small>mdi-cog</v-icon>
                          Configure Mappings
                        </v-btn>
                      </div>
                    </div>

                    <!-- JSON/Text Formatter Configuration (existing) -->
                    <div v-if="selectedStep.config.formatter_config?.output_format !== 'document'" class="property-group">
                      <label class="property-label">Select Outputs to Include</label>
                      <v-select
                        :value="selectedStep.config.formatter_config?.selected_outputs || []"
                        :items="getAvailableOutputsGrouped()"
                        outlined
                        dense
                        multiple
                        chips
                        small-chips
                        deletable-chips
                        hide-details
                        @change="updateSelectedOutputs($event)"
                      >
                        <template v-slot:selection="{ item, index }">
                          <v-chip v-if="index < 5" x-small color="blue" outlined>
                            {{ item.text }}
                          </v-chip>
                          <span v-if="index === 5" class="grey--text text-caption ml-1">
                            (+{{ (selectedStep.config.formatter_config?.selected_outputs || []).length - 5 }} more)
                          </span>
                        </template>
                        <template v-slot:item="{ item }">
                          <div v-if="!item.divider && !item.header" style="display: flex; align-items: center; width: 100%;">
                            <v-icon small color="grey" class="mr-2">mdi-code-braces</v-icon>
                            <span>{{ item.text }}</span>
                          </div>
                          <div v-else-if="item.header" class="select-header-item">
                            <v-icon small color="primary" class="mr-2">mdi-folder</v-icon>
                            <span class="font-weight-medium">{{ item.header }}</span>
                          </div>
                        </template>
                      </v-select>
                      <div class="caption grey--text mt-1">
                        Select which outputs from previous steps to include.
                      </div>
                    </div>

                    <!-- Output Mappings -->
                    <div v-if="selectedStep.config.formatter_config?.output_mappings && Object.keys(selectedStep.config.formatter_config.output_mappings).length > 0" class="property-group">
                      <label class="property-label">Output Field Names</label>
                      <div class="output-mappings-container">
                        <div
                          v-for="(mapping, outputValue) in selectedStep.config.formatter_config.output_mappings"
                          :key="outputValue"
                          class="output-mapping-entry"
                        >
                          <div class="output-mapping-grid">
                            <div>
                              <div class="caption grey--text mb-1">Source</div>
                              <div class="output-mapping-value">
                                {{ mapping.displayName }}
                              </div>
                            </div>
                            <v-icon color="grey" style="margin-bottom: 8px;">mdi-arrow-right</v-icon>
                            <div>
                              <div class="caption grey--text mb-1">Field Name in Output</div>
                              <v-text-field
                                :value="mapping.key"
                                outlined
                                dense
                                hide-details
                                placeholder="custom_field_name"
                                style="font-family: monospace;"
                                @input="updateOutputKey(outputValue, $event)"
                              ></v-text-field>
                            </div>
                            <v-btn
                              icon
                              small
                              color="error"
                              style="margin-bottom: 8px;"
                              @click="removeFormatterOutput(outputValue)"
                            >
                              <v-icon small>mdi-delete</v-icon>
                            </v-btn>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div class="property-group">
                      <label class="property-label">
                        Generated Template ({{ selectedStep.config.formatter_config?.output_format === 'text' ? 'Text' : 'JSON' }})
                        <v-chip v-if="selectedStep.config.formatter_config?.output_format === 'text'" x-small color="orange" text-color="white" class="ml-2">
                          Editable
                        </v-chip>
                      </label>
                      
                      <!-- Text Template - Editable -->
                      <v-textarea
                        v-if="selectedStep.config.formatter_config?.output_format === 'text'"
                        :value="getTextTemplate()"
                        @input="updateTextTemplate($event)"
                        outlined
                        dense
                        rows="12"
                        placeholder="Enter your custom text template here...

Example:
Here is my output text
Title:
${Generate_Responsibilities.result}

Title 2:
${Another_Step.output_property}"
                        hint="Use ${Step_Name.output_property} to reference outputs from previous steps"
                        persistent-hint
                        class="text-template-editor"
                      ></v-textarea>
                      
                      <!-- JSON Template - Read-only -->
                      <v-textarea
                        v-else
                        :value="JSON.stringify(selectedStep.config.formatter_config?.template || {}, null, 2)"
                        outlined
                        dense
                        rows="10"
                        readonly
                        filled
                      ></v-textarea>
                      
                      <div class="caption grey--text mt-1">
                        <span v-if="selectedStep.config.formatter_config?.output_format === 'text'">
                          Create your custom text output using variables from previous steps. Variables should be in the format ${Step_Name.output_property}.
                        </span>
                        <span v-else>
                          This template is auto-generated based on your field names above
                        </span>
                      </div>
                    </div>

                    <!-- Available Variables Helper -->
                    <div v-if="selectedStep.config.formatter_config?.output_format === 'text'" class="property-group">
                      <label class="property-label">Available Variables</label>
                      <div class="available-variables">
                        <div v-if="getAvailableVariables().length === 0" class="no-variables">
                          No previous steps available. Add steps before this formatter to use their outputs.
                        </div>
                        <div v-else class="variables-list">
                          <div 
                            v-for="variable in getAvailableVariables()" 
                            :key="variable.id"
                            class="variable-item"
                            @click="insertVariable(variable.syntax)"
                          >
                            <div class="variable-syntax">{{ variable.syntax }}</div>
                            <div class="variable-description">{{ variable.description }}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Service Configuration -->
                  <div v-if="selectedStep.step_type === 'service'" class="service-details">
                    <div class="service-info-box">
                      <div class="service-info-content">
                        <div class="info-item">
                          <span class="info-label">Endpoint:</span>
                          <div class="info-value">
                            {{ selectedStep.config.service_config?.http_method }} {{ selectedStep.config.service_config?.endpoint_path }}
                          </div>
                        </div>
                        <div class="info-item">
                          <span class="info-label">Base URL:</span>
                          <div class="info-value">{{ selectedStep.config.service_config?.base_url }}</div>
                        </div>
                      </div>
                    </div>

                    <div class="property-group">
                      <label class="property-label">Timeout (seconds)</label>
                      <v-text-field
                        :value="selectedStep.config.service_config?.timeout || 30"
                        type="number"
                        outlined
                        dense
                        hide-details
                        @input="updateServiceConfig('timeout', parseInt($event))"
                      ></v-text-field>
                    </div>

                    <div class="property-group">
                      <label class="property-label">Retry Attempts</label>
                      <v-text-field
                        :value="selectedStep.config.service_config?.retry_attempts || 3"
                        type="number"
                        outlined
                        dense
                        hide-details
                        @input="updateServiceConfig('retry_attempts', parseInt($event))"
                      ></v-text-field>
                    </div>

                    <div class="property-group" v-if="selectedStep.item?.input_schema">
                      <label class="property-label">Input Schema</label>
                      <div class="schema-box">
                        <pre>{{ JSON.stringify(selectedStep.item.input_schema, null, 2) }}</pre>
                      </div>
                    </div>

                    <div class="property-group" v-if="selectedStep.item?.output_schema">
                      <label class="property-label">Output Schema</label>
                      <div class="schema-box">
                        <pre>{{ JSON.stringify(selectedStep.item.output_schema, null, 2) }}</pre>
                      </div>
                    </div>
                  </div>

                  <!-- Chain Configuration -->
                  <div v-if="selectedStep.step_type === 'chain'" class="chain-details">
                    <div class="chain-info-box">
                      <div class="info-item">
                        <span class="info-label">Child Chain:</span>
                        <div class="info-value">{{ selectedStep.item?.name || 'Unknown Chain' }}</div>
                      </div>
                      <div class="info-item">
                        <span class="info-label">Steps:</span>
                        <div class="info-value">{{ selectedStep.item?.step_count || 0 }} steps</div>
                      </div>
                    </div>

                    <!-- Bulk Execution Mode -->
                    <div v-if="selectedStep.config.chain_config" class="property-group">
                      <v-switch
                        v-model="selectedStep.config.chain_config.bulk_mode.enabled"
                        label="Enable Bulk Execution Mode"
                        hint="Execute this chain multiple times for each item in an array"
                        persistent-hint
                        dense
                        @change="onBulkModeToggle"
                      ></v-switch>
                    </div>

                    <!-- Bulk Mode Configuration -->
                    <div v-if="selectedStep.config.chain_config?.bulk_mode?.enabled" class="bulk-config-section">
                      <div class="property-group">
                        <label class="property-label">Array Source Output</label>
                        <v-select
                          v-model="selectedStep.config.chain_config.bulk_mode.source_output"
                          :items="getAvailableOutputs()"
                          outlined
                          dense
                          hide-details
                          placeholder="Select output containing array"
                        >
                          <template v-slot:selection="{ item }">
                            <v-icon small class="mr-2">mdi-code-braces</v-icon>
                            {{ item.text }}
                          </template>
                        </v-select>
                        <div class="caption grey--text mt-1">
                          Select which output from previous steps contains the array
                        </div>
                      </div>

                      <div class="property-group">
                        <label class="property-label">Array Path (optional)</label>
                        <v-text-field
                          v-model="selectedStep.config.chain_config.bulk_mode.array_path"
                          outlined
                          dense
                          hide-details
                          placeholder="e.g., result or data.items"
                        ></v-text-field>
                        <div class="caption grey--text mt-1">
                          If the array is nested, specify the path (e.g., "result" for object.result)
                        </div>
                      </div>

                      <div class="property-group">
                        <label class="property-label">Array Item Property Mappings</label>
                        <div class="mapping-list">
                          <div v-for="(input, idx) in selectedStep.config.input.filter(i => i.source === 'chain_output')" :key="idx" class="mapping-row">
                            <div class="mapping-label">{{ input.property }}:</div>
                            <v-text-field
                              v-model="selectedStep.config.chain_config.bulk_mode.item_property_mappings[input.property]"
                              outlined
                              dense
                              hide-details
                              :placeholder="`Array item field (e.g., Title)`"
                              class="mapping-input"
                            ></v-text-field>
                          </div>
                          <div v-if="selectedStep.config.input.filter(i => i.source === 'chain_output').length === 0" class="no-inputs-message">
                            No chain inputs configured. Add inputs in the Inputs tab.
                          </div>
                        </div>
                        <div class="caption grey--text mt-1">
                          Map array item fields to chain inputs (e.g., item.Title → Title input)
                        </div>
                      </div>

                      <v-divider class="my-3"></v-divider>

                      <!-- Context Sharing Options -->
                      <div class="property-group">
                        <v-switch
                          v-model="selectedStep.config.chain_config.bulk_mode.share_context"
                          label="Share Context Between Iterations"
                          hint="Enable sequential execution with accumulated context — each iteration receives previous results"
                          persistent-hint
                          dense
                        ></v-switch>
                      </div>

                      <div v-if="selectedStep.config.chain_config.bulk_mode.share_context" class="context-sharing-config">
                        <div class="property-group">
                          <v-select
                            v-model="selectedStep.config.chain_config.bulk_mode.context_mode"
                            :items="[{text: 'Full Outputs', value: 'full'}, {text: 'Summaries Only', value: 'summary'}]"
                            label="Prior Iteration Context Mode"
                            outlined dense hide-details
                          ></v-select>
                          <div class="caption grey--text mt-1">
                            How prior iteration results are passed to each new iteration
                          </div>
                        </div>

                        <div class="property-group">
                          <v-text-field
                            v-model.number="selectedStep.config.chain_config.bulk_mode.max_context_iterations"
                            label="Max Prior Iterations in Context"
                            type="number"
                            outlined dense hide-details
                            placeholder="20"
                          ></v-text-field>
                          <div class="caption grey--text mt-1">
                            Limit how many prior results are passed (sliding window). Leave empty for unlimited.
                          </div>
                        </div>

                        <div class="property-group">
                          <div class="caption grey--text mb-2">
                            Group array items by property values. Multiple levels create recursive groups (e.g., department → role).
                          </div>
                          <div v-for="(prop, gIdx) in (selectedStep.config.chain_config.bulk_mode.group_by || [])" :key="'bulk-grp-' + gIdx" class="d-flex align-center mb-2">
                            <v-chip small label class="mr-2">Level {{ gIdx + 1 }}</v-chip>
                            <v-text-field
                              v-model="selectedStep.config.chain_config.bulk_mode.group_by[gIdx]"
                              :label="'Group By Property (Level ' + (gIdx + 1) + ')'"
                              outlined dense hide-details
                              placeholder="e.g., department"
                            ></v-text-field>
                            <v-btn icon small class="ml-1" @click="selectedStep.config.chain_config.bulk_mode.group_by.splice(gIdx, 1)">
                              <v-icon small>mdi-close</v-icon>
                            </v-btn>
                          </div>
                          <v-btn small text color="primary" @click="addBulkGroupByProperty()">
                            <v-icon small left>mdi-plus</v-icon>
                            Add Group Level
                          </v-btn>
                        </div>
                      </div>
                    </div>

                    <div class="property-group">
                      <v-switch
                        v-model="selectedStep.config.chain_config.inherit_context"
                        label="Inherit Parent Context"
                        hint="Pass all parent chain inputs to the child chain"
                        persistent-hint
                        dense
                      ></v-switch>
                    </div>
                  </div>

                  <!-- Manual Selection Configuration -->
                  <div v-if="selectedStep.step_type === 'manual_selection'" class="selection-details">
                    <div class="property-group">
                      <label class="property-label">Array Source Output</label>
                      <v-select
                        v-model="selectedStep.config.selection_config.source_output"
                        :items="getAvailableOutputs()"
                        outlined
                        dense
                        hide-details
                        placeholder="Select output containing array"
                      ></v-select>
                      <div class="caption grey--text mt-1">
                        Select which output from previous steps contains the array for user selection
                      </div>
                    </div>

                    <div class="property-group">
                      <label class="property-label">Array Path (optional)</label>
                      <v-text-field
                        v-model="selectedStep.config.selection_config.array_path"
                        outlined
                        dense
                        hide-details
                        placeholder="e.g., result or data.items"
                      ></v-text-field>
                      <div class="caption grey--text mt-1">
                        If the array is nested, specify the path (e.g., "result" for object.result)
                      </div>
                    </div>

                    <div class="property-group">
                      <label class="property-label">Display Fields</label>
                      <v-combobox
                        v-model="selectedStep.config.selection_config.display_fields"
                        outlined
                        dense
                        multiple
                        small-chips
                        deletable-chips
                        hide-details
                        placeholder="Add fields to display (e.g., Title, Level)"
                      ></v-combobox>
                      <div class="caption grey--text mt-1">
                        Fields from array items to display in selection UI
                      </div>
                    </div>

                    <div class="property-group">
                      <label class="property-label">Selection Mode</label>
                      <v-radio-group
                        v-model="selectedStep.config.selection_config.selection_mode"
                        row
                        dense
                        hide-details
                      >
                        <v-radio label="Single" value="single"></v-radio>
                        <v-radio label="Multiple" value="multiple"></v-radio>
                      </v-radio-group>
                      <div class="caption grey--text mt-1">
                        Whether user can select one or multiple items
                      </div>
                    </div>
                  </div>

                  <div v-if="selectedStep.step_type === 'prompt'" class="prompt-details">
                    <div class="property-group">
                      <label class="property-label">Model</label>
                      <v-select
                        :value="selectedStep.config.model || 'claude-sonnet-4-6'"
                        :items="[
                          {text: 'GPT-4o', value: 'GPT-4o'},
                          {text: 'Sonnet 4.5', value: 'claude-sonnet-4-5'},
                          {text: 'Sonnet 4.6', value: 'claude-sonnet-4-6'},
                          {text: 'Haiku', value: 'claude-haiku-4-5-20251001' }
                        ]"
                        outlined
                        dense
                        hide-details
                        @change="updatePromptModel($event)"
                      ></v-select>
                    </div>
                  </div>

                  <!-- Pre-Step Hook Configuration -->
                  <div class="property-group">
                    <label class="property-label">Pre-Step Hook</label>
                    <v-select
                      :value="selectedStep.config.pre_step_hook?.hook_function_id || null"
                      :items="[{ text: 'None', value: null }, ...hookFunctions.map(h => ({ text: h.function_name, value: h.hook_function_id }))]"
                      outlined
                      dense
                      hide-details
                      clearable
                      @change="updatePreStepHook('hook_function_id', $event)"
                    ></v-select>
                    <template v-if="selectedStep.config.pre_step_hook?.hook_function_id">
                      <v-select
                        :value="selectedStep.config.pre_step_hook?.target_input || null"
                        :items="[{ text: 'All inputs', value: null }, ...(selectedStep.config.input || []).filter(i => i.property).map(i => ({ text: i.property, value: i.property }))]"
                        label="Target Input"
                        outlined
                        dense
                        hide-details
                        clearable
                        class="mt-2"
                        @change="updatePreStepHook('target_input', $event)"
                      ></v-select>
                      <v-text-field
                        :value="selectedStep.config.pre_step_hook?.output_property || ''"
                        label="Output Property (blank = overwrite input)"
                        placeholder="e.g. skills_formatted"
                        outlined
                        dense
                        hide-details
                        clearable
                        class="mt-2"
                        @input="updatePreStepHook('output_property', $event || null)"
                      ></v-text-field>
                      <v-checkbox
                        :input-value="selectedStep.config.pre_step_hook?.only_internal_trigger !== false"
                        label="Only run on internal trigger"
                        dense
                        hide-details
                        class="mt-2"
                        @change="updatePreStepHook('only_internal_trigger', $event)"
                      ></v-checkbox>
                      <v-checkbox
                        :input-value="selectedStep.config.pre_step_hook?.bypass_on_empty || false"
                        label="Skip step if hook returns empty"
                        dense
                        hide-details
                        class="mt-1"
                        @change="updatePreStepHook('bypass_on_empty', $event)"
                      ></v-checkbox>
                    </template>
                  </div>

                  <div class="property-group">
                    <v-checkbox
                      v-model="selectedStep.continue_on_error"
                      label="Continue chain if this step fails"
                      hide-details
                      dense
                    ></v-checkbox>
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>

              <!-- Input Mapping Panel -->
              <v-expansion-panel>
                <v-expansion-panel-header>
                  <div class="panel-header">
                    <v-icon left small>mdi-import</v-icon>
                    Input Mapping
                    <v-chip x-small class="ml-2">{{ selectedStep.config?.input?.length || 0 }}</v-chip>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <div class="input-mapping-header">
                    <v-btn
                      x-small
                      color="#50B3CB"
                      dark
                      @click="addInput"
                    >
                      <v-icon left x-small>mdi-plus</v-icon>
                      Add Input
                    </v-btn>
                    <!--<v-btn
                      x-small
                      color="#5E2872"
                      dark
                      @click="showMappingDialog = true"
                    >
                      <v-icon left x-small>mdi-link-variant</v-icon>
                      Visual Mapper
                    </v-btn>-->
                  </div>

                  <div class="input-list">
                    <div
                      v-for="(input, idx) in (selectedStep.config?.input || [])"
                      :key="idx"
                      class="input-mapping-item"
                    >
                      <div class="input-mapping-row">
                        <v-text-field
                          :value="input.property"
                          :placeholder="input.map_as_body ? 'Internal reference name...' : 'Property name...'"
                          outlined
                          dense
                          hide-details
                          @input="updateInput(idx, 'property', $event)"
                        ></v-text-field>
                        <v-btn
                          icon
                          small
                          color="error"
                          @click="removeInput(idx)"
                        >
                          <v-icon small>mdi-delete</v-icon>
                        </v-btn>
                      </div>

                      <v-select
                        :value="input.source"
                        :items="inputSources"
                        outlined
                        dense
                        hide-details
                        @input="updateInput(idx, 'source', $event)"
                      ></v-select>

                      <v-text-field
                        v-if="input.source === 'chain_output'"
                        :value="input.outputProperty"
                        placeholder="Output property name from previous step..."
                        label="Output Property"
                        outlined
                        dense
                        hide-details
                        @input="updateInput(idx, 'outputProperty', $event)"
                        hint="Specify which output property to retrieve from the previous step"
                        persistent-hint
                        class="mt-2"
                      ></v-text-field>

                      <v-checkbox
                        v-if="input.source === 'chain_output' && selectedStep.step_type === 'service'"
                        :input-value="input.map_as_body"
                        label="Use as entire request body"
                        hide-details
                        dense
                        @change="updateInput(idx, 'map_as_body', $event)"
                        class="mt-1"
                      >
                        <template v-slot:append>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on }">
                              <v-icon small v-on="on">mdi-information-outline</v-icon>
                            </template>
                            <span>Sends the entire output value (array/object) as the request body instead of setting it as a property</span>
                          </v-tooltip>
                        </template>
                      </v-checkbox>

                      <v-alert
                        v-if="input.map_as_body && input.source === 'chain_output'"
                        type="info"
                        dense
                        text
                        class="mt-2 mb-0"
                      >
                        This output will be sent as the entire request body (replaces input schema template)
                      </v-alert>

                      <v-checkbox
                        :value="input.required"
                        label="Required input"
                        hide-details
                        dense
                        @change="updateInput(idx, 'required', $event)"
                      ></v-checkbox>
                    </div>
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>

              <!-- Output Configuration Panel -->
              <v-expansion-panel>
                <v-expansion-panel-header>
                  <div class="panel-header">
                    <v-icon left small>mdi-export</v-icon>
                    Output Configuration
                    <v-chip x-small class="ml-2">{{ selectedStep.config?.output?.length || 0 }}</v-chip>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <div class="output-mapping-header">
                    <v-btn
                      x-small
                      color="#50B3CB"
                      dark
                      @click="addOutput"
                    >
                      <v-icon left x-small>mdi-plus</v-icon>
                      Add Output
                    </v-btn>
                    <v-btn
                      x-small
                      color="#48A401"
                      dark
                      class="ml-2"
                      @click="addPassThroughOutput"
                    >
                      <v-icon left x-small>mdi-arrow-right-bold-circle</v-icon>
                      Add Pass-Through
                    </v-btn>
                  </div>

                  <div class="output-list mt-3">
                    <div
                      v-for="(output, idx) in (selectedStep.config?.output || [])"
                      :key="idx"
                      class="output-mapping-item"
                    >
                      <div class="output-mapping-row">
                        <v-text-field
                          :value="output.property"
                          placeholder="Property name..."
                          outlined
                          dense
                          hide-details
                          @input="updateOutput(idx, 'property', $event)"
                        ></v-text-field>
                        <v-btn
                          icon
                          small
                          color="error"
                          @click="removeOutput(idx)"
                        >
                          <v-icon small>mdi-delete</v-icon>
                        </v-btn>
                      </div>

                      <v-select
                        :value="output.extractFrom"
                        :items="outputExtractionSources"
                        label="Extract from"
                        outlined
                        dense
                        hide-details
                        @input="updateOutput(idx, 'extractFrom', $event)"
                      ></v-select>

                      <!-- Dot-path into the parsed response. The platform
                           wraps prompt responses as { result, reason }, so
                           field extraction paths start with `result.` —
                           blank = the whole response. -->
                      <v-text-field
                        v-if="output.extractFrom === 'response' || output.extractFrom === 'custom'"
                        :value="output.path"
                        label="JSON path (optional)"
                        placeholder="e.g. result.job_title"
                        hint="Blank = whole response. Responses are wrapped as { result, reason }, so field paths start with result."
                        persistent-hint
                        outlined
                        dense
                        class="mt-2"
                        @input="updateOutput(idx, 'path', $event)"
                      ></v-text-field>

                      <v-select
                        v-if="output.extractFrom === 'pass_through'"
                        :value="output.sourceInputProperty"
                        :items="availableInputProperties"
                        item-text="text"
                        item-value="value"
                        label="Source Input Property"
                        placeholder="Select input to pass through..."
                        outlined
                        dense
                        hide-details
                        @input="updateOutput(idx, 'sourceInputProperty', $event)"
                        hint="Select which input parameter value to include in the output"
                        persistent-hint
                        class="mt-2"
                      >
                        <template v-slot:item="{ item }">
                          <v-list-item-content>
                            <v-list-item-title>{{ item.text }}</v-list-item-title>
                            <v-list-item-subtitle class="caption">Source: {{ item.source }}</v-list-item-subtitle>
                          </v-list-item-content>
                        </template>
                        <template v-slot:prepend-item>
                          <v-list-item v-if="availableInputProperties.length === 0" disabled>
                            <v-list-item-content>
                              <v-list-item-subtitle class="text-wrap">
                                No inputs configured. Add inputs in the Input Mapping panel first.
                              </v-list-item-subtitle>
                            </v-list-item-content>
                          </v-list-item>
                          <v-divider v-else class="mb-2"></v-divider>
                        </template>
                      </v-select>

                      <v-checkbox
                        v-if="output.extractFrom === 'pass_through'"
                        :value="output.isPassThrough"
                        label="Pass-through parameter"
                        hide-details
                        dense
                        disabled
                        class="mt-2"
                      ></v-checkbox>

                      <v-checkbox
                        :input-value="output.isContextOutput || false"
                        hide-details
                        dense
                        @change="updateOutput(idx, 'isContextOutput', $event)"
                        class="mt-2"
                      >
                        <template v-slot:label>
                          <span>Share as context in sequence mode</span>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-icon small class="ml-1" v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
                            </template>
                            <span>When running in sequence mode, this output will be shared with subsequent rows as context</span>
                          </v-tooltip>
                        </template>
                      </v-checkbox>

                      <v-divider class="my-3"></v-divider>

                      <!-- Validation and Retry Configuration -->
                      <div class="validation-config">
                        <v-checkbox
                          :value="output.validation || false"
                          v-model="output.validation"
                          label="Enable validation for this output"
                          hide-details
                          dense
                          @change="updateOutput(idx, 'validation', $event)"
                          class="mb-2"
                        >
                          <template v-slot:label>
                            <span>Enable validation for this output</span>
                            <v-tooltip bottom>
                              <template v-slot:activator="{ on, attrs }">
                                <v-icon small class="ml-1" v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
                              </template>
                              <span>When enabled, this output will be validated before proceeding</span>
                            </v-tooltip>
                          </template>
                        </v-checkbox>

                        <v-text-field
                          v-if="output.validation"
                          :value="output.max_retries || 3"
                          label="Max Retries"
                          type="number"
                          min="0"
                          max="10"
                          outlined
                          dense
                          hide-details
                          @input="updateOutput(idx, 'max_retries', parseInt($event) || 3)"
                          class="mt-2"
                        >
                          <template v-slot:append>
                            <v-tooltip bottom>
                              <template v-slot:activator="{ on, attrs }">
                                <v-icon small v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
                              </template>
                              <span>Number of times to retry validation if it fails (0-10)</span>
                            </v-tooltip>
                          </template>
                        </v-text-field>
                        <v-select 
                          v-if="output.validation"
                          :items="['object','array','string']"
                          :value="output.type"
                          outlined
                          dense
                          hide-details
                          @change="updateOutput(idx, 'type', $event)"
                          class="mt-2"                          
                        >

                        </v-select>
                        <v-text-field
                          v-if="output.validation"
                          :value="output.sourceInputProperty"
                          label="Source Input Property Name"
                          outlined
                          dense
                          hide-details
                          @input="updateOutput(idx, 'sourceInputProperty', $event)"
                          class="mt-2"
                        >
                          <template v-slot:append>
                            <v-tooltip bottom>
                              <template v-slot:activator="{ on, attrs }">
                                <v-icon small v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
                              </template>
                              <span>Source Input Property Name For Comparison</span>
                            </v-tooltip>
                          </template>
                        </v-text-field>

                        <!-- Array Validation Parameters (only shown for array type) -->
                        <v-expansion-panels v-if="output.validation && output.type === 'array'" flat class="mt-2" :key="`validation-params-${selectedStep?.id}-${idx}`">
                          <v-expansion-panel>
                            <v-expansion-panel-header class="px-0">
                              <div class="d-flex align-center">
                                <v-icon small class="mr-2">mdi-cog</v-icon>
                                <span class="text-subtitle-2">Validation Parameters</span>
                              </div>
                            </v-expansion-panel-header>
                            <v-expansion-panel-content>
                              <v-text-field
                                :value="selectedStep.config?.output?.[idx]?.validationParams?.keyField || 'Description'"
                                label="Key Field"
                                outlined
                                dense
                                hide-details
                                @input="updateValidationParam(idx, 'keyField', $event)"
                                class="mb-2"
                              >
                                <template v-slot:append>
                                  <v-tooltip bottom>
                                    <template v-slot:activator="{ on, attrs }">
                                      <v-icon small v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
                                    </template>
                                    <span>The property name to use for comparing array items (default: Description)</span>
                                  </v-tooltip>
                                </template>
                              </v-text-field>

                              <v-checkbox
                                :input-value="selectedStep.config?.output?.[idx]?.validationParams?.checkLength !== false"
                                label="Check Array Length"
                                hide-details
                                dense
                                @change="updateValidationParam(idx, 'checkLength', $event)"
                                class="mb-2"
                              >
                                <template v-slot:label>
                                  <span>Check Array Length</span>
                                  <v-tooltip bottom>
                                    <template v-slot:activator="{ on, attrs }">
                                      <v-icon small class="ml-1" v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
                                    </template>
                                    <span>Verify that output array has the same length as input array</span>
                                  </v-tooltip>
                                </template>
                              </v-checkbox>

                              <v-checkbox
                                :input-value="selectedStep.config?.output?.[idx]?.validationParams?.checkMissing !== false"
                                label="Check Missing Keys"
                                hide-details
                                dense
                                @change="updateValidationParam(idx, 'checkMissing', $event)"
                                class="mb-2"
                              >
                                <template v-slot:label>
                                  <span>Check Missing Keys</span>
                                  <v-tooltip bottom>
                                    <template v-slot:activator="{ on, attrs }">
                                      <v-icon small class="ml-1" v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
                                    </template>
                                    <span>Verify that no items from input are missing in output</span>
                                  </v-tooltip>
                                </template>
                              </v-checkbox>

                              <v-checkbox
                                :input-value="selectedStep.config?.output?.[idx]?.validationParams?.checkExtra !== false"
                                label="Check Extra Keys"
                                hide-details
                                dense
                                @change="updateValidationParam(idx, 'checkExtra', $event)"
                                class="mb-2"
                              >
                                <template v-slot:label>
                                  <span>Check Extra Keys</span>
                                  <v-tooltip bottom>
                                    <template v-slot:activator="{ on, attrs }">
                                      <v-icon small class="ml-1" v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
                                    </template>
                                    <span>Verify that no new items were added to output that aren't in input</span>
                                  </v-tooltip>
                                </template>
                              </v-checkbox>
                            </v-expansion-panel-content>
                          </v-expansion-panel>
                        </v-expansion-panels>
                      </div>
                    </div>
                  </div>

                  <v-alert
                    v-if="(selectedStep.config?.output || []).some(o => o.extractFrom === 'pass_through')"
                    type="info"
                    dense
                    text
                    class="mt-3"
                  >
                    <strong>Pass-Through Outputs:</strong> These outputs will include input parameter values alongside the step's result, allowing downstream steps to access them.
                  </v-alert>
                </v-expansion-panel-content>
              </v-expansion-panel>

              <!-- Prompt Preview Panel -->
              <v-expansion-panel v-if="selectedStep.step_type === 'prompt'">
                <v-expansion-panel-header>
                  <div class="panel-header">
                    <v-icon left small>mdi-text-box</v-icon>
                    Prompt Preview
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <div class="prompt-preview-box">
                    <div class="prompt-section">
                      <div class="prompt-section-label">System:</div>
                      <pre>{{ selectedStep.item ? selectedStep.item.system_prompt : selectedStep.system_prompt }}</pre>
                    </div>
                    <div class="prompt-section">
                      <div class="prompt-section-label">User:</div>
                      <pre>{{ selectedStep.item ? selectedStep.item.user_prompt : selectedStep.user_prompt }}</pre>
                    </div>
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </div>

        <!-- Execution Viewer Modal -->
        <v-dialog
          v-model="showExecutionViewer"
          max-width="900"
          scrollable
        >
          <v-card>
            <v-card-title class="execution-header">
              Chain Execution
              <v-spacer></v-spacer>
              <v-btn icon @click="showExecutionViewer = false">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-card-title>

            <v-card-text class="execution-content" v-if="executionSteps">
              <div
                v-for="(step, idx) in executionSteps"
                :key="idx"
                :class="['execution-step', `status-${step.status}`]"
              >
                <div class="execution-step-header">
                  <div class="execution-icon">
                    <v-icon v-if="step.status === 'completed'" color="green">mdi-check-circle</v-icon>
                    <v-icon v-else-if="step.status === 'failed'" color="red">mdi-close-circle</v-icon>
                    <v-progress-circular
                      v-else-if="step.status === 'running'"
                      indeterminate
                      color="blue"
                      size="24"
                    ></v-progress-circular>
                    <v-icon v-else color="grey">mdi-circle-outline</v-icon>
                  </div>

                  <div class="execution-info">
                    <div class="execution-name">
                      {{ step.name }}
                      <v-chip v-if="step.type === 'service'" x-small color="purple" text-color="white">
                        SERVICE
                      </v-chip>
                    </div>
                    <div class="execution-meta">
                      <span v-if="step.duration > 0">{{ step.duration }}ms</span>
                      <span v-if="step.httpStatus" class="http-status">HTTP {{ step.httpStatus }}</span>
                    </div>
                  </div>
                </div>

                <div v-if="step.output" class="execution-details">
                  <v-btn
                    text
                    x-small
                    color="blue"
                    @click="step.expanded = !step.expanded"
                  >
                    View Details
                    <v-icon right small>{{ step.expanded ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
                  </v-btn>

                  <div v-if="step.expanded" class="execution-output">
                    <div v-if="step.type === 'service'">
                      <div v-if="step.requestPayload" class="output-section">
                        <div class="output-label">Request:</div>
                        <pre>{{ JSON.stringify(step.requestPayload, null, 2) }}</pre>
                      </div>
                      <div v-if="step.responsePayload" class="output-section">
                        <div class="output-label">Response:</div>
                        <pre>{{ JSON.stringify(step.responsePayload, null, 2) }}</pre>
                      </div>
                    </div>
                    <div v-else>
                      <pre>{{ JSON.stringify(step.output, null, 2) }}</pre>
                    </div>
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-dialog>

        <!-- Service Management Modal -->
        <v-dialog
          v-model="showServiceModal"
          max-width="1200"
          scrollable
        >
          <v-card>
            <v-card-title class="service-modal-header">
              Service Library
              <v-spacer></v-spacer>
              <v-btn icon @click="showServiceModal = false">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-card-title>

            <v-card-text class="service-modal-content pa-6">
              <v-btn
                large
                block
                color="purple"
                dark
                @click="openAddServiceDialog"
                class="mb-4"
              >
                <v-icon left>mdi-plus</v-icon>
                Add New Service
              </v-btn>

              <v-divider class="my-4"></v-divider>

              <div v-if="serviceItems.length === 0" class="text-center py-8 grey--text">
                <v-icon large color="grey lighten-1">mdi-api-off</v-icon>
                <p class="mt-3">No services configured yet</p>
              </div>

              <v-row v-else>
                <v-col
                  v-for="service in serviceItems.filter(s => s.itemType === 'service_parent')"
                  :key="service.id"
                  cols="12"
                  sm="6"
                  md="4"
                >
                  <v-card outlined hover class="service-card">
                    <v-card-title class="text-subtitle-1 pb-2">
                      <v-icon left color="purple">mdi-api</v-icon>
                      {{ service.name }}
                    </v-card-title>
                    <v-card-text>
                      <div class="text-caption grey--text mb-2">
                        {{ service.description || 'No description' }}
                      </div>
                      <v-chip x-small color="purple" text-color="white">
                        {{ service.endpoint_count }} endpoint{{ service.endpoint_count !== 1 ? 's' : '' }}
                      </v-chip>
                      <v-chip x-small class="ml-1" color="grey" text-color="white">
                        {{ service.service_type || 'rest_api' }}
                      </v-chip>
                      <v-chip v-if="service.is_system" x-small class="ml-1" color="blue" text-color="white">
                        <v-icon x-small left>mdi-earth</v-icon>
                        System
                      </v-chip>
                    </v-card-text>
                    <v-card-actions>
                      <v-btn small text color="primary" @click="editService(service)">
                        <v-icon small left>mdi-pencil</v-icon>
                        Edit
                      </v-btn>
                      <v-spacer></v-spacer>
                      <v-btn small text color="error" @click="deleteServiceConfirm(service)">
                        <v-icon small left>mdi-delete</v-icon>
                        Delete
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-dialog>

        <!-- Add New Service Dialog -->
        <v-dialog
          v-model="showAddServiceDialog"
          max-width="900"
          persistent
          scrollable
        >
          <v-card>
            <v-card-title class="service-modal-header">
              {{ editingServiceId ? 'Edit Service' : 'Add New Service' }}
              <v-spacer></v-spacer>
              <v-btn icon @click="showAddServiceDialog = false" :disabled="savingService">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-card-title>

            <v-card-text class="pt-4">
              <v-form v-model="serviceFormValid">
                <v-row>
                  <v-col cols="12">
                    <v-text-field
                      v-model="serviceForm.service_name"
                      label="Service Name"
                      :rules="[v => !!v || 'Service name is required']"
                      required
                      outlined
                      dense
                    ></v-text-field>
                  </v-col>

                  <v-col cols="12">
                    <v-switch
                      v-model="serviceForm.is_system"
                      label="System Level Service (available to all clients)"
                      color="purple"
                      hide-details
                      dense
                    ></v-switch>
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-select
                      v-model="serviceForm.service_type"
                      label="Service Type"
                      :items="[
                        { text: 'REST API', value: 'rest_api' }
                      ]"
                      outlined
                      dense
                    ></v-select>
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-select
                      v-model="serviceForm.auth_type"
                      label="Authentication Type"
                      :items="[
                        { text: 'None', value: 'none' },
                        { text: 'Internal (Auto JWT)', value: 'internal' },
                        { text: 'API Key', value: 'api_key' },
                        { text: 'Bearer Token', value: 'bearer' },
                        { text: 'OAuth2', value: 'oauth2' },
                        { text: 'Basic Auth', value: 'basic' }
                      ]"
                      outlined
                      dense
                    ></v-select>
                  </v-col>

                  <v-col cols="12">
                    <v-text-field
                      v-model="serviceForm.base_url"
                      label="Base URL"
                      :rules="[v => !!v || 'Base URL is required']"
                      required
                      outlined
                      dense
                      placeholder="https://api.example.com"
                    ></v-text-field>
                  </v-col>

                  <v-col cols="12">
                    <v-textarea
                      v-model="serviceForm.description"
                      label="Description"
                      outlined
                      dense
                      rows="3"
                    ></v-textarea>
                  </v-col>

                  <v-col cols="12">
                    <v-expansion-panels flat>
                      <v-expansion-panel>
                        <v-expansion-panel-header>
                          Advanced Settings
                        </v-expansion-panel-header>
                        <v-expansion-panel-content>
                          <v-row>
                            <v-col cols="12" md="6">
                              <v-text-field
                                v-model="serviceForm.healthcheck_endpoint"
                                label="Health Check Endpoint"
                                outlined
                                dense
                                placeholder="/health"
                              ></v-text-field>
                            </v-col>

                            <v-col cols="12" md="6">
                              <v-select
                                v-model="serviceForm.healthcheck_method"
                                label="Health Check Method"
                                :items="[
                                  { text: 'GET', value: 'GET' },
                                  { text: 'POST', value: 'POST' },
                                  { text: 'HEAD', value: 'HEAD' }
                                ]"
                                outlined
                                dense
                              ></v-select>
                            </v-col>

                            <v-col cols="12" md="4">
                              <v-text-field
                                v-model.number="serviceForm.timeout"
                                label="Timeout (seconds)"
                                type="number"
                                outlined
                                dense
                              ></v-text-field>
                            </v-col>

                            <v-col cols="12" md="4">
                              <v-text-field
                                v-model.number="serviceForm.retry_attempts"
                                label="Retry Attempts"
                                type="number"
                                outlined
                                dense
                              ></v-text-field>
                            </v-col>

                            <v-col cols="12" md="4">
                              <v-text-field
                                v-model.number="serviceForm.healthcheck_interval"
                                label="Health Check Interval (seconds)"
                                type="number"
                                outlined
                                dense
                              ></v-text-field>
                            </v-col>

                            <v-col cols="12" v-if="serviceForm.auth_type !== 'none' && serviceForm.auth_type !== 'internal'">
                              <v-text-field
                                v-model="serviceForm.auth_config_key"
                                label="Auth Config Key"
                                outlined
                                dense
                                placeholder="Enter configuration key for authentication"
                              ></v-text-field>
                            </v-col>

                            <v-col cols="12" v-if="serviceForm.auth_type !== 'none' && serviceForm.auth_type !== 'internal'">
                              <div class="mb-2">
                                <strong>Authentication Headers</strong>
                                <v-btn
                                  x-small
                                  color="purple"
                                  dark
                                  @click="addAuthHeader"
                                  class="ml-2"
                                >
                                  <v-icon small>mdi-plus</v-icon>
                                  Add Header
                                </v-btn>
                              </div>
                              <v-row
                                v-for="(header, index) in serviceForm.auth_config.headers"
                                :key="index"
                                class="mb-2"
                              >
                                <v-col cols="5">
                                  <v-text-field
                                    v-model="header.key"
                                    label="Header Key"
                                    outlined
                                    dense
                                    placeholder="e.g., Tx-AccountId"
                                  ></v-text-field>
                                </v-col>
                                <v-col cols="6">
                                  <v-text-field
                                    v-model="header.value"
                                    label="Header Value"
                                    outlined
                                    dense
                                    placeholder="e.g., ACCOUNT_ID"
                                  ></v-text-field>
                                </v-col>
                                <v-col cols="1" class="d-flex align-center">
                                  <v-btn
                                    icon
                                    small
                                    @click="removeAuthHeader(index)"
                                  >
                                    <v-icon>mdi-delete</v-icon>
                                  </v-btn>
                                </v-col>
                              </v-row>
                            </v-col>

                            <v-col cols="12">
                              <v-switch
                                v-model="serviceForm.active"
                                :true-value="1"
                                :false-value="0"
                                label="Active"
                              ></v-switch>
                            </v-col>
                          </v-row>
                        </v-expansion-panel-content>
                      </v-expansion-panel>
                    </v-expansion-panels>
                  </v-col>

                  <v-col cols="12">
                    <v-divider class="my-4"></v-divider>
                    <div class="d-flex align-center mb-3">
                      <h3>Service Endpoints</h3>
                      <v-spacer></v-spacer>
                      <v-btn
                        color="purple"
                        dark
                        small
                        @click="openEndpointEditor()"
                      >
                        <v-icon left small>mdi-plus</v-icon>
                        Add Endpoint
                      </v-btn>
                    </div>

                    <v-list v-if="serviceForm.endpoints.length > 0" dense>
                      <v-list-item
                        v-for="(endpoint, index) in serviceForm.endpoints"
                        :key="index"
                        class="mb-2"
                        outlined
                      >
                        <v-list-item-content>
                          <v-list-item-title>
                            <v-chip x-small class="mr-2">{{ endpoint.http_method }}</v-chip>
                            {{ endpoint.endpoint_name }}
                          </v-list-item-title>
                          <v-list-item-subtitle>{{ endpoint.endpoint_path }}</v-list-item-subtitle>
                        </v-list-item-content>
                        <v-list-item-action>
                          <div>
                            <v-btn icon small @click="openEndpointEditor(index)">
                              <v-icon small>mdi-pencil</v-icon>
                            </v-btn>
                            <v-btn icon small @click="removeEndpoint(index)">
                              <v-icon small>mdi-delete</v-icon>
                            </v-btn>
                          </div>
                        </v-list-item-action>
                      </v-list-item>
                    </v-list>
                    <div v-else class="text-center py-4 grey--text">
                      No endpoints defined yet. Click "Add Endpoint" to create one.
                    </div>
                  </v-col>
                </v-row>
              </v-form>
            </v-card-text>

            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                text
                @click="showAddServiceDialog = false"
                :disabled="savingService"
              >
                Cancel
              </v-btn>
              <v-btn
                color="purple"
                dark
                @click="saveService"
                :loading="savingService"
                :disabled="!serviceFormValid"
              >
                {{ editingServiceId ? 'Update Service' : 'Save Service' }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- Endpoint Editor Dialog -->
        <v-dialog
          v-model="showEndpointEditor"
          max-width="1000"
          persistent
          scrollable
        >
          <v-card>
            <v-card-title class="service-modal-header">
              {{ editingEndpointIndex >= 0 ? 'Edit' : 'Add' }} Endpoint
              <v-spacer></v-spacer>
              <v-btn icon @click="showEndpointEditor = false">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-card-title>

            <v-card-text class="pt-4">
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    v-model="currentEndpoint.endpoint_name"
                    label="Endpoint Name"
                    outlined
                    dense
                    required
                  ></v-text-field>
                </v-col>

                <v-col cols="12" md="8">
                  <v-text-field
                    v-model="currentEndpoint.endpoint_path"
                    label="Endpoint Path"
                    outlined
                    dense
                    required
                    placeholder="e.g., skills/extract"
                  ></v-text-field>
                </v-col>

                <v-col cols="12" md="4">
                  <v-select
                    v-model="currentEndpoint.http_method"
                    label="HTTP Method"
                    :items="['GET', 'POST', 'PUT', 'PATCH', 'DELETE']"
                    outlined
                    dense
                  ></v-select>
                </v-col>

                <v-col cols="12">
                  <v-textarea
                    v-model="currentEndpoint.description"
                    label="Description"
                    outlined
                    dense
                    rows="2"
                  ></v-textarea>
                </v-col>

                <v-col cols="12" md="6">
                  <v-textarea
                    v-model="currentEndpoint.input_schema"
                    label="Input Schema (JSON)"
                    outlined
                    rows="8"
                    placeholder='{"Text": "", "Language": "en", "Threshold": 0.5}'
                    hint="Define the input parameters as JSON"
                  ></v-textarea>
                </v-col>

                <v-col cols="12" md="6">
                  <v-textarea
                    v-model="currentEndpoint.output_schema"
                    label="Output Schema (JSON)"
                    outlined
                    rows="8"
                    placeholder='{"Skills": "data.Value.Skills"}'
                    hint="Define how to extract output from response"
                  ></v-textarea>
                </v-col>

                <v-col cols="12">
                  <v-expansion-panels flat>
                    <v-expansion-panel>
                      <v-expansion-panel-header>
                        Examples & Additional Configuration
                      </v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <v-row>
                          <v-col cols="12" md="6">
                            <v-textarea
                              v-model="currentEndpoint.request_example"
                              label="Request Example (JSON)"
                              outlined
                              rows="6"
                            ></v-textarea>
                          </v-col>

                          <v-col cols="12" md="6">
                            <v-textarea
                              v-model="currentEndpoint.response_example"
                              label="Response Example (JSON)"
                              outlined
                              rows="6"
                            ></v-textarea>
                          </v-col>

                          <v-col cols="12">
                            <v-switch
                              v-model="currentEndpoint.active"
                              :true-value="1"
                              :false-value="0"
                              label="Active"
                            ></v-switch>
                          </v-col>
                        </v-row>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </v-col>
              </v-row>
            </v-card-text>

            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn text @click="showEndpointEditor = false">
                Cancel
              </v-btn>
              <v-btn color="purple" dark @click="saveEndpoint">
                {{ editingEndpointIndex >= 0 ? 'Update' : 'Add' }} Endpoint
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- Test Prompt Dialog -->
        <v-dialog
          v-model="showTestPromptDialog"
          max-width="800"
          persistent
        >
          <v-card>
            <v-card-title class="headline">
              Test Prompt: {{ testPromptStep?.step_name || 'Unnamed Step' }}
            </v-card-title>

            <v-card-text>
              <div v-if="testPromptStep">
                <!-- Prompt Preview -->
                <div class="mb-4">
                  <h4 class="mb-2">System Prompt:</h4>
                  <div class="pa-3 prompt-preview-panel">
                    <pre class="prompt-preview-pre">{{ testPromptStep.item ? testPromptStep.item.system_prompt : testPromptStep.system_prompt }}</pre>
                  </div>
                </div>

                <div class="mb-4">
                  <h4 class="mb-2">User Prompt:</h4>
                  <div class="pa-3 prompt-preview-panel">
                    <pre class="prompt-preview-pre">{{ testPromptStep.item ? testPromptStep.item.user_prompt : testPromptStep.user_prompt }}</pre>
                  </div>
                </div>

                <!-- Input Fields -->
                <v-divider class="my-4"></v-divider>
                <h4 class="mb-3">Provide Test Values:</h4>

                <div v-if="Object.keys(testPromptInputs).length === 0" class="text-center pa-4 grey--text">
                  No inputs configured for this prompt
                </div>

                <div v-for="(value, key) in testPromptInputs" :key="key" class="mb-3">
                  <v-textarea
                    v-model="testPromptInputs[key]"
                    :label="key"
                    outlined
                    dense
                    rows="2"
                    hide-details
                  ></v-textarea>
                </div>

                <!-- Test Results -->
                <div v-if="testPromptResult" class="mt-4">
                  <v-divider class="my-4"></v-divider>
                  <h4 class="mb-2">Result:</h4>
                  <div class="pa-3 prompt-result-panel">
                    <pre class="prompt-preview-pre">{{ JSON.stringify(testPromptResult, null, 2) }}</pre>
                  </div>
                </div>
              </div>
            </v-card-text>

            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                text
                @click="closeTestPromptDialog"
                :disabled="testPromptLoading"
              >
                Close
              </v-btn>
              <v-btn
                color="#48A401"
                dark
                @click="executeTestPrompt"
                :loading="testPromptLoading"
                :disabled="Object.keys(testPromptInputs).length === 0"
              >
                <v-icon left>mdi-play</v-icon>
                Execute Test
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
<v-dialog
          v-model="showDocumentFormatterDialog"
          max-width="1000"
          persistent
          scrollable
        >
          <v-card>
            <v-card-title class="document-formatter-header">
              <v-icon left color="orange">mdi-file-document-plus</v-icon>
              Document Formatter Configuration
              <v-spacer></v-spacer>
              <v-btn icon @click="cancelDocumentMappings">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-card-title>

            <v-card-text class="document-formatter-content">
              <div class="formatter-description">
                <p>Map step inputs and outputs to template parts for document generation. Select data from previous steps and specify which template part and attribute to populate.</p>
              </div>

              <div class="mappings-header">
                <h3>Data Mappings</h3>
                <v-btn
                  color="#5E2872"
                  dark
                  small
                  @click="addDocumentMapping"
                  :disabled="loadingTemplateParts"
                >
                  <v-icon left small>mdi-plus</v-icon>
                  Add Mapping
                </v-btn>
              </div>
              <div v-if="loadingTemplateParts" class="loading-state">
                <v-progress-circular indeterminate color="primary"></v-progress-circular>
                <span class="ml-3">Loading template parts...</span>
              </div>

              <div v-else-if="documentFormatterMappings.length === 0" class="empty-mappings">
                <v-icon size="48" color="grey">mdi-link-variant-off</v-icon>
                <h4>No Mappings Configured</h4>
                <p>Add mappings to connect step data with template parts</p>
                <v-btn
                  color="#5E2872"
                  dark
                  large
                  class="mt-4"
                  @click="addDocumentMapping"
                >
                  <v-icon left>mdi-plus</v-icon>
                  Create Your First Mapping
                </v-btn>
              </div>

              <div v-else class="mappings-list">
                <v-card
                  v-for="(mapping, index) in documentFormatterMappings"
                  :key="mapping.id"
                  outlined
                  :class="['mapping-card', 'mb-4', { 'inactive-mapping': mapping.is_active === false }]"
                >
                  <!-- Collapsible Header -->
                  <div class="mapping-card-header" @click="toggleMappingCollapse(mapping.id)">
                    <div class="mapping-header-content">
                      <div class="mapping-title-row">
                        <span class="mapping-title">Mapping {{ index + 1 }}</span>
                        <v-chip
                          x-small
                          :color="mapping.is_active ? 'success' : 'grey'"
                          text-color="white"
                          class="ml-2"
                        >
                          {{ mapping.is_active ? 'Active' : 'Inactive' }}
                        </v-chip>
                        <div class="mapping-summary" v-if="mapping.stepDataId && (mapping.templatePart || mapping.mappingTargetType === 'hierarchy_value')">
                          <v-chip x-small color="blue" outlined class="mr-1">
                            {{ mapping.stepName }}
                          </v-chip>
                          <v-icon small color="grey" class="mx-1">mdi-arrow-right</v-icon>
                          <v-chip v-if="mapping.mappingTargetType === 'hierarchy_value'" x-small color="purple" outlined>
                            {{ getHierarchyDisplayName(mapping.hierarchyValueType, mapping.hierarchyValueLevel) }}
                          </v-chip>
                          <v-chip v-else-if="mapping.templatePart" x-small color="green" outlined>
                            {{ mapping.templatePart.name }}
                            <span v-if="mapping.childTemplatePart">
                              → {{ mapping.childTemplatePart.name }}
                            </span>
                          </v-chip>
                        </div>
                        <div v-else class="mapping-incomplete">
                          <v-chip x-small color="grey" outlined>
                            Incomplete mapping
                          </v-chip>
                        </div>
                      </div>
                      <div class="mapping-status-indicator" v-if="!isMappingCollapsed(mapping.id)">
                        <small class="text-grey">Click to collapse</small>
                      </div>
                    </div>

                    <div class="mapping-header-actions">
                      <v-switch
                        :input-value="mapping.is_active"
                        @change="updateDocumentMapping(index, 'is_active', $event)"
                        @click.stop
                        dense
                        hide-details
                        class="mt-0 mr-2"
                        :label="mapping.is_active ? 'Active' : 'Inactive'"
                        color="success"
                      ></v-switch>
                      <v-btn
                        icon
                        small
                        class="collapse-toggle"
                      >
                        <v-icon small>
                          {{ isMappingCollapsed(mapping.id) ? 'mdi-chevron-down' : 'mdi-chevron-up' }}
                        </v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        small
                        color="error"
                        @click.stop="removeDocumentMapping(index)"
                      >
                        <v-icon small>mdi-delete</v-icon>
                      </v-btn>
                    </div>
                  </div>

                  <!-- Collapsible Content -->
                  <v-expand-transition>
                    <v-card-text v-show="!isMappingCollapsed(mapping.id)">
                      <v-row dense class="mt-2">
                        <!-- Step Data Selection -->
                        <v-col cols="6">
                          <v-select
                            :value="mapping.stepDataId"
                            :items="getAvailableStepData()"
                            item-text="displayName"
                            item-value="id"
                            label="Source Data"
                            placeholder="Select step input or output..."
                            outlined
                            dense
                            hide-details
                            @change="updateDocumentMapping(index, 'stepDataId', $event)"
                          >
                            <template v-slot:item="{ item }">
                              <div class="step-data-item-enhanced">
                                <div class="step-data-main">
                                  <div class="step-indicator-small">
                                    {{ item.stepOrder }}
                                  </div>
                                  <div class="step-data-content">
                                    <div class="step-data-header">
                                      <span class="step-name-small">{{ item.stepName }}</span>
                                      <v-chip 
                                        x-small 
                                        :color="item.type === 'input' ? 'blue' : 'green'" 
                                        outlined
                                        class="type-indicator"
                                      >
                                        {{ item.type === 'input' ? 'INPUT' : 'OUTPUT' }}
                                      </v-chip>
                                    </div>
                                    <div class="property-name">{{ item.property }}</div>
                                    <div class="source-info" v-if="item.source && item.type === 'input'">
                                      Source: {{ item.source }}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </template>
                          </v-select>
                        </v-col>

                        <!-- Mapping Target Type Selection -->
                        <v-col cols="3">
                          <v-select
                            :value="mapping.mappingTargetType || 'template_part'"
                            :items="[
                              { text: 'Template Part', value: 'template_part' },
                              { text: 'Hierarchy Value', value: 'hierarchy_value' }
                            ]"
                            label="Map To"
                            outlined
                            dense
                            hide-details
                            @change="updateDocumentMapping(index, 'mappingTargetType', $event)"
                          ></v-select>
                        </v-col>

                        <!-- Template Part Selection (only if template_part is selected) -->
                        <v-col cols="3" v-if="mapping.mappingTargetType !== 'hierarchy_value'">
                          <v-select
                            :value="mapping.templatePart"
                            :items="availableTemplateParts.filter(tp => !tp.parent_tp_id)"
                            item-text="name"
                            item-value="tp_id"
                            return-object
                            label="Template Part"
                            placeholder="Select template part..."
                            outlined
                            dense
                            hide-details
                            @change="updateDocumentMapping(index, 'templatePartId', $event)"
                          >
                            <template v-slot:item="{ item }">
                              <div style="display: inline-flex; gap: 10px;">
                                <div class="template-part-name">{{ item.name }}</div>
                                <div class="template-part-description" v-if="item.child_parts_count > 0">
                                  ({{ item.child_parts_count }} child part(s))
                                </div>
                              </div>
                            </template>
                          </v-select>
                        </v-col>

                        <!-- Hierarchy Value Selection (only if hierarchy_value is selected) -->
                        <v-col cols="3" v-if="mapping.mappingTargetType === 'hierarchy_value'">
                          <v-select
                            :value="mapping.hierarchyValueType"
                            :items="hierarchyList"
                            item-text="label"
                            item-value="ht_id"
                            label="Hierarchy Value"
                            placeholder="Select hierarchy value..."
                            outlined
                            dense
                            hide-details
                            @change="updateDocumentMapping(index, 'hierarchyValueType', $event)"
                          >
                            <template v-slot:item="{ item }">
                              <div>
                                <div class="hierarchy-value-label">{{ item.label }}</div>
                                <div class="text-caption grey--text">{{ item.description }}</div>
                              </div>
                            </template>
                          </v-select>
                        </v-col>
                        <v-col cols="3" v-if="mapping.mappingTargetType === 'hierarchy_value' && mapping.hierarchyValueType">
                          <v-select
                            :value="mapping.hierarchyValueLevel"
                            :items="getAvailableHrLevel(mapping.hierarchyValueType)"
                            item-text="name"
                            item-value="index"
                            label="Hierarchy Value"
                            placeholder="Select hierarchy value..."
                            outlined
                            dense
                            hide-details
                            @change="updateDocumentMapping(index, 'hierarchyValueLevel', $event)"
                          >
                            <template v-slot:item="{ item }">
                              <div>
                                <div class="hierarchy-value-label">{{ item.name }}</div>
                              </div>
                            </template>
                          </v-select>
                        </v-col>                                                    
                      </v-row>

                      <!-- Child Template Part Selection (only for template parts with children) -->
                      <v-row dense class="mt-2" v-if="mapping.mappingTargetType !== 'hierarchy_value' && mapping.templatePart && mapping.templatePart.child_parts_count > 0">

                        <v-col cols="3">
                          <v-select
                            :value="mapping.childTemplatePart"
                            :items="mapping.templatePart.child_parts"
                            item-text="name"
                            item-value="tp_id"
                            return-object
                            label="Child Part Type"
                            placeholder="Select child part..."
                            outlined
                            dense
                            hide-details
                            @change="updateDocumentMapping(index, 'childTemplatePartId', $event)"
                          >
                          </v-select>
                        </v-col>

                        <!-- Attribute Selection (only if no children or child is selected) -->
                        <v-col cols="3" v-if="mapping.templatePart && mapping.templatePart.attributes?.length > 0 && mapping.templatePart.cardinality === 1">
                          <v-select
                            :value="mapping.attributeId"
                            :items="mapping.templatePart.attributes"
                            item-text="title"
                            item-value="tpa_id"
                            label="Attribute"
                            placeholder="Select attribute..."
                            outlined
                            dense
                            hide-details
                            @change="updateDocumentMapping(index, 'attributeId', $event)"
                          >
                            <template v-slot:no-data>
                              <div class="pa-2 grey--text">
                                No attributes available
                              </div>
                            </template>
                          </v-select>
                        </v-col>
                      </v-row>
                      
                      <v-row>
                        <!-- Use JSON Object Checkbox -->
                        <v-col cols="12">
                          <v-checkbox
                            :input-value="mapping.use_json_object"
                            @change="updateDocumentMapping(index, 'use_json_object', $event)"
                            label="Use JSON Object"
                            :hint="mapping.has_child_part ? 'Parse JSON objects/arrays with parent and child properties (e.g., [{&quot;Responsibility&quot;: &quot;&quot;, &quot;Tasks&quot;: [&quot;Task 1&quot;]}])' : 'Parse JSON objects/arrays instead of using delimiters'"
                            persistent-hint
                            dense
                            class="mt-1"
                          ></v-checkbox>
                        </v-col>    
                      </v-row>

                      <!-- Delimiter and Mapping Mode Configuration -->
                      <v-row dense class="mt-2" v-if="mapping.templatePart && (mapping.childTemplatePart || mapping.templatePart.cardinality === 0) || mapping.use_json_object">
                        <v-col cols="12">
                          <div class="delimiter-section-header">
                            <h4 class="section-title">Part -> Document Mapping Configuration</h4>
                            <span class="section-subtitle">{{ mapping.templatePart?.name || 'Unknown Part' }} (Multiple instances allowed)</span>
                          </div>
                        </v-col>

                        <!-- Delimiter-based configuration (shown when NOT using JSON) -->
                        <v-col cols="4" v-if="!mapping.use_json_object">
                          <v-text-field
                            :value="mapping.delimiter"
                            label="Primary Delimiter"
                            placeholder="e.g., || or ;"
                            outlined
                            dense
                            hide-details
                            @input="updateDocumentMapping(index, 'parentDelimiter', $event)"
                          ></v-text-field>
                        </v-col>

                        <!-- JSON property name configuration (shown when using JSON) -->
                        <v-col :cols="mapping.has_child_part ? 6 : 4" v-if="mapping.use_json_object && mapping.templatePart && !mapping.templatePart.isTagPart">
                          <v-text-field
                            :value="mapping.json_primary_property"
                            :label="mapping.has_child_part ? 'Parent Property Name' : 'Primary Property Name'"
                            placeholder="e.g., text, name, or Responsibility"
                            :hint="mapping.has_child_part ? 'JSON property containing the parent text value' : 'JSON property containing the main value'"
                            persistent-hint
                            outlined
                            dense
                            @input="updateDocumentMapping(index, 'json_primary_property', $event)"
                          ></v-text-field>
                        </v-col>
                        <v-col cols="4" v-if="mapping.templatePart?.attributes?.length > 0 && mapping.use_json_object && !mapping.templatePart?.isTagPart">
                          <v-select
                            :value="mapping.json_mapping_mode"
                            :items="[
                              { text: 'Use full object', value: 'full_object' },
                              { text: 'Map properties to attributes', value: 'map_properties' }
                            ]"
                            label="JSON Mapping Mode"
                            outlined
                            dense
                            hide-details
                            @change="updateDocumentMapping(index, 'json_mapping_mode', $event)"
                          ></v-select>
                        </v-col>
                        <!-- JSON Child Property field (when using JSON mode with children) -->
                        <v-col cols="6" v-if="mapping.has_child_part && mapping.use_json_object">
                          <v-text-field
                            :value="mapping.json_child_property"
                            label="Child Items Property Name"
                            placeholder="e.g., childItems, children, or Tasks"
                            hint="JSON property containing child items array"
                            persistent-hint
                            outlined
                            dense
                            @input="updateDocumentMapping(index, 'json_child_property', $event)"
                          ></v-text-field>
                        </v-col>

                        <!-- Show Child Delimiter field if mapping has child_part_delimiter -->
                        <v-col cols="6" v-if="mapping.has_child_part && !mapping.use_json_object">
                          <v-text-field
                            :value="mapping.child_part_delimiter"
                            label="Child Delimiter"
                            placeholder="e.g., |"
                            outlined
                            dense
                            hide-details
                            @input="updateDocumentMapping(index, 'childDelimiter', $event)"
                          ></v-text-field>
                        </v-col>
                        
                        <v-col cols="4" v-if="mapping.templatePart?.attributes?.length > 0 && !mapping.use_json_object">
                          <v-select
                            :value="mapping.parentMappingMode"
                            :items="[
                              { text: 'Use full value', value: 'full_value' },
                              { text: 'Map parts to attributes', value: 'map_parts' }
                            ]"
                            label="Mapping Mode"
                            outlined
                            dense
                            hide-details
                            @change="updateDocumentMapping(index, 'parentMappingMode', $event)"
                          ></v-select>
                        </v-col>
                        
                        <v-col cols="4" v-if="mapping.parentMappingMode === 'map_parts' && !mapping.use_json_object">
                          <v-text-field
                            :value="mapping.parentSecondaryDelimiter"
                            label="Secondary Delimiter"
                            placeholder="e.g., : or -"
                            outlined
                            dense
                            hide-details
                            @input="updateDocumentMapping(index, 'parentSecondaryDelimiter', $event)"
                          ></v-text-field>
                        </v-col>
                      </v-row>

                      <!-- Tag Part Delimiter Configuration (only for tag parts) -->
                      <v-row dense class="mt-2" v-if="mapping.templatePart && mapping.templatePart.isTagPart">
                        <!-- Delimiter-based tag configuration -->
                        <v-col cols="4" v-if="!mapping.use_json_object">
                          <v-text-field
                            :value="mapping.delimiter"
                            label="Tag Delimiter"
                            placeholder="e.g., || or ;"
                            hint="Character(s) used to separate individual tags"
                            persistent-hint
                            outlined
                            dense
                            @input="updateDocumentMapping(index, 'parentDelimiter', $event)"
                          ></v-text-field>
                        </v-col>

                        <!-- JSON property for tag name -->
                        <v-col cols="4" v-if="mapping.use_json_object">
                          <v-text-field
                            :value="mapping.json_tag_property"
                            label="Tag Property Name"
                            placeholder="e.g., tag or name"
                            hint="JSON property containing tag name"
                            persistent-hint
                            outlined
                            dense
                            @input="updateDocumentMapping(index, 'json_tag_property', $event)"
                          ></v-text-field>
                        </v-col>

                        <v-col cols="4">
                          <v-checkbox
                            :input-value="mapping.uses_tag_rating"
                            @change="updateDocumentMapping(index, 'uses_tag_rating', $event)"
                            label="Map Tag Ratings"
                            :hint="mapping.use_json_object ? 'Extract ratings from JSON property' : 'Split tag values to extract ratings'"
                            persistent-hint
                            dense
                            class="mt-1"
                          ></v-checkbox>
                        </v-col>

                        <!-- Delimiter-based rating configuration -->
                        <v-col cols="4" v-if="mapping.uses_tag_rating && !mapping.use_json_object">
                          <v-text-field
                            :value="mapping.tag_rating_delimiter"
                            label="Rating Delimiter"
                            placeholder="e.g., : or |"
                            hint="Separates tag name from rating value"
                            persistent-hint
                            outlined
                            dense
                            @input="updateDocumentMapping(index, 'tag_rating_delimiter', $event)"
                          ></v-text-field>
                        </v-col>

                        <!-- JSON property for rating -->
                        <v-col cols="4" v-if="mapping.uses_tag_rating && mapping.use_json_object">
                          <v-text-field
                            :value="mapping.json_rating_property"
                            label="Rating Property Name"
                            placeholder="e.g., rating or value"
                            hint="JSON property containing rating value"
                            persistent-hint
                            outlined
                            dense
                            @input="updateDocumentMapping(index, 'json_rating_property', $event)"
                          ></v-text-field>
                        </v-col>

                        <!-- Top X limit -->
                        <v-col cols="4">
                          <v-text-field
                            :value="mapping.tag_limit || ''"
                            label="Top X Limit"
                            placeholder="e.g., 10"
                            hint="Limit to top X items (blank = all)"
                            persistent-hint
                            outlined
                            dense
                            type="number"
                            @input="updateDocumentMapping(index, 'tag_limit', $event ? parseInt($event) : null)"
                          ></v-text-field>
                        </v-col>
                      </v-row>

                      <!-- Parent Part Attribute Selection for Full Value Mode -->
                      <v-row dense class="mt-2" v-if="mapping.templatePart && mapping.parentMappingMode === 'full_value' && mapping.templatePart.attributes?.length > 0 && !mapping.use_json_object">
                        <v-col cols="6">
                          <v-select
                            :value="mapping.attributeId"
                            :items="mapping.templatePart.attributes"
                            item-text="title"
                            item-value="tpa_id"
                            label="Target Attribute"
                            placeholder="Select attribute..."
                            outlined
                            dense
                            hide-details
                            @change="updateDocumentMapping(index, 'attributeId', $event)"
                          >
                            <template v-slot:no-data>
                              <div class="pa-2 grey--text">
                                No attributes available for this part
                              </div>
                            </template>
                          </v-select>
                        </v-col>
                      </v-row>
                      <v-row dense class="mt-2" v-if="mapping.templatePart && mapping.json_mapping_mode === 'full_object' && mapping.use_json_object && mapping.templatePart.attributes?.length > 0 && !mapping.templatePart.isTagPart">
                        <v-col cols="6">
                          <v-select
                            :value="mapping.json_target_attribute_id"
                            :items="mapping.templatePart.attributes"
                            item-text="title"
                            item-value="tpa_id"
                            label="Target Attribute for JSON Object"
                            placeholder="Select attribute..."
                            outlined
                            dense
                            hide-details
                            @change="updateDocumentMapping(index, 'json_target_attribute_id', $event)"
                          >
                            <template v-slot:no-data>
                              <div class="pa-2 grey--text">
                                No attributes available for this part
                              </div>
                            </template>
                          </v-select>
                        </v-col>
                      </v-row>
                      <v-row dense class="mt-2" v-if="mapping.json_mapping_mode === 'map_properties' && mapping.use_json_object && mapping.templatePart && !mapping.templatePart.isTagPart">
                        <v-col cols="12">
                          <div class="attribute-mappings-section">
                            <h5 class="mappings-title">JSON Property to Attribute Mappings</h5>
                            <small class="mappings-subtitle">Map JSON object properties to document attributes</small>

                            <div class="json-property-mappings">
                              <div
                                v-for="(propMapping, propIdx) in (mapping.json_property_mappings || [])"
                                :key="propIdx"
                                class="json-mapping-row"
                              >
                                <v-text-field
                                  :value="propMapping.json_property"
                                  label="JSON Property"
                                  placeholder="e.g., name, description"
                                  outlined
                                  dense
                                  hide-details
                                  class="json-property-input"
                                  @input="updateJsonPropertyMapping(index, propIdx, 'json_property', $event)"
                                ></v-text-field>
                                <v-icon class="mapping-arrow">mdi-arrow-right</v-icon>
                                <v-select
                                  :value="propMapping.attribute_id"
                                  :items="mapping.templatePart.attributes"
                                  item-text="title"
                                  item-value="tpa_id"
                                  label="Target Attribute"
                                  placeholder="Select attribute..."
                                  outlined
                                  dense
                                  hide-details
                                  class="attribute-select"
                                  @change="updateJsonPropertyMapping(index, propIdx, 'attribute_id', $event)"
                                >
                                  <template v-slot:no-data>
                                    <div class="pa-2 grey--text">
                                      No attributes available
                                    </div>
                                  </template>
                                </v-select>
                                <v-btn
                                  icon
                                  small
                                  color="error"
                                  @click="removeJsonPropertyMapping(index, propIdx)"
                                  class="remove-mapping-btn"
                                >
                                  <v-icon small>mdi-delete</v-icon>
                                </v-btn>
                              </div>
                              <v-btn
                                text
                                small
                                color="primary"
                                @click="addJsonPropertyMapping(index)"
                                class="mt-2"
                              >
                                <v-icon small left>mdi-plus</v-icon>
                                Add Property Mapping
                              </v-btn>
                            </div>
                          </div>
                        </v-col>
                      </v-row>
                      
                      <!-- Parent Attribute Mappings (when mapping mode is map_parts) -->
                      <v-row dense class="mt-2" v-if="mapping.parentMappingMode === 'map_parts' && mapping.templatePart && mapping.attr_list && mapping.attr_list.length > 0">
                        <v-col cols="12">
                          <div class="attribute-mappings-section">
                            <h5 class="mappings-title">Attribute Selection & Index Mappings</h5>
                            <small class="mappings-subtitle">Select which attributes to map and assign split value indices</small>
                            
                            <div class="mappings-grid">
                              <div 
                                v-for="(attrMapping, attrIdx) in mapping.attr_list" 
                                :key="attrMapping.tpa_id"
                                class="mapping-row"
                              >
                                <v-checkbox
                                  :input-value="attrMapping.mapped"
                                  @change="toggleAttributeMapping(index, attrIdx)"
                                  hide-details
                                  dense
                                  class="mapping-checkbox"
                                ></v-checkbox>
                                <div class="attribute-name" :class="{ 'disabled-attribute': !attrMapping.mapped }">
                                  {{ attrMapping.title }}
                                </div>
                                <v-text-field
                                  :value="attrMapping.source_index"
                                  label="Index"
                                  type="number"
                                  min="0"
                                  outlined
                                  dense
                                  hide-details
                                  class="index-input"
                                  :disabled="!attrMapping.mapped"
                                  @input="updateAttributeMapping(index, attrIdx, 'sourceIndex', parseInt($event) || 0)"
                                ></v-text-field>
                              </div>
                            </div>
                          </div>
                        </v-col>
                      </v-row>

                      <!-- Child Attribute Mappings (when child mapping mode is map_parts) -->
                      <v-row dense class="mt-2" v-if="mapping.childMappingMode === 'map_parts' && mapping.childTemplatePart && mapping.attr_list && mapping.attr_list.length > 0">
                        <v-col cols="12">
                          <div class="attribute-mappings-section">
                            <h5 class="mappings-title">Child Attribute Selection & Index Mappings</h5>
                            <small class="mappings-subtitle">Select which child attributes to map and assign split value indices</small>
                            
                            <div class="mappings-grid">
                              <div 
                                v-for="(attrMapping, attrIdx) in mapping.attr_list" 
                                :key="attrMapping.tpa_id"
                                class="mapping-row"
                              >
                                <v-checkbox
                                  :input-value="attrMapping.mapped"
                                  @change="toggleAttributeMapping(index, attrIdx)"
                                  hide-details
                                  dense
                                  class="mapping-checkbox"
                                ></v-checkbox>
                                <div class="attribute-name" :class="{ 'disabled-attribute': !attrMapping.mapped }">
                                  {{ attrMapping.title }}
                                </div>
                                <v-text-field
                                  :value="attrMapping.source_index"
                                  label="Index"
                                  type="number"
                                  min="0"
                                  outlined
                                  dense
                                  hide-details
                                  class="index-input"
                                  :disabled="!attrMapping.mapped"
                                  @input="updateAttributeMapping(index, attrIdx, 'sourceIndex', parseInt($event) || 0)"
                                ></v-text-field>
                              </div>
                            </div>
                          </div>
                        </v-col>
                        <!-- Show Child Delimiter field if mapping has child_part_delimiter -->
                        <v-col cols="6" v-if="mapping.child_part_delimiter !== undefined">
                          <v-text-field
                            :value="mapping.child_part_delimiter"
                            label="Child Delimiter"
                            placeholder="e.g., |"
                            outlined
                            dense
                            hide-details
                            @input="updateDocumentMapping(index, 'childDelimiter', $event)"
                          ></v-text-field>
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-expand-transition>                    
                  </v-card>
                </div>
              </v-card-text>

              <v-card-actions class="document-formatter-actions">
                <v-spacer></v-spacer>
                <v-btn
                  text
                  @click="cancelDocumentMappings"
                >
                  Cancel
                </v-btn>
                <v-btn
                  color="#5E2872"
                  dark
                  @click="saveDocumentMappings"
                  :disabled="documentFormatterMappings.length === 0"
                >
                  Save Mappings
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </div>
        <div v-else-if="activeTab === 'triggers'" class="triggers-view">
          <div class="triggers-container">
            <div class="triggers-header">
              <div>
                <h2 class="triggers-title">Chain Triggers</h2>
                <p class="triggers-subtitle">Configure when and how this chain executes</p>
              </div>
              <v-btn
                color="#5E2872"
                dark
                @click="openTriggerModal()"
              >
                <v-icon left>mdi-plus</v-icon>
                Add Trigger
              </v-btn>
            </div>

            <!-- Empty State -->
            <div v-if="chain.triggers.length === 0" class="triggers-empty">
              <v-icon size="64" color="grey">mdi-flash</v-icon>
              <h3 class="empty-title">No Triggers Configured</h3>
              <p class="empty-subtitle">Add triggers to automatically execute this chain</p>
              <v-btn
                color="#5E2872"
                dark
                large
                class="mt-4"
                @click="openTriggerModal()"
              >
                <v-icon left>mdi-plus</v-icon>
                Create Your First Trigger
              </v-btn>
            </div>

            <!-- Triggers List -->
            <div v-else class="triggers-list">
              <v-card
                v-for="trigger in chain.triggers"
                :key="trigger.trigger_id"
                class="trigger-card"
                outlined
              >
                <v-card-text>
                  <div class="trigger-card-content">
                    <div class="trigger-card-main">
                      <div class="trigger-icon">
                        <v-icon size="32" color="purple">{{ getTriggerIcon(trigger.trigger_type) }}</v-icon>
                      </div>
                      <div class="trigger-info">
                        <div class="trigger-header-row">
                          <h3 class="trigger-name">{{ trigger.trigger_name }}</h3>
                          <v-chip
                            x-small
                            :color="trigger.active ? 'success' : 'grey'"
                            text-color="white"
                          >
                            {{ trigger.active ? 'Active' : 'Inactive' }}
                          </v-chip>
                        </div>
                        <p class="trigger-description">{{ trigger.description }}</p>
                        <div class="trigger-meta">
                          <v-chip x-small color="purple" outlined class="mr-2">
                            {{ getTriggerTypeLabel(trigger.trigger_type) }}
                          </v-chip>
                          <v-chip x-small outlined class="mr-2">
                            Priority: {{ trigger.priority }}
                          </v-chip>
                          <v-chip
                            v-if="trigger.context_mapping && trigger.context_mapping.mappings && trigger.context_mapping.mappings.length > 0"
                            x-small
                            color="#50B3CB"
                            outlined
                          >
                            {{ trigger.context_mapping.mappings.length }} Mappings
                          </v-chip>
                        </div>
                      </div>
                    </div>
                    <div class="trigger-actions">
                      <v-btn
                        icon
                        small
                        @click="openTriggerModal(trigger)"
                      >
                        <v-icon small>mdi-cog</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        small
                        color="error"
                        @click="deleteTrigger(trigger.trigger_id)"
                      >
                        <v-icon small>mdi-delete</v-icon>
                      </v-btn>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </div>
          </div>

        <!-- Trigger Configuration Modal -->
        <v-dialog
          v-model="showTriggerModal"
          max-width="900"
          scrollable
        >
          <v-card v-if="currentTrigger">
            <v-card-title class="trigger-modal-header">
              <span>{{ currentTrigger.trigger_id ? 'Edit Trigger' : 'Create New Trigger' }}</span>
              <v-spacer></v-spacer>
              <v-btn icon @click="showTriggerModal = false">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-card-title>

            <v-card-text class="trigger-modal-content">
              <!-- Basic Info -->
              <div class="modal-section">
                <v-text-field
                  v-model="currentTrigger.trigger_name"
                  label="Trigger Name"
                  placeholder="e.g., Generate JD from Hierarchy Node"
                  outlined
                  dense
                ></v-text-field>

                <v-select
                  v-model="currentTrigger.trigger_type"
                  :items="triggerTypes"
                  item-text="label"
                  item-value="value"
                  label="Trigger Type"
                  outlined
                  dense
                >
                  <template #selection="{ item }">
                    <v-icon left small>{{ item.icon }}</v-icon>
                    {{ item.label }}
                  </template>
                  <template #item="{ item }">
                    <v-icon left>{{ item.icon }}</v-icon>
                    <div>
                      <div>{{ item.label }}</div>
                      <div class="text-caption grey--text">{{ item.description }}</div>
                    </div>
                  </template>
                </v-select>

                <v-textarea
                  v-model="currentTrigger.description"
                  label="Description"
                  rows="2"
                  outlined
                  dense
                ></v-textarea>

                <!-- Hierarchy Node Action Configuration -->
                <div v-if="currentTrigger.trigger_type === 'hierarchy_node_action'" class="mt-4">
                  <h4 class="mb-2">Hierarchy Node Configuration</h4>
                  <v-select
                    :value="currentTrigger.trigger_config.ht_id"
                    @input="updateTriggerConfig('ht_id', $event)"
                    :items="hierarchyList"
                    item-text="label"
                    item-value="ht_id"
                    label="Hierarchy Type"
                    placeholder="Select hierarchy type (leave empty for all)"
                    outlined
                    dense
                    clearable
                    hint="Leave empty to trigger for all hierarchy templates"
                    persistent-hint
                    class="mb-4"
                  >
                    <template v-slot:item="{ item }">
                      <div>
                        <div class="font-weight-medium">{{ item.label || item.name }}</div>
                        <div class="text-caption grey--text">ID: {{ item.ht_id }}</div>
                      </div>
                    </template>
                  </v-select>

                  <v-select
                    :value="currentTrigger.trigger_config.level"
                    @input="updateTriggerConfig('level', $event)"
                    :items="getAvailableHrLevel(currentTrigger.trigger_config.ht_id)"
                    item-text="name"
                    item-value="index"
                    label="Hierarchy Level"
                    placeholder="Select hierarchy level (leave empty for all)"
                    outlined
                    dense
                    clearable
                    :disabled="!currentTrigger.trigger_config.ht_id"
                    hint="Leave empty to trigger for all levels"
                    persistent-hint
                  >
                    <template v-slot:item="{ item }">
                      <div>
                        <div class="font-weight-medium">{{ item.name || item.label }}</div>
                        <div class="text-caption grey--text">Level {{ item.index }}</div>
                      </div>
                    </template>
                  </v-select>
                </div>

                <!-- API Trigger Configuration -->
                <div v-if="currentTrigger.trigger_type === 'api'" class="mt-4">
                  <h4 class="mb-2">API Trigger</h4>
                  <v-alert type="info" dense text class="mb-3">
                    <div class="text-caption">
                      <strong>Expected Payload Structure:</strong><br>
                      When calling the API, send your data in this format:
                      <pre class="mt-1 payload-example-pre">{{ `{
  "prompt_chain_id": ${chain.prompt_chain_id || 'YOUR_CHAIN_ID'},
  "source_data": {
    "custom": YOUR_DATA_HERE
  }
}` }}</pre>
                      Define the <strong>Input Schema</strong> and field mappings on the <strong>connection line</strong> from this trigger to each step.
                    </div>
                  </v-alert>
                </div>

                <!-- Chain Output Trigger Configuration -->
                <div v-if="currentTrigger.trigger_type === 'chain_output'" class="mt-4">
                  <h4 class="mb-2">Source Chain Configuration</h4>
                  <p class="text-caption grey--text mb-3">
                    Configure which chain's output will trigger this chain.
                  </p>

                  <v-select
                    v-model="currentTrigger.trigger_config.source_chain_id"
                    :items="savedChains"
                    item-text="name"
                    item-value="prompt_chain_id"
                    label="Source Chain"
                    placeholder="Select the chain whose output triggers this chain"
                    outlined
                    dense
                    clearable
                    class="mb-4"
                  >
                    <template v-slot:item="{ item }">
                      <div>
                        <div class="font-weight-medium">{{ item.name }}</div>
                        <div class="text-caption grey--text">{{ item.description || 'No description' }}</div>
                      </div>
                    </template>
                  </v-select>

                  <v-text-field
                    v-model="currentTrigger.trigger_config.source_output_property"
                    label="Output Property"
                    placeholder="generated_profiles"
                    hint="The output property from the source chain that triggers this chain"
                    persistent-hint
                    outlined
                    dense
                    class="mb-4"
                  ></v-text-field>

                  <!-- Input Mappings for Chain Output -->
                  <div class="input-mappings-section">
                    <div class="section-header mb-2">
                      <h4>Input Mappings</h4>
                      <v-btn x-small color="primary" dark @click="addInputMapping">
                        <v-icon left x-small>mdi-plus</v-icon>
                        Add Mapping
                      </v-btn>
                    </div>
                    <p class="text-caption grey--text mb-2">
                      Map output properties from the source chain to this chain's inputs.
                    </p>

                    <v-card
                      v-for="(mapping, idx) in currentTrigger.trigger_config.input_mappings || []"
                      :key="idx"
                      outlined
                      class="mapping-card mb-2 pa-2"
                    >
                      <v-row dense align="center">
                        <v-col cols="5">
                          <v-text-field
                            v-model="mapping.source"
                            label="Source Output"
                            placeholder="generated_profiles"
                            hint="Output from source chain"
                            outlined
                            dense
                            hide-details
                          ></v-text-field>
                        </v-col>
                        <v-col cols="1" class="text-center">
                          <v-icon small>mdi-arrow-right</v-icon>
                        </v-col>
                        <v-col cols="5">
                          <v-text-field
                            v-model="mapping.target"
                            label="Chain Input"
                            placeholder="profiles_to_process"
                            hint="Variable name in this chain"
                            outlined
                            dense
                            hide-details
                          ></v-text-field>
                        </v-col>
                        <v-col cols="1">
                          <v-btn icon small color="error" @click="removeInputMapping(idx)">
                            <v-icon small>mdi-delete</v-icon>
                          </v-btn>
                        </v-col>
                      </v-row>
                    </v-card>

                    <v-alert v-if="!currentTrigger.trigger_config.input_mappings?.length" type="info" dense text class="mt-2">
                      No input mappings defined. Add mappings to pass data from the source chain.
                    </v-alert>
                  </div>
                </div>

                <!-- Excel File Trigger Configuration -->
                <div v-if="currentTrigger.trigger_type === 'excel_file'" class="mt-4">
                  <h4 class="mb-2">Excel File Configuration</h4>
                  <v-alert type="info" dense text class="mb-3">
                    <div class="text-caption">
                      Map Excel columns to chain input properties. Users will upload a file and configure mappings at execution time via the Excel Session Runner.
                    </div>
                  </v-alert>

                  <!-- Column Mappings -->
                  <div class="section-header mb-2">
                    <h4>Column Mappings</h4>
                    <v-btn x-small color="primary" dark @click="addExcelColumnMapping">
                      <v-icon x-small left>mdi-plus</v-icon>Add
                    </v-btn>
                  </div>

                  <v-card v-for="(mapping, idx) in (currentTrigger.trigger_config.column_mappings || [])" :key="'ecm-' + idx" outlined class="pa-2 mb-2">
                    <v-row dense align="center">
                      <v-col cols="5">
                        <v-text-field
                          v-model="mapping.excel_column"
                          label="Excel Column"
                          placeholder="e.g. Job Title"
                          outlined dense hide-details
                        ></v-text-field>
                      </v-col>
                      <v-col cols="1" class="text-center">
                        <v-icon small>mdi-arrow-right</v-icon>
                      </v-col>
                      <v-col cols="4">
                        <v-combobox
                          v-model="mapping.chain_input_key"
                          :items="activeChainInputs.map(i => i.key)"
                          label="Chain Input"
                          outlined dense hide-details
                        ></v-combobox>
                      </v-col>
                      <v-col cols="1">
                        <v-btn icon small color="error" @click="currentTrigger.trigger_config.column_mappings.splice(idx, 1)">
                          <v-icon small>mdi-delete</v-icon>
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-card>

                  <v-divider class="my-3"></v-divider>

                  <!-- Grouping -->
                  <h4 class="mb-2">Row Grouping (Optional)</h4>
                  <div class="caption grey--text mb-2">
                    Group rows by column values. Multiple levels create recursive groups (e.g., first by Skills Track, then by Grade within each track).
                    Add a Group Processor step to generate shared context per group level.
                  </div>
                  <div v-for="(col, gIdx) in (currentTrigger.trigger_config.group_by_columns || [])" :key="'grp-' + gIdx" class="d-flex align-center mb-2">
                    <v-chip small label class="mr-2">Level {{ gIdx + 1 }}</v-chip>
                    <v-text-field
                      v-model="currentTrigger.trigger_config.group_by_columns[gIdx]"
                      :label="'Group By Column (Level ' + (gIdx + 1) + ')'"
                      placeholder="e.g. Department"
                      outlined dense hide-details class="flex-grow-1"
                    ></v-text-field>
                    <v-btn icon small class="ml-1" @click="currentTrigger.trigger_config.group_by_columns.splice(gIdx, 1)">
                      <v-icon small>mdi-close</v-icon>
                    </v-btn>
                  </div>
                  <v-btn small text color="primary" @click="addGroupByColumn(currentTrigger)">
                    <v-icon small left>mdi-plus</v-icon>
                    Add Group Level
                  </v-btn>

                  <v-divider class="my-3"></v-divider>

                  <!-- Context Settings -->
                  <h4 class="mb-2">Context Sharing</h4>
                  <v-select
                    v-model="currentTrigger.trigger_config.row_context_mode"
                    :items="[{text: 'Full Outputs', value: 'full'}, {text: 'Summaries Only', value: 'summary'}]"
                    label="Prior Row Context Mode"
                    outlined dense class="mb-2"
                  ></v-select>
                  <v-text-field
                    v-model.number="currentTrigger.trigger_config.max_context_rows"
                    label="Max Prior Rows in Context"
                    type="number"
                    hint="Limit how many prior row results are passed to each new row (0 = unlimited)"
                    persistent-hint outlined dense
                  ></v-text-field>
                </div>
              </div>

              <!-- Chain Input Resolution -->
              <div class="modal-section">
                <div class="section-header">
                  <h3 class="section-title">Input Resolution</h3>
                  <v-chip x-small color="blue" outlined>
                    {{ activeChainInputs.length }} chain inputs
                  </v-chip>
                </div>
                <p class="section-subtitle">
                  Configure how this trigger provides data for each chain input. Inputs marked "Manual" will prompt the user at execution time.
                </p>

                <div v-if="activeChainInputs.length > 0" class="input-resolution-list">
                  <v-card
                    v-for="input in activeChainInputs"
                    :key="input.key"
                    outlined
                    class="mapping-card mb-3"
                  >
                    <v-card-text class="pa-3">
                      <v-row dense align="center">
                        <v-col cols="3">
                          <div class="d-flex align-center">
                            <v-icon small class="mr-2" :color="getSourceColor(input.step_bindings[0]?.source)">
                              {{ getInputSourceIcon(input.step_bindings[0]?.source) }}
                            </v-icon>
                            <div>
                              <div class="font-weight-medium" style="font-size: 13px;">{{ input.label || input.key }}</div>
                              <div class="grey--text text--darken-1" style="font-size: 11px;">
                                {{ input.step_bindings[0]?.source || 'unknown' }}
                                <span v-if="input.required" class="red--text"> *</span>
                              </div>
                            </div>
                          </div>
                        </v-col>
                        <v-col cols="3">
                          <v-select
                            :value="getTriggerInputResolution(currentTrigger, input.key).resolution_type"
                            @input="setTriggerInputResolution(currentTrigger, input.key, $event)"
                            :items="getResolutionOptions(currentTrigger.trigger_type, input)"
                            label="Resolution"
                            outlined
                            dense
                            hide-details
                          ></v-select>
                        </v-col>
                        <v-col cols="4">
                          <!-- Source path for trigger_data resolution -->
                          <v-text-field
                            v-if="getTriggerInputResolution(currentTrigger, input.key).resolution_type === 'trigger_data'"
                            :value="getTriggerInputResolution(currentTrigger, input.key).source_path"
                            @input="setTriggerInputResolutionConfig(currentTrigger, input.key, 'source_path', $event)"
                            label="Source Path"
                            :placeholder="getSourcePathPlaceholder(currentTrigger.trigger_type, input)"
                            outlined
                            dense
                            hide-details
                          ></v-text-field>
                          <!-- Static value -->
                          <v-text-field
                            v-else-if="getTriggerInputResolution(currentTrigger, input.key).resolution_type === 'static'"
                            :value="getTriggerInputResolution(currentTrigger, input.key).static_value"
                            @input="setTriggerInputResolutionConfig(currentTrigger, input.key, 'static_value', $event)"
                            label="Static Value"
                            outlined
                            dense
                            hide-details
                          ></v-text-field>
                          <!-- Manual — info text -->
                          <div v-else-if="getTriggerInputResolution(currentTrigger, input.key).resolution_type === 'manual'" class="d-flex align-center" style="height: 40px;">
                            <v-icon small color="orange" class="mr-1">mdi-alert-circle</v-icon>
                            <span class="grey--text" style="font-size: 12px;">User will be prompted</span>
                          </div>
                          <!-- Auto — info text -->
                          <div v-else class="d-flex align-center" style="height: 40px;">
                            <v-icon small color="green" class="mr-1">mdi-check-circle</v-icon>
                            <span class="grey--text" style="font-size: 12px;">Auto-resolved from trigger</span>
                          </div>
                        </v-col>
                        <v-col cols="2" class="d-flex align-center justify-center">
                          <v-chip
                            x-small
                            :color="getTriggerInputResolution(currentTrigger, input.key).resolution_type === 'manual' ? 'orange' : 'green'"
                            :text-color="getTriggerInputResolution(currentTrigger, input.key).resolution_type === 'manual' ? 'white' : 'white'"
                          >
                            {{ getTriggerInputResolution(currentTrigger, input.key).resolution_type === 'manual' ? 'Manual' : 'Mapped' }}
                          </v-chip>
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </div>

                <div v-else class="text-center pa-4 grey--text">
                  <v-icon color="grey lighten-1" class="mb-2">mdi-information-outline</v-icon>
                  <div style="font-size: 13px;">No chain inputs detected. Add steps with external inputs to see resolution options.</div>
                </div>

                <!-- Legacy Context Mapping (collapsible for advanced use) -->
                <v-expansion-panels flat class="mt-3">
                  <v-expansion-panel>
                    <v-expansion-panel-header class="pa-3" style="min-height: 36px; font-size: 13px;">
                      <div class="d-flex align-center">
                        <v-icon small class="mr-2">mdi-cog</v-icon>
                        Advanced Context Mapping
                        <v-chip v-if="currentTrigger.context_mapping?.mappings?.length" x-small class="ml-2" color="grey" outlined>
                          {{ currentTrigger.context_mapping.mappings.length }}
                        </v-chip>
                      </div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <div class="d-flex justify-end mb-2">
                        <v-btn x-small color="purple" dark @click="addContextMapping">
                          <v-icon left x-small>mdi-plus</v-icon>
                          Add Mapping
                        </v-btn>
                      </div>
                      <div v-if="currentTrigger.context_mapping && currentTrigger.context_mapping.mappings" class="mappings-list">
                        <v-card
                          v-for="(mapping, idx) in currentTrigger.context_mapping.mappings"
                          :key="idx"
                          outlined
                          class="mapping-card mb-2"
                        >
                          <v-card-text class="pa-2">
                            <v-row dense>
                              <v-col cols="4">
                                <v-text-field v-model="mapping.source" label="Source" placeholder="hierarchy_node_id" outlined dense hide-details></v-text-field>
                              </v-col>
                              <v-col cols="4">
                                <v-text-field v-model="mapping.target" label="Target" placeholder="ht_id" outlined dense hide-details></v-text-field>
                              </v-col>
                              <v-col cols="3">
                                <v-select v-model="mapping.transform" :items="transformOptions" label="Transform" outlined dense hide-details clearable></v-select>
                              </v-col>
                              <v-col cols="1" class="d-flex align-center">
                                <v-btn icon small color="error" @click="removeContextMapping(idx)">
                                  <v-icon small>mdi-delete</v-icon>
                                </v-btn>
                              </v-col>
                            </v-row>
                          </v-card-text>
                        </v-card>
                      </div>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </div>

              <!-- Priority & Status -->
              <div class="modal-section">
                <v-row>
                  <v-col cols="6">
                    <v-text-field
                      v-model.number="currentTrigger.priority"
                      label="Priority"
                      type="number"
                      outlined
                      dense
                      hint="Lower numbers execute first"
                      persistent-hint
                    ></v-text-field>
                  </v-col>
                  <v-col cols="6" class="d-flex align-center">
                    <v-switch
                      v-model="currentTrigger.active"
                      label="Active"
                      color="purple"
                      hide-details
                    ></v-switch>
                  </v-col>
                </v-row>
              </div>
            </v-card-text>

            <v-card-actions class="trigger-modal-actions">
              <v-spacer></v-spacer>
              <v-btn
                text
                @click="showTriggerModal = false"
              >
                Cancel
              </v-btn>
              <v-btn
                color="#5E2872"
                dark
                @click="saveTrigger"
              >
                Save Trigger
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </div>    
      <!-- Schema Tab -->
      <div v-else-if="activeTab === 'schema'" class="schema-view">
        <div class="schema-container">
          <div class="schema-toolbar">
            <div class="d-flex align-center">
              <h3 class="schema-section-title">Chain Input/Output Schema</h3>
              <v-chip small outlined color="blue" class="ml-3">
                {{ activeChainInputs.length }} inputs
              </v-chip>
              <v-chip small outlined color="green" class="ml-2">
                {{ chainOutputSchema.outputs.length }} outputs
              </v-chip>
            </div>
            <v-spacer></v-spacer>
            <v-btn
              color="#50B3CB"
              dark
              small
              @click="refreshChainSchema"
            >
              <v-icon left small>mdi-refresh</v-icon>
              Refresh from Steps
            </v-btn>
          </div>

          <!-- Atlas exposure: may Atlas offer/run this chain standalone in chat? -->
          <v-card class="schema-card mb-4" outlined>
            <v-card-title class="schema-card-title">
              <v-icon left color="deep-purple">mdi-robot-outline</v-icon>
              Atlas Exposure
              <v-spacer></v-spacer>
              <v-chip small :color="(chain.agent_exposure || (isSystemChainMode ? 'internal' : 'standalone')) === 'internal' ? 'grey' : 'deep-purple'" text-color="white">
                {{ (chain.agent_exposure || (isSystemChainMode ? 'internal' : 'standalone')) === 'internal' ? 'Internal' : 'Standalone' }}
              </v-chip>
            </v-card-title>
            <v-card-subtitle class="pb-0">
              Standalone chains can be offered and run directly by Atlas in chat. Internal chains are building blocks of
              other processes (workflows, document creation) — Atlas will name them but never offer to run them.
            </v-card-subtitle>
            <v-card-text>
              <div class="d-flex align-center" style="gap: 16px; flex-wrap: wrap;">
                <v-select
                  v-model="chain.agent_exposure"
                  :items="[
                    { text: isSystemChainMode ? 'Default — Internal (system chain)' : 'Default — Standalone (client chain)', value: null },
                    { text: 'Standalone — Atlas can offer and run it', value: 'standalone' },
                    { text: 'Internal — never offered standalone', value: 'internal' },
                  ]"
                  label="Exposure"
                  dense
                  outlined
                  hide-details
                  style="max-width: 380px"
                  :readonly="isViewingSystemChain"
                ></v-select>
                <v-text-field
                  v-if="(chain.agent_exposure || (isSystemChainMode ? 'internal' : 'standalone')) === 'internal'"
                  v-model="chain.available_via"
                  label="Available via (shown to users)"
                  placeholder="e.g. the Create a Job Profile workflow"
                  dense
                  outlined
                  hide-details
                  style="max-width: 420px; flex: 1"
                  :readonly="isViewingSystemChain"
                ></v-text-field>
              </div>
            </v-card-text>
          </v-card>

          <!-- Input Schema -->
          <v-card class="schema-card mb-4" outlined>
            <v-card-title class="schema-card-title">
              <v-icon left color="blue">mdi-import</v-icon>
              Input Schema
              <v-spacer></v-spacer>
              <v-chip small color="blue" text-color="white">{{ activeChainInputs.length }} inputs</v-chip>
              <v-chip v-if="excludedChainInputs.length > 0" small color="grey" text-color="white" class="ml-1">{{ excludedChainInputs.length }} excluded</v-chip>
            </v-card-title>
            <v-card-subtitle class="pb-0">
              External data required by this chain. Auto-derived from active connected steps; labels and descriptions are editable.
              Exclude inputs that are resolved internally (e.g. via mappings or static values).
            </v-card-subtitle>
            <v-card-text>
              <v-simple-table v-if="activeChainInputs.length > 0" dense class="schema-table">
                <thead>
                  <tr>
                    <th style="width: 200px">Key</th>
                    <th style="width: 200px">Label</th>
                    <th style="width: 250px">Description</th>
                    <th style="width: 100px">Type</th>
                    <th style="width: 80px">Required</th>
                    <th style="width: 120px">Source</th>
                    <th>Used By Steps</th>
                    <th style="width: 50px"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(input) in activeChainInputs" :key="input.key">
                    <td>
                      <code class="schema-key">{{ input.key }}</code>
                    </td>
                    <td>
                      <v-text-field
                        v-model="input.label"
                        dense
                        hide-details
                        solo
                        flat
                        class="schema-edit-field"
                        placeholder="Label..."
                        @change="onSchemaEdited"
                      ></v-text-field>
                    </td>
                    <td>
                      <v-text-field
                        v-model="input.description"
                        dense
                        hide-details
                        solo
                        flat
                        class="schema-edit-field"
                        placeholder="Description..."
                        @change="onSchemaEdited"
                      ></v-text-field>
                    </td>
                    <td>
                      <v-select
                        v-model="input.data_type"
                        :items="['string', 'number', 'boolean', 'array', 'object']"
                        dense
                        hide-details
                        solo
                        flat
                        class="schema-edit-field"
                        @change="onSchemaEdited"
                      ></v-select>
                    </td>
                    <td class="text-center">
                      <v-checkbox
                        v-model="input.required"
                        hide-details
                        dense
                        class="mt-0 pt-0"
                        @change="onSchemaEdited"
                      ></v-checkbox>
                    </td>
                    <td>
                      <v-chip x-small :color="getSourceColor(input.step_bindings[0]?.source)" outlined>
                        {{ input.step_bindings[0]?.source || 'unknown' }}
                      </v-chip>
                    </td>
                    <td>
                      <v-chip
                        v-for="binding in input.step_bindings"
                        :key="binding.step_name + binding.input_property"
                        x-small
                        class="mr-1 mb-1"
                        color="grey"
                        outlined
                      >
                        {{ binding.step_name }}
                      </v-chip>
                    </td>
                    <td>
                      <v-btn icon x-small color="grey" @click="excludeSchemaInput(input.key)" title="Exclude from schema (input remains in prompt steps)">
                        <v-icon x-small>mdi-eye-off</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
              <div v-else-if="chainInputSchema.inputs.length === 0" class="text-center pa-6 grey--text">
                <v-icon size="48" color="grey lighten-1" class="mb-2">mdi-import</v-icon>
                <div class="text-body-2">No external inputs detected. Add steps with hierarchy, template part, or other external inputs.</div>
              </div>
              <div v-else class="text-center pa-4 grey--text">
                <v-icon color="grey lighten-1" class="mb-1">mdi-eye-off</v-icon>
                <div class="text-body-2">All inputs excluded from schema. These inputs are expected to be resolved internally.</div>
              </div>

              <!-- Excluded Inputs -->
              <div v-if="excludedChainInputs.length > 0" class="mt-3">
                <v-expansion-panels flat>
                  <v-expansion-panel>
                    <v-expansion-panel-header class="pa-0 text-caption">
                      <div class="d-flex align-center">
                        <v-icon x-small class="mr-1">mdi-eye-off</v-icon>
                        {{ excludedChainInputs.length }} excluded input{{ excludedChainInputs.length > 1 ? 's' : '' }}
                        <span class="grey--text ml-1">(resolved internally — not part of external schema)</span>
                      </div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <v-simple-table dense class="schema-table excluded-schema-table">
                        <thead>
                          <tr>
                            <th>Key</th>
                            <th>Source</th>
                            <th>Used By Steps</th>
                            <th style="width: 50px"></th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="input in excludedChainInputs" :key="'excl_' + input.key" class="excluded-row">
                            <td>
                              <code class="schema-key" style="opacity: 0.6;">{{ input.key }}</code>
                            </td>
                            <td>
                              <v-chip x-small :color="getSourceColor(input.step_bindings[0]?.source)" outlined style="opacity: 0.6;">
                                {{ input.step_bindings[0]?.source || 'unknown' }}
                              </v-chip>
                            </td>
                            <td>
                              <v-chip
                                v-for="binding in input.step_bindings"
                                :key="binding.step_name + binding.input_property"
                                x-small
                                class="mr-1"
                                color="grey"
                                outlined
                                style="opacity: 0.6;"
                              >
                                {{ binding.step_name }}
                              </v-chip>
                            </td>
                            <td>
                              <v-btn icon x-small color="blue" @click="restoreSchemaInput(input.key)" title="Restore to schema">
                                <v-icon x-small>mdi-eye</v-icon>
                              </v-btn>
                            </td>
                          </tr>
                        </tbody>
                      </v-simple-table>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </div>
            </v-card-text>
          </v-card>

          <!-- Output Schema -->
          <v-card class="schema-card mb-4" outlined>
            <v-card-title class="schema-card-title">
              <v-icon left color="green">mdi-export</v-icon>
              Output Schema
              <v-spacer></v-spacer>
              <v-chip small color="green" text-color="white">{{ chainOutputSchema.outputs.length }} outputs</v-chip>
            </v-card-title>
            <v-card-subtitle class="pb-0">
              Data produced by this chain. Derived from final steps and output formatters.
            </v-card-subtitle>
            <v-card-text>
              <v-simple-table v-if="chainOutputSchema.outputs.length > 0" dense class="schema-table">
                <thead>
                  <tr>
                    <th style="width: 200px">Key</th>
                    <th style="width: 200px">Label</th>
                    <th style="width: 250px">Description</th>
                    <th style="width: 100px">Type</th>
                    <th style="width: 120px">Extract From</th>
                    <th>Source Step</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="output in chainOutputSchema.outputs" :key="output.key">
                    <td><code class="schema-key">{{ output.key }}</code></td>
                    <td>
                      <v-text-field
                        v-model="output.label"
                        dense
                        hide-details
                        solo
                        flat
                        class="schema-edit-field"
                        placeholder="Label..."
                        @change="onSchemaEdited"
                      ></v-text-field>
                    </td>
                    <td>
                      <v-text-field
                        v-model="output.description"
                        dense
                        hide-details
                        solo
                        flat
                        class="schema-edit-field"
                        placeholder="Description..."
                        @change="onSchemaEdited"
                      ></v-text-field>
                    </td>
                    <td>
                      <v-chip x-small outlined>{{ output.data_type }}</v-chip>
                    </td>
                    <td>
                      <v-chip x-small outlined color="teal">{{ output.extract_from }}</v-chip>
                    </td>
                    <td>
                      <v-chip x-small outlined color="grey">{{ output.source_step_name }}</v-chip>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
              <div v-else class="text-center pa-6 grey--text">
                <v-icon size="48" color="grey lighten-1" class="mb-2">mdi-export</v-icon>
                <div class="text-body-2">No outputs detected. Mark a step as "Final Step" or add an output formatter.</div>
              </div>
            </v-card-text>
          </v-card>

          <!-- Execution Configuration -->
          <v-card class="schema-card mb-4" outlined>
            <v-card-title class="schema-card-title">
              <v-icon left color="deep-orange">mdi-cog-play</v-icon>
              Execution Configuration
            </v-card-title>
            <v-card-subtitle class="pb-0">
              Default execution settings when this chain is run in bulk (Excel sessions, hierarchy triggers). These can be overridden per execution.
            </v-card-subtitle>
            <v-card-text>
              <v-row dense>
                <v-col cols="6">
                  <v-select
                    v-model="chainExecutionConfig.execution_mode"
                    :items="[{ text: 'Run in Sequence', value: 'sequence' }, { text: 'Run in Parallel', value: 'parallel' }]"
                    label="Default Execution Mode"
                    outlined dense
                    hint="Sequence shares context between rows; Parallel runs all independently"
                    persistent-hint
                    @change="onExecutionConfigChanged"
                  ></v-select>
                </v-col>
                <v-col cols="6">
                  <v-select
                    v-model="chainExecutionConfig.context_mode"
                    :items="[{ text: 'Summary (lighter)', value: 'summary' }, { text: 'Full (detailed)', value: 'full' }]"
                    label="Context Mode"
                    outlined dense
                    hint="How prior row results are shared (sequence mode only)"
                    persistent-hint
                    :disabled="chainExecutionConfig.execution_mode === 'parallel'"
                    @change="onExecutionConfigChanged"
                  ></v-select>
                </v-col>
              </v-row>
              <v-row dense class="mt-2">
                <v-col cols="6">
                  <v-text-field
                    v-model.number="chainExecutionConfig.max_context_rows"
                    label="Max Context Rows"
                    type="number"
                    outlined dense
                    hint="Sliding window limit. Leave empty for unlimited."
                    persistent-hint
                    :disabled="chainExecutionConfig.execution_mode === 'parallel'"
                    @input="onExecutionConfigChanged"
                  ></v-text-field>
                </v-col>
              </v-row>

              <v-divider class="my-4"></v-divider>
              <div class="text-subtitle-2 mb-2">Context Output Fields</div>
              <div class="caption grey--text mb-3">
                Outputs flagged as "Share as context" on individual steps will be shared between rows in sequence mode.
                If no outputs are flagged, all outputs are shared.
              </div>
              <div v-if="contextFlaggedOutputs.length > 0">
                <v-chip
                  v-for="co in contextFlaggedOutputs"
                  :key="co.property"
                  small
                  outlined
                  color="deep-orange"
                  class="mr-2 mb-1"
                >
                  <v-icon left x-small>mdi-share-variant</v-icon>
                  {{ co.stepName }} &rarr; {{ co.property }}
                </v-chip>
              </div>
              <div v-else class="caption grey--text font-italic">
                No outputs flagged — all outputs will be shared as context.
              </div>
            </v-card-text>
          </v-card>

          <!-- Variable Aliasing / Cross-Step Consolidation -->
          <v-card class="schema-card mb-4" outlined>
            <v-card-title class="schema-card-title">
              <v-icon left color="purple">mdi-link-variant</v-icon>
              Variable Aliasing
              <v-spacer></v-spacer>
              <v-btn
                color="#50B3CB"
                dark
                x-small
                class="mr-2"
                @click="autoGroupSimilarProperties"
              >
                <v-icon left x-small>mdi-auto-fix</v-icon>
                Auto-Detect Similar
              </v-btn>
              <v-btn
                color="#48A401"
                dark
                x-small
                :disabled="selectedProperties.length < 2"
                @click="openMergeDialog"
              >
                <v-icon left x-small>mdi-merge</v-icon>
                Merge Selected ({{ selectedProperties.length }})
              </v-btn>
            </v-card-title>
            <v-card-subtitle class="pb-0">
              Variables may be aliased differently across prompts. Select similar variables to merge them under a common source, simplifying the chain's external requirements.
            </v-card-subtitle>
            <v-card-text>
              <!-- Suggestions Banner -->
              <v-alert v-if="propertySuggestions.length > 0" type="warning" dense text class="mb-3">
                <div class="d-flex align-center">
                  <div class="flex-grow-1">
                    <strong>{{ propertySuggestions.length }} similar variable{{ propertySuggestions.length > 1 ? 's' : '' }} detected</strong>
                    — properties with similar names may represent the same data across steps.
                  </div>
                  <v-btn text small color="orange" @click="reviewSuggestions" class="ml-2">
                    Review
                  </v-btn>
                </div>
              </v-alert>

              <!-- Search -->
              <v-text-field
                v-if="propertyGroups.length > 5"
                v-model="consolidationSearch"
                placeholder="Search properties..."
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-magnify"
                class="mb-3"
              ></v-text-field>

              <v-simple-table v-if="filteredPropertyGroups.length > 0" dense class="schema-table">
                <thead>
                  <tr>
                    <th style="width: 40px"></th>
                    <th style="width: 220px">Property Name</th>
                    <th style="width: 160px">Mapped Alias</th>
                    <th style="width: 70px">I/O</th>
                    <th style="width: 110px">Source</th>
                    <th>Steps Using This Variable</th>
                    <th style="width: 60px"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="group in filteredPropertyGroups"
                    :key="group.id"
                    :class="{
                      'suggested-row': isSuggestedGroup(group.id),
                      'selected-row': isGroupSelected(group.id)
                    }"
                  >
                    <td>
                      <v-checkbox
                        v-if="group.type === 'input'"
                        :input-value="isGroupSelected(group.id)"
                        @change="toggleGroupSelection(group.id)"
                        hide-details
                        dense
                        class="mt-0 pt-0"
                      ></v-checkbox>
                    </td>
                    <td>
                      <div class="d-flex align-center">
                        <code class="schema-key mr-2">{{ group.name }}</code>
                        <v-chip
                          v-if="group.instances.length > 1"
                          x-small
                          color="orange"
                          text-color="white"
                        >
                          {{ group.instances.length }} steps
                        </v-chip>
                        <v-chip
                          v-if="group.required"
                          x-small
                          color="red"
                          text-color="white"
                          class="ml-1"
                        >
                          req
                        </v-chip>
                      </div>
                    </td>
                    <td>
                      <div v-if="group.alias" class="d-flex align-center">
                        <v-icon x-small color="purple" class="mr-1">mdi-arrow-right-bold</v-icon>
                        <code class="schema-key" style="color: #9333EA;">{{ group.alias }}</code>
                      </div>
                      <span v-else class="grey--text text-caption">—</span>
                    </td>
                    <td>
                      <v-chip
                        x-small
                        :color="group.type === 'input' ? 'blue' : 'green'"
                        text-color="white"
                      >
                        {{ group.type }}
                      </v-chip>
                    </td>
                    <td>
                      <v-chip x-small :color="getSourceColor(getMostCommonSource(group.instances))" outlined>
                        {{ getMostCommonSource(group.instances) }}
                      </v-chip>
                    </td>
                    <td>
                      <v-chip
                        v-for="inst in group.instances"
                        :key="inst.stepId + '_' + inst.property"
                        x-small
                        class="mr-1 mb-1"
                        :color="getStepTypeColor(chain.steps.find(s => s.id === inst.stepId)?.step_type)"
                        text-color="white"
                      >
                        <span class="font-weight-bold mr-1">{{ inst.stepOrder }}.</span>
                        {{ inst.stepName }}
                        <span v-if="inst.source !== getMostCommonSource(group.instances)" class="ml-1 font-italic">({{ inst.source }})</span>
                      </v-chip>
                    </td>
                    <td>
                      <v-menu offset-y v-if="group.type === 'input'">
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn icon x-small v-bind="attrs" v-on="on">
                            <v-icon small>mdi-dots-vertical</v-icon>
                          </v-btn>
                        </template>
                        <v-list dense>
                          <v-list-item @click="addToMergeSelection(group)">
                            <v-list-item-icon><v-icon small>mdi-merge</v-icon></v-list-item-icon>
                            <v-list-item-title>Add to Merge Selection</v-list-item-title>
                          </v-list-item>
                          <v-list-item @click="duplicateProperty(group)">
                            <v-list-item-icon><v-icon small>mdi-content-copy</v-icon></v-list-item-icon>
                            <v-list-item-title>Add to All Steps</v-list-item-title>
                          </v-list-item>
                        </v-list>
                      </v-menu>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
              <div v-else class="text-center pa-4 grey--text">
                <v-icon color="grey lighten-1" class="mb-1">mdi-link-variant-off</v-icon>
                <div class="text-body-2">No step properties found. Add steps with inputs or outputs to see variable aliasing.</div>
              </div>
            </v-card-text>
          </v-card>

          <!-- API Schema Preview -->
          <v-card class="schema-card mb-4" outlined v-if="hasApiTrigger">
            <v-card-title class="schema-card-title">
              <v-icon left color="orange">mdi-api</v-icon>
              API Schema
              <v-chip x-small class="ml-2" :color="effectiveApiSchema.isCustom ? 'orange' : 'grey'" text-color="white">
                {{ effectiveApiSchema.isCustom ? 'Custom' : 'Auto-Generated' }}
              </v-chip>
              <v-spacer></v-spacer>
              <v-btn icon small @click="copyApiSchema">
                <v-icon small>mdi-content-copy</v-icon>
              </v-btn>
            </v-card-title>
            <v-card-subtitle class="pb-0">
              <template v-if="effectiveApiSchema.isCustom">
                Custom schema defined on the API trigger connection. Edit via the connection mapping dialog.
              </template>
              <template v-else>
                Auto-generated from chain inputs. Define a custom schema on the API trigger connection line to override.
              </template>
            </v-card-subtitle>
            <v-card-text>
              <pre class="api-schema-preview">{{ effectiveApiSchema.json }}</pre>
            </v-card-text>
          </v-card>

          <!-- Trigger Resolution Summary -->
          <v-card class="schema-card" outlined v-if="chain.triggers.length > 0">
            <v-card-title class="schema-card-title">
              <v-icon left color="purple">mdi-flash</v-icon>
              Trigger Input Resolution
            </v-card-title>
            <v-card-subtitle class="pb-0">
              Shows how each trigger type can satisfy the chain's input requirements.
            </v-card-subtitle>
            <v-card-text>
              <div v-for="trigger in chain.triggers" :key="trigger.trigger_id" class="trigger-resolution-block mb-4">
                <div class="d-flex align-center mb-2">
                  <v-icon small class="mr-2" :color="getTriggerTypeColor(trigger.trigger_type)">
                    {{ getTriggerIcon(trigger.trigger_type) }}
                  </v-icon>
                  <strong>{{ trigger.trigger_name || 'Unnamed Trigger' }}</strong>
                  <v-chip x-small class="ml-2" outlined>{{ getTriggerTypeLabel(trigger.trigger_type) }}</v-chip>
                </div>
                <v-simple-table dense class="schema-table">
                  <thead>
                    <tr>
                      <th>Chain Input</th>
                      <th>Resolution</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="input in activeChainInputs" :key="trigger.trigger_id + '_' + input.key">
                      <td>{{ input.label || input.key }}</td>
                      <td>
                        <v-chip x-small :color="getTriggerResolutionColor(trigger, input)" outlined>
                          {{ getTriggerResolutionLabel(trigger, input) }}
                        </v-chip>
                      </td>
                      <td>
                        <v-icon small :color="canTriggerResolveInput(trigger, input) ? 'green' : 'orange'">
                          {{ canTriggerResolveInput(trigger, input) ? 'mdi-check-circle' : 'mdi-alert-circle' }}
                        </v-icon>
                        {{ canTriggerResolveInput(trigger, input) ? 'Auto' : 'Manual' }}
                      </td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </div>
            </v-card-text>
          </v-card>
        </div>

        <!-- Merge Dialog -->
        <v-dialog v-model="showMergeDialog" max-width="800" persistent>
          <v-card>
            <v-card-title class="merge-dialog-header">
              <v-icon left color="purple">mdi-merge</v-icon>
              Merge / Alias Variables
            </v-card-title>
            <v-card-text class="merge-dialog-content">
              <div class="merge-preview">
                <div class="merge-from">
                  <h4>Variables to Merge:</h4>
                  <v-chip-group column>
                    <v-chip
                      v-for="group in selectedPropertyGroups"
                      :key="group.id"
                      close
                      @click:close="removeFromMerge(group.id)"
                    >
                      {{ group.name }}
                      <v-chip x-small class="ml-2">{{ group.instances.length }} steps</v-chip>
                    </v-chip>
                  </v-chip-group>
                </div>

                <v-alert type="info" dense text class="mt-4">
                  Property names in prompt text remain unchanged. This configures how they all resolve from the same source, so the chain only needs one external value.
                </v-alert>

                <div class="merge-arrow">
                  <v-icon large color="purple">mdi-arrow-down-bold</v-icon>
                </div>

                <div class="merge-to">
                  <h4>Shared Source Configuration:</h4>

                  <v-select
                    v-model="mergeTargetSource"
                    :items="inputSources"
                    label="Source"
                    outlined
                    dense
                    class="mt-4"
                  ></v-select>

                  <v-text-field
                    v-if="mergeTargetSource === 'chain_output'"
                    v-model="mergeTargetName"
                    label="Output Property Name"
                    outlined
                    dense
                    placeholder="e.g., consolidated_content"
                    :rules="[v => !!v || 'Output property name is required']"
                    hint="The property name from the previous step's output that all these inputs will reference"
                    persistent-hint
                    class="mt-4"
                  ></v-text-field>

                  <v-select
                    v-if="mergeTargetSource === 'api_payload'"
                    v-model="mergeTargetApiPayloadField"
                    :items="getApiPayloadFields"
                    label="API Payload Field"
                    outlined
                    dense
                    placeholder="Select field from API payload"
                    :rules="[v => !!v || 'API payload field is required']"
                    hint="The field from the API trigger payload that all these inputs will reference"
                    persistent-hint
                    class="mt-4"
                  ></v-select>

                  <v-checkbox
                    v-model="mergeAsRequired"
                    label="Mark as required"
                    hide-details
                    dense
                    class="mt-4"
                  ></v-checkbox>
                </div>
              </div>

              <v-divider class="my-4"></v-divider>

              <div class="affected-steps">
                <h4>Affected Steps ({{ affectedStepsCount }}):</h4>
                <v-simple-table dense>
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th>Step</th>
                        <th>Property Name</th>
                        <th>New Source</th>
                        <th v-if="mergeTargetSource === 'chain_output'">Output Property</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="change in mergeChanges" :key="change.stepId + change.oldName">
                        <td>
                          <div class="step-ref">
                            <span class="step-number-mini">{{ change.stepOrder }}</span>
                            {{ change.stepName }}
                          </div>
                        </td>
                        <td>{{ change.oldName }}</td>
                        <td class="success--text font-weight-bold">{{ mergeTargetSource }}</td>
                        <td v-if="mergeTargetSource === 'chain_output'" class="info--text font-weight-bold">
                          {{ mergeTargetName }}
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </div>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn text @click="showMergeDialog = false">Cancel</v-btn>
              <v-btn
                color="purple"
                dark
                @click="executeMerge"
                :disabled="(mergeTargetSource === 'chain_output' && !mergeTargetName) || (mergeTargetSource === 'api_payload' && !mergeTargetApiPayloadField) || selectedProperties.length < 2"
              >
                <v-icon left small>mdi-check</v-icon>
                Apply Merge
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- Suggestions Dialog -->
        <v-dialog v-model="showSuggestionsDialog" max-width="900" scrollable>
          <v-card>
            <v-card-title class="suggestions-dialog-header">
              <v-icon left color="orange">mdi-lightbulb</v-icon>
              Similar Variables Detected
            </v-card-title>
            <v-card-text class="suggestions-dialog-content">
              <p class="text-body-2 grey--text mb-4">
                These variables have similar names across steps and likely represent the same data.
                Merging them ensures they resolve from one source, simplifying the chain's input requirements.
              </p>
              <div
                v-for="suggestion in propertySuggestions"
                :key="suggestion.id"
                class="suggestion-card"
              >
                <div class="suggestion-header">
                  <div class="suggestion-title">
                    <v-icon color="orange">mdi-alert-circle</v-icon>
                    <span>Possible aliases</span>
                  </div>
                  <v-chip x-small color="orange" text-color="white">
                    {{ suggestion.similarity }}% match
                  </v-chip>
                </div>
                <div class="suggestion-body">
                  <div class="suggestion-properties">
                    <v-chip
                      v-for="prop in suggestion.properties"
                      :key="prop.id"
                      class="ma-1"
                    >
                      {{ prop.name }}
                      <v-chip x-small class="ml-1">{{ prop.stepCount }} steps</v-chip>
                    </v-chip>
                  </div>
                  <div class="suggestion-actions">
                    <v-text-field
                      v-model="suggestion.suggestedName"
                      label="Canonical Name"
                      outlined
                      dense
                      hide-details
                      class="suggestion-input"
                    ></v-text-field>
                    <v-btn color="purple" dark small @click="acceptSuggestion(suggestion)">
                      <v-icon left small>mdi-check</v-icon>
                      Merge
                    </v-btn>
                    <v-btn text small @click="dismissSuggestion(suggestion.id)">
                      Dismiss
                    </v-btn>
                  </div>
                </div>
              </div>
            </v-card-text>
            <v-card-actions>
              <v-btn text @click="dismissAllSuggestions">Dismiss All</v-btn>
              <v-spacer></v-spacer>
              <v-btn color="purple" dark @click="acceptAllSuggestions">
                Accept All
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </div>
    </div>

    <!-- Pre-Execution Form Dialog -->
    <v-dialog v-model="showPreExecDialog" max-width="600" persistent>
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon left color="orange">mdi-form-textbox</v-icon>
          Additional Inputs Required
        </v-card-title>
        <v-card-subtitle class="px-6 pb-0">
          This trigger cannot auto-resolve all chain inputs. Please provide the missing values below.
        </v-card-subtitle>
        <v-card-text>
          <div v-for="input in preExecUnresolvedInputs" :key="input.key" class="mb-4">
            <v-text-field
              v-if="input.source_hint !== 'hierarchy'"
              v-model="preExecManualValues[input.key]"
              :label="(input.label || input.key) + (input.required ? ' *' : '')"
              :hint="input.description"
              persistent-hint
              outlined
              dense
            ></v-text-field>
            <!-- For hierarchy inputs, show a hierarchy picker if available, else text field -->
            <v-text-field
              v-else
              v-model="preExecManualValues[input.key]"
              :label="(input.label || input.key) + (input.required ? ' *' : '')"
              :hint="input.description || 'Hierarchy value'"
              persistent-hint
              outlined
              dense
            ></v-text-field>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="cancelPreExec">Cancel</v-btn>
          <v-btn
            color="#50B3CB"
            dark
            @click="submitPreExecForm"
            :disabled="!allPreExecFieldsFilled"
          >
            <v-icon left small>mdi-play</v-icon>
            Execute
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Test Chain Dialog -->
    <v-dialog
      v-model="showTestChainDialog"
      max-width="800"
      persistent
    >
      <v-card>
        <v-card-title class="test-chain-header">
          <v-icon left color="#10B981">mdi-play-circle</v-icon>
          Test Chain Execution
          <v-spacer></v-spacer>
          <v-btn icon @click="closeTestChainDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="test-chain-content">
          <div v-if="!testExecutionId">
            <p class="mb-4 text-body-2">
              Enter JSON data to test your chain. This simulates an API trigger with custom payload.
            </p>

            <v-textarea
              v-model="testJsonInput"
              label="Test JSON Payload"
              placeholder='{\n  "items": [\n    {"name": "Item 1", "value": 100}\n  ]\n}'
              outlined
              rows="12"
              :error-messages="testJsonError"
              @input="validateTestJson"
              class="monospace-input"
            ></v-textarea>

            <v-alert
              v-if="testJsonError"
              type="error"
              dense
              outlined
              class="mb-0"
            >
              {{ testJsonError }}
            </v-alert>
          </div>

          <div v-else class="execution-status-container">
            <div class="status-header">
              <h3 class="status-title">Execution Status</h3>
              <v-chip
                small
                :color="getStatusColor(testExecutionStatus)"
                text-color="white"
              >
                {{ testExecutionStatus || 'Initializing' }}
              </v-chip>
            </div>

            <!-- Current Progress Info -->
            <div v-if="testProgressInfo && testExecutionStatus === 'running'" class="current-progress-box mb-3">
              <v-icon small color="#10B981" class="mr-2">mdi-cog</v-icon>
              <div class="progress-text">
                <div class="progress-step">{{ testProgressInfo.step_name }}</div>
                <div class="progress-count">Step {{ testProgressInfo.completed }} of {{ testProgressInfo.total }}</div>
              </div>
              <v-progress-circular
                :value="testProgressInfo.percentage"
                :size="32"
                :width="3"
                color="#10B981"
                class="ml-auto"
              >
                <span style="font-size: 10px;">{{ testProgressInfo.percentage }}%</span>
              </v-progress-circular>
            </div>

            <v-progress-linear
              v-if="testExecutionStatus === 'running'"
              indeterminate
              color="#10B981"
              class="mb-4"
            ></v-progress-linear>

            <!-- Status Messages Log -->
            <div class="status-messages-wrapper">
              <div class="status-messages">
                <div
                  v-for="(message, index) in testStatusMessages"
                  :key="index"
                  class="status-message"
                  :class="{ 'status-message-final': message.final }"
                >
                  <v-icon
                    small
                    :color="message.type === 'error' ? 'error' : message.type === 'success' ? '#10B981' : '#6B7280'"
                    class="mr-2"
                  >
                    {{ message.type === 'error' ? 'mdi-alert-circle' : message.type === 'success' ? 'mdi-check-circle' : 'mdi-information' }}
                  </v-icon>
                  <span>{{ message.text }}</span>
                </div>
              </div>
            </div>

            <!-- Execution Result -->
            <div v-if="testExecutionResult" class="execution-result mt-4">
              <div class="result-header">
                <h4 class="result-title">Execution Result</h4>
                <v-chip x-small color="#10B981" text-color="white">
                  <v-icon x-small left>mdi-check</v-icon>
                  Success
                </v-chip>
              </div>
              <div class="result-content-wrapper">
                <pre class="result-content">{{ JSON.stringify(testExecutionResult, null, 2) }}</pre>
              </div>
            </div>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="closeTestChainDialog"
          >
            {{ testExecutionId ? 'Close' : 'Cancel' }}
          </v-btn>
          <v-btn
            v-if="!testExecutionId"
            color="#10B981"
            dark
            @click="executeTestChain"
            :disabled="!isTestJsonValid || isExecutingTest"
            :loading="isExecutingTest"
          >
            <v-icon left small>mdi-play</v-icon>
            Execute Test
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Connection Mapping Dialog -->
    <v-dialog
      v-model="showConnectionMappingDialog"
      :max-width="isApiTriggerConnection ? 900 : 800"
      persistent
    >
      <v-card>
        <v-card-title class="connection-mapping-header">
          <v-icon left color="#9333EA">mdi-map-marker-path</v-icon>
          Configure Trigger Mapping
          <v-spacer></v-spacer>
          <v-btn icon @click="showConnectionMappingDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="connection-mapping-content">
          <v-alert
            type="info"
            dense
            text
            class="mb-4"
          >
            Map trigger context fields to step inputs. This defines how data flows from the trigger to the step.
          </v-alert>

          <!-- API Input Schema (only for API trigger connections) -->
          <div v-if="isApiTriggerConnection" class="api-schema-section mb-4">
            <v-card outlined>
              <v-card-title class="py-2 px-4 text-subtitle-2">
                <v-icon left small color="orange">mdi-code-json</v-icon>
                API Input Schema
                <v-spacer></v-spacer>
                <v-btn
                  x-small
                  text
                  color="primary"
                  @click="connectionApiSchema = generatedApiSchemaJson"
                  title="Populate with auto-generated schema from chain inputs"
                >
                  <v-icon left x-small>mdi-auto-fix</v-icon>
                  Use Auto-Generated
                </v-btn>
              </v-card-title>
              <v-card-text class="pt-0">
                <p class="text-caption grey--text mb-2">
                  Define the expected JSON format for the API payload (<code>source_data.custom</code>).
                  This can be a JSON Schema or an example data structure.
                </p>
                <v-textarea
                  v-model="connectionApiSchema"
                  placeholder='Example:
[{"Id": "", "Type": "", "Confidence": 1, "Description": ""}]

or JSON Schema:
{"type": "array", "items": {"type": "object", "properties": {"Id": {"type": "string"}}}}'
                  outlined
                  rows="8"
                  hide-details
                  class="schema-textarea"
                  style="font-family: monospace; font-size: 12px;"
                ></v-textarea>
                <div v-if="connectionApiSchema" class="mt-2">
                  <v-chip x-small :color="isValidJson(connectionApiSchema) ? 'success' : 'warning'" text-color="white">
                    {{ isValidJson(connectionApiSchema) ? 'Valid JSON' : 'Not valid JSON (will be stored as text)' }}
                  </v-chip>
                </div>
              </v-card-text>
            </v-card>
          </div>

          <div class="mapping-section">
            <div class="section-header">
              <h4>Field Mappings <span class="grey--text text-caption">({{ connectionMappings.length }})</span></h4>
              <v-btn
                small
                color="#9333EA"
                dark
                @click="addConnectionMapping"
              >
                <v-icon left small>mdi-plus</v-icon>
                Add Mapping
              </v-btn>
            </div>

            <div v-if="connectionMappings.length === 0" class="empty-mappings">
              <v-icon size="48" color="grey lighten-1">mdi-map-marker-off</v-icon>
              <p class="grey--text">No mappings configured. Add a mapping to connect trigger data to step inputs.</p>
            </div>

            <div v-else class="mappings-list">
              <div
                v-for="(mapping, idx) in connectionMappings"
                :key="`${mapping.id}_${mapping.array_mode}`"
                class="mapping-row-config"
              >
                <div class="mapping-row-number">{{ idx + 1 }}</div>

                <div class="mapping-fields-container">
                  <div class="mapping-fields-row">
                    <v-select
                      :value="mapping.trigger_field"
                      @input="updateMappingField(idx, 'trigger_field', $event)"
                      :items="getAvailableTriggerFieldsForMapping(mapping)"
                      label="Trigger Field"
                      placeholder="Select trigger field"
                      outlined
                      dense
                      hide-details
                      class="mapping-select"
                    ></v-select>

                    <v-icon class="mapping-arrow">mdi-arrow-right</v-icon>

                    <v-select
                      :value="mapping.step_input"
                      @input="updateMappingField(idx, 'step_input', $event)"
                      :items="getConnectedStepInputs"
                      label="Step Input"
                      placeholder="Select step input"
                      outlined
                      dense
                      hide-details
                      class="mapping-select"
                    ></v-select>

                    <v-btn
                      icon
                      small
                      color="error"
                      @click="removeConnectionMapping(idx)"
                    >
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </div>

                  <div class="mapping-array-mode">
                    <v-radio-group
                      :value="mapping.array_mode"
                      @change="updateMappingArrayMode(idx, $event)"
                      row
                      dense
                      hide-details
                      class="mt-2"
                    >
                      <v-radio
                        value="singular"
                        color="#9333EA"
                      >
                        <template v-slot:label>
                          <div class="d-flex align-center">
                            <v-icon small class="mr-1" color="#9333EA">mdi-file-document</v-icon>
                            <span>Singular (entire value)</span>
                          </div>
                        </template>
                      </v-radio>
                      <v-radio
                        value="items"
                        color="#9333EA"
                      >
                        <template v-slot:label>
                          <div class="d-flex align-center">
                            <v-icon small class="mr-1" color="#9333EA">mdi-format-list-bulleted</v-icon>
                            <span>Array Items (map each item)</span>
                          </div>
                        </template>
                      </v-radio>
                    </v-radio-group>
                    <div class="caption grey--text mt-1">
                      <span v-if="mapping.array_mode === 'singular'">
                        <v-icon x-small class="mr-1">mdi-information</v-icon>
                        Maps the entire value (object/array) to the input
                      </span>
                      <span v-else>
                        <v-icon x-small class="mr-1">mdi-information</v-icon>
                        Iterates through array items and maps properties from each item. Use [*] notation.
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <v-divider class="my-4"></v-divider>

          <div class="mapping-summary">
            <h4 class="mb-2">Summary</h4>
            <div v-if="connectionMappings.length > 0" class="summary-content">
              <div v-for="(mapping, idx) in connectionMappings.filter(m => m.trigger_field && m.step_input)" :key="idx" class="summary-item">
                <v-icon small color="success" class="mr-2">mdi-check-circle</v-icon>
                <div class="summary-text">
                  <div class="mapping-path">
                    <strong>{{ mapping.trigger_field }}</strong> → <strong>{{ mapping.step_input }}</strong>
                  </div>
                  <div class="mapping-mode-badge">
                    <v-chip x-small :color="mapping.array_mode === 'items' ? 'orange' : 'blue'" text-color="white">
                      {{ mapping.array_mode === 'items' ? 'Array Items' : 'Singular' }}
                    </v-chip>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="grey--text text-caption">
              No mappings configured yet
            </div>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="showConnectionMappingDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="#9333EA"
            dark
            @click="saveConnectionMappings"
          >
            <v-icon left small>mdi-check</v-icon>
            Save Mappings
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Import Chain Wizard -->
    <v-dialog
      v-model="showImportDialog"
      max-width="960"
      persistent
    >
      <v-card class="import-wizard-card">
        <v-card-title class="import-dialog-header">
          <v-icon left color="purple">mdi-import</v-icon>
          Import Chain
          <v-spacer></v-spacer>
          <span class="import-wizard-step-label">Step {{ importWizardStep }} of {{ importHasDocMappings ? 5 : 4 }}</span>
        </v-card-title>

        <v-stepper v-model="importWizardStep" class="import-wizard-stepper elevation-0" non-linear>
          <v-stepper-header class="import-stepper-header">
            <v-stepper-step :complete="importWizardStep > 1" step="1" color="purple">Upload</v-stepper-step>
            <v-divider></v-divider>
            <v-stepper-step :complete="importWizardStep > 2" step="2" color="purple" :rules="[() => allPromptsMapped || importWizardStep <= 2]">Prompts</v-stepper-step>
            <v-divider></v-divider>
            <v-stepper-step v-if="importHasDocMappings" :complete="importWizardStep > 3" step="3" color="purple">Document Mappings</v-stepper-step>
            <v-divider v-if="importHasDocMappings"></v-divider>
            <v-stepper-step :complete="importWizardStep > (importHasDocMappings ? 4 : 3)" :step="importHasDocMappings ? 4 : 3" color="purple">Options</v-stepper-step>
            <v-divider></v-divider>
            <v-stepper-step :step="importHasDocMappings ? 5 : 4" color="purple">Review</v-stepper-step>
          </v-stepper-header>

          <v-stepper-items class="import-stepper-items">
            <!-- Step 1: Upload & Preview -->
            <v-stepper-content step="1">
              <div class="import-step-content">
                <v-file-input
                  v-model="importFile"
                  label="Select Chain JSON File"
                  accept=".json"
                  outlined
                  prepend-icon="mdi-file-document"
                  @change="handleFileUpload"
                  hint="Upload a previously exported chain JSON file"
                  persistent-hint
                ></v-file-input>

                <div v-if="importedChainData" class="import-preview-card mt-4">
                  <div class="import-preview-header">
                    <v-icon color="green" class="mr-2">mdi-check-circle</v-icon>
                    <strong>{{ importedChainData.chain_name }}</strong>
                  </div>
                  <div v-if="importedChainData.description" class="import-preview-desc">{{ importedChainData.description }}</div>
                  <div class="import-preview-stats">
                    <v-chip x-small class="mr-1" v-for="stat in importChainStats" :key="stat.label" outlined>
                      <v-icon x-small left>{{ stat.icon }}</v-icon>
                      {{ stat.count }} {{ stat.label }}
                    </v-chip>
                  </div>
                  <div class="import-preview-meta">
                    <span v-if="importedChainData.exportedAt">Exported: {{ new Date(importedChainData.exportedAt).toLocaleString() }}</span>
                    <span class="ml-3">Version: {{ importedChainData.version || '1.0' }}</span>
                  </div>
                  <v-alert v-if="importedChainData.version !== '3.0'" type="warning" dense text class="mt-2 mb-0">
                    This file was exported with an older version. Template and hierarchy mappings may contain stale IDs.
                  </v-alert>
                </div>
              </div>
            </v-stepper-content>

            <!-- Step 2: Prompt Mapping -->
            <v-stepper-content step="2">
              <div class="import-step-content">
                <v-alert type="info" dense text class="mb-3">
                  Map each prompt step to an existing prompt, or create a new one from the exported definition.
                </v-alert>

                <div v-if="importedChainData" class="import-prompt-list">
                  <div
                    v-for="step in importedChainData.steps.filter(s => s.step_type === 'prompt')"
                    :key="step.id"
                    class="prompt-mapping-row"
                    :class="{
                      'prompt-row--mapped': !!promptMappings[step.id],
                      'prompt-row--creating': promptCreateState[step.id]?.mode === 'create'
                    }"
                  >
                    <!-- Top row: source info + controls -->
                    <div class="prompt-row-main">
                      <div class="mapping-left">
                        <div class="step-indicator">
                          <span class="step-number-mini">{{ step.step_order }}</span>
                        </div>
                        <div class="imported-prompt-info">
                          <div class="imported-prompt-name">{{ step._promptName || step.step_name }}</div>
                          <div class="imported-prompt-meta">
                            Type: {{ step._promptType || 'N/A' }}
                            <template v-if="step._promptData">
                              &middot;
                              {{ (step._promptData.configuration_data?.input || []).length }} input(s)
                            </template>
                          </div>
                          <div v-if="step._promptData" class="import-input-chips mt-1">
                            <v-chip
                              v-for="(inp, iIdx) in (step._promptData.configuration_data?.input || [])"
                              :key="iIdx"
                              x-small
                              :color="inp.source === 'hierarchy' ? 'blue lighten-4' : inp.source === 'tmpl_part' ? 'purple lighten-4' : 'grey lighten-3'"
                              class="mr-1 mb-1"
                            >
                              {{ inp.property }}
                              <span class="ml-1 text--secondary" style="font-size:9px">{{ inp.source }}</span>
                            </v-chip>
                          </div>
                        </div>

                        <!-- Compatibility badge -->
                        <v-chip
                          v-if="step._promptData"
                          x-small
                          :color="
                            getPromptCompatibility(step) === 'exact_match' ? 'green' :
                            getPromptCompatibility(step) === 'name_match' ? 'orange' :
                            'red lighten-4'
                          "
                          :text-color="getPromptCompatibility(step) === 'no_match' ? 'red darken-3' : 'white'"
                          class="ml-2 flex-shrink-0"
                        >
                          <v-icon x-small left>
                            {{ getPromptCompatibility(step) === 'exact_match' ? 'mdi-check-circle' : getPromptCompatibility(step) === 'name_match' ? 'mdi-alert-circle' : 'mdi-close-circle' }}
                          </v-icon>
                          {{ getPromptCompatibility(step) === 'exact_match' ? 'Match' : getPromptCompatibility(step) === 'name_match' ? 'Partial' : 'No Match' }}
                        </v-chip>
                        <v-icon v-else-if="promptMappings[step.id]" small color="green" class="ml-2">mdi-check-circle</v-icon>
                      </div>

                      <v-icon class="mx-2" color="grey lighten-1">mdi-arrow-right</v-icon>

                      <div class="mapping-right">
                        <!-- Mode: map to existing -->
                        <template v-if="!promptCreateState[step.id] || promptCreateState[step.id].mode !== 'create'">
                          <v-select
                            v-model="promptMappings[step.id]"
                            :items="availablePrompts"
                            item-text="name"
                            item-value="tmpl_ai_prompt_id"
                            label="Select Target Prompt"
                            outlined dense hide-details clearable
                          >
                            <template v-slot:selection="{ item }">
                              <span>{{ item.name }}</span>
                              <v-chip x-small class="ml-2" :color="item.specificity === 'Template' ? 'success' : item.specificity === 'Client' ? 'info' : 'grey'" :dark="item.specificity !== 'grey'" outlined>{{ item.specificity }}</v-chip>
                            </template>
                            <template v-slot:item="{ item }">
                              <v-list-item-content>
                                <v-list-item-title>{{ item.name }}
                                  <v-chip x-small class="ml-2" :color="item.specificity === 'Template' ? 'success' : item.specificity === 'Client' ? 'info' : 'grey'" outlined>{{ item.specificity }}</v-chip>
                                </v-list-item-title>
                                <v-list-item-subtitle>{{ item.prompt_type }}</v-list-item-subtitle>
                              </v-list-item-content>
                            </template>
                          </v-select>
                          <div v-if="step._promptData" class="create-from-import-hint mt-1">
                            <v-btn x-small text color="purple" @click="initPromptCreateState(step)">
                              <v-icon x-small left>mdi-plus-circle-outline</v-icon>
                              Create from import
                            </v-btn>
                            <span v-if="getPromptCompatibility(step) === 'no_match'" class="text-caption red--text ml-1">Recommended</span>
                          </div>
                        </template>

                        <!-- Mode: create from import -->
                        <template v-else>
                          <div class="create-mode-label">
                            <v-icon small color="purple" class="mr-1">mdi-creation</v-icon>
                            <span class="text-caption purple--text font-weight-bold">Will create: "{{ step._promptData?.prompt_name }}"</span>
                            <v-btn x-small text color="grey" class="ml-2" @click="cancelCreateFromImport(step)">Cancel</v-btn>
                          </div>
                        </template>
                      </div>
                    </div>

                    <!-- Expansion: input remap panel (create mode only) -->
                    <div
                      v-if="promptCreateState[step.id]?.mode === 'create' && step._promptData"
                      class="create-remap-panel mt-3"
                    >
                      <div class="create-remap-header">
                        <v-icon small class="mr-1">mdi-cog-outline</v-icon>
                        Remap Inputs
                      </div>

                      <div
                        v-for="(inp, iIdx) in (step._promptData.configuration_data?.input || [])"
                        :key="iIdx"
                        class="create-remap-input-row"
                      >
                        <div class="create-remap-input-info">
                          <code class="create-remap-var">{{ inp.property }}</code>
                          <v-chip x-small :color="inp.source === 'hierarchy' ? 'blue lighten-4' : inp.source === 'tmpl_part' ? 'purple lighten-4' : 'grey lighten-3'" class="ml-2">
                            {{ inp.source }}
                          </v-chip>
                          <span v-if="inp._hint" class="text-caption grey--text ml-2">from: {{ inp._hint }}</span>
                        </div>

                        <!-- Hierarchy: select hierarchy + level -->
                        <div v-if="inp.source === 'hierarchy'" class="create-remap-selects">
                          <v-select
                            :value="promptCreateState[step.id]?.inputRemaps?.[iIdx]?.ht_id"
                            :items="hierarchyList"
                            item-text="label"
                            item-value="ht_id"
                            label="Hierarchy"
                            outlined dense hide-details clearable
                            class="remap-select"
                            @change="setInputRemap(step.id, iIdx, 'ht_id', $event)"
                          ></v-select>
                          <v-select
                            v-if="promptCreateState[step.id]?.inputRemaps?.[iIdx]?.ht_id"
                            :value="promptCreateState[step.id]?.inputRemaps?.[iIdx]?.level"
                            :items="getHierarchyLevelsForRemap(promptCreateState[step.id]?.inputRemaps?.[iIdx]?.ht_id)"
                            item-text="name"
                            item-value="index"
                            label="Level"
                            outlined dense hide-details clearable
                            class="remap-select"
                            @change="setInputRemap(step.id, iIdx, 'level', $event)"
                          ></v-select>
                        </div>

                        <!-- Template Part: select template part -->
                        <div v-else-if="inp.source === 'tmpl_part'" class="create-remap-selects">
                          <v-select
                            :value="promptCreateState[step.id]?.inputRemaps?.[iIdx]?.tp_id"
                            :items="importTemplateParts"
                            item-text="name"
                            item-value="tp_id"
                            label="Template Part"
                            outlined dense hide-details clearable
                            class="remap-select"
                            :loading="loadingImportTemplateParts"
                            @change="setInputRemap(step.id, iIdx, 'tp_id', $event)"
                          ></v-select>
                        </div>

                        <!-- Auto-resolved sources -->
                        <div v-else class="create-remap-auto">
                          <v-icon small color="green">mdi-check-circle</v-icon>
                          <span class="text-caption green--text ml-1">Auto-resolved</span>
                        </div>
                      </div>

                      <!-- Create Prompt button -->
                      <div class="create-remap-actions mt-3">
                        <v-btn
                          color="purple" dark small
                          :disabled="!isCreateConfigComplete(step)"
                          :loading="promptCreateState[step.id]?.creating"
                          @click="createPromptFromImport(step)"
                        >
                          <v-icon left small>mdi-plus</v-icon>
                          Create Prompt
                        </v-btn>
                        <span v-if="!isCreateConfigComplete(step)" class="text-caption grey--text ml-2">
                          Remap all hierarchy/template part inputs to continue
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <v-alert v-if="importedChainData && importedChainData.steps.filter(s => s.step_type === 'prompt').length === 0" type="info" dense text>
                  No prompt steps found in this chain. You can skip this step.
                </v-alert>
              </div>
            </v-stepper-content>

            <!-- Step 3: Document Mapping Remapping (conditional) -->
            <v-stepper-content v-if="importHasDocMappings" step="3">
              <div class="import-step-content">
                <v-alert type="info" dense text class="mb-4">
                  Remap document output targets to the current template's parts and hierarchies.
                  Unmapped entries can be configured later in the formatter panel.
                  <strong>{{ importDocMappedCount }} of {{ importDocTotalCount }} mapped</strong>
                </v-alert>

                <div v-if="loadingImportTemplateParts" class="text-center py-4">
                  <v-progress-circular indeterminate color="purple"></v-progress-circular>
                  <div class="mt-2 text-caption">Loading template parts...</div>
                </div>

                <div v-else-if="importedChainData" class="import-doc-mappings-list">
                  <div
                    v-for="fmtStep in importFormatterSteps"
                    :key="fmtStep.id"
                    class="import-doc-formatter-group"
                  >
                    <div class="import-doc-group-header">
                      <v-icon small class="mr-1">mdi-file-document-outline</v-icon>
                      {{ fmtStep.step_name }}
                      <v-chip x-small class="ml-2" outlined>{{ (fmtStep.config?.formatter_config?.document_mappings || []).length }} mappings</v-chip>
                    </div>

                    <div
                      v-for="(mapping, mIdx) in (fmtStep.config?.formatter_config?.document_mappings || [])"
                      :key="mIdx"
                      class="import-doc-mapping-row"
                    >
                      <div class="import-doc-mapping-left">
                        <v-chip x-small :color="mapping.mappingTargetType === 'hierarchy_value' ? 'blue' : 'purple'" dark class="mr-2">
                          {{ mapping.mappingTargetType === 'hierarchy_value' ? 'HIERARCHY' : 'TEMPLATE PART' }}
                        </v-chip>
                        <div class="import-doc-mapping-hint">
                          <div class="import-doc-mapping-name">
                            {{ mapping._tmpl_part_name || mapping._hierarchy_name || 'Unknown target' }}
                          </div>
                          <div v-if="mapping._hierarchy_level" class="import-doc-mapping-meta">Level {{ mapping._hierarchy_level }}</div>
                          <div v-if="mapping.property" class="import-doc-mapping-meta">Property: {{ mapping.property }}</div>
                        </div>
                      </div>

                      <v-icon class="mx-2">mdi-arrow-right</v-icon>

                      <div class="import-doc-mapping-right">
                        <!-- Template Part mapping -->
                        <v-select
                          v-if="mapping.mappingTargetType !== 'hierarchy_value'"
                          :value="getImportDocMapping(fmtStep.id, mIdx, 'tp_id')"
                          @input="setImportDocMapping(fmtStep.id, mIdx, 'template_part', $event)"
                          :items="importTemplateParts"
                          item-text="name"
                          item-value="tp_id"
                          label="Select Template Part"
                          outlined dense hide-details clearable
                          class="import-doc-mapping-select"
                        ></v-select>

                        <!-- Hierarchy mapping -->
                        <div v-else class="d-flex align-center" style="gap: 8px;">
                          <v-select
                            :value="getImportDocMapping(fmtStep.id, mIdx, 'ht_id')"
                            @input="setImportDocMapping(fmtStep.id, mIdx, 'hierarchy', $event)"
                            :items="hierarchyList"
                            item-text="label"
                            item-value="ht_id"
                            label="Hierarchy"
                            outlined dense hide-details clearable
                            style="flex: 1;"
                          ></v-select>
                          <v-text-field
                            :value="getImportDocMapping(fmtStep.id, mIdx, 'level')"
                            @input="setImportDocMappingLevel(fmtStep.id, mIdx, $event)"
                            label="Level"
                            type="number"
                            outlined dense hide-details
                            style="max-width: 80px;"
                          ></v-text-field>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </v-stepper-content>

            <!-- Step 4 (or 3 if no doc mappings): Import Options -->
            <v-stepper-content :step="importHasDocMappings ? 4 : 3">
              <div class="import-step-content">
                <div v-if="chain.prompt_chain_id">
                  <v-alert type="warning" dense text>
                    You are currently working on an existing saved chain: <strong>"{{ chain.chain_name }}"</strong>
                  </v-alert>

                  <p class="mt-4 mb-3">How would you like to import this chain?</p>

                  <v-radio-group v-model="importAsNewChain" column>
                    <v-radio :value="true" class="mb-2">
                      <template v-slot:label>
                        <div>
                          <div class="font-weight-bold">Create as New Chain</div>
                          <div class="text-caption">Import will create a brand new chain, leaving the current chain unchanged</div>
                        </div>
                      </template>
                    </v-radio>
                    <v-radio :value="false">
                      <template v-slot:label>
                        <div>
                          <div class="font-weight-bold error--text">Replace Current Chain</div>
                          <div class="text-caption">Import will overwrite the current chain "{{ chain.chain_name }}" with the imported content</div>
                        </div>
                      </template>
                    </v-radio>
                  </v-radio-group>
                </div>

                <div v-else>
                  <v-alert type="info" dense text>
                    This will create a new chain with the imported content.
                  </v-alert>
                </div>
              </div>
            </v-stepper-content>

            <!-- Step 5 (or 4): Review & Confirm -->
            <v-stepper-content :step="importHasDocMappings ? 5 : 4">
              <div class="import-step-content">
                <h4 class="mb-3">Import Summary</h4>

                <div class="import-review-section">
                  <div class="import-review-label">Chain Name</div>
                  <div class="import-review-value">
                    {{ importAsNewChain ? (importedChainData?.chain_name + ' (Imported)') : importedChainData?.chain_name }}
                    <v-chip v-if="importAsNewChain" x-small color="green" dark class="ml-1">NEW</v-chip>
                    <v-chip v-else x-small color="orange" dark class="ml-1">REPLACE</v-chip>
                  </div>
                </div>

                <div class="import-review-section">
                  <div class="import-review-label">Prompt Mappings</div>
                  <div class="import-review-table">
                    <div v-for="(step, idx) in (importedChainData?.steps || []).filter(s => s.step_type === 'prompt')" :key="idx" class="import-review-row">
                      <span class="import-review-from">{{ step._promptName || step.step_name }}</span>
                      <v-icon small class="mx-1">mdi-arrow-right</v-icon>
                      <span class="import-review-to">{{ getPromptNameById(promptMappings[step.id]) || 'Not mapped' }}</span>
                      <v-icon v-if="promptMappings[step.id]" x-small color="green" class="ml-1">mdi-check</v-icon>
                    </div>
                  </div>
                </div>

                <div v-if="importHasDocMappings" class="import-review-section">
                  <div class="import-review-label">Document Mappings</div>
                  <div class="import-review-value">
                    {{ importDocMappedCount }} of {{ importDocTotalCount }} mapped
                    <span v-if="importDocTotalCount - importDocMappedCount > 0" class="text-caption grey--text ml-1">
                      ({{ importDocTotalCount - importDocMappedCount }} will need manual configuration)
                    </span>
                  </div>
                </div>

                <div class="import-review-section">
                  <div class="import-review-label">Steps</div>
                  <div class="import-review-value">
                    <span v-for="stat in importChainStats" :key="stat.label" class="mr-3">{{ stat.count }} {{ stat.label }}</span>
                  </div>
                </div>
              </div>
            </v-stepper-content>
          </v-stepper-items>
        </v-stepper>

        <v-card-actions class="import-wizard-actions">
          <v-btn text @click="cancelImport">Cancel</v-btn>
          <v-spacer></v-spacer>
          <v-btn
            v-if="importWizardStep > 1"
            text
            @click="importWizardPrev"
          >
            <v-icon left small>mdi-arrow-left</v-icon>
            Back
          </v-btn>
          <v-btn
            v-if="importWizardStep < (importHasDocMappings ? 5 : 4)"
            color="purple"
            dark
            @click="importWizardNext"
            :disabled="!importCanAdvance"
          >
            Next
            <v-icon right small>mdi-arrow-right</v-icon>
          </v-btn>
          <v-btn
            v-else
            color="purple"
            dark
            @click="executeImport"
            :disabled="!allPromptsResolved"
          >
            <v-icon left small>mdi-check</v-icon>
            Import Chain
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Notification Component -->
    <Notification :notification="componentState.notification" />
  </div>
</template>

<script setup>
//#region Imports
import { ref, reactive, computed, onMounted, onUnmounted, watch, nextTick, inject, set as vueSet } from 'vue';
import axios from 'axios';
import Notification from "@/components/common/SnackBar.vue";
//import StepMappingDialog from "@/components/common/StepMappingDialog.vue";
//#endregion

//#region Reactive
const props = defineProps({
    value: Number
});
const store = inject('store');
const loginState = inject('loginState');

const viewMode = ref('canvas');
const canvasRef = ref(null);
const showLandingPage = ref(true);
const chainSearchQuery = ref('');
const loadingPrompts = ref(false);

// Consolidation view state
const consolidationSearch = ref('');
const selectedProperties = ref([]);
const showMergeDialog = ref(false);
const mergeTargetName = ref('');
const mergeTargetApiPayloadField = ref('');
const mergeTargetSource = ref('hierarchy');
const mergeAsRequired = ref(false);
const showSuggestionsDialog = ref(false);
const propertySuggestions = ref([]);
const stepColumnWidth = '160px';

// Import/Export state
const showImportDialog = ref(false);
const importedChainData = ref(null);
const promptMappings = ref({});
const importFile = ref(null);
const importAsNewChain = ref(true);
const importWizardStep = ref(1);
const importDocMappings = ref({});       // { [exportStepId]: { [mappingIndex]: { tp_id, name } or { ht_id, level } } }
const importTemplateParts = ref([]);     // loaded template parts for import wizard step 3
const loadingImportTemplateParts = ref(false);
const promptCreateState = ref({});      // { [stepId]: { mode: 'map'|'create', inputRemaps: { [idx]: { ht_id, level, tp_id } }, creating: false } }
const expandedCreatePanel = ref(null);  // stepId of expanded create panel

// Add history view state
const showHistoryView = ref(false);
const selectedChainForHistory = ref(null);
const executionHistory = ref([]);
const loadingHistory = ref(false);
const selectedExecution = ref(null);
const showExecutionDetails = ref(false);
const loadingExecutionSteps = ref(false);

// Add report view state
const showReportView = ref(false);
const selectedChainForReport = ref(null);
const selectedExecutionForReport = ref(null);
const reportData = ref(null);
const loadingReportData = ref(false);
const reportTab = ref(0);

// Drilldown state for report tables
const expandedTrendRows = ref([]);
const expandedValidationRows = ref([]);
const trendDetails = ref({});
const validationDetails = ref({});
const loadingTrendDetails = ref({});
const loadingValidationDetails = ref({});

// Validation drilldown filters
const validationFilters = ref({});
const validationGrouping = ref({});

const hookFunctions = ref([]);

const savedChains = ref([]);
const systemChains = ref([]);
const chainScope = ref('template'); // 'template' or 'system'
const isSystemChainMode = ref(false); // true when viewing, editing, or creating a system chain
const isViewingSystemChain = ref(false); // true only when viewing an existing system chain (read-only)

const chain = reactive({
  prompt_chain_id: null,
  chain_name: 'New Prompt Chain',
  description: '',
  // Atlas exposure override: null = default by origin
  // (system chain → internal, client chain → standalone).
  agent_exposure: null,
  available_via: '',
  steps: [],
  connections: [],
  triggers: [],
  input_schema: null,
  output_schema: null,
  execution_config: null
});

const chainExecutionConfig = reactive({
  execution_mode: 'sequence',
  context_mode: 'summary',
  max_context_rows: 20
});

const contextFlaggedOutputs = computed(() => {
  const flagged = [];
  for (const step of chain.steps) {
    if (!step.active && step.active !== undefined) continue;
    const outputs = step.config?.output || [];
    const stepName = step.step_name || step.name || `Step ${step.step_order}`;
    for (const output of outputs) {
      if (output.isContextOutput && output.property) {
        flagged.push({ stepName, property: output.property });
      }
    }
  }
  return flagged;
});

const onExecutionConfigChanged = () => {
  chain.execution_config = { ...chainExecutionConfig };
  schemaEdited.value = true;
};

const schemaEdited = ref(false);

const promptCategories = ref([
  {
    type: 'client_prompts',
    name: 'Client Prompts',
    expanded: true,
    items: []
  },
  {
    type: 'output_formatter',
    name: 'Output Formatters',
    expanded: true,
    items: [
      {
        id: 1,
        itemType: 'output_formatter',
        name: 'JSON Formatter',
        description: 'Consolidates all previous outputs into a structured JSON format',
        output_format: 'json',
        template: {
          // This will be defined by the user in the configuration
        }
      },
      {
        id: 2,
        itemType: 'output_formatter',
        name: 'Text Formatter',
        description: 'Combines outputs into formatted text',
        output_format: 'text',
        template: ''
      },
      {
        id: 3,
        itemType: 'output_formatter',
        name: 'Document Formatter',
        description: 'Maps outputs to template parts for document generation',
        output_format: 'document',
        template: {}
      }
    ]
  },
  {
    type: 'manual_selection',
    name: 'Manual Selection',
    expanded: true,
    items: [
      {
        id: 1,
        itemType: 'manual_selection',
        name: 'Manual Selection Step',
        description: 'Pauses execution to allow user to select items before proceeding',
        selection_config: {
          source_output: '',
          array_path: '',
          display_fields: []
        }
      }
    ]
  },
  {
    type: 'group_processor',
    name: 'Group Processor',
    expanded: true,
    items: [
      {
        id: 1,
        itemType: 'group_processor',
        name: 'Group Processor',
        description: 'Processes grouped rows to generate shared context (e.g., common responsibilities). Only runs during Excel/bulk sessions with grouping enabled.',
        inputs: [
          { id: 'gp_1', property: '_group_rows', prompt: 'JSON array of all rows in the group', source: 'dynamic', required: true },
          { id: 'gp_2', property: '_group_value', prompt: 'The group identifier value', source: 'dynamic', required: true },
          { id: 'gp_3', property: '_group_row_count', prompt: 'Number of rows in the group', source: 'dynamic', required: false }
        ]
      }
    ]
  }//,
  /*{
    type: 'services',
    name: 'External Services',
    expanded: true,
    items: [
      {
        id: 101,
        itemType: 'service',
        name: 'Skills Analysis API',
        service_type: 'rest_api',
        endpoint_name: 'Analyze Job Description',
        endpoint_path: '/v1/analyze/job-description',
        http_method: 'POST',
        base_url: 'https://api.skillsanalyzer.com',
        healthcheck_status: 'healthy',
        input_schema: {
        job_title: 'string',
        responsibilities: 'array',
        industry: 'string'
        },
        output_schema: {
        required_skills: 'array',
        skill_levels: 'object',
        recommendations: 'array'
        }
      },
      {
        id: 102,
        itemType: 'service',
        name: 'Content Enhancement',
        service_type: 'rest_api',
        endpoint_name: 'Enhance Text',
        endpoint_path: '/v1/enhance',
        http_method: 'POST',
        base_url: 'https://api.contentenhancer.com',
        healthcheck_status: 'healthy',
        input_schema: {
        text: 'string',
        style: 'string'
        },
        output_schema: {
        enhanced_text: 'string',
        changes: 'array'
        }
      },
      {
        id: 103,
        itemType: 'service',
        name: 'Data Validation Service',
        service_type: 'rest_api',
        endpoint_name: 'Validate Structure',
        endpoint_path: '/v1/validate',
        http_method: 'POST',
        base_url: 'https://api.datavalidator.com',
        healthcheck_status: 'degraded',
        input_schema: {
        data: 'object',
        schema: 'object'
        },
        output_schema: {
        valid: 'boolean',
        errors: 'array'
        }
      }
      ]
    }*/
]);

const componentState = reactive({
    notification: {
      text: "",
      type: "success"
    },
})

const searchQuery = ref('');
const selectedStep = ref(null);
const isDraggingItem = ref(false);
const draggedStep = ref(null);
const dragOffset = reactive({ x: 0, y: 0, canvasLeft: 0, canvasTop: 0, scrollLeft: 0, scrollTop: 0, rafId: null, initialX: 0, initialY: 0, hasMoved: false });
const isDraggingStep = ref(false);
const isDrawingConnection = ref(false);
const connectionStart = ref(null);
const mousePos = reactive({ x: 0, y: 0 });
const snapTarget = ref(null);
const hoveredConnection = ref(null);
const showExecutionViewer = ref(false);
const executionSteps = ref([]);
const showServiceModal = ref(false);
const expandedPanels = ref([0, 1, 2, 3]);
const configPanels = ref([0, 1, 2]); // All panels expanded by default
const documentFormatterMappings = ref([]);
const showDocumentFormatterDialog = ref(false);
const loadingTemplateParts = ref(false);
const availableTemplateParts = ref([]);

// Add state for tracking collapsed mappings
const collapsedMappings = ref(new Set());

function triggerNotification(msg, type) {
    componentState.notification = {
        text: msg,
        type: type
    };
}

// Test Prompt Dialog
const showTestPromptDialog = ref(false);
const testPromptStep = ref(null);
const testPromptInputs = reactive({});
const testPromptLoading = ref(false);
const testPromptResult = ref(null);

// Test Chain Dialog
const showTestChainDialog = ref(false);
const testJsonInput = ref('{\n  "items": [\n    {"name": "Example Item", "value": 100}\n  ]\n}');
const testJsonError = ref('');
const isTestJsonValid = ref(true);
const isExecutingTest = ref(false);
const testExecutionId = ref(null);
const testExecutionStatus = ref('');
const testStatusMessages = ref([]);
const testExecutionResult = ref(null);
const testPollingInterval = ref(null);
const testCurrentStep = ref(null); // Track current step to avoid duplicate messages
const testProgressInfo = ref(null); // Track progress info

// Connection Mapping Dialog State
const showConnectionMappingDialog = ref(false);
const currentConnection = ref(null);
const connectionMappings = ref([]);
const connectionApiSchema = ref('');

const connectionSourceTrigger = computed(() => {
  if (!currentConnection.value || !currentConnection.value.from) return null;
  const fromStr = String(currentConnection.value.from);
  if (!fromStr.startsWith('trigger-')) return null;
  const triggerId = fromStr.replace('trigger-', '');
  return chain.triggers?.find(t => String(t.trigger_id) === triggerId) || null;
});

const isApiTriggerConnection = computed(() => {
  return connectionSourceTrigger.value?.trigger_type === 'api';
});

// Step Mapping Dialog
const showMappingDialog = ref(false);

// Service Management
const showAddServiceDialog = ref(false);
const editingServiceId = ref(null);
const serviceForm = reactive({
  service_name: '',
  service_type: 'rest_api',
  description: '',
  base_url: '',
  auth_type: 'none',
  auth_config: {
    headers: []
  },
  auth_config_key: '',
  healthcheck_endpoint: '',
  healthcheck_method: 'GET',
  healthcheck_interval: 300,
  timeout: 30,
  retry_attempts: 3,
  active: 1,
  is_system: false,
  endpoints: []
});
const savingService = ref(false);
const serviceFormValid = ref(false);
const currentEndpoint = reactive({
  endpoint_name: '',
  endpoint_path: '',
  http_method: 'POST',
  description: '',
  input_schema: '{}',
  output_schema: '{}',
  headers: [],
  query_params: [],
  request_example: '{}',
  response_example: '{}',
  active: 1
});
const editingEndpointIndex = ref(-1);
const showEndpointEditor = ref(false);

// Triggers
const activeTab = ref('builder'); // 'builder' or 'triggers'
const showTriggerModal = ref(false);
const currentTrigger = ref(null);
const selectedTrigger = ref(null);
const isDraggingTrigger = ref(false);
const draggedTrigger = ref(null);
const triggerDragOffset = reactive({ x: 0, y: 0, canvasLeft: 0, canvasTop: 0, scrollLeft: 0, scrollTop: 0, rafId: null, initialX: 0, initialY: 0, hasMoved: false });

const triggerTypes = [
  {
    value: 'hierarchy_node_action',
    label: 'Hierarchy Node Action',
    icon: 'mdi-file-tree',
    description: 'Triggered when user performs action on hierarchy node',
    configFields: ['action_types', 'hierarchy_levels']
  },
  {
    value: 'document_action',
    label: 'Document Action',
    icon: 'mdi-file-document-plus',
    description: 'Triggered when user performs action on a document',
    configFields: ['action_types', 'document_types']
  },
  {
    value: 'scheduled',
    label: 'Scheduled',
    icon: 'mdi-calendar-clock',
    description: 'Execute on a schedule',
    configFields: ['cron_expression', 'timezone']
  },
  {
    value: 'webhook',
    label: 'Webhook',
    icon: 'mdi-webhook',
    description: 'Triggered by external webhook',
    configFields: ['webhook_url', 'auth_token']
  },
  {
    value: 'batch_operation',
    label: 'Batch Operation',
    icon: 'mdi-account-multiple',
    description: 'Process multiple items in batch',
    configFields: ['batch_size', 'parallel_execution']
  },
  {
    value: 'manual',
    label: 'Manual',
    icon: 'mdi-play-circle',
    description: 'Manually triggered by user',
    configFields: []
  },
  {
    value: 'doc_create',
    label: 'Document Create',
    icon: 'mdi-play-circle',
    description: 'Document Create Dialog',
    configFields: []
  },
  {
    value: 'api',
    label: 'API Trigger',
    icon: 'mdi-api',
    description: 'Triggered via external API call with custom payload',
    configFields: ['input_schema', 'input_mappings']
  },
  {
    value: 'chain_output',
    label: 'Chain Output',
    icon: 'mdi-link-variant',
    description: 'Triggered by output of another prompt chain',
    configFields: ['source_chain_id', 'output_property', 'input_mappings']
  },
  {
    value: 'excel_file',
    label: 'Excel File Input',
    icon: 'mdi-file-excel',
    description: 'Process rows from an uploaded Excel file with shared session context',
    configFields: ['column_mappings', 'group_by_columns']
  },
];

const inputSources = [
  { text: 'Dynamic', value: 'dynamic' },
  { text: 'Hierarchy', value: 'hierarchy' },
  { text: 'Template Part', value: 'tmpl_part' },
  { text: 'Level Framework', value: 'level_framework' },
  { text: 'Previous Step Output', value: 'chain_output' },
  { text: 'API Payload', value: 'api_payload' },
  { text: 'Static Value', value: 'static' }
];

const outputExtractionSources = [
  { text: 'Response', value: 'response' },
  { text: 'Pass-Through Input', value: 'pass_through' },
  { text: 'Metadata', value: 'metadata' },
  { text: 'Custom Field', value: 'custom' }
];

const transformOptions = [
  { text: 'None', value: null },
  { text: 'Lookup Hierarchy Value', value: 'lookup_hierarchy_value' },
  { text: 'Extract Document Content', value: 'extract_document_content' },
  { text: 'Get Hierarchy Ancestors', value: 'get_hierarchy_ancestors' }
];
//#endregion

// Trigger data source registry — defines what data each trigger type can provide
const triggerDataSources = {
  document_action: [
    { path: 'document.hierarchy', label: 'Document Hierarchy', provides: 'hierarchy', description: 'Hierarchy levels from the associated document' },
    { path: 'document.parts', label: 'Template Parts', provides: 'tmpl_part', description: 'Content from document template parts' }
  ],
  hierarchy_node_action: [
    { path: 'hierarchy.node', label: 'Hierarchy Node', provides: 'hierarchy', description: 'Selected node and ancestors (limited to node level and above)' }
  ],
  api: [
    { path: 'payload.*', label: 'API Payload', provides: 'dynamic', description: 'Custom fields from API request body' }
  ],
  manual: [],
  scheduled: [],
  webhook: [
    { path: 'payload.*', label: 'Webhook Payload', provides: 'dynamic' }
  ],
  chain_output: [
    { path: 'source_chain.outputs', label: 'Source Chain Outputs', provides: 'chain_output' }
  ],
  batch_operation: [
    { path: 'batch.item', label: 'Batch Item', provides: 'dynamic' }
  ],
  doc_create: [
    { path: 'document.hierarchy', label: 'Document Hierarchy', provides: 'hierarchy' }
  ]
};

//#region Computed
const serviceItems = computed(() => {
  const servicesCategory = promptCategories.value.find(c => c.type === 'services');
  return servicesCategory ? servicesCategory.items : [];
});

const availablePrompts = computed(() => {
  const clientPromptsCategory = promptCategories.value.find(c => c.type === 'client_prompts');
  return clientPromptsCategory ? clientPromptsCategory.items : [];
});

const filteredChains = computed(() => {
  const source = chainScope.value === 'system' ? systemChains.value : savedChains.value;
  if (!chainSearchQuery.value) return source;
  return source.filter(c =>
    c.chain_name?.toLowerCase().includes(chainSearchQuery.value.toLowerCase()) ||
    c.description?.toLowerCase().includes(chainSearchQuery.value.toLowerCase())
  );
});

const hasApiTrigger = computed(() => {
  return chain.triggers && chain.triggers.some(t => t.trigger_type === 'api');
});

/**
 * Computed chain input schema — auto-derived from step inputs.
 * Uses stored schema if available (preserves user edits), otherwise computes live.
 */
const chainInputSchema = computed(() => {
  if (chain.input_schema && chain.input_schema.inputs && chain.input_schema.inputs.length > 0) {
    return chain.input_schema;
  }
  return computeChainInputSchema();
});

/**
 * Computed chain output schema — auto-derived from final step outputs.
 */
const chainOutputSchema = computed(() => {
  if (chain.output_schema && chain.output_schema.outputs && chain.output_schema.outputs.length > 0) {
    return chain.output_schema;
  }
  return computeChainOutputSchema();
});

/**
 * Auto-generated JSON Schema for API consumers.
 */
const generatedApiSchemaJson = computed(() => {
  const schema = generateApiJsonSchema();
  return JSON.stringify(schema, null, 2);
});

/**
 * Effective API schema — user-defined (from trigger config) takes priority, falls back to auto-generated.
 */
const effectiveApiSchema = computed(() => {
  const apiTrigger = chain.triggers?.find(t => t.trigger_type === 'api');
  if (apiTrigger?.trigger_config?.input_schema) {
    // User has defined a custom schema
    try {
      const parsed = JSON.parse(apiTrigger.trigger_config.input_schema);
      return { json: JSON.stringify(parsed, null, 2), isCustom: true };
    } catch (e) {
      // Not valid JSON — return raw text
      return { json: apiTrigger.trigger_config.input_schema, isCustom: true };
    }
  }
  return { json: generatedApiSchemaJson.value, isCustom: false };
});

/**
 * Get available API payload fields from the chain's API trigger schema
 * Returns array of field paths that can be used in input mappings
 */
const getApiPayloadFields = computed(() => {
  const apiTrigger = chain.triggers?.find(t => t.trigger_type === 'api');
  if (!apiTrigger || !apiTrigger.trigger_config?.input_schema) {
    return [];
  }

  try {
    const schema = JSON.parse(apiTrigger.trigger_config.input_schema);
    const fields = [];

    // Extract field paths from JSON schema
    const extractFields = (obj, prefix = '') => {
      if (!obj || typeof obj !== 'object') return;

      if (obj.properties) {
        Object.keys(obj.properties).forEach(key => {
          const fieldPath = prefix ? `${prefix}.${key}` : key;
          const property = obj.properties[key];

          // Add this field
          fields.push({
            text: fieldPath,
            value: fieldPath,
            type: property.type || 'any'
          });

          // Recursively extract nested properties
          if (property.properties) {
            extractFields(property, fieldPath);
          }

          // Handle array items
          if (property.type === 'array' && property.items?.properties) {
            extractFields(property.items, `${fieldPath}[*]`);
          }
        });
      }
    };

    extractFields(schema);
    return fields;
  } catch (error) {
    console.error('Failed to parse API trigger schema:', error);
    return [];
  }
});

const hierarchyList = computed(() => {
  return store.state.hierarchies?.hierarchies || []
});

/**
 * Get available input properties for the selected step
 * Used to populate dropdown for pass-through output source selection
 */
const availableInputProperties = computed(() => {
  if (!selectedStep.value || !selectedStep.value.config?.input) {
    return [];
  }

  return selectedStep.value.config.input
    .filter(input => input.property && input.property.trim() !== '')
    .map(input => ({
      text: input.property,
      value: input.property,
      source: input.source || 'unknown'
    }));
});

const activeSteps = computed(() => {
  return chain.steps
    .filter(s => s.active !== false && s.step_order !== null)
    .sort((a, b) => a.step_order - b.step_order);
});

// Computed property for connection paths - explicitly depends on trigger/step positions for reactivity
const connectionPaths = computed(() => {
  // Access trigger positions to establish reactive dependency
  const triggerPositions = chain.triggers.map(t => ({ id: t.trigger_id, x: t.x, y: t.y }));
  // Access step positions to establish reactive dependency
  const stepPositions = chain.steps.map(s => ({ id: s.id, x: s.x, y: s.y }));

  return chain.connections.map(conn => {
    const fromPos = getNodePosition(conn.from, conn.fromPoint || 'right');
    const toPos = getNodePosition(conn.to, conn.toPoint || 'left');

    // Skip connections with invalid positions (both x and y are 0)
    if ((fromPos.x === 0 && fromPos.y === 0) || (toPos.x === 0 && toPos.y === 0)) {
      return null;
    }

    const midX = (fromPos.x + toPos.x) / 2;
    const midY = (fromPos.y + toPos.y) / 2;
    return {
      ...conn,
      path: createPathWithPoints(fromPos, toPos, conn.fromPoint || 'right', conn.toPoint || 'left'),
      fromPos,
      toPos,
      midpointStyle: {
        position: 'absolute',
        left: `${midX - 16}px`,
        top: `${midY - 16}px`,
        pointerEvents: 'auto'
      }
    };
  }).filter(conn => conn !== null);
});

const propertyGroups = computed(() => {
  const groups = new Map();

  // Use same eligibility filter as schema derivation: active + connected (step_order set)
  const eligibleSteps = (chain.steps || [])
    .filter(s => s.active !== false && s.step_order != null)
    .sort((a, b) => (a.step_order || 0) - (b.step_order || 0));

  // Process both inputs and outputs
  eligibleSteps.forEach(step => {
    // Process inputs
    const inputs = step.config?.input || [];
    inputs.forEach(prop => {
      const key = `input_${prop.property.toLowerCase().trim()}`;

      if (!groups.has(key)) {
        groups.set(key, {
          id: `group_${key}`,
          name: prop.property,
          type: 'input',
          instances: [],
          required: false
        });
      }

      const group = groups.get(key);
      group.instances.push({
        stepId: step.id,
        stepName: step.step_name,
        stepOrder: step.step_order,
        property: prop.property,
        source: prop.source,
        sourceField: prop.sourceField,
        outputProperty: prop.outputProperty,
        apiPayloadField: prop.apiPayloadField,
        required: prop.required || false,
        originalProp: prop
      });

      if (prop.required) {
        group.required = true;
      }
    });

    // Process outputs
    const outputs = step.config?.output || [];
    outputs.forEach(prop => {
      const key = `output_${prop.property.toLowerCase().trim()}`;

      if (!groups.has(key)) {
        groups.set(key, {
          id: `group_${key}`,
          name: prop.property,
          type: 'output',
          instances: [],
          required: false
        });
      }

      const group = groups.get(key);
      group.instances.push({
        stepId: step.id,
        stepName: step.step_name,
        stepOrder: step.step_order,
        property: prop.property,
        source: prop.source,
        extractFrom: prop.extractFrom,
        required: prop.required || false,
        originalProp: prop
      });

      if (prop.required) {
        group.required = true;
      }
    });
  });

  // Compute alias for each group from instance mappings
  for (const group of groups.values()) {
    if (group.type === 'input') {
      // Collect unique aliases from instances
      const aliases = new Set();
      for (const inst of group.instances) {
        if (inst.source === 'chain_output' && inst.outputProperty) {
          aliases.add(inst.outputProperty);
        } else if (inst.source === 'api_payload' && inst.apiPayloadField) {
          aliases.add(inst.apiPayloadField);
        } else if (inst.sourceField) {
          aliases.add(inst.sourceField);
        }
      }
      group.alias = aliases.size === 1 ? [...aliases][0] : (aliases.size > 1 ? [...aliases].join(', ') : null);
    } else {
      group.alias = null;
    }
  }

  return Array.from(groups.values()).sort((a, b) => {
    // Sort by type first (inputs before outputs), then by name
    if (a.type !== b.type) {
      return a.type === 'input' ? -1 : 1;
    }
    return a.name.localeCompare(b.name);
  });
});

const filteredPropertyGroups = computed(() => {
  if (!consolidationSearch.value) return propertyGroups.value;

  const search = consolidationSearch.value.toLowerCase();
  return propertyGroups.value.filter(group =>
    group.name.toLowerCase().includes(search) ||
    (group.alias && group.alias.toLowerCase().includes(search)) ||
    group.instances.some(inst => inst.stepName.toLowerCase().includes(search))
  );
});

const selectedPropertyGroups = computed(() => {
  return propertyGroups.value.filter(g => selectedProperties.value.includes(g.id));
});

const allPropertiesSelected = computed(() => {
  return filteredPropertyGroups.value.length > 0 &&
    filteredPropertyGroups.value.every(g => selectedProperties.value.includes(g.id));
});

const mergeChanges = computed(() => {
  const changes = [];
  selectedPropertyGroups.value.forEach(group => {
    group.instances.forEach(inst => {
      changes.push({
        stepId: inst.stepId,
        stepName: inst.stepName,
        stepOrder: inst.stepOrder,
        oldName: inst.property,
        newName: mergeTargetName.value
      });
    });
  });
  return changes.sort((a, b) => a.stepOrder - b.stepOrder);
});

const affectedStepsCount = computed(() => {
  const uniqueSteps = new Set(mergeChanges.value.map(c => c.stepId));
  return uniqueSteps.size;
});
//#endregion

//#region Schema Methods

/**
 * Computes chain input schema from current step configurations.
 * Mirrors the backend deriveChainInputSchema logic.
 */
const computeChainInputSchema = () => {
  const chainInputs = new Map();

  // Only consider active steps that are connected in the chain flow
  // step_order is set by calculateStepOrder() — null means disconnected
  const eligibleSteps = (chain.steps || []).filter(s =>
    s.active !== false && s.step_order != null
  );

  // Build set of step inputs fed by step-to-step connections (internal)
  const stepFedInputs = new Set();
  for (const conn of (chain.connections || [])) {
    const fromStr = String(conn.from || '');
    const isFromStep = fromStr !== 'start' && !fromStr.startsWith('trigger-');
    if (isFromStep && conn.mapping_config?.mappings) {
      for (const m of conn.mapping_config.mappings) {
        if (m.step_input) {
          stepFedInputs.add(`${conn.to}_${m.step_input}`);
        }
      }
    }
  }

  for (const step of eligibleSteps) {
    const inputs = step.config?.input || [];
    for (const input of inputs) {
      if (input.source === 'chain_output' || input.source === 'static') continue;
      if (stepFedInputs.has(`${step.id}_${input.property}`)) continue;

      const dedupeKey = input.property;
      if (chainInputs.has(dedupeKey)) {
        chainInputs.get(dedupeKey).step_bindings.push({
          step_name: step.step_name || step.name || 'Unnamed',
          input_property: input.property,
          source: input.source,
          source_metadata: input.metadata || {}
        });
      } else {
        const derivedLabel = input.prompt || input.property;
        chainInputs.set(dedupeKey, {
          key: dedupeKey,
          label: derivedLabel,
          description: input.prompt || '',
          data_type: 'string',
          required: input.required !== false,
          excluded: false,
          step_bindings: [{
            step_name: step.step_name || step.name || 'Unnamed',
            input_property: input.property,
            source: input.source,
            source_metadata: input.metadata || {}
          }]
        });
      }
    }
  }

  return { version: '1.0', inputs: Array.from(chainInputs.values()) };
};

/**
 * Computes chain output schema from final step / output formatter outputs.
 */
const computeChainOutputSchema = () => {
  const outputs = [];

  // Only consider active steps that are connected in the chain flow
  // step_order is set by calculateStepOrder() — null means disconnected
  const eligibleSteps = (chain.steps || []).filter(s =>
    s.active !== false && s.step_order != null
  );

  for (const step of eligibleSteps) {
    const isFinal = step.is_final_step === true || step.is_final_step === 1;
    const isFormatter = step.step_type === 'output_formatter';
    if (!isFinal && !isFormatter) continue;

    for (const output of (step.config?.output || [])) {
      outputs.push({
        key: output.property,
        label: output.property,
        description: '',
        data_type: output.type || 'string',
        source_step_name: step.step_name || step.name || 'Unnamed',
        extract_from: output.extractFrom || 'response'
      });
    }
  }

  // Fallback: use last connected active step's outputs
  if (outputs.length === 0 && eligibleSteps.length > 0) {
    const lastStep = eligibleSteps[eligibleSteps.length - 1];
    for (const output of (lastStep.config?.output || [])) {
      outputs.push({
        key: output.property,
        label: output.property,
        description: '',
        data_type: output.type || 'string',
        source_step_name: lastStep.step_name || lastStep.name || 'Unnamed',
        extract_from: output.extractFrom || 'response'
      });
    }
  }

  return { version: '1.0', outputs };
};

/**
 * Generates JSON Schema for API consumers from chain input schema.
 */
const generateApiJsonSchema = () => {
  const schema = chainInputSchema.value || computeChainInputSchema();
  const properties = {};
  const required = [];

  for (const input of (schema.inputs || [])) {
    if (input.excluded) continue;
    properties[input.label || input.key] = {
      type: input.data_type || 'string',
      description: input.description || ''
    };
    if (input.required) {
      required.push(input.label || input.key);
    }
  }

  return {
    type: 'object',
    properties,
    required
  };
};

/**
 * Refreshes the chain schema by re-deriving from current steps.
 * Preserves user-edited labels and descriptions.
 */
const refreshChainSchema = () => {
  const derived = computeChainInputSchema();
  const derivedOutput = computeChainOutputSchema();

  // Merge user refinements from existing schema (labels, descriptions, excluded flag)
  if (chain.input_schema?.inputs) {
    const existingMap = new Map();
    for (const input of chain.input_schema.inputs) {
      existingMap.set(input.key, input);
    }
    for (const input of derived.inputs) {
      const prev = existingMap.get(input.key);
      if (prev) {
        if (prev.label && prev.label !== prev.key) input.label = prev.label;
        if (prev.description) input.description = prev.description;
        if (prev.excluded) input.excluded = true;
        if (prev.required !== undefined) input.required = prev.required;
      }
    }
  }

  if (chain.output_schema?.outputs) {
    const existingMap = new Map();
    for (const output of chain.output_schema.outputs) {
      existingMap.set(output.key, output);
    }
    for (const output of derivedOutput.outputs) {
      const prev = existingMap.get(output.key);
      if (prev) {
        if (prev.label && prev.label !== prev.key) output.label = prev.label;
        if (prev.description) output.description = prev.description;
      }
    }
  }

  chain.input_schema = derived;
  chain.output_schema = derivedOutput;
  schemaEdited.value = false;
  triggerNotification('Schema refreshed from step configurations', 'success');
};

const onSchemaEdited = () => {
  schemaEdited.value = true;
};

/**
 * Active (non-excluded) chain inputs — used for display in schema table,
 * trigger resolution, and API schema generation.
 */
const activeChainInputs = computed(() => {
  return (chainInputSchema.value?.inputs || []).filter(i => !i.excluded);
});

/**
 * Excluded chain inputs — resolved internally, hidden from external schema.
 */
const excludedChainInputs = computed(() => {
  return (chainInputSchema.value?.inputs || []).filter(i => i.excluded);
});

const excludeSchemaInput = (key) => {
  const input = chainInputSchema.value?.inputs?.find(i => i.key === key);
  if (input) {
    vueSet(input, 'excluded', true);
    onSchemaEdited();
  }
};

const restoreSchemaInput = (key) => {
  const input = chainInputSchema.value?.inputs?.find(i => i.key === key);
  if (input) {
    vueSet(input, 'excluded', false);
    onSchemaEdited();
  }
};

const copyApiSchema = () => {
  navigator.clipboard.writeText(effectiveApiSchema.value.json);
  triggerNotification('API schema copied to clipboard', 'success');
};

const getSourceColor = (source) => {
  const colors = {
    hierarchy: 'blue',
    tmpl_part: 'teal',
    level_framework: 'purple',
    api_payload: 'orange',
    dynamic: 'grey',
    prompt: 'indigo'
  };
  return colors[source] || 'grey';
};

const getTriggerTypeColor = (triggerType) => {
  const colors = {
    document_action: 'blue',
    hierarchy_node_action: 'teal',
    api: 'orange',
    manual: 'grey',
    scheduled: 'purple',
    webhook: 'red',
    chain_output: 'green',
    batch_operation: 'indigo',
    doc_create: 'cyan'
  };
  return colors[triggerType] || 'grey';
};

/**
 * Determines if a trigger type can auto-resolve a given chain input.
 */
const canTriggerResolveInput = (trigger, input) => {
  const sources = triggerDataSources[trigger.trigger_type] || [];
  const inputSource = input.step_bindings?.[0]?.source;

  // Check if trigger type provides data matching the input's source type
  return sources.some(s => s.provides === inputSource);
};

const getTriggerResolutionLabel = (trigger, input) => {
  if (canTriggerResolveInput(trigger, input)) {
    const sources = triggerDataSources[trigger.trigger_type] || [];
    const inputSource = input.step_bindings?.[0]?.source;
    const match = sources.find(s => s.provides === inputSource);
    return match ? match.label : 'Auto';
  }
  return 'Manual Input';
};

const getTriggerResolutionColor = (trigger, input) => {
  return canTriggerResolveInput(trigger, input) ? 'green' : 'orange';
};

/**
 * Gets the input resolution config for a specific chain input on a trigger.
 * Stored in trigger.trigger_config.input_resolution[].
 */
const getTriggerInputResolution = (trigger, chainInputKey) => {
  if (!trigger?.trigger_config?.input_resolution) {
    // Default: auto if trigger type can provide it, manual otherwise
    const schemaInput = chainInputSchema.value.inputs.find(i => i.key === chainInputKey);
    if (schemaInput && canTriggerResolveInput(trigger, schemaInput)) {
      return { resolution_type: 'auto', source_path: '', static_value: '' };
    }
    return { resolution_type: 'manual', source_path: '', static_value: '' };
  }
  const entry = trigger.trigger_config.input_resolution.find(r => r.chain_input_key === chainInputKey);
  if (!entry) {
    const schemaInput = chainInputSchema.value.inputs.find(i => i.key === chainInputKey);
    if (schemaInput && canTriggerResolveInput(trigger, schemaInput)) {
      return { resolution_type: 'auto', source_path: '', static_value: '' };
    }
    return { resolution_type: 'manual', source_path: '', static_value: '' };
  }
  return entry;
};

/**
 * Sets the resolution type for a chain input on a trigger.
 */
const setTriggerInputResolution = (trigger, chainInputKey, resolutionType) => {
  if (!trigger.trigger_config) trigger.trigger_config = {};
  if (!trigger.trigger_config.input_resolution) trigger.trigger_config.input_resolution = [];

  const idx = trigger.trigger_config.input_resolution.findIndex(r => r.chain_input_key === chainInputKey);
  const entry = {
    chain_input_key: chainInputKey,
    resolution_type: resolutionType,
    source_path: '',
    static_value: ''
  };

  if (idx >= 0) {
    trigger.trigger_config.input_resolution[idx] = { ...trigger.trigger_config.input_resolution[idx], resolution_type: resolutionType };
  } else {
    trigger.trigger_config.input_resolution.push(entry);
  }
};

/**
 * Sets a specific config value on a trigger input resolution entry.
 */
const setTriggerInputResolutionConfig = (trigger, chainInputKey, field, value) => {
  if (!trigger.trigger_config) trigger.trigger_config = {};
  if (!trigger.trigger_config.input_resolution) trigger.trigger_config.input_resolution = [];

  let entry = trigger.trigger_config.input_resolution.find(r => r.chain_input_key === chainInputKey);
  if (!entry) {
    entry = { chain_input_key: chainInputKey, resolution_type: 'trigger_data', source_path: '', static_value: '' };
    trigger.trigger_config.input_resolution.push(entry);
  }
  entry[field] = value;
};

/**
 * Returns available resolution options for a given trigger type and chain input.
 */
const getResolutionOptions = (triggerType, input) => {
  const options = [
    { text: 'Manual (User Input)', value: 'manual' },
    { text: 'Static Value', value: 'static' }
  ];

  const sources = triggerDataSources[triggerType] || [];
  const inputSource = input.step_bindings?.[0]?.source;

  // If trigger type can auto-resolve this input type, show "Auto" option
  if (sources.some(s => s.provides === inputSource)) {
    options.unshift({ text: 'Auto (from Trigger)', value: 'auto' });
  }

  // Always allow explicit trigger data mapping
  options.splice(1, 0, { text: 'Mapped Field', value: 'trigger_data' });

  return options;
};

/**
 * Returns a placeholder for the source path field based on trigger type.
 */
const getSourcePathPlaceholder = (triggerType, input) => {
  const placeholders = {
    api: 'payload.field_name',
    webhook: 'payload.field_name',
    document_action: 'source_data.doc_id',
    hierarchy_node_action: 'source_data.node_ids',
    chain_output: 'output.property_name',
    batch_operation: 'batch.item.field'
  };
  return placeholders[triggerType] || 'source_data.field_name';
};

/**
 * Returns an icon for a given input source type.
 */
const getInputSourceIcon = (source) => {
  const icons = {
    hierarchy: 'mdi-file-tree',
    tmpl_part: 'mdi-file-document',
    level_framework: 'mdi-stairs',
    api_payload: 'mdi-api',
    dynamic: 'mdi-swap-horizontal',
    prompt: 'mdi-message-text'
  };
  return icons[source] || 'mdi-help-circle-outline';
};

// Pre-execution form state
const showPreExecDialog = ref(false);
const preExecUnresolvedInputs = ref([]);
const preExecManualValues = reactive({});
const preExecTriggerContext = ref(null);

const allPreExecFieldsFilled = computed(() => {
  return preExecUnresolvedInputs.value
    .filter(i => i.required)
    .every(i => preExecManualValues[i.key] && String(preExecManualValues[i.key]).trim() !== '');
});

/**
 * Checks if a trigger has unresolved inputs before execution.
 * If yes, shows the pre-execution form. If no, executes immediately.
 */
const checkAndExecuteChain = async (triggerContext) => {
  if (!chain.prompt_chain_id) {
    triggerNotification('Please save the chain before executing', 'warning');
    return;
  }

  const triggerId = triggerContext.trigger_id;
  if (!triggerId) {
    // No trigger — execute directly
    executeChainWithContext(triggerContext);
    return;
  }

  try {
    const response = await axios.get(`/externalContent/chainInputRequirements/${chain.prompt_chain_id}/${triggerId}`);
    if (response.data.success && response.data.data) {
      const { unresolved, all_resolved } = response.data.data;
      if (all_resolved) {
        executeChainWithContext(triggerContext);
      } else {
        // Show pre-execution form
        preExecUnresolvedInputs.value = unresolved;
        Object.keys(preExecManualValues).forEach(k => delete preExecManualValues[k]);
        unresolved.forEach(input => {
          preExecManualValues[input.key] = '';
        });
        preExecTriggerContext.value = triggerContext;
        showPreExecDialog.value = true;
      }
    } else {
      // Fallback: execute without pre-check
      executeChainWithContext(triggerContext);
    }
  } catch (error) {
    console.error('Pre-execution check failed, executing anyway:', error);
    executeChainWithContext(triggerContext);
  }
};

const cancelPreExec = () => {
  showPreExecDialog.value = false;
  preExecUnresolvedInputs.value = [];
  preExecTriggerContext.value = null;
};

const submitPreExecForm = () => {
  if (!preExecTriggerContext.value) return;

  // Merge manual values into the trigger context
  const context = { ...preExecTriggerContext.value };
  if (!context.manual_values) context.manual_values = {};
  for (const [key, value] of Object.entries(preExecManualValues)) {
    if (value && String(value).trim() !== '') {
      context.manual_values[key] = value;
    }
  }

  showPreExecDialog.value = false;
  executeChainWithContext(context);
};

const executeChainWithContext = async (triggerContext) => {
  try {
    const response = await axios.post('/externalContent/executePromptChain', {
      prompt_chain_id: chain.prompt_chain_id,
      trigger_context: triggerContext
    });

    if (response.data && response.data.success) {
      triggerNotification('Chain execution started', 'success');
    } else {
      triggerNotification('Execution failed: ' + (response.data?.error || 'Unknown error'), 'error');
    }
  } catch (error) {
    console.error('Chain execution error:', error);
    triggerNotification('Error starting execution: ' + (error.message || 'Unknown error'), 'error');
  }
};
//#endregion

//#region Methods
const isGroupSelected = (groupId) => {
  return selectedProperties.value.includes(groupId);
};

const toggleGroupSelection = (groupId) => {
  const index = selectedProperties.value.indexOf(groupId);
  if (index > -1) {
    selectedProperties.value.splice(index, 1);
  } else {
    selectedProperties.value.push(groupId);
  }
};

const toggleSelectAll = (selected) => {
  if (selected) {
    selectedProperties.value = filteredPropertyGroups.value.map(g => g.id);
  } else {
    selectedProperties.value = [];
  }
};

const hasProperty = (step, group) => {
  return group.instances.some(inst => inst.stepId === step.id);
};

const isPropertyRequired = (step, group) => {
  const instance = group.instances.find(inst => inst.stepId === step.id);
  return instance?.required || false;
};

const getPropertySource = (step, group) => {
  const instance = group.instances.find(inst => inst.stepId === step.id);
  return instance?.source || '';
};

const getMostCommonSource = (instances) => {
  const sourceCounts = {};
  instances.forEach(inst => {
    sourceCounts[inst.source] = (sourceCounts[inst.source] || 0) + 1;
  });
  return Object.keys(sourceCounts).reduce((a, b) => 
    sourceCounts[a] > sourceCounts[b] ? a : b
  );
};

const getStepTypeColor = (stepType) => {
  const colors = {
    'prompt': '#50B3CB',
    'service': '#5E2872',
    'output_formatter': '#ED742D',
    'chain': '#4CAF50',
    'manual_selection': '#FF9800'
  };
  return colors[stepType] || '#9CA3AF';
};

const toggleProperty = (step, group) => {
  const stepIndex = chain.steps.findIndex(s => s.id === step.id);
  if (stepIndex === -1) return;

  const properties = group.type === 'input'
    ? chain.steps[stepIndex].config.input
    : chain.steps[stepIndex].config.output;

  const propIndex = properties.findIndex(p =>
    p.property.toLowerCase() === group.name.toLowerCase()
  );

  if (propIndex > -1) {
    properties.splice(propIndex, 1);
  } else {
    const newProp = {
      id: Date.now(),
      property: group.name,
      source: getMostCommonSource(group.instances),
      required: group.required
    };

    if (group.type === 'input') {
      newProp.sourceField = 'value';
      newProp.outputProperty = '';
    } else {
      newProp.extractFrom = 'response';
    }

    properties.push(newProp);
  }
};

// Removed updateGroupName and applyGroupNameChange - property names cannot be changed
// as they are referenced in prompt text as placeholders {{property}}

const autoGroupSimilarProperties = () => {
  const suggestions = [];
  const groups = propertyGroups.value;
  
  for (let i = 0; i < groups.length; i++) {
    for (let j = i + 1; j < groups.length; j++) {
      const similarity = calculateSimilarity(groups[i].name, groups[j].name);
      
      if (similarity >= 70) {
        suggestions.push({
          id: `suggestion_${i}_${j}`,
          properties: [
            { id: groups[i].id, name: groups[i].name, stepCount: groups[i].instances.length },
            { id: groups[j].id, name: groups[j].name, stepCount: groups[j].instances.length }
          ],
          similarity,
          suggestedName: findCommonName(groups[i].name, groups[j].name)
        });
      }
    }
  }
  
  propertySuggestions.value = suggestions.sort((a, b) => b.similarity - a.similarity);
  
  if (suggestions.length > 0) {
    showSuggestionsDialog.value = true;
  } else {
    triggerNotification('No similar properties found', 'info');
  }
};

const calculateSimilarity = (str1, str2) => {
  const s1 = str1.toLowerCase();
  const s2 = str2.toLowerCase();
  
  const longer = s1.length > s2.length ? s1 : s2;
  const shorter = s1.length > s2.length ? s2 : s1;
  
  if (longer.length === 0) return 100;
  
  const editDistance = levenshteinDistance(longer, shorter);
  return Math.round(((longer.length - editDistance) / longer.length) * 100);
};

const levenshteinDistance = (str1, str2) => {
  const matrix = [];
  
  for (let i = 0; i <= str2.length; i++) {
    matrix[i] = [i];
  }
  
  for (let j = 0; j <= str1.length; j++) {
    matrix[0][j] = j;
  }
  
  for (let i = 1; i <= str2.length; i++) {
    for (let j = 1; j <= str1.length; j++) {
      if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        );
      }
    }
  }
  
  return matrix[str2.length][str1.length];
};

const findCommonName = (name1, name2) => {
  const s1 = name1.toLowerCase();
  const s2 = name2.toLowerCase();
  
  let longest = '';
  
  for (let i = 0; i < s1.length; i++) {
    for (let j = i + 1; j <= s1.length; j++) {
      const substr = s1.substring(i, j);
      if (s2.includes(substr) && substr.length > longest.length) {
        longest = substr;
      }
    }
  }
  
  return longest.charAt(0).toUpperCase() + longest.slice(1);
};

const openMergeDialog = () => {
  if (selectedProperties.value.length < 2) return;
  
  const firstGroup = selectedPropertyGroups.value[0];
  mergeTargetName.value = firstGroup.name;
  mergeTargetSource.value = getMostCommonSource(firstGroup.instances);
  mergeAsRequired.value = firstGroup.required;
  
  showMergeDialog.value = true;
};

const removeFromMerge = (groupId) => {
  const index = selectedProperties.value.indexOf(groupId);
  if (index > -1) {
    selectedProperties.value.splice(index, 1);
  }
};

const executeMerge = () => {
  if (mergeTargetSource.value === 'chain_output' && !mergeTargetName.value) return;
  if (mergeTargetSource.value === 'api_payload' && !mergeTargetApiPayloadField.value) return;
  if (selectedProperties.value.length < 2) return;

  selectedPropertyGroups.value.forEach(group => {
    // Only update inputs (outputs don't have source/outputProperty)
    if (group.type !== 'input') return;

    group.instances.forEach(inst => {
      const step = chain.steps.find(s => s.id === inst.stepId);
      if (!step) return;

      const properties = step.config.input;
      const prop = properties.find(p => p === inst.originalProp);

      if (prop) {
        // Don't change property name - it's referenced in prompts
        prop.source = mergeTargetSource.value;
        prop.required = mergeAsRequired.value;

        // Set the consolidated output property reference if using chain output
        if (mergeTargetSource.value === 'chain_output') {
          prop.outputProperty = mergeTargetName.value;
        }

        // Set the API payload field reference if using api_payload
        if (mergeTargetSource.value === 'api_payload') {
          prop.apiPayloadField = mergeTargetApiPayloadField.value;
        }
      }
    });
  });

  selectedProperties.value = [];
  showMergeDialog.value = false;

  triggerNotification(`Successfully configured ${selectedPropertyGroups.value.length} properties`, 'success');
};

const reviewSuggestions = () => {
  showSuggestionsDialog.value = true;
};

const acceptSuggestion = (suggestion) => {
  selectedProperties.value = suggestion.properties.map(p => p.id);
  mergeTargetName.value = suggestion.suggestedName;
  executeMerge();
  dismissSuggestion(suggestion.id);
};

const dismissSuggestion = (suggestionId) => {
  propertySuggestions.value = propertySuggestions.value.filter(s => s.id !== suggestionId);
};

const dismissAllSuggestions = () => {
  propertySuggestions.value = [];
  showSuggestionsDialog.value = false;
};

const acceptAllSuggestions = () => {
  propertySuggestions.value.forEach(suggestion => {
    acceptSuggestion(suggestion);
  });
  showSuggestionsDialog.value = false;
};

const isSuggestedGroup = (groupId) => {
  return propertySuggestions.value.some(s =>
    s.properties.some(p => p.id === groupId)
  );
};

const addToMergeSelection = (group) => {
  if (!selectedProperties.value.includes(group.id)) {
    selectedProperties.value.push(group.id);
  }
};

const duplicateProperty = (group) => {
  activeSteps.value.forEach(step => {
    if (!hasProperty(step, group)) {
      const newProp = {
        id: Date.now() + Math.random(),
        property: group.name,
        source: getMostCommonSource(group.instances),
        required: group.required
      };

      if (group.type === 'input') {
        newProp.sourceField = 'value';
        newProp.outputProperty = '';
        step.config.input.push(newProp);
      } else {
        newProp.extractFrom = 'response';
        step.config.output.push(newProp);
      }
    }
  });

  triggerNotification(`Added "${group.name}" to all steps`, 'success');
};

const deleteProperty = (group) => {
  if (!confirm(`Delete "${group.name}" from ${group.instances.length} step(s)?`)) {
    return;
  }

  group.instances.forEach(inst => {
    const step = chain.steps.find(s => s.id === inst.stepId);
    if (!step) return;

    const properties = group.type === 'input' ? step.config.input : step.config.output;
    const index = properties.indexOf(inst.originalProp);

    if (index > -1) {
      properties.splice(index, 1);
    }
  });

  triggerNotification(`Deleted "${group.name}" from all steps`, 'success');
};

// Cell-level editing methods
const getCellPropertyName = (step, group) => {
  const instance = group.instances.find(inst => inst.stepId === step.id);
  return instance?.property || group.name;
};

const getCellOutputProperty = (step, group) => {
  const instance = group.instances.find(inst => inst.stepId === step.id);
  return instance?.outputProperty || '';
};

const updateCellOutputProperty = (step, group, value) => {
  const instance = group.instances.find(inst => inst.stepId === step.id);
  if (instance && instance.originalProp) {
    instance.originalProp.outputProperty = value;
  }
};

const getCellApiPayloadField = (step, group) => {
  const instance = group.instances.find(inst => inst.stepId === step.id);
  return instance?.apiPayloadField || '';
};

const updateCellApiPayloadField = (step, group, value) => {
  const instance = group.instances.find(inst => inst.stepId === step.id);
  if (instance && instance.originalProp) {
    instance.originalProp.apiPayloadField = value;
  }
};

const updateCellSource = (step, group, newSource) => {
  const instance = group.instances.find(inst => inst.stepId === step.id);
  if (instance && instance.originalProp) {
    instance.originalProp.source = newSource;
  }
};

const updateCellRequired = (step, group, value) => {
  const instance = group.instances.find(inst => inst.stepId === step.id);
  if (instance && instance.originalProp) {
    instance.originalProp.required = value;
  }
};

const getSourceOptions = (type) => {
  if (type === 'input') {
    return [
      { text: 'Hierarchy', value: 'hierarchy' },
      { text: 'Previous Step Output', value: 'chain_output' },
      { text: 'API Payload', value: 'api_payload' },
      { text: 'Static', value: 'static' },
      { text: 'User Input', value: 'user_input' }
    ];
  } else {
    return [
      { text: 'Response', value: 'response' },
      { text: 'Metadata', value: 'metadata' }
    ];
  }
};

const addPropertyToStep = (step, group) => {
  const stepIndex = chain.steps.findIndex(s => s.id === step.id);
  if (stepIndex === -1) return;

  const properties = group.type === 'input'
    ? chain.steps[stepIndex].config.input
    : chain.steps[stepIndex].config.output;

  const newProp = {
    id: Date.now() + Math.random(),
    property: group.name,
    source: group.type === 'input' ? 'hierarchy' : 'response',
    required: false
  };

  if (group.type === 'input') {
    newProp.sourceField = 'value';
    newProp.outputProperty = '';
  } else {
    newProp.extractFrom = 'response';
  }

  properties.push(newProp);
};

const removePropertyFromStep = (step, group) => {
  const stepIndex = chain.steps.findIndex(s => s.id === step.id);
  if (stepIndex === -1) return;

  const properties = group.type === 'input'
    ? chain.steps[stepIndex].config.input
    : chain.steps[stepIndex].config.output;

  const instance = group.instances.find(inst => inst.stepId === step.id);
  if (instance) {
    const propIndex = properties.indexOf(instance.originalProp);
    if (propIndex > -1) {
      properties.splice(propIndex, 1);
    }
  }
};

// Computed for import dialog
const allPromptsMapped = computed(() => {
  if (!importedChainData.value) return false;
  const promptSteps = importedChainData.value.steps.filter(s => s.step_type === 'prompt');
  if (promptSteps.length === 0) return true;
  return promptSteps.every(step => promptMappings.value[step.id]);
});

// True when all prompt steps are either mapped OR scheduled for creation with complete remaps
const allPromptsResolved = computed(() => {
  if (!importedChainData.value) return false;
  const promptSteps = importedChainData.value.steps.filter(s => s.step_type === 'prompt');
  if (promptSteps.length === 0) return true;
  return promptSteps.every(step => {
    const state = promptCreateState.value[step.id];
    if (state?.mode === 'create') return isCreateConfigComplete(step);
    return !!promptMappings.value[step.id];
  });
});

const isCreateConfigComplete = (step) => {
  const promptData = step._promptData;
  if (!promptData) return false;
  const state = promptCreateState.value[step.id];
  const inputs = promptData.configuration_data?.input || [];
  return inputs.every((inp, idx) => {
    if (['dynamic', 'static', 'chain_output', 'level_framework'].includes(inp.source)) return true;
    const remap = state?.inputRemaps?.[idx];
    if (inp.source === 'hierarchy') return !!(remap?.ht_id);
    if (inp.source === 'tmpl_part') return !!(remap?.tp_id);
    return true;
  });
};

const getPromptCompatibility = (step) => {
  if (!step._promptData) return 'legacy';
  const hintName = (step._promptName || step.step_name || '').toLowerCase().trim();
  const exactMatch = availablePrompts.value.find(p => p.name.toLowerCase().trim() === hintName);
  if (exactMatch) {
    const exportProps = (step._promptData.configuration_data?.input || []).map(i => i.property).sort().join(',');
    const localProps = (exactMatch.inputs || []).map(i => i.property).sort().join(',');
    return exportProps === localProps ? 'exact_match' : 'name_match';
  }
  return 'no_match';
};

// Import wizard computed helpers
const importFormatterSteps = computed(() => {
  if (!importedChainData.value) return [];
  return importedChainData.value.steps.filter(s =>
    s.step_type === 'output_formatter' &&
    s.config?.formatter_config?.document_mappings?.length > 0
  );
});

const importHasDocMappings = computed(() => {
  return importFormatterSteps.value.length > 0;
});

const importChainStats = computed(() => {
  if (!importedChainData.value) return [];
  const steps = importedChainData.value.steps;
  const stats = [];
  const promptCount = steps.filter(s => s.step_type === 'prompt').length;
  const serviceCount = steps.filter(s => s.step_type === 'service').length;
  const formatterCount = steps.filter(s => s.step_type === 'output_formatter').length;
  const chainCount = steps.filter(s => s.step_type === 'chain').length;
  const otherCount = steps.length - promptCount - serviceCount - formatterCount - chainCount;
  if (promptCount) stats.push({ label: 'Prompts', count: promptCount, icon: 'mdi-message-text' });
  if (serviceCount) stats.push({ label: 'Services', count: serviceCount, icon: 'mdi-api' });
  if (formatterCount) stats.push({ label: 'Formatters', count: formatterCount, icon: 'mdi-file-document-outline' });
  if (chainCount) stats.push({ label: 'Sub-chains', count: chainCount, icon: 'mdi-link-variant' });
  if (otherCount) stats.push({ label: 'Other', count: otherCount, icon: 'mdi-cog' });
  stats.push({ label: 'Connections', count: (importedChainData.value.connections || []).length, icon: 'mdi-transit-connection-variant' });
  stats.push({ label: 'Triggers', count: (importedChainData.value.triggers || []).length, icon: 'mdi-flash' });
  return stats;
});

const importDocTotalCount = computed(() => {
  return importFormatterSteps.value.reduce((sum, step) =>
    sum + (step.config?.formatter_config?.document_mappings || []).length, 0);
});

const importDocMappedCount = computed(() => {
  let count = 0;
  for (const stepId of Object.keys(importDocMappings.value)) {
    for (const mIdx of Object.keys(importDocMappings.value[stepId])) {
      const m = importDocMappings.value[stepId][mIdx];
      if (m && (m.tp_id || m.ht_id)) count++;
    }
  }
  return count;
});

const importCanAdvance = computed(() => {
  if (importWizardStep.value === 1) return !!importedChainData.value;
  if (importWizardStep.value === 2) return allPromptsResolved.value;
  return true; // Steps 3, 4 are always passable
});

const getImportDocMapping = (stepId, mIdx, field) => {
  return importDocMappings.value[stepId]?.[mIdx]?.[field] || null;
};

const setImportDocMapping = (stepId, mIdx, type, value) => {
  const current = importDocMappings.value[stepId] || {};
  if (type === 'template_part') {
    const part = importTemplateParts.value.find(p => p.tp_id === value);
    importDocMappings.value = {
      ...importDocMappings.value,
      [stepId]: { ...current, [mIdx]: { tp_id: value, name: part?.name || null } }
    };
  } else {
    const existing = current[mIdx] || {};
    importDocMappings.value = {
      ...importDocMappings.value,
      [stepId]: { ...current, [mIdx]: { ...existing, ht_id: value } }
    };
  }
};

const setImportDocMappingLevel = (stepId, mIdx, value) => {
  const current = importDocMappings.value[stepId] || {};
  const existing = current[mIdx] || {};
  importDocMappings.value = {
    ...importDocMappings.value,
    [stepId]: { ...current, [mIdx]: { ...existing, level: parseInt(value) || 0 } }
  };
};

const getPromptNameById = (promptId) => {
  if (!promptId) return null;
  const prompt = availablePrompts.value.find(p => p.tmpl_ai_prompt_id === promptId);
  return prompt?.name || null;
};

const importWizardNext = async () => {
  const currentStep = importWizardStep.value;
  if (currentStep === 1 && importedChainData.value) {
    // Entering step 2: auto-match prompts by name
    autoMatchPrompts();
    importWizardStep.value = 2;
  } else if (currentStep === 2) {
    if (importHasDocMappings.value) {
      // Entering step 3: load template parts for mapping
      await loadImportTemplateParts();
      autoMatchDocMappings();
      importWizardStep.value = 3;
    } else {
      importWizardStep.value = 3; // Goes to options (step 3 when no doc mappings)
    }
  } else {
    importWizardStep.value = currentStep + 1;
  }
};

const importWizardPrev = () => {
  const currentStep = importWizardStep.value;
  if (!importHasDocMappings.value && currentStep === 3) {
    importWizardStep.value = 2; // Skip back over doc mappings
  } else {
    importWizardStep.value = currentStep - 1;
  }
};

const autoMatchPrompts = () => {
  if (!importedChainData.value) return;
  const promptSteps = importedChainData.value.steps.filter(s => s.step_type === 'prompt');
  promptSteps.forEach(step => {
    if (promptMappings.value[step.id]) return; // Already mapped
    const hintName = (step._promptName || step.step_name || '').toLowerCase().trim();
    if (!hintName) return;
    const match = availablePrompts.value.find(p => p.name.toLowerCase().trim() === hintName);
    if (match) {
      promptMappings.value[step.id] = match.tmpl_ai_prompt_id;
    }
  });
};

const loadImportTemplateParts = async () => {
  try {
    loadingImportTemplateParts.value = true;
    const response = await axios.get(`/templateBuilder/templateDetails/${props.value}`);
    if (response.data && response.data.Data) {
      const parts = response.data.Data.parts.filter(x => x.tp_active);
      importTemplateParts.value = parts.map((tp, idx) => ({
        tp_id: tp.tp_id,
        name: tp.tp_name,
        parent_tp_id: tp.parent_tp_id,
        cardinality: tp.cardinality,
        attributes: tp.attributes || [],
        isTagPart: tp.isTagPart,
        sequence: idx + 1
      }));
    }
  } catch (error) {
    console.error('Error loading template parts for import:', error);
  } finally {
    loadingImportTemplateParts.value = false;
  }
};

const autoMatchDocMappings = () => {
  if (!importedChainData.value) return;
  importFormatterSteps.value.forEach(fmtStep => {
    const mappings = fmtStep.config?.formatter_config?.document_mappings || [];
    mappings.forEach((mapping, mIdx) => {
      if (mapping.mappingTargetType === 'hierarchy_value') return; // Can't auto-match hierarchies reliably
      const hintName = (mapping._tmpl_part_name || '').toLowerCase().trim();
      if (!hintName) return;
      const match = importTemplateParts.value.find(p => p.name.toLowerCase().trim() === hintName);
      if (match) {
        setImportDocMapping(fmtStep.id, mIdx, 'template_part', match.tp_id);
      }
    });
  });
};

// "Create from Import" prompt methods
const initPromptCreateState = async (step) => {
  const existing = promptCreateState.value[step.id] || {};
  promptCreateState.value = {
    ...promptCreateState.value,
    [step.id]: {
      mode: 'create',
      inputRemaps: existing.inputRemaps || {},
      creating: false
    }
  };
  promptMappings.value = { ...promptMappings.value, [step.id]: null };
  expandedCreatePanel.value = step.id;

  // Eagerly load template parts if any input needs tmpl_part remapping
  const hasTmplPartInput = (step._promptData?.configuration_data?.input || [])
    .some(inp => inp.source === 'tmpl_part');
  if (hasTmplPartInput && importTemplateParts.value.length === 0 && !loadingImportTemplateParts.value) {
    await loadImportTemplateParts();
  }
};

const cancelCreateFromImport = (step) => {
  const existing = promptCreateState.value[step.id] || {};
  promptCreateState.value = {
    ...promptCreateState.value,
    [step.id]: { ...existing, mode: 'map' }
  };
  expandedCreatePanel.value = null;
};

const setInputRemap = (stepId, inputIndex, field, value) => {
  const state = promptCreateState.value[stepId] || { mode: 'create', inputRemaps: {}, creating: false };
  const existing = state.inputRemaps[inputIndex] || {};
  const updated = { ...existing, [field]: value };
  if (field === 'ht_id') delete updated.level;
  promptCreateState.value = {
    ...promptCreateState.value,
    [stepId]: {
      ...state,
      inputRemaps: { ...state.inputRemaps, [inputIndex]: updated }
    }
  };
};

const getHierarchyLevelsForRemap = (htId) => {
  if (!htId) return [];
  const hr = store.state.hierarchies?.hierarchies || [];
  const item = hr.find(x => x.ht_id === htId);
  return item?.levels || [];
};

const createPromptFromImport = async (step) => {
  const state = promptCreateState.value[step.id];
  if (!state || !step._promptData) return;

  promptCreateState.value = {
    ...promptCreateState.value,
    [step.id]: { ...state, creating: true }
  };

  try {
    const promptData = step._promptData;
    const inputRemaps = state.inputRemaps || {};

    // Build remapped input array
    const remappedInputs = (promptData.configuration_data?.input || []).map((inp, idx) => {
      const remap = inputRemaps[idx];
      const cleaned = { ...inp };
      delete cleaned._hint; // Remove export hint field
      if (!remap) return cleaned;
      const updatedMetadata = { ...cleaned.metadata };
      if (inp.source === 'hierarchy' && remap.ht_id) {
        updatedMetadata.ht_id = remap.ht_id;
        if (remap.level !== undefined) updatedMetadata.level = remap.level;
      } else if (inp.source === 'tmpl_part' && remap.tp_id) {
        updatedMetadata.tp_id = remap.tp_id;
      }
      return { ...cleaned, metadata: updatedMetadata };
    });

    const configurationData = {
      ...promptData.configuration_data,
      input: remappedInputs
    };

    const request = {
      tmpl_ai_prompt_id: 0,
      prompt_name: promptData.prompt_name,
      prompt_type: promptData.prompt_type,
      system_prompt: promptData.system_prompt,
      user_prompt: promptData.user_prompt,
      client_id: loginState.user?.client_id,
      tmpl_id: props.value,
      active: 1,
      configuration_data: JSON.stringify(configurationData)
    };

    const response = await axios.post('templateBuilder/savePrompt', request);
    const newPrompt = response.data?.Data;

    if (!newPrompt?.tmpl_ai_prompt_id) {
      throw new Error('No tmpl_ai_prompt_id returned from savePrompt');
    }

    // Refresh available prompts so the new prompt appears
    await loadPrompts();

    // Auto-map this step to the newly created prompt
    promptMappings.value = {
      ...promptMappings.value,
      [step.id]: newPrompt.tmpl_ai_prompt_id
    };

    // Switch back to map mode
    promptCreateState.value = {
      ...promptCreateState.value,
      [step.id]: { mode: 'map', inputRemaps: {}, creating: false }
    };
    expandedCreatePanel.value = null;

    triggerNotification(`Prompt "${promptData.prompt_name}" created successfully`, 'success');
  } catch (error) {
    console.error('Error creating prompt from import:', error);
    promptCreateState.value = {
      ...promptCreateState.value,
      [step.id]: { ...state, creating: false }
    };
    triggerNotification('Failed to create prompt: ' + (error.message || error), 'error');
  }
};

// Import/Export methods
const exportChain = () => {
  if (!chain.prompt_chain_id) {
    triggerNotification('Please save the chain before exporting', 'warning');
    return;
  }

  // Only export active steps
  const activeExportSteps = chain.steps.filter(s => s.active !== false);

  // Build a stable mapping from DB step IDs to portable export IDs (active steps only)
  const stepExportIdMap = {};
  activeExportSteps.forEach((step, index) => {
    stepExportIdMap[step.id] = `export_step_${index}`;
  });

  // Build a set of active step IDs for filtering connections
  const activeStepIds = new Set(activeExportSteps.map(s => s.id));

  // Build a stable mapping from DB trigger IDs to portable export IDs
  const triggerExportIdMap = {};
  chain.triggers.forEach((trigger, index) => {
    triggerExportIdMap[trigger.trigger_id] = `export_trigger_${index}`;
  });

  // Lookup hierarchy names for hints
  const hierarchies = hierarchyList.value || [];

  const exportData = {
    chain_name: chain.chain_name,
    description: chain.description,
    steps: activeExportSteps.map((step, index) => ({
      id: stepExportIdMap[step.id],
      step_type: step.step_type || step.type || 'prompt',
      step_name: step.step_name || step.name || 'Unnamed Step',
      step_order: step.step_order || index,
      tmpl_ai_prompt_id: step.tmpl_ai_prompt_id || null,
      service_endpoint_id: step.service_endpoint_id || null,
      service_id: step.service_id || null,
      output_formatter_id: step.output_formatter_id || null,
      child_chain_id: step.child_chain_id || null,
      is_final_step: step.is_final_step || false,
      is_post_process: step.is_post_process || false,
      continue_on_error: step.continue_on_error || false,
      active: true,
      x: step.x || 0,
      y: step.y || 0,
      config: {
        input: step.config?.input || [],
        output: step.config?.output || [],
        prompt_id: step.config?.prompt_id || step.tmpl_ai_prompt_id || null,
        service_config: step.config?.service_config || null,
        formatter_config: step.config?.formatter_config ? {
          ...step.config.formatter_config,
          // Remap step references inside formatter config to portable IDs
          // and wipe template-specific metadata from document_mappings
          document_mappings: (step.config.formatter_config.document_mappings || []).filter(m => m.is_active !== false).map(m => ({
            ...m,
            stepId: m.stepId ? stepExportIdMap[m.stepId] || m.stepId : m.stepId,
            // Wipe template-specific IDs (will be remapped on import)
            tmpl_part_id: null,
            tmpl_part: null,
            templatePart: null,
            child_part: null,
            childTemplatePart: null,
            tpa_map: null,
            json_target_attribute_id: null,
            hierarchyValueType: null,
            attr_list: [],
            // Add readable hints for the import wizard
            _tmpl_part_name: m.tmpl_part?.name || m.templatePart?.name || null,
            _hierarchy_name: m.hierarchyValueType ? (hierarchies.find(h => h.ht_id === m.hierarchyValueType)?.name || `HT ${m.hierarchyValueType}`) : null,
            _hierarchy_level: m.hierarchyValueLevel || null
          })),
          selected_outputs: (step.config.formatter_config.selected_outputs || []).map(output => {
            const [oldStepId, property] = output.split(':');
            const newId = stepExportIdMap[oldStepId];
            return newId ? `${newId}:${property}` : output;
          })
        } : null,
        chain_config: step.config?.chain_config || null,
        selection_config: step.config?.selection_config || null,
        model: step.config?.model || null,
        pre_step_hook: step.config?.pre_step_hook || null
      },
      // Reference info for prompt remapping on import
      _promptName: step.item?.name || step.step_name || null,
      _promptType: step.prompt_type || step.item?.prompt_type || null,
      // Full prompt definition for "create from import" on target client
      _promptData: (step.step_type === 'prompt' && step.item) ? {
        prompt_name: step.item.name || step.item.prompt_name,
        prompt_type: step.item.prompt_type,
        system_prompt: step.item.system_prompt || '',
        user_prompt: step.item.user_prompt || '',
        configuration_data: (() => {
          const configData = step.item.configuration_data || {};
          const enrichedInputs = (configData.input || []).map(inp => {
            let _hint = inp.property;
            if (inp.source === 'hierarchy' && inp.metadata?.ht_id) {
              const ht = hierarchies.find(h => h.ht_id === inp.metadata.ht_id);
              const levelName = ht?.levels?.find(l => l.index === inp.metadata.level)?.name;
              _hint = `${ht?.name || ht?.label || 'Hierarchy ' + inp.metadata.ht_id}${levelName ? ' / ' + levelName : ''}`;
            } else if (inp.source === 'tmpl_part' && inp.metadata?.tp_id) {
              _hint = `Template Part (ID: ${inp.metadata.tp_id})`;
            }
            return { ...inp, _hint };
          });
          return { ...configData, input: enrichedInputs };
        })()
      } : null
    })),
    connections: chain.connections
      // Only include connections where both endpoints are active steps or triggers
      .filter(conn => {
        const fromOk = !conn.from || conn.from === 'start' || String(conn.from).startsWith('trigger-') || activeStepIds.has(conn.from);
        const toOk = activeStepIds.has(conn.to);
        return fromOk && toOk;
      })
      .map(conn => {
      let fromId = conn.from;
      // Remap step IDs to portable export IDs
      if (fromId && String(fromId).startsWith('trigger-')) {
        const triggerId = String(fromId).replace('trigger-', '');
        const exportId = triggerExportIdMap[triggerId] || triggerExportIdMap[Number(triggerId)];
        fromId = exportId ? `trigger-${exportId}` : fromId;
      } else if (fromId && fromId !== 'start') {
        fromId = stepExportIdMap[fromId] || fromId;
      }

      return {
        from: fromId,
        to: stepExportIdMap[conn.to] || conn.to,
        fromPoint: conn.fromPoint || 'right',
        toPoint: conn.toPoint || 'left',
        mapping_config: conn.mapping_config || null
      };
    }),
    triggers: chain.triggers.map((trigger, index) => ({
      id: triggerExportIdMap[trigger.trigger_id],
      trigger_name: trigger.trigger_name,
      trigger_type: trigger.trigger_type,
      description: trigger.description || '',
      trigger_config: trigger.trigger_config || {},
      context_mapping: trigger.context_mapping || { mappings: [] },
      conditions: trigger.conditions || { rules: [], logic: 'AND' },
      priority: trigger.priority || 100,
      active: trigger.active !== false,
      x: trigger.x || 50,
      y: trigger.y || (100 + index * 140)
    })),
    input_schema: chain.input_schema || null,
    output_schema: chain.output_schema || null,
    execution_config: chain.execution_config || null,
    exportedAt: new Date().toISOString(),
    version: '3.0'
  };

  const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${chain.chain_name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${Date.now()}.json`;
  link.click();
  URL.revokeObjectURL(url);

  triggerNotification('Chain exported successfully', 'success');
};

const openImportDialog = () => {
  showImportDialog.value = true;
  importedChainData.value = null;
  promptMappings.value = {};
  importFile.value = null;
  importWizardStep.value = 1;
  importDocMappings.value = {};
  importTemplateParts.value = [];
  importAsNewChain.value = true;
  promptCreateState.value = {};
  expandedCreatePanel.value = null;
};

const handleFileUpload = (file) => {
  if (!file) return;

  const reader = new FileReader();
  reader.onload = (e) => {
    try {
      const data = JSON.parse(e.target.result);

      // Validate the structure
      if (!data.chain_name || !Array.isArray(data.steps)) {
        triggerNotification('Invalid chain file format', 'error');
        return;
      }

      importedChainData.value = data;

      // Initialize prompt mappings
      const mappings = {};
      data.steps.filter(s => s.step_type === 'prompt').forEach(step => {
        mappings[step.id] = null;
      });
      promptMappings.value = mappings;

    } catch (error) {
      console.error('Error parsing import file:', error);
      triggerNotification('Failed to parse chain file', 'error');
    }
  };
  reader.readAsText(file);
};

const cancelImport = () => {
  showImportDialog.value = false;
  importedChainData.value = null;
  promptMappings.value = {};
  importFile.value = null;
  importWizardStep.value = 1;
  importDocMappings.value = {};
  importTemplateParts.value = [];
  promptCreateState.value = {};
  expandedCreatePanel.value = null;
};

const executeImport = () => {
  if (!importedChainData.value || !allPromptsResolved.value) return;

  const data = importedChainData.value;

  // Create a mapping from exported step IDs to new UI step IDs
  const stepIdMap = {};
  const promptIdMap = {};

  data.steps.forEach(step => {
    const newId = `step_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    stepIdMap[step.id] = newId;

    if (step.step_type === 'prompt' && promptMappings.value[step.id]) {
      const oldPromptId = step.tmpl_ai_prompt_id || step.config?.prompt_id;
      const newPromptId = promptMappings.value[step.id];
      if (oldPromptId && newPromptId) {
        promptIdMap[oldPromptId] = newPromptId;
      }
    }
  });

  // Create a mapping from exported trigger IDs to new temp trigger IDs
  const triggerIdMap = {};
  (data.triggers || []).forEach((trigger, index) => {
    const newTriggerId = `temp_${Date.now()}_${index}`;
    // Map both the export `id` field (v2.0) and the `trigger_id` field (v1.0 legacy)
    if (trigger.id) {
      triggerIdMap[trigger.id] = newTriggerId;
    }
    if (trigger.trigger_id) {
      triggerIdMap[trigger.trigger_id] = newTriggerId;
    }
  });

  // Build new steps — strip all DB-specific IDs, only keep portable data
  const newSteps = data.steps.map((step, index) => {
    const newStep = {
      id: stepIdMap[step.id],
      step_type: step.step_type || step.type || 'prompt',
      step_name: step.step_name || step.name || 'Unnamed Step',
      step_order: step.step_order || index,
      tmpl_ai_prompt_id: step.tmpl_ai_prompt_id || null,
      service_endpoint_id: step.service_endpoint_id || null,
      service_id: step.service_id || null,
      output_formatter_id: step.output_formatter_id || null,
      child_chain_id: step.child_chain_id || null,
      is_final_step: step.is_final_step || false,
      is_post_process: step.is_post_process || false,
      continue_on_error: step.continue_on_error || false,
      active: step.active !== undefined ? step.active : true,
      x: step.x || 0,
      y: step.y || 0,
      // Explicitly null out DB IDs so saveChain creates new records
      prompt_chain_step_id: null,
      dependencies: [],
      config: {
        input: step.config?.input || [],
        output: step.config?.output || [{ property: 'result', extractFrom: 'response' }],
        prompt_id: step.config?.prompt_id || step.tmpl_ai_prompt_id || null,
        service_config: step.config?.service_config || null,
        formatter_config: step.config?.formatter_config || null,
        chain_config: step.config?.chain_config || null,
        selection_config: step.config?.selection_config || null,
        model: step.config?.model || null,
        pre_step_hook: step.config?.pre_step_hook || null
      },
      item: null,
      // Derive outputs from config.output (matching editChain logic) so getAvailableStepData() produces correct items
      outputs: (() => {
        if (step.step_type === 'manual_selection') return ['selected_items'];
        if (step.step_type === 'output_formatter') return ['result'];
        const configOutputs = (step.config?.output || [])
          .filter(o => o.property && o.property.trim() !== '')
          .map(o => o.property);
        return configOutputs.length > 0 ? configOutputs : ['result'];
      })(),
      prompt_type: step._promptType || step.prompt_type || null
    };

    // Remap prompt steps to the user's selected target prompt
    if (step.step_type === 'prompt' && promptMappings.value[step.id]) {
      const newPromptId = promptMappings.value[step.id];
      const mappedPrompt = availablePrompts.value.find(p => p.tmpl_ai_prompt_id === newPromptId);

      if (mappedPrompt) {
        newStep.tmpl_ai_prompt_id = mappedPrompt.tmpl_ai_prompt_id;
        newStep.item = mappedPrompt;
        newStep.prompt_type = mappedPrompt.prompt_type;
        newStep.step_name = mappedPrompt.name;
        newStep.config.prompt_id = mappedPrompt.tmpl_ai_prompt_id;

        // Merge inputs: use new prompt's inputs but preserve source/mapping config from export
        const newInputs = (mappedPrompt.inputs || []).map(input => {
          const existingInput = (step.config?.input || []).find(i =>
            i.property?.toLowerCase() === input.property?.toLowerCase()
          );

          return {
            id: `input_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            property: input.property,
            prompt: input.prompt,
            source: existingInput?.source || 'hierarchy',
            sourceField: existingInput?.sourceField || 'value',
            outputProperty: existingInput?.outputProperty || '',
            required: input.metadata?.required || false,
            metadata: input.metadata || {}
          };
        });

        newStep.config.input = newInputs;
      }
    }

    return newStep;
  });

  // Remap formatter config references to use new step IDs and apply document mapping remappings
  // Note: formatter_config can exist on ANY step type (prompt, service, etc.), not just output_formatter
  newSteps.forEach(step => {
    if (step.config?.formatter_config) {
      const formatterConfig = step.config.formatter_config;
      // Find the original export step ID for this formatter (reverse lookup)
      const originalExportStepId = Object.keys(stepIdMap).find(k => stepIdMap[k] === step.id);
      const docRemaps = importDocMappings.value[originalExportStepId] || {};

      if (formatterConfig.document_mappings) {
        formatterConfig.document_mappings = formatterConfig.document_mappings.map((mapping, mIdx) => {
          const updatedMapping = { ...mapping };

          // Remap stepId to new step ID
          if (mapping.stepId && stepIdMap[mapping.stepId]) {
            updatedMapping.stepId = stepIdMap[mapping.stepId];
          }

          // Remap stepRefId (prompt ID) if it was remapped
          if (mapping.stepRefType === 'prompt' && mapping.stepRefId) {
            const newPromptId = promptIdMap[mapping.stepRefId];
            if (newPromptId) {
              updatedMapping.stepRefId = newPromptId;
              const targetStep = newSteps.find(s => s.tmpl_ai_prompt_id === newPromptId);
              if (targetStep) {
                updatedMapping.stepId = targetStep.id;
                updatedMapping.stepName = targetStep.step_name;
              }
            }
          }

          // Apply user's document mapping remappings from wizard step 3
          const remap = docRemaps[mIdx];
          if (remap) {
            if (remap.tp_id) {
              const part = importTemplateParts.value.find(p => p.tp_id === remap.tp_id);
              updatedMapping.tmpl_part_id = remap.tp_id;
              updatedMapping.tmpl_part = part ? { name: part.name, tp_id: part.tp_id } : null;
              updatedMapping.templatePart = part || null;
            }
            if (remap.ht_id) {
              updatedMapping.hierarchyValueType = remap.ht_id;
              if (remap.level !== undefined) {
                updatedMapping.hierarchyValueLevel = remap.level;
              }
            }
          }

          // Regenerate stepDataId based on new step references so the
          // Source Data dropdown in the document formatter dialog can match
          if (updatedMapping.stepId) {
            const sourceStep = newSteps.find(s => s.id === updatedMapping.stepId);
            if (sourceStep) {
              const refType = sourceStep.step_type === 'prompt' ? 'prompt'
                            : sourceStep.step_type === 'output_formatter' ? 'formatter'
                            : sourceStep.step_type === 'service' ? 'service'
                            : 'step';
              const refId = sourceStep.tmpl_ai_prompt_id || sourceStep.output_formatter_id || sourceStep.service_id || sourceStep.id;
              updatedMapping.stepRefType = refType;
              updatedMapping.stepRefId = refId;
              updatedMapping.stepDataId = `${refType}:${refId}:${updatedMapping.type || 'output'}:${updatedMapping.property || 'result'}`;
            }
          }

          // Clean up export hint fields
          delete updatedMapping._tmpl_part_name;
          delete updatedMapping._hierarchy_name;
          delete updatedMapping._hierarchy_level;

          return updatedMapping;
        });
      }

      if (Array.isArray(formatterConfig.selected_outputs)) {
        formatterConfig.selected_outputs = formatterConfig.selected_outputs.map(output => {
          const [oldStepId, property] = output.split(':');
          const newStepId = stepIdMap[oldStepId];
          return newStepId ? `${newStepId}:${property}` : output;
        });
      }
    }
  });

  // Build new triggers — strip DB trigger_id so backend creates fresh records
  const newTriggers = (data.triggers || []).map((trigger, index) => {
    const newTriggerId = triggerIdMap[trigger.id] || triggerIdMap[trigger.trigger_id] || `temp_${Date.now()}_${index}`;
    return {
      trigger_id: newTriggerId,
      trigger_name: trigger.trigger_name,
      trigger_type: trigger.trigger_type,
      description: trigger.description || '',
      trigger_config: trigger.trigger_config || {},
      context_mapping: trigger.context_mapping || { mappings: [] },
      conditions: trigger.conditions || { rules: [], logic: 'AND' },
      priority: trigger.priority || 100,
      active: trigger.active !== false,
      x: trigger.x ?? 50,
      y: trigger.y ?? (100 + index * 140)
    };
  });

  // Remap connections — handle both v2.0 (portable IDs) and v1.0 (DB IDs) formats
  const newConnections = (data.connections || []).map((conn, index) => {
    let fromId = conn.from;
    let toId = conn.to;

    // Remap trigger references in `from`
    if (fromId && typeof fromId === 'string' && fromId.startsWith('trigger-')) {
      const oldTriggerId = fromId.replace('trigger-', '');
      const newTriggerId = triggerIdMap[oldTriggerId] || triggerIdMap[Number(oldTriggerId)];
      if (newTriggerId) {
        fromId = `trigger-${newTriggerId}`;
      } else if (newTriggers.length > 0) {
        // Fallback: assign to first trigger
        fromId = `trigger-${newTriggers[0].trigger_id}`;
      }
    }
    // Remap step references in `from`
    else if (fromId && fromId !== 'start' && fromId !== null) {
      fromId = stepIdMap[fromId] || fromId;
    }
    // Legacy 'start' or null — migrate to first trigger if available
    else if (fromId === 'start' || fromId === null) {
      if (newTriggers.length > 0) {
        fromId = `trigger-${newTriggers[0].trigger_id}`;
      } else {
        fromId = 'start';
      }
    }

    // Remap step reference in `to`
    toId = stepIdMap[toId] || toId;

    return {
      from: fromId,
      to: toId,
      fromPoint: conn.fromPoint || 'right',
      toPoint: conn.toPoint || 'left',
      mapping_config: conn.mapping_config || null,
      id: `conn_${Date.now()}_${index}`
    };
  });

  // Build dependencies from connections
  newConnections.forEach(conn => {
    const toStep = newSteps.find(s => s.id === conn.to);
    if (toStep && conn.from) {
      if (!toStep.dependencies.includes(conn.from)) {
        toStep.dependencies.push(conn.from);
      }
    }
  });

  if (importAsNewChain.value) {
    chain.prompt_chain_id = null;
    chain.chain_name = `${data.chain_name} (Imported)`;
  }

  chain.description = data.description || '';
  chain.agent_exposure = data.agent_exposure || null;
  chain.available_via = data.available_via || '';
  chain.steps = newSteps;
  chain.connections = newConnections;
  chain.triggers = newTriggers;
  chain.input_schema = data.input_schema || null;
  chain.output_schema = data.output_schema || null;
  if (data.execution_config) {
    chain.execution_config = data.execution_config;
    chainExecutionConfig.execution_mode = data.execution_config.execution_mode || 'sequence';
    chainExecutionConfig.context_mode = data.execution_config.context_mode || 'summary';
    chainExecutionConfig.max_context_rows = data.execution_config.max_context_rows || 20;
  }

  showImportDialog.value = false;
  importedChainData.value = null;
  promptMappings.value = {};
  importFile.value = null;
  importWizardStep.value = 1;
  importDocMappings.value = {};
  importTemplateParts.value = [];
  promptCreateState.value = {};
  expandedCreatePanel.value = null;
  showLandingPage.value = false;
  showHistoryView.value = false;
  showReportView.value = false;

  // Recalculate step ordering based on new connections
  calculateStepOrder();

  const message = importAsNewChain.value
    ? 'Chain imported successfully! This will be saved as a NEW chain.'
    : 'Chain imported successfully! The current chain has been replaced. Remember to save.';

  triggerNotification(message, 'success');
};

// Watch for tab changes to trigger suggestion analysis
// watch(activeTab, (newTab) => {
//   if (newTab === 'inputs-outputs' && propertySuggestions.value.length === 0) {
//     nextTick(() => {
//       //autoGroupSimilarProperties();
//     });
//   }
// });
const getItemIcon = (item) => {
    if (!item) return 'mdi-file-document';

    if (item.itemType === 'service' || item.step_type === 'service') {
      return 'mdi-flash';
    }

    if (item.itemType === 'output_formatter' || item.step_type === 'output_formatter') {
      return 'mdi-code-json';
    }

    if (item.itemType === 'chain' || item.step_type === 'chain') {
      return 'mdi-link-variant';
    }

    if (item.itemType === 'manual_selection' || item.step_type === 'manual_selection') {
      return 'mdi-hand-pointing-up';
    }

    const icons = {
      'generation': 'mdi-file-document',
      'analysis': 'mdi-magnify',
      'transformation': 'mdi-shuffle-variant',
      'extraction': 'mdi-filter'
    };
    return icons[item.prompt_type] || 'mdi-file-document';
    };

const getHealthStatusColor = (status) => {
    switch(status) {
      case 'healthy': return 'health-green';
      case 'degraded': return 'health-yellow';
      case 'unhealthy': return 'health-red';
      default: return 'health-grey';
    }
    };

const onDragStart = (e, item) => {
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('text/plain', JSON.stringify(item));
    isDraggingItem.value = true;
    };

const onDragEnd = () => {
    isDraggingItem.value = false;
    };

const onCanvasDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    isDraggingItem.value = false;
    if (isViewingSystemChain.value) return;

    try {
      const data = e.dataTransfer.getData('text/plain');
      if (!data) return;

      // Check if data looks like JSON before parsing
      const trimmedData = data.trim();
      if (!trimmedData.startsWith('{') && !trimmedData.startsWith('[')) {
        console.warn('Dropped data is not JSON, ignoring:', trimmedData.substring(0, 50));
        return;
      }

      const itemData = JSON.parse(data);

      // Validate that itemData has required properties
      if (!itemData || !itemData.itemType) {
        console.warn('Dropped item missing itemType, ignoring');
        return;
      }

      const rect = canvasRef.value.getBoundingClientRect();

      const x = e.clientX - rect.left + canvasRef.value.scrollLeft - 150;
      const y = e.clientY - rect.top + canvasRef.value.scrollTop - 75;

      addStepToChain(itemData, x, y);
    } catch (error) {
      console.error('Drop failed:', error);
    }
    };

const onCanvasDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
    };

const addStepToChain = (item, x, y) => {
    // Determine the step name - use prompt_type if name is "Unnamed Prompt"
    let stepName = item.name;
    if (item.itemType === 'prompt' && item.prompt_name === 'Unnamed Prompt' && item.prompt_type) {
      stepName = item.prompt_type.charAt(0).toUpperCase() + item.prompt_type.slice(1);
    }

    // Check if an inactive step with the same item already exists
    const existingInactiveStep = chain.steps.find(step =>
      step.active === false && (
        (item.itemType === 'prompt' && step.tmpl_ai_prompt_id === item.tmpl_ai_prompt_id) ||
        (item.itemType === 'service' && step.service_endpoint_id === item.service_endpoint_id) ||
        (item.itemType === 'output_formatter' && step.output_formatter_id === item.id) ||
        (item.itemType === 'chain' && step.child_chain_id === item.prompt_chain_id)
      )
    );

    // If an inactive step exists, reactivate it at the new position
    if (existingInactiveStep) {
      existingInactiveStep.active = true;
      existingInactiveStep.x = x;
      existingInactiveStep.y = y;
      selectedStep.value = existingInactiveStep;
      triggerNotification('Step reactivated', 'info');
      return;
    }

    const newStep = {
      id: `step_${Date.now()}`,
      step_type: item.itemType,
      step_name: stepName,
      step_order: chain.steps.length + 1,
      item: item,
      x: x,
      y: y,
      dependencies: [],
      outputs: item.itemType === 'service' ? Object.keys(item.output_schema || {}) : item.itemType === 'output_formatter' ? ['result'] : item.itemType === 'chain' ? ['result'] : item.itemType === 'manual_selection' ? ['selected_items'] : item.itemType === 'group_processor' ? ['result'] : ['result'],
      continue_on_error: false,
      active: true,
      is_final_step: false,
      is_post_process: false,
      // Store the IDs directly for easier access when saving
      tmpl_ai_prompt_id: item.itemType === 'prompt' ? item.tmpl_ai_prompt_id : null,
      service_id: item.itemType === 'service' ? item.service_id : null,
      service_endpoint_id: item.itemType === 'service' ? item.service_endpoint_id : null,
      output_formatter_id: item.itemType === 'output_formatter' ? item.id : null,
      child_chain_id: item.itemType === 'chain' ? item.prompt_chain_id : null,
      config: {
        input: item.itemType === 'chain' ? [] : (item.inputs || []),  // Chain inputs will be loaded async
        output: item.itemType === 'service'
          ? Object.keys(item.output_schema || {}).map(key => ({ property: key, extractFrom: 'response' }))
          : item.itemType === 'output_formatter'
          ? [{ property: 'result', extractFrom: 'formatted_output' }]
          : item.itemType === 'chain'
          ? [{ property: 'result', extractFrom: 'response' }]
          : [{ property: 'result', extractFrom: 'response' }],
        service_config: item.itemType === 'service' ? {
          endpoint_path: item.endpoint_path,
          http_method: item.http_method,
          base_url: item.base_url,
          timeout: item.timeout || 30,
          retry_attempts: item.retry_attempts || 3,
          service_endpoint_id: item.service_endpoint_id
        } : null,
        model: item.config?.model || 'claude-sonnet-4-6',
        formatter_config: item.itemType === 'output_formatter' ? {
          output_format: item.output_format || 'json',
          template: item.template || {},
          collect_from_steps: [] // Will be configured by user to select which steps' outputs to include
        } : null,
        chain_config: item.itemType === 'chain' ? {
          child_chain_id: item.prompt_chain_id,
          inherit_context: true,
          output_mapping: {},
          bulk_mode: {
            enabled: false,
            source_output: '',
            array_path: '',
            item_property_mappings: {}
          }
        } : null,
        selection_config: item.itemType === 'manual_selection' ? {
          source_output: '',
          array_path: '',
          display_fields: [],
          selection_mode: 'multiple'  // 'single' or 'multiple'
        } : null
      }
    }

    chain.steps.push(newStep);

    // If this is a chain step, load its input requirements from the child chain
    if (item.itemType === 'chain') {
      loadChainInputs(newStep);
    }
    };

const selectStep = (step) => {
    selectedStep.value = step;
    };

const toggleStepActive = (step) => {
    // Remove connections to/from this step when it's disabled
    if (step.active === false) {
      chain.connections = chain.connections.filter(c => c.from !== step.id && c.to !== step.id);

      // Clear selection if this step was selected
      if (selectedStep.value?.id === step.id) {
        selectedStep.value = null;
      }

      triggerNotification('Step disabled and connections removed', 'info');
    } else {
      triggerNotification('Step enabled', 'success');
    }
};

const deleteStep = (step) => {
    if (isViewingSystemChain.value) return;
    // Remove the step from the canvas entirely
    const stepIndex = chain.steps.findIndex(s => s.id === step.id);
    if (stepIndex !== -1) {
      chain.steps.splice(stepIndex, 1);
    }

    // Remove connections to/from this step
    chain.connections = chain.connections.filter(c => c.from !== step.id && c.to !== step.id);

    // Clear selection if this step was selected
    if (selectedStep.value?.id === step.id) {
      selectedStep.value = null;
    }

    triggerNotification('Step deleted from canvas', 'success');
    };

const duplicateStep = (step) => {
    const duplicate = {
      ...step,
      id: `step_${Date.now()}`,
      step_name: `${step.step_name} (Copy)`,
      x: step.x + 50,
      y: step.y + 50,
      step_order: chain.steps.length + 1
    };
    chain.steps.push(duplicate);
};

const openTestPromptDialog = (step) => {
  testPromptStep.value = step;
  testPromptResult.value = null;

  // Initialize input values from step config
  Object.keys(testPromptInputs).forEach(key => delete testPromptInputs[key]);

  const inputs = step.config?.input || [];
  inputs.forEach(input => {
    testPromptInputs[input.property] = '';
  });

  showTestPromptDialog.value = true;
};

const closeTestPromptDialog = () => {
  showTestPromptDialog.value = false;
  testPromptStep.value = null;
  testPromptResult.value = null;
  Object.keys(testPromptInputs).forEach(key => delete testPromptInputs[key]);
};

const executeTestPrompt = async () => {
  if (!testPromptStep.value) return;

  try {
    testPromptLoading.value = true;
    testPromptResult.value = null;

    const response = await axios.post('/externalContent/executeSinglePrompt', {
      prompt_id: testPromptStep.value.tmpl_ai_prompt_id,
      test_values: testPromptInputs,
      client_id: store.client_id
    });

    if (response.data && response.data.success) {
      testPromptResult.value = response.data.outputs.result || response.data.raw_response;
      triggerNotification('Prompt executed successfully!', 'success');
    } else {
      triggerNotification('Failed to execute prompt: ' + response.data.error || 'Unknown error', 'error');
    }
  } catch (error) {
    console.error('Error executing test prompt:', error);
    triggerNotification('Error executing prompt: ' + error.message, 'error');
  } finally {
    testPromptLoading.value = false;
  }
};

/**
 * Returns the set of step IDs that are connected (directly or indirectly) from the "start" node.
 * Uses the current chain.connections and chain.steps.
 */
function getConnectedStepIds() {
  // Build adjacency map: from -> [to]
  const fromToMap = new Map();
  chain.connections.forEach(conn => {
    const from = conn.from === null ? 'start' : conn.from;
    const to = conn.to;
    if (!fromToMap.has(from)) fromToMap.set(from, []);
    fromToMap.get(from).push(to);
  });

  // BFS from "start" AND all trigger nodes to find all reachable step ids
  const visited = new Set();
  const queue = [];

  // Seed from 'start'
  if (fromToMap.has('start')) {
    fromToMap.get('start').forEach(toStepId => {
      queue.push(toStepId);
      visited.add(toStepId);
    });
  }

  // Seed from all trigger nodes (trigger-1, trigger-2, etc.)
  for (const [fromId, toStepIds] of fromToMap.entries()) {
    if (typeof fromId === 'string' && fromId.startsWith('trigger-')) {
      toStepIds.forEach(toStepId => {
        if (!visited.has(toStepId)) {
          queue.push(toStepId);
          visited.add(toStepId);
        }
      });
    }
  }

  while (queue.length > 0) {
    const current = queue.shift();
    if (fromToMap.has(current)) {
      fromToMap.get(current).forEach(nextStepId => {
        if (!visited.has(nextStepId)) {
          visited.add(nextStepId);
          queue.push(nextStepId);
        }
      });
    }
  }
  return visited;
}

/**
 * Recalculates step_order for all steps based only on connected steps.
 * Disconnected steps have step_order set to null.
 * Connected steps are ordered 1..N in topological order.
 */
const calculateStepOrder = () => {
  if (chain.steps.length === 0) return;

  // Get only connected step IDs
  const connectedIds = getConnectedStepIds();

  // Build adjacency list for connected steps
  const graph = new Map();
  const inDegree = new Map();

  // Only include connected steps in the graph
  chain.steps.forEach(step => {
    if (connectedIds.has(step.id)) {
      graph.set(step.id, []);
      inDegree.set(step.id, 0);
    }
  });

  // Add start node for topological sort
  graph.set('start', []);
  inDegree.set('start', 0);

  // Add trigger nodes as entry points (like 'start')
  chain.connections.forEach(conn => {
    const fromStr = String(conn.from || '');
    if (fromStr.startsWith('trigger-') && !graph.has(fromStr)) {
      graph.set(fromStr, []);
      inDegree.set(fromStr, 0);
    }
  });

  // Build graph from connections (only for connected steps)
  chain.connections.forEach(conn => {
    const fromStr = String(conn.from || '');
    const isEntryPoint = conn.from === 'start' || fromStr.startsWith('trigger-');
    if (
      (isEntryPoint || connectedIds.has(conn.from)) &&
      connectedIds.has(conn.to)
    ) {
      if (!graph.has(conn.from)) graph.set(conn.from, []);
      if (!graph.has(conn.to)) graph.set(conn.to, []);
      graph.get(conn.from).push(conn.to);
      inDegree.set(conn.to, (inDegree.get(conn.to) || 0) + 1);
    }
  });

  // Topological sort using Kahn's algorithm (only for connected steps)
  const queue = [];
  const result = [];

  // Start with nodes that have no incoming edges
  for (const [nodeId, degree] of inDegree.entries()) {
    if (degree === 0) {
      queue.push(nodeId);
    }
  }

  while (queue.length > 0) {
    const current = queue.shift();
    if (current !== 'start' && connectedIds.has(current)) {
      result.push(current);
    }
    const neighbors = graph.get(current) || [];
    neighbors.forEach(neighbor => {
      inDegree.set(neighbor, inDegree.get(neighbor) - 1);
      if (inDegree.get(neighbor) === 0) {
        queue.push(neighbor);
      }
    });
  }

  // Update step_order for all steps
  chain.steps.forEach(step => {
    if (connectedIds.has(step.id)) {
      const orderIndex = result.indexOf(step.id);
      step.step_order = orderIndex !== -1 ? orderIndex + 1 : null;
    } else {
      // Disconnected steps: clear step_order
      step.step_order = null;
    }
  });
};

const getStepOrderByDependency = (depId) => {
  if (depId === 'start') return 'Start';
  const step = chain.steps.find(s => s.id === depId);
  return step ? (step.step_order === 999 ? '?' : step.step_order) : '?';
};

const hasIncomingConnections = (stepId) => {
  return chain.connections.some(conn => conn.to === stepId);
};

const deleteConnection = (connectionId) => {
  const connection = chain.connections.find(c => c.id === connectionId);
  if (!connection) return;

  // Remove connection
  chain.connections = chain.connections.filter(c => c.id !== connectionId);

  // Remove from step dependencies
  const toStep = chain.steps.find(s => s.id === connection.to);
  if (toStep && toStep.dependencies) {
    toStep.dependencies = toStep.dependencies.filter(d => d !== connection.from);
  }

  // After removing the connection, recalculate step order and clear disconnected steps
  calculateStepOrder();

  hoveredConnection.value = null;
};

const getConnectionMidpoint = (conn) => {
  const fromPos = getNodePosition(conn.from, conn.fromPoint || 'right');
  const toPos = getNodePosition(conn.to, conn.toPoint || 'left');

  const midX = (fromPos.x + toPos.x) / 2;
  const midY = (fromPos.y + toPos.y) / 2;

  return {
    position: 'absolute',
    left: `${midX - 16}px`,
    top: `${midY - 16}px`,
    pointerEvents: 'auto'
  };
};

const startConnection = (e, fromId, fromPoint = 'right') => {
    e.stopPropagation();
    e.preventDefault();
    isDrawingConnection.value = true;
    connectionStart.value = { id: fromId, point: fromPoint };
    };

const startDragStep = (e, step) => {
  if (e.target.closest('button') || e.target.closest('input')) {
    return;
  }

  // Store initial position to detect if this is a drag or just a click
  dragOffset.initialX = e.clientX;
  dragOffset.initialY = e.clientY;
  dragOffset.hasMoved = false;

  e.stopPropagation();
  e.preventDefault();
  isDraggingStep.value = true;
  draggedStep.value = step;

  const rect = e.currentTarget.getBoundingClientRect();
  const canvas = canvasRef.value.getBoundingClientRect();

  // Cache canvas position to avoid recalculating on every move
  dragOffset.x = e.clientX - rect.left;
  dragOffset.y = e.clientY - rect.top;
  dragOffset.canvasLeft = canvas.left;
  dragOffset.canvasTop = canvas.top;
  dragOffset.scrollLeft = canvasRef.value.scrollLeft;
  dragOffset.scrollTop = canvasRef.value.scrollTop;
};

const onStepDrag = (e) => {
  if (!isDraggingStep.value || !draggedStep.value) return;

  // Check if mouse has moved significantly (more than 5px in any direction)
  const deltaX = Math.abs(e.clientX - dragOffset.initialX);
  const deltaY = Math.abs(e.clientY - dragOffset.initialY);
  if (deltaX > 5 || deltaY > 5) {
    dragOffset.hasMoved = true;
  }

  // Use requestAnimationFrame for smoother animation
  if (!dragOffset.rafId) {
    dragOffset.rafId = requestAnimationFrame(() => {
      const newX = e.clientX - dragOffset.canvasLeft + dragOffset.scrollLeft - dragOffset.x;
      const newY = e.clientY - dragOffset.canvasTop + dragOffset.scrollTop - dragOffset.y;

      const stepIndex = chain.steps.findIndex(s => s.id === draggedStep.value.id);
      if (stepIndex !== -1) {
        chain.steps[stepIndex].x = newX;
        chain.steps[stepIndex].y = newY;
      }

      dragOffset.rafId = null;
    });
  }
};

const stopDragStep = () => {
  // If the mouse didn't move, treat this as a click to select the step
  if (!dragOffset.hasMoved && draggedStep.value) {
    selectStep(draggedStep.value);
  }

  isDraggingStep.value = false;
  draggedStep.value = null;
  dragOffset.hasMoved = false;
  if (dragOffset.rafId) {
    cancelAnimationFrame(dragOffset.rafId);
    dragOffset.rafId = null;
  }
};

const handleMouseMove = (e) => {
  if (isDrawingConnection.value && canvasRef.value) {
    const rect = canvasRef.value.getBoundingClientRect();
    const currentX = e.clientX - rect.left + canvasRef.value.scrollLeft;
    const currentY = e.clientY - rect.top + canvasRef.value.scrollTop;
    const nearestPoint = findNearestConnectionPoint(currentX, currentY, connectionStart.value?.id);

    if (nearestPoint) {
      // Snap to nearest connection point
      mousePos.x = nearestPoint.x;
      mousePos.y = nearestPoint.y;
      snapTarget.value = nearestPoint.stepId;
    } else {
      // Use mouse position
      mousePos.x = currentX;
      mousePos.y = currentY;
      snapTarget.value = null;
    }
  }
};

const endConnection = async (e, toId, toPoint = 'left') => {
  e.stopPropagation();
  e.preventDefault();

  if (!isDrawingConnection.value || !connectionStart.value) return;

  const fromId = connectionStart.value.id || connectionStart.value;
  const fromPoint = connectionStart.value.point || 'right';

  // Check if we're snapping - use the snap target's connection type if available
  if (snapTarget.value === toId) {
    const rect = canvasRef.value.getBoundingClientRect();
    const currentX = e.clientX - rect.left + canvasRef.value.scrollLeft;
    const currentY = e.clientY - rect.top + canvasRef.value.scrollTop;
    const nearestPoint = findNearestConnectionPoint(currentX, currentY, fromId);
    if (nearestPoint && nearestPoint.connectionType) {
      toPoint = nearestPoint.connectionType;
    }
  }

  if (fromId === toId) {
    cancelConnection();
    return;
  }

  const exists = chain.connections.some(
    c => c.from === fromId && c.to === toId
  );

  if (!exists) {
    // Add the connection with point information
    const newConnection = {
      id: `conn_${Date.now()}`,
      from: fromId,
      to: toId,
      fromPoint: fromPoint,
      toPoint: toPoint,
      mapping_config: {
        mappings: []
      }
    };

    chain.connections.push(newConnection);

    // Add dependency
    const stepIndex = chain.steps.findIndex(s => s.id === toId);
    if (stepIndex !== -1) {
      if (!chain.steps[stepIndex].dependencies.includes(fromId)) {
        chain.steps[stepIndex].dependencies.push(fromId);
      }
    }

    // After adding the connection, recalculate step order for all steps
    calculateStepOrder();

    // Wait for DOM to update with new dependencies before recalculating positions
    await nextTick();

    // If this is a trigger-to-step connection, open mapping dialog
    if (typeof fromId === 'string' && fromId.startsWith('trigger-')) {
      // Find the connection we just added (by ID) to ensure we have the correct reference
      const addedConnection = chain.connections.find(c => c.id === newConnection.id);
      if (addedConnection) {
        openConnectionMappingDialog(addedConnection);
      }
    }
  }

  cancelConnection();
};

const cancelConnection = () => {
  isDrawingConnection.value = false;
  connectionStart.value = null;
  snapTarget.value = null;
};

const getNodePosition = (nodeId, type = 'right') => {
  if (nodeId === 'start') {
    // Start node: left: 50px, top: 100px, width: 160px, height: 96px
    const startCenterX = 130; // 50 + (160 / 2)
    const startCenterY = 148; // 100 + (96 / 2)

    switch(type) {
      case 'right':
      case 'output':
        return { x: 210, y: startCenterY };
      case 'left':
      case 'input':
        return { x: 50, y: startCenterY };
      case 'top':
        return { x: startCenterX, y: 100 };
      case 'bottom':
        return { x: startCenterX, y: 196 };
      default:
        return { x: 210, y: startCenterY };
    }
  }

  // Handle trigger nodes (format: 'trigger-{id}')
  if (typeof nodeId === 'string' && nodeId.startsWith('trigger-')) {
    const triggerId = nodeId.replace('trigger-', '');
    const triggerIndex = chain.triggers.findIndex(t => String(t.trigger_id) === triggerId);
    const trigger = chain.triggers[triggerIndex];

    if (trigger) {
      const triggerX = trigger.x || 50;
      const triggerY = trigger.y || (100 + triggerIndex * 140);
      const triggerWidth = 200;

      // Try to get actual DOM element for accurate positioning
      if (canvasRef.value) {
        const triggerElement = canvasRef.value.querySelector(`[data-trigger-id="${triggerId}"]`);
        if (triggerElement) {
          const rect = triggerElement.getBoundingClientRect();
          const canvasRect = canvasRef.value.getBoundingClientRect();
          const relativeTop = rect.top - canvasRect.top + canvasRef.value.scrollTop;
          const actualCenterY = relativeTop + (rect.height / 2);
          const actualCenterX = triggerX + (triggerWidth / 2);

          switch(type) {
            case 'right':
            case 'output':
              return { x: triggerX + triggerWidth, y: actualCenterY };
            case 'left':
            case 'input':
              return { x: triggerX, y: actualCenterY };
            case 'top':
              return { x: actualCenterX, y: relativeTop };
            case 'bottom':
              return { x: actualCenterX, y: relativeTop + rect.height };
            default:
              return { x: triggerX + triggerWidth, y: actualCenterY };
          }
        }
      }

      // Fallback to estimated height
      const triggerHeight = 100;
      const triggerCenterX = triggerX + (triggerWidth / 2);
      const triggerCenterY = triggerY + (triggerHeight / 2);

      switch(type) {
        case 'right':
        case 'output':
          return { x: triggerX + triggerWidth, y: triggerCenterY };
        case 'left':
        case 'input':
          return { x: triggerX, y: triggerCenterY };
        case 'top':
          return { x: triggerCenterX, y: triggerY };
        case 'bottom':
          return { x: triggerCenterX, y: triggerY + triggerHeight };
        default:
          return { x: triggerX + triggerWidth, y: triggerCenterY };
      }
    }
  }

  const step = chain.steps.find(s => s.id === nodeId);
  if (!step) return { x: 0, y: 0 };

  const nodeWidth = 288;

  // Try to get actual DOM element height for more accurate positioning
  if (canvasRef.value) {
    const stepElement = canvasRef.value.querySelector(`[data-step-id="${step.id}"]`);
    if (stepElement) {
      const rect = stepElement.getBoundingClientRect();
      const canvasRect = canvasRef.value.getBoundingClientRect();
      const relativeTop = rect.top - canvasRect.top + canvasRef.value.scrollTop;
      const actualCenterY = relativeTop + (rect.height / 2);
      const actualCenterX = step.x + (nodeWidth / 2);

      switch(type) {
        case 'right':
        case 'output':
          return { x: step.x + nodeWidth, y: actualCenterY };
        case 'left':
        case 'input':
          return { x: step.x, y: actualCenterY };
        case 'top':
          return { x: actualCenterX, y: relativeTop };
        case 'bottom':
          return { x: actualCenterX, y: relativeTop + rect.height };
        default:
          return { x: step.x + nodeWidth, y: actualCenterY };
      }
    }
  }

  // Fallback: Calculate estimated height based on step content
  let contentHeight = 48; // header height
  let bodyHeight = 0;

  // Step type section
  bodyHeight += 32;

  // Endpoint for services
  if (step.step_type === 'service') {
    bodyHeight += 30;
  }

  // Dependencies section
  if (step.dependencies && step.dependencies.length > 0) {
    bodyHeight += 48;
  }

  // Outputs section
  if (step.outputs && step.outputs.length > 0) {
    bodyHeight += 48;
  }

  // Error continue indicator
  if (step.continue_on_error) {
    bodyHeight += 28;
  }

  // Post-process indicator
  if (step.is_post_process) {
    bodyHeight += 28;
  }

  // Add body padding (16px top + 16px bottom)
  contentHeight += bodyHeight + 32;

  const centerY = step.y + (contentHeight / 2);
  const centerX = step.x + (nodeWidth / 2);

  switch(type) {
    case 'right':
    case 'output':
      return { x: step.x + nodeWidth, y: centerY };
    case 'left':
    case 'input':
      return { x: step.x, y: centerY };
    case 'top':
      return { x: centerX, y: step.y };
    case 'bottom':
      return { x: centerX, y: step.y + contentHeight };
    default:
      return { x: step.x + nodeWidth, y: centerY };
  }
};

// Helper function to find nearest connection point for snapping
const findNearestConnectionPoint = (x, y, excludeId = null) => {
  const snapDistance = 30; // pixels
  let nearest = null;
  let minDistance = snapDistance;

  // Check all steps for all four connection points
  const connectionTypes = ['left', 'right', 'top', 'bottom'];

  chain.steps.forEach(step => {
    if (step.id === excludeId) return;

    connectionTypes.forEach(connType => {
      const pos = getNodePosition(step.id, connType);
      const distance = Math.sqrt(Math.pow(x - pos.x, 2) + Math.pow(y - pos.y, 2));

      if (distance < minDistance) {
        minDistance = distance;
        nearest = { stepId: step.id, connectionType: connType, ...pos };
      }
    });
  });

  // Check all triggers for all four connection points
  chain.triggers.forEach(trigger => {
    const triggerId = 'trigger-' + trigger.trigger_id;
    if (triggerId === excludeId) return;

    connectionTypes.forEach(connType => {
      const pos = getNodePosition(triggerId, connType);
      const distance = Math.sqrt(Math.pow(x - pos.x, 2) + Math.pow(y - pos.y, 2));

      if (distance < minDistance) {
        minDistance = distance;
        nearest = { stepId: triggerId, connectionType: connType, ...pos };
      }
    });
  });

  return nearest;
};

const createPath = (from, to) => {
    const dx = to.x - from.x;
    const dy = to.y - from.y;

    // Calculate control points based on connection direction
    // For horizontal connections (left-right or right-left)
    if (Math.abs(dx) > Math.abs(dy)) {
        const controlOffset = Math.abs(dx) * 0.5;
        return `M ${from.x} ${from.y} C ${from.x + controlOffset} ${from.y}, ${to.x - controlOffset} ${to.y}, ${to.x} ${to.y}`;
    } else {
        // For vertical connections (top-bottom or bottom-top)
        const controlOffset = Math.abs(dy) * 0.5;
        return `M ${from.x} ${from.y} C ${from.x} ${from.y + controlOffset}, ${to.x} ${to.y - controlOffset}, ${to.x} ${to.y}`;
    }
};

const createPathWithPoints = (from, to, fromPoint, toPoint) => {
    const offset = 60; // Control point offset distance

    // Determine control point directions based on connection points
    let cp1x = from.x;
    let cp1y = from.y;
    let cp2x = to.x;
    let cp2y = to.y;

    // Set control points based on FROM connection point
    switch(fromPoint) {
        case 'right':
            cp1x = from.x + offset;
            break;
        case 'left':
            cp1x = from.x - offset;
            break;
        case 'bottom':
            cp1y = from.y + offset;
            break;
        case 'top':
            cp1y = from.y - offset;
            break;
    }

    // Set control points based on TO connection point
    switch(toPoint) {
        case 'right':
            cp2x = to.x + offset;
            break;
        case 'left':
            cp2x = to.x - offset;
            break;
        case 'bottom':
            cp2y = to.y + offset;
            break;
        case 'top':
            cp2y = to.y - offset;
            break;
    }

    // Create cubic bezier curve
    return `M ${from.x} ${from.y} C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${to.x} ${to.y}`;
};

const addInput = () => {
    if (!selectedStep.value) return;

    const stepIndex = chain.steps.findIndex(s => s.id === selectedStep.value.id);
    if (stepIndex !== -1) {
      chain.steps[stepIndex].config.input.push({
      id: Date.now(),
      property: '',
      source: 'hierarchy',
      sourceField: 'value',
      outputProperty: '', // For chain_output source - which output property to reference
      apiPayloadField: '', // For api_payload source - which field from API payload to map
      required: false,
      hasFallback: false
      });
    }
    };

const removeInput = (idx) => {
    if (!selectedStep.value) return;

    const stepIndex = chain.steps.findIndex(s => s.id === selectedStep.value.id);
    if (stepIndex !== -1) {
      chain.steps[stepIndex].config.input.splice(idx, 1);
    }
    };

const updateInput = (idx, field, value) => {
    if (!selectedStep.value) return;

    const stepIndex = chain.steps.findIndex(s => s.id === selectedStep.value.id);
    if (stepIndex !== -1 && chain.steps[stepIndex].config.input[idx]) {
      chain.steps[stepIndex].config.input[idx][field] = value;
    }
    };

const addOutput = () => {
    if (!selectedStep.value) return;

    const stepIndex = chain.steps.findIndex(s => s.id === selectedStep.value.id);
    if (stepIndex !== -1) {
      if (!chain.steps[stepIndex].config.output) {
        chain.steps[stepIndex].config.output = [];
      }
      chain.steps[stepIndex].config.output.push({
        id: Date.now(),
        property: '',
        extractFrom: 'response',
        isPassThrough: false,
        validation: false,
        max_retries: 3,
        type: null,
        sourceInputProperty: ""
      });

      // Update step outputs array
      updateStepOutputs(stepIndex);
    }
};

const addPassThroughOutput = () => {
    if (!selectedStep.value) return;

    const stepIndex = chain.steps.findIndex(s => s.id === selectedStep.value.id);
    if (stepIndex !== -1) {
      if (!chain.steps[stepIndex].config.output) {
        chain.steps[stepIndex].config.output = [];
      }
      chain.steps[stepIndex].config.output.push({
        id: Date.now(),
        property: '',
        extractFrom: 'pass_through',
        sourceInputProperty: '',
        isPassThrough: true,
        validation: false,
        max_retries: 3,
        type: null
      });

      // Update step outputs array
      updateStepOutputs(stepIndex);
    }
};

const removeOutput = (idx) => {
    if (!selectedStep.value) return;

    const stepIndex = chain.steps.findIndex(s => s.id === selectedStep.value.id);
    if (stepIndex !== -1) {
      chain.steps[stepIndex].config.output.splice(idx, 1);

      // Update step outputs array
      updateStepOutputs(stepIndex);
    }
};

const updateOutput = (idx, field, value) => {
    if (!selectedStep.value) return;

    const stepIndex = chain.steps.findIndex(s => s.id === selectedStep.value.id);
    if (stepIndex !== -1 && chain.steps[stepIndex].config.output[idx]) {
      chain.steps[stepIndex].config.output[idx][field] = value;

      // Update step outputs array when property changes
      if (field === 'property') {
        updateStepOutputs(stepIndex);
      }
    }
};

const updateValidationParam = (idx, param, value) => {
    if (!selectedStep.value) return;

    const stepIndex = chain.steps.findIndex(s => s.id === selectedStep.value.id);
    if (stepIndex !== -1 && chain.steps[stepIndex].config.output[idx]) {
      // Initialize validationParams if it doesn't exist, or clone existing to trigger reactivity
      const currentParams = chain.steps[stepIndex].config.output[idx].validationParams || {};

      // Create new object to ensure Vue detects the change
      chain.steps[stepIndex].config.output[idx].validationParams = {
        ...currentParams,
        [param]: value
      };

      // Update selectedStep reference to trigger reactivity in the UI
      selectedStep.value = chain.steps[stepIndex];
    }
};

const updateStepOutputs = (stepIndex) => {
    if (chain.steps[stepIndex] && chain.steps[stepIndex].config.output) {
      chain.steps[stepIndex].outputs = chain.steps[stepIndex].config.output
        .filter(o => o.property && o.property.trim() !== '')
        .map(o => o.property);
    }
};

const toggleMappingCollapse = (mappingId) => {
  if (collapsedMappings.value.has(mappingId)) {
    collapsedMappings.value.delete(mappingId);
  } else {
    collapsedMappings.value.add(mappingId);
  }
  
  // Force reactivity by triggering a specific update
  // Instead of recreating the entire Set, just trigger a ref update
  collapsedMappings.value = new Set([...collapsedMappings.value]);
};

const isMappingCollapsed = (mappingId) => {
  return collapsedMappings.value.has(mappingId);
};

const removeDocumentMapping = (index) => {
  const mapping = documentFormatterMappings.value[index];
  if (mapping) {
    // Remove from collapsed set if it exists
    collapsedMappings.value.delete(mapping.id);
    // Remove the mapping
    documentFormatterMappings.value.splice(index, 1);
    
    // Force reactivity for the collapsed set
    collapsedMappings.value = new Set(collapsedMappings.value);
  }
};

const applyMappings = (mappings) => {
    if (!selectedStep.value) return;

    const stepIndex = chain.steps.findIndex(s => s.id === selectedStep.value.id);
    if (stepIndex === -1) return;

    // Update each mapped input
    mappings.forEach(mapping => {
      // Find the input by property name
      const inputIndex = chain.steps[stepIndex].config.input.findIndex(
        i => i.property === mapping.inputProperty
      );

      if (inputIndex !== -1) {
        // Update existing input
        chain.steps[stepIndex].config.input[inputIndex].source = 'chain_output';
        chain.steps[stepIndex].config.input[inputIndex].outputProperty = mapping.outputProperty;
      }
    });

    // Force reactivity update
    selectedStep.value = { ...chain.steps[stepIndex] };

    triggerNotification('Input mappings applied successfully', 'success');
};

const updateServiceConfig = (field, value) => {
    if (!selectedStep.value) return;

    const stepIndex = chain.steps.findIndex(s => s.id === selectedStep.value.id);
    if (stepIndex !== -1 && chain.steps[stepIndex].config.service_config) {
      chain.steps[stepIndex].config.service_config[field] = value;
    }
    };

const updateFormatterConfig = (field, value) => {
  if (!selectedStep.value) return;

  const stepIndex = chain.steps.findIndex(s => s.id === selectedStep.value.id);
  if (stepIndex !== -1) {
    if (!chain.steps[stepIndex].config.formatter_config) {
      chain.steps[stepIndex].config.formatter_config = {
        output_format: 'json',
        template: {},
        text_template: '',
        selected_outputs: [],
        document_mappings: []
      };
    }
    
    chain.steps[stepIndex].config.formatter_config[field] = value;
    
    // If switching to text format, initialize text_template if it doesn't exist
    if (field === 'output_format' && value === 'text') {
      if (!chain.steps[stepIndex].config.formatter_config.text_template) {
        // Create a basic template from existing mappings
        const mappings = chain.steps[stepIndex].config.formatter_config.output_mappings || {};
        let initialTemplate = '';
        
        Object.values(mappings).forEach((mapping, index) => {
          if (index > 0) initialTemplate += '\n\n';
          initialTemplate += `${mapping.displayName}:\n\${${mapping.stepName.replace(/[^a-zA-Z0-9]/g, '_')}.${mapping.outputProperty}}`;
        });
        
        chain.steps[stepIndex].config.formatter_config.text_template = initialTemplate;
      }
    }
    
    selectedStep.value = { ...chain.steps[stepIndex] };
  }
};

const updatePreStepHook = (field, value) => {
  if (!selectedStep.value) return;
  const stepIndex = chain.steps.findIndex(s => s.id === selectedStep.value.id);
  if (stepIndex !== -1) {
    if (!chain.steps[stepIndex].config.pre_step_hook) {
      chain.steps[stepIndex].config.pre_step_hook = {
        hook_function_id: null,
        only_internal_trigger: true,
        target_input: null,
        output_property: null,
        bypass_on_empty: false
      };
    }
    chain.steps[stepIndex].config.pre_step_hook[field] = value;

    if (field === 'hook_function_id' && !value) {
      chain.steps[stepIndex].config.pre_step_hook = null;
    }

    selectedStep.value = { ...chain.steps[stepIndex] };
  }
};

const updatePromptModel = (value) => {
  if (!selectedStep.value) return;

  const stepIndex = chain.steps.findIndex(s => s.id === selectedStep.value.id);
  if (stepIndex !== -1) {
    chain.steps[stepIndex].config.model = value;

    // Force reactivity update
    selectedStep.value = { ...chain.steps[stepIndex] };
  }
};

const changeStepPrompt = (newPromptId) => {
  if (!selectedStep.value || selectedStep.value.step_type !== 'prompt') return;

  const stepIndex = chain.steps.findIndex(s => s.id === selectedStep.value.id);
  if (stepIndex === -1) return;

  // Find the new prompt from available prompts
  const newPrompt = availablePrompts.value.find(p => p.tmpl_ai_prompt_id === newPromptId);
  if (!newPrompt) {
    triggerNotification('Selected prompt not found', 'error');
    return;
  }

  // Update step with new prompt data
  const currentStep = chain.steps[stepIndex];
  
  // Preserve existing configuration where possible
  const existingConfig = currentStep.config;
  
  // Update prompt-specific data
  chain.steps[stepIndex].tmpl_ai_prompt_id = newPrompt.tmpl_ai_prompt_id;
  chain.steps[stepIndex].item = newPrompt;
  chain.steps[stepIndex].prompt_type = newPrompt.prompt_type;
  
  // Update step name if it matches the old prompt name (user hasn't customized it)
  if (currentStep.step_name === currentStep.item?.name) {
    chain.steps[stepIndex].step_name = newPrompt.name;
  }

  // Recalculate inputs based on new prompt's requirements
  const newInputs = (newPrompt.inputs || []).map(input => {
    // Check if this input existed in the old configuration
    const existingInput = existingConfig.input?.find(i => i.property === input.property);
    
    if (existingInput) {
      // Preserve existing configuration but update metadata from new prompt
      return {
        ...existingInput,
        prompt: input.prompt || existingInput.prompt,
        metadata: input.metadata || existingInput.metadata
      };
    } else {
      // New input, use defaults from prompt
      return {
        id: input.id || Date.now() + Math.random(),
        property: input.property,
        prompt: input.prompt,
        source: input.source || 'hierarchy',
        sourceField: input.sourceField || 'value',
        outputProperty: '',
        required: input.metadata?.required || false,
        metadata: input.metadata || {}
      };
    }
  });

  // Update config with new inputs
  chain.steps[stepIndex].config.input = newInputs;

  // Recalculate outputs - keep existing output configuration structure
  // but ensure we have at least a 'result' output
  if (!chain.steps[stepIndex].config.output || chain.steps[stepIndex].config.output.length === 0) {
    chain.steps[stepIndex].config.output = [
      { 
        property: 'result', 
        extractFrom: 'response',
        validation: false,
        max_retries: 3
      }
    ];
  }

  // Update step outputs array
  updateStepOutputs(stepIndex);

  // Force reactivity update
  selectedStep.value = { ...chain.steps[stepIndex] };

  triggerNotification(`Prompt changed to "${newPrompt.name}". Inputs have been recalculated.`, 'success');
};

const getPromptTypeColor = (promptType) => {
  const colors = {
    'generation': '#4CAF50',
    'analysis': '#2196F3',
    'transformation': '#FF9800',
    'extraction': '#9C27B0'
  };
  return colors[promptType] || '#50B3CB';
};

const getTextTemplate = () => {
  if (!selectedStep.value || !selectedStep.value.config.formatter_config) {
    return '';
  }
  
  // For text formatters, store the template as a string
  const template = selectedStep.value.config.formatter_config.text_template;
  return template || '';
};

const updateTextTemplate = (newTemplate) => {
  if (!selectedStep.value) return;

  const stepIndex = chain.steps.findIndex(s => s.id === selectedStep.value.id);
  if (stepIndex === -1) return;

  // Initialize formatter config if needed
  if (!chain.steps[stepIndex].config.formatter_config) {
    chain.steps[stepIndex].config.formatter_config = {
      output_format: 'text',
      text_template: '',
      selected_outputs: [],
      output_mappings: {}
    };
  }

  // Update the text template
  chain.steps[stepIndex].config.formatter_config.text_template = newTemplate;

  // Force reactivity update
  selectedStep.value = { ...chain.steps[stepIndex] };
};

const getAvailableVariables = () => {
  if (!selectedStep.value) return [];

  const variables = [];
  
  // Get all previous steps (steps before the current formatter step)
  const previousSteps = chain.steps.filter(s => s.id !== selectedStep.value.id);

  previousSteps.forEach(step => {
    const outputs = step.outputs || ['result'];
    outputs.forEach(output => {
      // Convert step name to variable-friendly format
      const stepName = step.step_name.replace(/[^a-zA-Z0-9]/g, '_');
      
      variables.push({
        id: `${step.id}:${output}`,
        syntax: `\${${stepName}.${output}}`,
        description: `${output} from "${step.step_name}"`,
        stepId: step.id,
        stepName: step.step_name,
        outputProperty: output
      });
    });
  });

  return variables;
};

const insertVariable = (variableSyntax) => {
  if (!selectedStep.value) return;

  const currentTemplate = getTextTemplate();
  const newTemplate = currentTemplate + variableSyntax;
  updateTextTemplate(newTemplate);
  
  // Focus back on textarea after insertion
  nextTick(() => {
    const textarea = document.querySelector('.text-template-editor textarea');
    if (textarea) {
      textarea.focus();
      // Set cursor position at the end
      textarea.setSelectionRange(newTemplate.length, newTemplate.length);
    }
  });
};

const getAvailableOutputsGrouped = () => {
  if (!selectedStep.value) return [];

  const items = [];

  // Get all previous steps (steps before the current formatter step)
  const previousSteps = chain.steps.filter(s => s.id !== selectedStep.value.id);

  previousSteps.forEach((step, stepIndex) => {
    // Add divider between step groups (except before first)
    if (stepIndex > 0) {
      items.push({ divider: true });
    }

    // Add step name as non-selectable header
    items.push({
      header: step.step_name
    });

    // Add all outputs for this step as selectable items
    const outputs = step.outputs || ['result'];
    outputs.forEach(output => {
      items.push({
        text: `  ${step.step_name}.${output}`,
        value: `${step.id}:${output}`,
        stepId: step.id,
        stepName: step.step_name,
        outputProperty: output
      });
    });
  });

  return items;
};

const updateSelectedOutputs = (selectedValues) => {
  if (!selectedStep.value) return;
  const stepIndex = chain.steps.findIndex(s => s.id === selectedStep.value.id);
  if (stepIndex === -1) return;

  if (!chain.steps[stepIndex].config.formatter_config) {
    chain.steps[stepIndex].config.formatter_config = {
      output_format: 'json',
      template: {},
      selected_outputs: [],
      output_mappings: {}
    };
  }

  chain.steps[stepIndex].config.formatter_config.selected_outputs = selectedValues;

  const newMappings = {};
  const template = {};

  selectedValues.forEach(value => {
    if (!value || typeof value !== 'string') return;
    const [stepIdStr, outputProperty] = value.split(':');
    const step = chain.steps.find(s => String(s.id) === String(stepIdStr));

    if (step) {
      // Check if mapping already exists (preserve custom key if it does)
      const existingMapping = chain.steps[stepIndex].config.formatter_config.output_mappings?.[value];

      // Generate default key name
      const defaultKey = `${step.step_name}_${outputProperty}`
        .toLowerCase()
        .replace(/[^a-zA-Z0-9]+/g, '_');

      // Create mapping entry
      newMappings[value] = {
        key: existingMapping?.key || defaultKey,
        displayName: `${step.step_name}.${outputProperty}`,
        stepId: stepIdStr,
        outputProperty: outputProperty,
        stepName: step.step_name
      };

      // Add to template
      template[newMappings[value].key] = `\${${step.step_name}.${outputProperty}}`;
    }
  });

  // Update mappings and template
  chain.steps[stepIndex].config.formatter_config.output_mappings = newMappings;
  chain.steps[stepIndex].config.formatter_config.template = template;

  // Force reactivity update
  selectedStep.value = { ...chain.steps[stepIndex] };
};

const getTotalTokensUsed = () => {
  if (!executionSteps.value || executionSteps.value.length === 0) return 0;
  return executionSteps.value.reduce((total, step) => {
    return total + (step.tokens_used || 0);
  }, 0);
};

const getStepStatusIconColor = (status) => {
  const colors = {
    'completed': 'success',
    'failed': 'error',
    'running': 'primary',
    'queued': 'warning'
  };
  return colors[status] || 'grey';
};

const updateOutputKey = (outputValue, newKey) => {
  if (!selectedStep.value) return;

  const stepIndex = chain.steps.findIndex(s => s.id === selectedStep.value.id);
  if (stepIndex === -1) return;

  const mapping = chain.steps[stepIndex].config.formatter_config?.output_mappings?.[outputValue];
  if (!mapping) return;

  // Update the key
  mapping.key = newKey;

  // Regenerate template with new key
  regenerateTemplate();
};

const removeFormatterOutput = (outputValue) => {
  if (!selectedStep.value) return;

  const stepIndex = chain.steps.findIndex(s => s.id === selectedStep.value.id);
  if (stepIndex === -1) return;

  // Remove from selected outputs
  const selectedOutputs = chain.steps[stepIndex].config.formatter_config?.selected_outputs || [];
  chain.steps[stepIndex].config.formatter_config.selected_outputs = selectedOutputs.filter(v => v !== outputValue);

  // Remove from mappings
  if (chain.steps[stepIndex].config.formatter_config?.output_mappings) {
    delete chain.steps[stepIndex].config.formatter_config.output_mappings[outputValue];
  }

  // Regenerate template
  regenerateTemplate();
};

const regenerateTemplate = () => {
  if (!selectedStep.value) return;

  const stepIndex = chain.steps.findIndex(s => s.id === selectedStep.value.id);
  if (stepIndex === -1) return;

  const mappings = chain.steps[stepIndex].config.formatter_config?.output_mappings || {};
  const template = {};

  Object.values(mappings).forEach(mapping => {
    template[mapping.key] = `\${${mapping.stepName}.${mapping.outputProperty}}`;
  });

  chain.steps[stepIndex].config.formatter_config.template = template;

  // Force reactivity update
  selectedStep.value = { ...chain.steps[stepIndex] };
};

const saveChain = async () => {
  try {

    // Capture current schema state BEFORE calculateStepOrder mutates steps.
    chain.input_schema = chainInputSchema.value;
    chain.output_schema = chainOutputSchema.value;

    // Ensure step order is calculated before saving
    calculateStepOrder();

    // Prepare chain data for saving - sort steps by their calculated order
    const sortedSteps = [...chain.steps].sort((a, b) => a.step_order - b.step_order);

    const chainData = {
      prompt_chain_id: chain.prompt_chain_id || null,
      chain_name: chain.chain_name,
      description: chain.description,
      tmpl_id: isSystemChainMode.value ? null : props.value,
      is_system: isSystemChainMode.value,
      active: true,
      agent_exposure: chain.agent_exposure || null,
      available_via: (chain.available_via || '').trim() || null,
      input_schema: chain.input_schema || null,
      output_schema: chain.output_schema || null,
      execution_config: { ...chainExecutionConfig },
      steps: sortedSteps.map((step, index) => {
        return {
          id: step.id, // UI step ID for connection mapping
          prompt_chain_step_id: step.prompt_chain_step_id || null,
          step_type: step.step_type || 'prompt',
          step_name: step.step_name || step.name || 'Unnamed Step',
          step_order: step.step_order, // Use calculated step order
          tmpl_ai_prompt_id: step.tmpl_ai_prompt_id || null,
          service_id: step.service_id || null,
          service_endpoint_id: step.service_endpoint_id || null,
          child_chain_id: step.child_chain_id || null,
          is_final_step: step.is_final_step || false,
          is_post_process: step.is_post_process || false,
          output_formatter_id: step.output_formatter_id || null,
          continue_on_error: step.continue_on_error || false,
          active: step.active !== undefined ? step.active : true,
          x: step.x || 0,
          y: step.y || 0,
          configuration_data: {
            input: step.config?.input || [],
            output: step.config?.output || [],
            service_config: step.config?.service_config || null,
            formatter_config: step.config?.formatter_config || null,
            chain_config: step.config?.chain_config || null,
            selection_config: step.config?.selection_config || null,
            model: step.config?.model || null,
            pre_step_hook: step.config?.pre_step_hook || null,
            position: {
              x: step.x || 0,
              y: step.y || 0
            }
          }
        };
      }),
      connections: chain.connections.map(conn => {
        let fromId = conn.from;

        // Extract trigger ID from 'trigger-{id}' format (same pattern as steps)
        if (fromId && String(fromId).startsWith('trigger-')) {
          fromId = String(fromId).replace('trigger-', '');
        }

        return {
          from: fromId,
          to: conn.to,
          fromPoint: conn.fromPoint || 'right',
          toPoint: conn.toPoint || 'left',
          mapping_config: conn.mapping_config || { mappings: [] }
        };
      }),
      triggers: (chain.triggers || []).map(trigger => ({
        trigger_id: trigger.trigger_id && !String(trigger.trigger_id).startsWith('temp_') ? trigger.trigger_id : null,
        client_id: String(trigger.trigger_id), // Send client ID as string for consistent mapping
        trigger_name: trigger.trigger_name,
        trigger_type: trigger.trigger_type,
        description: trigger.description || '',
        trigger_config: trigger.trigger_config || {},
        context_mapping: trigger.context_mapping || { mappings: [] },
        conditions: trigger.conditions || { rules: [], logic: 'AND' },
        priority: trigger.priority || 100,
        active: trigger.active !== false,
        x: trigger.x || 50,
        y: trigger.y || 100
      }))
    };

    const response = await axios.post('/externalContent/savePromptChain', chainData);

    if (response.data && response.data.success) {
      const savedChainId = response.data.prompt_chain_id || chain.prompt_chain_id;

      // Update chain with returned ID if it's a new chain
      if (response.data.prompt_chain_id) {
        chain.prompt_chain_id = response.data.prompt_chain_id;
      }

      // Show success notification
      triggerNotification('Prompt chain saved successfully!', 'success');

      // Reload chains list
      if (isSystemChainMode.value) {
        await loadSystemChains();
      } else {
        await loadChains();
      }

      // Reload the current chain to get updated trigger IDs from database
      if (savedChainId) {
        const searchList = isSystemChainMode.value ? systemChains.value : savedChains.value;
        const chainItem = searchList.find(c => c.prompt_chain_id === savedChainId || c.id === savedChainId);
        if (chainItem) {
          const wasSystemMode = isSystemChainMode.value;
          await editChain(chainItem);
          isSystemChainMode.value = wasSystemMode;
        }
      }
    } else {
      console.error('Failed to save chain:', response.data);
      triggerNotification('Failed to save chain: ' + (response.data.error || 'Unknown error'), 'error');
    }
  } catch (error) {
    console.error('Error saving chain:', error);
    triggerNotification('Error saving chain: ' + error.message, 'error');
  }
};

// Test Chain Functions
const openTestChainDialog = () => {
  // Reset state
  testJsonInput.value = '{\n  "items": [\n    {"name": "Example Item", "value": 100}\n  ]\n}';
  testJsonError.value = '';
  isTestJsonValid.value = true;
  testExecutionId.value = null;
  testExecutionStatus.value = '';
  testStatusMessages.value = [];
  testExecutionResult.value = null;
  testCurrentStep.value = null;
  testProgressInfo.value = null;

  showTestChainDialog.value = true;
};

const closeTestChainDialog = () => {
  // Clear polling interval if active
  if (testPollingInterval.value) {
    clearInterval(testPollingInterval.value);
    testPollingInterval.value = null;
  }

  showTestChainDialog.value = false;
};

const validateTestJson = () => {
  try {
    JSON.parse(testJsonInput.value);
    testJsonError.value = '';
    isTestJsonValid.value = true;
  } catch (error) {
    testJsonError.value = 'Invalid JSON: ' + error.message;
    isTestJsonValid.value = false;
  }
};

const executeTestChain = async () => {
  try {
    isExecutingTest.value = true;
    testStatusMessages.value = [];
    testExecutionResult.value = null;

    // Parse the JSON input
    let testData;
    try {
      testData = JSON.parse(testJsonInput.value);
    } catch (error) {
      testJsonError.value = 'Invalid JSON: ' + error.message;
      isExecutingTest.value = false;
      return;
    }

    // Add status message
    testStatusMessages.value.push({
      type: 'info',
      text: 'Triggering chain execution...'
    });

    // Call the API to trigger the chain
    const response = await axios.post('/externalContent/triggerPromptChain', {
      prompt_chain_id: chain.prompt_chain_id,
      source_data: {
        custom: testData
      }
    });

    if (response.data && response.data.success && response.data.execution_id) {
      testExecutionId.value = response.data.execution_id;
      testExecutionStatus.value = 'running';

      testStatusMessages.value.push({
        type: 'success',
        text: `Execution started (ID: ${response.data.execution_id})`
      });

      // Start polling for execution status
      pollExecutionStatus(response.data.execution_id);
    } else {
      testStatusMessages.value.push({
        type: 'error',
        text: 'Failed to start execution: ' + (response.data.error || 'Unknown error')
      });
      testExecutionStatus.value = 'failed';
      isExecutingTest.value = false;
    }
  } catch (error) {
    console.error('Error executing test chain:', error);
    testStatusMessages.value.push({
      type: 'error',
      text: 'Error: ' + error.message
    });
    testExecutionStatus.value = 'failed';
    isExecutingTest.value = false;
  }
};

const pollExecutionStatus = async (executionId) => {
  // Clear any existing polling interval
  if (testPollingInterval.value) {
    clearInterval(testPollingInterval.value);
  }

  // Reset tracking variables
  testCurrentStep.value = null;
  testProgressInfo.value = null;

  const checkStatus = async () => {
    try {
      const response = await axios.get(`/externalContent/execution/${executionId}/status`);

      if (response.data && response.data.success) {
        const data = response.data.data;
        testExecutionStatus.value = data.execution_status;

        // Update progress info - only add message if step changed
        if (data.current_step) {
          const stepKey = `${data.current_step.step_id}_${data.current_step.step_name}`;
          const progressText = `${data.progress?.completed_steps || 0}/${data.progress?.total_steps || 0}`;

          // Only add new message if the step changed
          if (testCurrentStep.value !== stepKey) {
            testCurrentStep.value = stepKey;
            testStatusMessages.value.push({
              type: 'info',
              text: `Processing: ${data.current_step.step_name} (${progressText})`
            });
          }

          // Update stored progress info for display
          testProgressInfo.value = {
            step_name: data.current_step.step_name,
            completed: data.progress?.completed_steps || 0,
            total: data.progress?.total_steps || 0,
            percentage: data.progress?.progress_percentage || 0
          };
        }

        // Check if execution is complete
        if (data.execution_status === 'completed' || data.execution_status === 'failed') {
          clearInterval(testPollingInterval.value);
          testPollingInterval.value = null;
          isExecutingTest.value = false;
          testCurrentStep.value = null;

          testStatusMessages.value.push({
            type: data.execution_status === 'completed' ? 'success' : 'error',
            text: `Execution ${data.execution_status}`,
            final: true
          });

          // Fetch final result if completed successfully
          if (data.execution_status === 'completed') {
            try {
              const resultResponse = await axios.get(`/externalContent/execution/${executionId}/result`);
              if (resultResponse.data && resultResponse.data.success) {
                testExecutionResult.value = resultResponse.data.outputs;
              }
            } catch (error) {
              console.error('Error fetching execution result:', error);
              testStatusMessages.value.push({
                type: 'error',
                text: 'Failed to fetch execution result'
              });
            }
          }
        }
      }
    } catch (error) {
      console.error('Error polling execution status:', error);
      testStatusMessages.value.push({
        type: 'error',
        text: 'Error checking execution status: ' + error.message
      });
    }
  };

  // Poll immediately, then every 2 seconds
  await checkStatus();
  testPollingInterval.value = setInterval(checkStatus, 2000);
};

const filteredItems = (items) => {
    if (!items || !Array.isArray(items)) return [];
    // Filter out undefined/null items first
    const validItems = items.filter(item => item != null && typeof item === 'object');
    if (!searchQuery.value) return validItems;
    return validItems.filter(item =>
      item.name?.toLowerCase().includes(searchQuery.value.toLowerCase())
    );
    };

    // Landing page functions
const createNewChain = () => {
    chain.chain_name = 'New Prompt Chain';
    chain.prompt_chain_id = null;
    chain.description = '';
    chain.agent_exposure = null;
    chain.available_via = '';
    chain.steps = [];
    chain.connections = [];
    chain.triggers = [];
    chain.execution_config = null;
    chainExecutionConfig.execution_mode = 'sequence';
    chainExecutionConfig.context_mode = 'summary';
    chainExecutionConfig.max_context_rows = 20;
    isSystemChainMode.value = false;
    isViewingSystemChain.value = false;
    showLandingPage.value = false;
    showHistoryView.value = false;
    showReportView.value = false;
    };

const createNewSystemChain = () => {
    chain.chain_name = 'New System Chain';
    chain.prompt_chain_id = null;
    chain.description = '';
    chain.agent_exposure = null;
    chain.available_via = '';
    chain.steps = [];
    chain.connections = [];
    chain.triggers = [];
    chain.execution_config = null;
    chainExecutionConfig.execution_mode = 'sequence';
    chainExecutionConfig.context_mode = 'summary';
    chainExecutionConfig.max_context_rows = 20;
    isSystemChainMode.value = true;
    isViewingSystemChain.value = false;
    showLandingPage.value = false;
    showHistoryView.value = false;
    showReportView.value = false;
    };


const getHierarchyDisplayName = (hierarchyTypeId, levelIndex) => {
  if (!hierarchyTypeId) return 'Select hierarchy';
  
  const hierarchy = hierarchyList.value.find(h => h.ht_id === hierarchyTypeId);
  if (!hierarchy) return 'Unknown hierarchy';
  
  if (levelIndex === null || levelIndex === undefined) {
    return hierarchy.label || hierarchy.name || 'Hierarchy';
  }
  
  const level = hierarchy.levels && hierarchy.levels[levelIndex-1];
  if (!level) return `${hierarchy.label || hierarchy.name || 'Hierarchy'} - Level ${levelIndex}`;
  
  return `${hierarchy.label || hierarchy.name || 'Hierarchy'} - ${level.name || level.label || `Level ${levelIndex}`}`;
};    

const editChain = async (chainItem) => {
  try {
    if (!chainItem) {
      triggerNotification('Invalid chain data', 'error');
      return;
    }

    const chainId = chainItem.prompt_chain_id || chainItem.id;
    
    if (!chainId) {
      triggerNotification('Chain ID not found', 'error');
      return;
    }

    // Load full chain data from API
    const response = await axios.get(`/externalContent/promptChain/${chainId}`);
    
    if (response.data.success) {
      const chainData = response.data.data;
      
      chain.chain_name = chainData.chain_name || chainItem.chain_name || 'Unnamed Chain';
      chain.prompt_chain_id = chainData.prompt_chain_id;
      chain.description = chainData.description || '';
      chain.agent_exposure = chainData.agent_exposure || null;
      chain.available_via = chainData.available_via || '';

      const freshPromptData = {};
      const clientPromptsCategory = promptCategories.value.find(c => c.type === 'client_prompts');
      
      if (clientPromptsCategory && clientPromptsCategory.items) {
        const promptIds = chainData.steps
          .filter(step => step.step_type === 'prompt' && step.tmpl_ai_prompt_id)
          .map(step => step.tmpl_ai_prompt_id);

        promptIds.forEach(promptId => {
          const freshPrompt = clientPromptsCategory.items.find(prompt => prompt.tmpl_ai_prompt_id === promptId);
          if (freshPrompt) {
            freshPromptData[promptId] = {
              tmpl_ai_prompt_id: freshPrompt.tmpl_ai_prompt_id,
              name: freshPrompt.name || 'Unnamed Prompt',
              prompt_type: freshPrompt.prompt_type || 'other',
              system_prompt: freshPrompt.system_prompt || '',
              user_prompt: freshPrompt.user_prompt || '',
              temperature: freshPrompt.temperature,
              top_p: freshPrompt.top_p,
              max_tokens: freshPrompt.max_tokens,
              configuration_data: freshPrompt.configuration_data,
              inputs: freshPrompt.inputs || [],
              itemType: 'prompt'
            };
          }
        });
      }

      const serviceData = {};
      if (serviceItems.value && serviceItems.value.length > 0) {
        chainData.steps
          .filter(step => step.step_type === 'service' && step.service_id)
          .forEach(step => {
            // Find service parent by service_id
            const serviceParent = serviceItems.value.find(s =>
              s.itemType === 'service_parent' && s.service_id === step.service_id
            );

            if (serviceParent) {
              // If step has a service_endpoint_id, find that specific endpoint
              if (step.service_endpoint_id) {
                const endpoint = serviceItems.value.find(s =>
                  s.itemType === 'service' &&
                  s.isEndpoint &&
                  s.service_endpoint_id === step.service_endpoint_id
                );
                if (endpoint) {
                  serviceData[step.service_id] = endpoint;
                } else {
                  // Fallback to service parent if endpoint not found
                  serviceData[step.service_id] = {
                    ...serviceParent,
                    itemType: 'service'
                  };
                }
              } else {
                // No endpoint specified, use service parent
                serviceData[step.service_id] = {
                  ...serviceParent,
                  itemType: 'service'
                };
              }
            }
          });
      }

      const formatterData = {};
      const formattersCategory = promptCategories.value.find(c => c.type === 'output_formatter');
      if (formattersCategory && formattersCategory.items) {
        chainData.steps
          .filter(step => step.step_type === 'output_formatter' && step.output_formatter_id)
          .forEach(step => {
            const formatter = formattersCategory.items.find(f => f.id === step.output_formatter_id);
            if (formatter) {
              formatterData[step.output_formatter_id] = {
                ...formatter,
                itemType: 'output_formatter'
              };
            }
          });
      }
      
      chain.steps = (chainData.steps || []).map(step => {
        const config = step.configuration_data;
        const position = config?.position || { x: 100, y: 100 };

        const baseStep = {
          ...step,
          id: step.prompt_chain_step_id || step.id || `step_${Date.now()}_${Math.random()}`,
          x: position.x,
          y: position.y,
          name: step.step_name,
          itemType: step.step_type,
          type: step.step_type,
          dependencies: [],
          continue_on_error: step.continue_on_error || false,
          is_post_process: step.is_post_process || false,
          active: step.active !== undefined ? Boolean(step.active) : true,
          output_formatter_id: step.output_formatter_id || null,
          step_order: step.step_order || 999,
          config: {
            input: config?.input || [],
            output: (config?.output || [{ property: 'result', extractFrom: 'response' }]).map(output => {
              return {
              property: output.property || '',
              extractFrom: output.extractFrom || 'response',
              // `path` is what the execution engine walks (dot-path into
              // the parsed { result, reason } envelope) — dropping it
              // here silently reverted saved paths on the next save.
              path: output.path,
              jsonPath: output.jsonPath,
              transform: output.transform,
              sourceInputProperty: output.sourceInputProperty,
              isPassThrough: output.isPassThrough || false,
              isContextOutput: output.isContextOutput || false,
              validation: output.validation || false,
              validationParams: output.validationParams || {},
              max_retries: output.max_retries !== undefined ? output.max_retries : 3,
              type: output.type,
              id: output.id || Date.now()
            }
            }),
            service_config: config?.service_config || null,
            formatter_config: config?.formatter_config || null,
            chain_config: config?.chain_config || null,
            selection_config: config?.selection_config || null,
            model: config?.model || null,
            pre_step_hook: config?.pre_step_hook || null,
            position: {
              x: step.x || 0,
              y: step.y || 0
            }
          }
        };

        let stepOutputs = [];
        if (step.step_type === 'service') {
          stepOutputs = (config?.output || [])
            .filter(o => o.property && o.property.trim() !== '')
            .map(o => o.property);
        } else if (step.step_type === 'output_formatter') {
          stepOutputs = ['result'];
        } else {
          stepOutputs = (config?.output || [])
            .filter(o => o.property && o.property.trim() !== '')
            .map(o => o.property);
          
          if (stepOutputs.length === 0) {
            stepOutputs = ['result'];
          }
        }

        if (step.step_type === 'service' && step.service_id && serviceData[step.service_id]) {
          const serviceItem = serviceData[step.service_id];

          return {
            ...baseStep,
            item: serviceItem,
            outputs: stepOutputs,
            config: {
              ...baseStep.config,
              service_config: {
                endpoint_path: config?.service_config?.endpoint_path || null,
                http_method: config?.service_config?.http_method || 'POST',
                base_url: serviceItem.base_url,
                timeout: config?.service_config?.timeout || 30,
                retry_attempts: config?.service_config?.retry_attempts || 3,
                service_endpoint_id: config?.service_config?.service_endpoint_id || null
              }
            }
          };
        }

        if (step.step_type === 'output_formatter' && step.output_formatter_id && formatterData[step.output_formatter_id]) {
          const formatterItem = formatterData[step.output_formatter_id];
          
          return {
            ...baseStep,
            item: formatterItem,
            outputs: stepOutputs
          };
        }

        if (step.step_type === 'chain' && step.child_chain_id) {
          // Ensure chain_config exists with proper structure
          if (!config?.chain_config) {
            baseStep.config.chain_config = {
              child_chain_id: step.child_chain_id,
              inherit_context: true,
              output_mapping: {},
              bulk_mode: {
                enabled: false,
                source_output: '',
                array_path: '',
                item_property_mappings: {}
              }
            };
          } else {
            // Ensure bulk_mode exists even if chain_config is present
            if (!config.chain_config.bulk_mode) {
              baseStep.config.chain_config = {
                ...config.chain_config,
                bulk_mode: {
                  enabled: false,
                  source_output: '',
                  array_path: '',
                  item_property_mappings: {}
                }
              };
            } else {
              baseStep.config.chain_config = config.chain_config;
            }
          }

          // Load available chains to get chain item data
          const chainsCategory = promptCategories.value.find(c => c.type === 'chains');
          const chainItem = chainsCategory?.items?.find(c => c.prompt_chain_id === step.child_chain_id) || {
            prompt_chain_id: step.child_chain_id,
            name: 'Unknown Chain',
            step_count: 0
          };

          return {
            ...baseStep,
            item: chainItem,
            outputs: stepOutputs
          };
        }

        if (step.step_type === 'manual_selection') {
          // Ensure selection_config exists with proper structure
          if (!config?.selection_config) {
            baseStep.config.selection_config = {
              source_output: '',
              array_path: '',
              display_fields: [],
              selection_mode: 'multiple'
            };
          } else {
            // Ensure all fields exist
            baseStep.config.selection_config = {
              source_output: config.selection_config.source_output || '',
              array_path: config.selection_config.array_path || '',
              display_fields: config.selection_config.display_fields || [],
              selection_mode: config.selection_config.selection_mode || 'multiple'
            };
          }

          const selectionItem = {
            id: 1,
            itemType: 'manual_selection',
            name: 'Manual Selection Step',
            description: 'Pauses execution to allow user to select items before proceeding'
          };

          return {
            ...baseStep,
            item: selectionItem,
            outputs: stepOutputs
          };
        }

        if (step.step_type === 'prompt' && step.tmpl_ai_prompt_id && freshPromptData[step.tmpl_ai_prompt_id]) {
          const freshPrompt = freshPromptData[step.tmpl_ai_prompt_id];

          const mergedInputs = freshPrompt.inputs.map(freshInput => {
            const storedInput = (config?.input || []).find(stored => stored.property === freshInput.property);
            
            if (storedInput) {
              return {
                ...freshInput,
                source: storedInput.source || freshInput.source,
                sourceField: storedInput.sourceField || freshInput.sourceField,
                outputProperty: storedInput.outputProperty || freshInput.outputProperty,
                required: storedInput.required !== undefined ? storedInput.required : freshInput.required,
                metadata: { ...freshInput.metadata, ...storedInput.metadata }
              };
            } else {
              return freshInput;
            }
          });
          
          const legacyInputs = (config?.input || []).filter(storedInput => 
            !freshPrompt.inputs.some(freshInput => freshInput.property === storedInput.property)
          );
          
          return {
            ...baseStep,
            item: freshPrompt,
            prompt_type: freshPrompt.prompt_type,
            outputs: stepOutputs,
            config: {
              ...baseStep.config,
              input: [...mergedInputs, ...legacyInputs]
            }
          };
        }

        return {
          ...baseStep,
          item: null,
          prompt_type: step.step_type === 'service' ? 'service' : step.step_type === 'output_formatter' ? 'formatter' : 'client_prompts',
          outputs: stepOutputs
        };
      });

      // Ensure triggers have x/y positions for reactivity - load triggers FIRST
      chain.triggers = (chainData.triggers || []).map((trigger, index) => ({
        ...trigger,
        x: trigger.x ?? 50,
        y: trigger.y ?? (100 + index * 140)
      }));

      // Only migrate 'start' connections if there are NO connections with proper trigger IDs
      const hasModernTriggerConnections = (chainData.connections || []).some(conn =>
        typeof conn.from === 'string' && conn.from.startsWith('trigger-')
      );

      const firstTriggerId = chain.triggers.length > 0 ? chain.triggers[0].trigger_id : null;
      const triggerIds = new Set(chain.triggers.map(t => t.trigger_id));

      chain.connections = (chainData.connections || []).map((conn, index) => {
        let fromId = conn.from;

        // Convert trigger ID to 'trigger-{id}' format (same as steps are handled)
        if (fromId && typeof fromId === 'number' && triggerIds.has(fromId)) {
          fromId = `trigger-${fromId}`;
        }
        // Only migrate legacy 'start' or null connections if this appears to be an old chain
        else if ((fromId === 'start' || fromId === null) && !hasModernTriggerConnections) {
          if (firstTriggerId) {
            fromId = `trigger-${firstTriggerId}`;
          } else {
            fromId = 'start'; // Keep as 'start' if no triggers exist
          }
        }

        return {
          ...conn,
          from: fromId,
          id: conn.id || `conn_${Date.now()}_${index}`,
          fromPoint: conn.fromPoint || 'right',
          toPoint: conn.toPoint || 'left'
        };
      });

      chain.connections.forEach(conn => {
        const toId = String(conn.to || '');
        if (conn.to && toId !== 'start' && !toId.startsWith('trigger-')) {
          const toStep = chain.steps.find(s => s.id === conn.to);
          if (toStep && conn.from) {
            if (!toStep.dependencies) {
              toStep.dependencies = [];
            }
            if (!toStep.dependencies.includes(conn.from)) {
              toStep.dependencies.push(conn.from);
            }
          }
        }
      });

      // Calculate step order first so schema derivation can filter by connected steps
      calculateStepOrder();

      // Load chain schemas (from backend or auto-derive)
      chain.input_schema = chainData.input_schema || null;
      chain.output_schema = chainData.output_schema || null;

      // Load execution config
      const execConfig = chainData.execution_config
        ? (typeof chainData.execution_config === 'string' ? JSON.parse(chainData.execution_config) : chainData.execution_config)
        : null;
      chain.execution_config = execConfig;
      chainExecutionConfig.execution_mode = execConfig?.execution_mode || 'sequence';
      chainExecutionConfig.context_mode = execConfig?.context_mode || 'summary';
      chainExecutionConfig.max_context_rows = execConfig?.max_context_rows ?? 20;

      // If no stored schema, auto-derive from steps
      if (!chain.input_schema) {
        chain.input_schema = computeChainInputSchema();
      }
      if (!chain.output_schema) {
        chain.output_schema = computeChainOutputSchema();
      }
      showLandingPage.value = false;
      showHistoryView.value = false;
      showReportView.value = false;
    }
  } catch (error) {
    console.error('Error loading chain:', error);
    triggerNotification('Error loading chain: ' + (error.message || 'Unknown error'), 'error');

    // Fallback handling - check if chainItem has required data
    if (chainItem && (chainItem.chain_name || chainItem.name)) {
      chain.chain_name = chainItem.chain_name || chainItem.name;
      chain.prompt_chain_id = chainItem.prompt_chain_id || chainItem.id;
      chain.description = chainItem.description || '';
      chain.agent_exposure = chainItem.agent_exposure || null;
      chain.available_via = chainItem.available_via || '';
      chain.steps = chainItem.steps || [];

      // Ensure triggers have x/y positions for reactivity - load triggers FIRST
      chain.triggers = (chainItem.triggers || []).map((trigger, index) => ({
        ...trigger,
        x: trigger.x ?? 50,
        y: trigger.y ?? (100 + index * 140)
      }));

      // Only migrate 'start' connections if there are NO connections with proper trigger IDs
      const hasModernTriggerConnections = (chainItem.connections || []).some(conn =>
        typeof conn.from === 'string' && conn.from.startsWith('trigger-')
      );

      const firstTriggerId = chain.triggers.length > 0 ? chain.triggers[0].trigger_id : null;

      chain.connections = (chainItem.connections || []).map((conn, index) => {
        let fromId = conn.from;

        // Remap trigger_index_N back to trigger-{id}
        if (fromId && String(fromId).startsWith('trigger_index_')) {
          const triggerIndex = parseInt(String(fromId).replace('trigger_index_', ''));
          if (triggerIndex >= 0 && triggerIndex < chain.triggers.length) {
            const triggerId = chain.triggers[triggerIndex].trigger_id;
            fromId = `trigger-${triggerId}`;
          }
        }
        // Only migrate legacy 'start' or null connections if this appears to be an old chain
        else if ((fromId === 'start' || fromId === null) && !hasModernTriggerConnections) {
          if (firstTriggerId) {
            fromId = `trigger-${firstTriggerId}`;
          } else {
            fromId = 'start';
          }
        }

        return {
          ...conn,
          from: fromId,
          id: conn.id || `conn_${Date.now()}_${index}`,
          fromPoint: conn.fromPoint || 'right',
          toPoint: conn.toPoint || 'left'
        };
      });
      showLandingPage.value = false;
      showHistoryView.value = false;
      showReportView.value = false;
    }
  }
};

const backToLanding = () => {
    showLandingPage.value = true;
    showHistoryView.value = false;
    showReportView.value = false;
    isSystemChainMode.value = false;
    isViewingSystemChain.value = false;
    };

const deleteChainFromList = (chainItem) => {
    const index = savedChains.value.findIndex(c => c.id === chainItem.id);
    if (index !== -1) {
      savedChains.value.splice(index, 1);
    }
};

const formatDate = (date) => {
    if (!date) return 'Unknown';

    const dateObj = date instanceof Date ? date : new Date(date);

    if (isNaN(dateObj.getTime())) return 'Invalid date';

    const now = new Date();
    const diff = Math.floor((now - dateObj) / 1000);

    if (diff < 86400) return 'Today';
    if (diff < 172800) return 'Yesterday';
    if (diff < 604800) return `${Math.floor(diff / 86400)} days ago`;
    return dateObj.toLocaleDateString();
    };

// Trigger Functions
const getTriggerIcon = (triggerType) => {
  const triggerDef = triggerTypes.find(t => t.value === triggerType);
  return triggerDef ? triggerDef.icon : 'mdi-flash';
};

const getTriggerTypeLabel = (triggerType) => {
  const triggerDef = triggerTypes.find(t => t.value === triggerType);
  return triggerDef ? triggerDef.label : triggerType;
};

const openTriggerModal = (trigger = null) => {
  // Switch to triggers tab when opening trigger modal
  activeTab.value = 'triggers';

  currentTrigger.value = trigger || {
    trigger_name: '',
    trigger_type: 'hierarchy_node_action',
    description: '',
    trigger_config: {
      action_types: [],
      auto_execute: true,
      ht_id: null,
      level: null
    },
    context_mapping: {
      mappings: []
    },
    conditions: {
      rules: [],
      logic: 'AND'
    },
    priority: 100,
    active: true
  };
  showTriggerModal.value = true;
};

const saveTrigger = () => {
  if (currentTrigger.value.trigger_id) {
    // Update existing
    const index = chain.triggers.findIndex(t => t.trigger_id === currentTrigger.value.trigger_id);
    if (index !== -1) {
      chain.triggers[index] = { ...currentTrigger.value };
    }
  } else {
    // Add new - use temporary client-side ID until saved
    const newTriggerId = `temp_trigger_${Date.now()}_${Math.random()}`;
    chain.triggers.push({
      ...currentTrigger.value,
      trigger_id: newTriggerId
    });
  }
  showTriggerModal.value = false;
};

const deleteTrigger = (triggerId) => {
  chain.triggers = chain.triggers.filter(t => t.trigger_id !== triggerId);
};

const addContextMapping = () => {
  if (!currentTrigger.value.context_mapping) {
    currentTrigger.value.context_mapping = { mappings: [] };
  }
  currentTrigger.value.context_mapping.mappings.push({
    source: '',
    target: '',
    transform: null
  });
};

const removeContextMapping = (index) => {
  if (currentTrigger.value.context_mapping && currentTrigger.value.context_mapping.mappings) {
    currentTrigger.value.context_mapping.mappings.splice(index, 1);
  }
};

// Select a trigger on the canvas
const selectTrigger = (trigger) => {
  selectedTrigger.value = trigger;
  selectedStep.value = null; // Deselect any step
};

/**
 * Open connection mapping dialog for trigger-to-step connections
 * Allows mapping trigger context fields to step inputs
 *
 * ARRAY MAPPING EXAMPLES:
 *
 * 1. Singular Mode (entire array/object):
 *    - Trigger Field: payload.items
 *    - Step Input: profiles
 *    - Result: step receives entire array as-is
 *    - Use Case: Pass array to step for bulk processing
 *
 * 2. Array Items Mode (map properties from each item):
 *    - Trigger Field: payload.items[*].name
 *    - Step Input: profile_name
 *    - Result: step receives array of names extracted from each item
 *    - Use Case: Extract specific property from array items
 *
 * Example API Payload:
 * {
 *   "items": [
 *     { "name": "John", "level": "Senior", "email": "john@example.com" },
 *     { "name": "Jane", "level": "Junior", "email": "jane@example.com" }
 *   ]
 * }
 *
 * Singular: payload.items → [{ name: "John", ... }, { name: "Jane", ... }]
 * Array Items: payload.items[*].name → ["John", "Jane"]
 */
const openConnectionMappingDialog = (connection) => {
  currentConnection.value = connection;

  // Initialize mappings from existing connection config or empty array
  // Create a deep copy to avoid direct mutation of the connection object
  const existingMappings = connection.mapping_config?.mappings || [];
  connectionMappings.value = existingMappings.map((m, idx) => ({
    id: m.id || `mapping_${Date.now()}_${idx}`,
    trigger_field: m.trigger_field || '',
    step_input: m.step_input || '',
    array_mode: m.array_mode || 'singular',
    transform: m.transform || null
  }));

  // Load API schema from trigger config (if this is an API trigger connection)
  const fromStr = String(connection.from || '');
  if (fromStr.startsWith('trigger-')) {
    const triggerId = fromStr.replace('trigger-', '');
    const trigger = chain.triggers?.find(t => String(t.trigger_id) === triggerId);
    if (trigger?.trigger_type === 'api') {
      connectionApiSchema.value = trigger.trigger_config?.input_schema || '';
    } else {
      connectionApiSchema.value = '';
    }
  } else {
    connectionApiSchema.value = '';
  }

  showConnectionMappingDialog.value = true;
};

/**
 * Get available trigger fields based on trigger type
 * Returns fields that can be mapped from the trigger context
 */
const getAvailableTriggerFields = computed(() => {
  if (!currentConnection.value || !currentConnection.value.from) return [];

  const triggerId = currentConnection.value.from.replace('trigger-', '');
  const trigger = chain.triggers?.find(t => String(t.trigger_id) === triggerId);

  if (!trigger) return [];

  const fields = [];

  switch (trigger.trigger_type) {
    case 'api': {
      // For API triggers, extract fields from JSON schema or example data
      // Use draft schema from connection dialog if open, otherwise use saved trigger config
      const apiSchemaStr = (showConnectionMappingDialog.value && connectionApiSchema.value)
        ? connectionApiSchema.value
        : trigger.trigger_config?.input_schema;
      if (apiSchemaStr) {
        try {
          const schema = JSON.parse(apiSchemaStr);

          // Helper to infer type from example value
          const inferType = (value) => {
            if (value === null || value === undefined) return 'any';
            if (Array.isArray(value)) return 'array';
            return typeof value;
          };

          // Extract fields from JSON Schema format (has 'type' and 'properties')
          const extractFieldsFromSchema = (obj, prefix = 'source_data.custom') => {
            if (!obj || typeof obj !== 'object') return;

            if (obj.properties) {
              Object.keys(obj.properties).forEach(key => {
                const fieldPath = prefix ? `${prefix}.${key}` : key;
                const property = obj.properties[key];

                // Add the field itself
                fields.push({
                  text: `${fieldPath} (${property.type || 'any'})`,
                  value: fieldPath,
                  type: property.type || 'any',
                  isArray: property.type === 'array'
                });

                // If it has nested properties, extract them
                if (property.properties) {
                  extractFieldsFromSchema(property, fieldPath);
                }

                // If it's an array with object items, extract item properties
                if (property.type === 'array' && property.items?.properties) {
                  extractFieldsFromSchema(property.items, `${fieldPath}[*]`);
                }
              });
            } else if (obj.type === 'array' && obj.items?.properties) {
              // Handle root-level array schema
              Object.keys(obj.items.properties).forEach(key => {
                const property = obj.items.properties[key];
                const fieldPath = `${prefix}[*].${key}`;

                fields.push({
                  text: `${fieldPath} (${property.type || 'any'})`,
                  value: fieldPath,
                  type: property.type || 'any',
                  isArray: false
                });
              });

              // Also add the array itself as singular
              fields.push({
                text: `${prefix} (array)`,
                value: prefix,
                type: 'array',
                isArray: true
              });
            }
          };

          // Extract fields from example data format (raw objects/arrays)
          // Note: Input schema represents the structure of 'source_data.custom'
          const extractFieldsFromExample = (obj, prefix = 'source_data.custom', depth = 0) => {
            if (depth > 3) return; // Prevent infinite recursion

            if (Array.isArray(obj)) {
              // Add the array itself as a singular field
              fields.push({
                text: `${prefix} (entire array)`,
                value: prefix,
                type: 'array',
                isArray: true
              });

              // If array has items, extract properties from first item
              if (obj.length > 0) {
                const firstItem = obj[0];

                if (typeof firstItem === 'object' && firstItem !== null) {
                  // Extract each property as an array items field
                  Object.keys(firstItem).forEach(key => {
                    const value = firstItem[key];
                    const itemFieldPath = `${prefix}[*].${key}`;
                    const type = inferType(value);

                    fields.push({
                      text: `${itemFieldPath} (from each item)`,
                      value: itemFieldPath,
                      type: type,
                      isArray: false
                    });

                    // Recursively extract nested objects (but not arrays to avoid confusion)
                    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                      Object.keys(value).forEach(nestedKey => {
                        const nestedValue = value[nestedKey];
                        const nestedType = inferType(nestedValue);
                        fields.push({
                          text: `${itemFieldPath}.${nestedKey} (${nestedType})`,
                          value: `${itemFieldPath}.${nestedKey}`,
                          type: nestedType,
                          isArray: false
                        });
                      });
                    }
                  });
                } else {
                  // Array of primitives
                  fields.push({
                    text: `${prefix}[*] (each value)`,
                    value: `${prefix}[*]`,
                    type: inferType(firstItem),
                    isArray: false
                  });
                }
              }
            } else if (typeof obj === 'object' && obj !== null) {
              // Extract fields from object
              Object.keys(obj).forEach(key => {
                const value = obj[key];
                const fieldPath = prefix ? `${prefix}.${key}` : key;
                const type = inferType(value);

                fields.push({
                  text: `${fieldPath} (${type})`,
                  value: fieldPath,
                  type: type,
                  isArray: Array.isArray(value)
                });

                // Recursively extract nested structures
                if (typeof value === 'object' && value !== null) {
                  if (Array.isArray(value) && value.length > 0) {
                    // Handle nested array
                    const firstItem = value[0];
                    if (typeof firstItem === 'object' && firstItem !== null) {
                      Object.keys(firstItem).forEach(nestedKey => {
                        const nestedValue = firstItem[nestedKey];
                        const nestedType = inferType(nestedValue);
                        fields.push({
                          text: `${fieldPath}[*].${nestedKey} (${nestedType})`,
                          value: `${fieldPath}[*].${nestedKey}`,
                          type: nestedType,
                          isArray: false
                        });
                      });
                    }
                  } else if (!Array.isArray(value) && depth < 2) {
                    // Handle nested object (limit depth to avoid too many fields)
                    extractFieldsFromExample(value, fieldPath, depth + 1);
                  }
                }
              });
            }
          };

          // Detect format and extract fields
          if (schema.type || schema.properties) {
            // JSON Schema format
            extractFieldsFromSchema(schema);
          } else {
            // Example data format
            extractFieldsFromExample(schema);
          }
        } catch (error) {
          console.error('Failed to parse API trigger schema:', error);
        }
      }
      break;
    }

    case 'document_action':
      fields.push(
        { text: 'source_data.doc_id', value: 'source_data.doc_id', type: 'number', isArray: false },
        { text: 'source_data.hr_ids', value: 'source_data.hr_ids', type: 'array', isArray: true },
        { text: 'source_data.ht_id', value: 'source_data.ht_id', type: 'number', isArray: false },
        { text: 'source_data.client_id', value: 'source_data.client_id', type: 'number', isArray: false },
        { text: 'source_data.user_id', value: 'source_data.user_id', type: 'number', isArray: false }
      );
      break;

    case 'hierarchy_node_action':
      fields.push(
        { text: 'source_data.node_ids', value: 'source_data.node_ids', type: 'array', isArray: true },
        { text: 'source_data.ht_id', value: 'source_data.ht_id', type: 'number', isArray: false },
        { text: 'source_data.client_id', value: 'source_data.client_id', type: 'number', isArray: false },
        { text: 'source_data.user_id', value: 'source_data.user_id', type: 'number', isArray: false }
      );
      break;

    case 'webhook':
      fields.push(
        { text: 'payload.body', value: 'payload.body', type: 'object', isArray: false },
        { text: 'payload.headers', value: 'payload.headers', type: 'object', isArray: false },
        { text: 'payload.query', value: 'payload.query', type: 'object', isArray: false }
      );
      break;

    case 'scheduled':
      fields.push(
        { text: 'execution_time', value: 'execution_time', type: 'date', isArray: false },
        { text: 'source_data.client_id', value: 'source_data.client_id', type: 'number', isArray: false }
      );
      break;

    case 'chain_output':
      if (trigger.trigger_config?.source_output_property) {
        fields.push({
          text: `output.${trigger.trigger_config.source_output_property}`,
          value: `output.${trigger.trigger_config.source_output_property}`,
          type: 'any',
          isArray: false
        });
      }
      break;
  }

  return fields;
});

/**
 * Get available trigger fields for a specific mapping
 * Filters based on array_mode
 */
const getAvailableTriggerFieldsForMapping = (mapping) => {
  const allFields = getAvailableTriggerFields.value;

  if (mapping.array_mode === 'items') {
    // In array items mode, only show fields with [*] notation (array item properties)
    return allFields.filter(f => f.value.includes('[*]'));
  } else {
    // In singular mode, show all fields EXCEPT those with [*] notation
    return allFields.filter(f => !f.value.includes('[*]'));
  }
};

/**
 * Get required step inputs for the connected step
 * Returns inputs that need to be mapped
 */
const getConnectedStepInputs = computed(() => {
  if (!currentConnection.value || !currentConnection.value.to) return [];

  const step = chain.steps?.find(s => s.id === currentConnection.value.to);

  if (!step || !step.config?.input) return [];

  return step.config.input.map(input => ({
    text: `${input.property}${input.required ? ' *' : ''}`,
    value: input.property,
    required: input.required || false
  }));
});

/**
 * Add a new mapping to the connection
 */
const addConnectionMapping = async () => {
  const newMapping = {
    id: `mapping_${Date.now()}_${Math.random()}`,
    trigger_field: '',
    step_input: '',
    array_mode: 'singular', // Default to singular
    transform: null
  };

  // Create a new array to trigger reactivity
  connectionMappings.value = [...connectionMappings.value, newMapping];

  // Force Vue to update
  await nextTick();
};

/**
 * Remove a mapping from the connection
 */
const removeConnectionMapping = (index) => {
  // Create a new array without the item at the specified index to trigger reactivity
  connectionMappings.value = connectionMappings.value.filter((_, idx) => idx !== index);
};

/**
 * Update a field on a specific mapping
 * Forces reactivity by recreating the array
 */
const updateMappingField = (index, field, value) => {
  if (index >= 0 && index < connectionMappings.value.length) {
    // Create new array with updated mapping to trigger reactivity
    connectionMappings.value = connectionMappings.value.map((mapping, idx) => {
      if (idx === index) {
        return {
          ...mapping,
          [field]: value
        };
      }
      return mapping;
    });
  }
};

/**
 * Update the array mode for a specific mapping
 * Forces reactivity by recreating the array
 */
const updateMappingArrayMode = (index, newMode) => {
  if (index >= 0 && index < connectionMappings.value.length) {
    // Create new array with updated mapping to trigger reactivity
    connectionMappings.value = connectionMappings.value.map((mapping, idx) => {
      if (idx === index) {
        // When changing array mode, clear the trigger_field to force reselection
        return {
          ...mapping,
          array_mode: newMode,
          trigger_field: '' // Clear field when mode changes
        };
      }
      return mapping;
    });
  }
};

/**
 * Save connection mappings and close dialog
 */
const saveConnectionMappings = () => {
  if (currentConnection.value) {
    // Ensure mapping_config exists
    if (!currentConnection.value.mapping_config) {
      currentConnection.value.mapping_config = { mappings: [] };
    }

    // Create a new array to trigger reactivity
    currentConnection.value.mapping_config.mappings = [...connectionMappings.value];

    // Save API schema to the trigger config (if this is an API trigger connection)
    if (isApiTriggerConnection.value && connectionSourceTrigger.value) {
      if (!connectionSourceTrigger.value.trigger_config) {
        connectionSourceTrigger.value.trigger_config = {};
      }
      connectionSourceTrigger.value.trigger_config.input_schema = connectionApiSchema.value || null;
    }

    // Force reactivity by finding and updating the connection in chain.connections
    const connectionIndex = chain.connections.findIndex(c => c.id === currentConnection.value.id);
    if (connectionIndex !== -1) {
      // Trigger reactivity by reassigning
      chain.connections[connectionIndex] = { ...chain.connections[connectionIndex] };
    }
  }

  showConnectionMappingDialog.value = false;
  currentConnection.value = null;
  triggerNotification('Connection mappings saved', 'success');
};

/**
 * Check if a connection is from a trigger
 */
const isTriggerConnection = (connection) => {
  return connection && typeof connection.from === 'string' && connection.from.startsWith('trigger-');
};

// Start dragging a trigger node
const startDragTrigger = (e, trigger) => {
  // Ignore if clicking on connection point or buttons
  if (e.target.classList.contains('connection-point') || e.target.closest('button')) {
    return;
  }

  // Store initial position to detect if this is a drag or just a click
  triggerDragOffset.initialX = e.clientX;
  triggerDragOffset.initialY = e.clientY;
  triggerDragOffset.hasMoved = false;

  e.stopPropagation();
  e.preventDefault();

  isDraggingTrigger.value = true;
  draggedTrigger.value = trigger;
  selectedTrigger.value = trigger;
  selectedStep.value = null;

  const rect = e.currentTarget.getBoundingClientRect();
  const canvas = canvasRef.value.getBoundingClientRect();

  // Cache canvas position to avoid recalculating on every move
  triggerDragOffset.x = e.clientX - rect.left;
  triggerDragOffset.y = e.clientY - rect.top;
  triggerDragOffset.canvasLeft = canvas.left;
  triggerDragOffset.canvasTop = canvas.top;
  triggerDragOffset.scrollLeft = canvasRef.value.scrollLeft;
  triggerDragOffset.scrollTop = canvasRef.value.scrollTop;
};

// Handle trigger dragging
const onTriggerDrag = (e) => {
  if (!isDraggingTrigger.value || !draggedTrigger.value) return;

  // Check if mouse has moved significantly (more than 5px in any direction)
  const deltaX = Math.abs(e.clientX - triggerDragOffset.initialX);
  const deltaY = Math.abs(e.clientY - triggerDragOffset.initialY);
  if (deltaX > 5 || deltaY > 5) {
    triggerDragOffset.hasMoved = true;
  }

  // Use requestAnimationFrame for smoother animation
  if (!triggerDragOffset.rafId) {
    triggerDragOffset.rafId = requestAnimationFrame(() => {
      const newX = e.clientX - triggerDragOffset.canvasLeft + triggerDragOffset.scrollLeft - triggerDragOffset.x;
      const newY = e.clientY - triggerDragOffset.canvasTop + triggerDragOffset.scrollTop - triggerDragOffset.y;

      const triggerIndex = chain.triggers.findIndex(t => t.trigger_id === draggedTrigger.value?.trigger_id);
      if (triggerIndex !== -1) {
        chain.triggers[triggerIndex].x = Math.max(10, newX);
        chain.triggers[triggerIndex].y = Math.max(10, newY);
      }

      triggerDragOffset.rafId = null;
    });
  }
};

// Stop dragging trigger
const stopDragTrigger = () => {
  // If the mouse didn't move, treat this as a click to select the trigger
  if (!triggerDragOffset.hasMoved && draggedTrigger.value) {
    selectTrigger(draggedTrigger.value);
  }

  isDraggingTrigger.value = false;
  draggedTrigger.value = null;
  triggerDragOffset.hasMoved = false;
  if (triggerDragOffset.rafId) {
    cancelAnimationFrame(triggerDragOffset.rafId);
    triggerDragOffset.rafId = null;
  }
};

// Add a default trigger when double-clicking Start node
const addDefaultTrigger = () => {
  // Calculate position for new trigger (stack vertically with spacing)
  const existingTriggers = chain.triggers.length;
  const yOffset = existingTriggers * 150; // 150px vertical spacing between triggers

  const newTrigger = {
    trigger_id: `temp_trigger_${Date.now()}_${Math.random()}`,
    trigger_name: 'API Trigger',
    trigger_type: 'api',
    description: '',
    trigger_config: {
      input_schema: '',
      input_mappings: []
    },
    context_mapping: {
      mappings: []
    },
    x: 50,
    y: 100 + yOffset,
    active: true
  };
  chain.triggers.push(newTrigger);
  openTriggerModal(newTrigger);
};

// Get a snippet of the JSON schema for display
const getSchemaSnippet = (schema) => {
  if (!schema) return 'No schema';
  try {
    const parsed = typeof schema === 'string' ? JSON.parse(schema) : schema;
    if (parsed.type === 'object' && parsed.properties) {
      const props = Object.keys(parsed.properties);
      if (props.length <= 2) {
        return `{ ${props.join(', ')} }`;
      }
      return `{ ${props.slice(0, 2).join(', ')}, ... }`;
    }
    if (parsed.type === 'array') {
      return `[ ${parsed.items?.type || 'items'} ]`;
    }
    return parsed.type || 'object';
  } catch (e) {
    return 'Invalid schema';
  }
};

const isValidJson = (str) => {
  if (!str || !str.trim()) return false;
  try {
    JSON.parse(str);
    return true;
  } catch (e) {
    return false;
  }
};

const getApiTriggerConnectionCount = (trigger) => {
  const triggerId = `trigger-${trigger.trigger_id}`;
  return chain.connections.filter(c => c.from === triggerId).length;
};

// Get source chain name by ID
const getSourceChainName = (chainId) => {
  const sourceChain = savedChains.value.find(c => c.prompt_chain_id === chainId);
  return sourceChain ? sourceChain.name : `Chain #${chainId}`;
};

// Get hierarchy name by ID
const getHierarchyName = (htId) => {
  const hierarchies = store.state.hierarchies?.hierarchies || [];
  const hierarchy = hierarchies.find(h => h.ht_id === htId);
  return hierarchy ? hierarchy.label : `Hierarchy #${htId}`;
};

// Add input mapping for API/chain_output triggers
const addInputMapping = () => {
  if (!currentTrigger.value.trigger_config) {
    currentTrigger.value.trigger_config = {};
  }
  if (!currentTrigger.value.trigger_config.input_mappings) {
    currentTrigger.value.trigger_config.input_mappings = [];
  }
  currentTrigger.value.trigger_config.input_mappings.push({
    source: '',
    target: '',
    transform: null
  });
};

// Remove input mapping
const removeInputMapping = (index) => {
  if (currentTrigger.value.trigger_config?.input_mappings) {
    currentTrigger.value.trigger_config.input_mappings.splice(index, 1);
  }
};

// Add Excel column mapping
const addExcelColumnMapping = () => {
  if (!currentTrigger.value.trigger_config) {
    currentTrigger.value.trigger_config = {};
  }
  if (!currentTrigger.value.trigger_config.column_mappings) {
    currentTrigger.value.trigger_config.column_mappings = [];
  }
  currentTrigger.value.trigger_config.column_mappings.push({
    excel_column: '',
    chain_input_key: '',
    transform: null
  });
};

const addGroupByColumn = (trigger) => {
  if (!trigger.trigger_config) {
    trigger.trigger_config = {};
  }
  if (!trigger.trigger_config.group_by_columns) {
    trigger.trigger_config.group_by_columns = [];
  }
  trigger.trigger_config.group_by_columns.push('');
};

const addBulkGroupByProperty = () => {
  if (!selectedStep.value?.config?.chain_config?.bulk_mode) return;
  if (!selectedStep.value.config.chain_config.bulk_mode.group_by) {
    selectedStep.value.config.chain_config.bulk_mode.group_by = [];
  }
  if (!Array.isArray(selectedStep.value.config.chain_config.bulk_mode.group_by)) {
    // Migrate single string to array
    const existing = selectedStep.value.config.chain_config.bulk_mode.group_by;
    selectedStep.value.config.chain_config.bulk_mode.group_by = existing ? [existing] : [];
  }
  selectedStep.value.config.chain_config.bulk_mode.group_by.push('');
};

    // Load prompts from API
const loadPrompts = async () => {
  try {
    loadingPrompts.value = true;
    const response = await axios.get('templateBuilder/getPrompts/' + props.value);

    if (response.data && response.data.Data) {
      const prompts = response.data.Data.prompts;

      // Convert all prompts to component format
      const clientPrompts = prompts.map(prompt => {
        // Parse configuration_data if it's a string
        let configData = prompt.configuration_data;
        if (typeof configData === 'string') {
          try {
            configData = JSON.parse(configData);
          } catch (e) {
            configData = { input: [], examples: {} };
          }
        }

        // Extract inputs from configuration_data
        const inputs = (configData?.input || []).map(input => ({
          id: input.id,
          property: input.property,
          prompt: input.prompt,
          source: input.source,
          sourceField: input.sourceField,
          metadata: input.metadata || {}
        }));

        return {
          id: prompt.content_generation_prompt_id,
          itemType: 'prompt',
          name: prompt.prompt_name || 'Unnamed Prompt',
          prompt_type: prompt.prompt_type || 'other',
          system_prompt: prompt.system_prompt || '',
          user_prompt: prompt.user_prompt || '',
          temperature: prompt.temperature,
          top_p: prompt.top_p,
          max_tokens: prompt.max_tokens,
          configuration_data: configData,
          tmpl_ai_prompt_id: prompt.tmpl_ai_prompt_id,
          client_id: prompt.client_id || null,
          tmpl_id: prompt.tmpl_id || null,
          specificity: prompt.client_id ? (prompt.tmpl_id ? 'Template' : 'Client') : 'System',
          inputs: inputs
        };
      });

      // Update the client_prompts category items instead of replacing entire array
      const clientPromptsCategory = promptCategories.value.find(c => c.type === 'client_prompts');
      if (clientPromptsCategory) {
        clientPromptsCategory.items = clientPrompts;
      } else {
        // If it doesn't exist, add it at the beginning
        promptCategories.value.unshift({
          type: 'client_prompts',
          name: 'Client Prompts',
          expanded: true,
          items: clientPrompts
        });
      }
    }
  } catch (error) {
    console.error('Error loading prompts:', error);
  } finally {
    loadingPrompts.value = false;
  }
};

const mapChainData = (chain) => ({
  id: chain.prompt_chain_id,
  chain_name: chain.chain_name,
  description: chain.description || '',
  step_count: chain.step_count || 0,
  status: chain.active ? 'active' : 'inactive',
  updated_at: chain.updated_at || chain.created_at,
  prompt_chain_id: chain.prompt_chain_id,
  client_id: chain.client_id,
  tmpl_id: chain.tmpl_id || null
});

const loadChains = async () => {
  try {
    const response = await axios.get(`/externalContent/promptChains/${props.value}`);
    if (response.data.success) {
      savedChains.value = response.data.data.map(mapChainData);
    }
  } catch (error) {
    console.error('Error loading chains:', error);
  }
};

const loadSystemChains = async () => {
  try {
    const response = await axios.get('/externalContent/systemPromptChains');
    if (response.data.success) {
      systemChains.value = response.data.data.map(mapChainData);
    }
  } catch (error) {
    console.error('Error loading system chains:', error);
  }
};

const editSystemChain = async (chainItem) => {
  isSystemChainMode.value = true;
  isViewingSystemChain.value = false;
  await editChain(chainItem);
};

const viewSystemChain = async (chainItem) => {
  isSystemChainMode.value = true;
  isViewingSystemChain.value = true;
  await editChain(chainItem);
};

const copyChainToTemplate = async (chainItem) => {
  try {
    const chainId = chainItem.prompt_chain_id || chainItem.id;
    if (!chainId) {
      triggerNotification('Chain ID not found', 'error');
      return;
    }

    const response = await axios.get(`/externalContent/promptChain/${chainId}`);
    if (!response.data.success) {
      triggerNotification('Failed to load chain data', 'error');
      return;
    }

    const chainData = response.data.data;

    // Prepare a copy with new name and reset IDs
    const copyData = {
      prompt_chain_id: null,
      chain_name: `${chainData.chain_name} (Copy)`,
      description: chainData.description || '',
      tmpl_id: props.value,
      active: true,
      steps: (chainData.steps || []).map(step => ({
        prompt_chain_step_id: null,
        step_type: step.step_type || 'prompt',
        step_name: step.step_name || 'Unnamed Step',
        step_order: step.step_order || 0,
        tmpl_ai_prompt_id: step.tmpl_ai_prompt_id || null,
        service_id: step.service_id || null,
        service_endpoint_id: step.service_endpoint_id || null,
        child_chain_id: step.child_chain_id || null,
        is_final_step: step.is_final_step || false,
        is_post_process: step.is_post_process || false,
        output_formatter_id: step.output_formatter_id || null,
        continue_on_error: step.continue_on_error || false,
        active: step.active !== undefined ? step.active : true,
        configuration_data: step.configuration_data || {}
      })),
      connections: (chainData.connections || []).map(conn => ({
        from: conn.from,
        to: conn.to,
        fromPoint: conn.fromPoint || 'right',
        toPoint: conn.toPoint || 'left',
        mapping_config: conn.mapping_config || { mappings: [] }
      })),
      triggers: (chainData.triggers || []).map(trigger => ({
        trigger_id: null,
        trigger_name: trigger.trigger_name,
        trigger_type: trigger.trigger_type,
        description: trigger.description || '',
        trigger_config: trigger.trigger_config || {},
        context_mapping: trigger.context_mapping || { mappings: [] },
        conditions: trigger.conditions || { rules: [], logic: 'AND' },
        priority: trigger.priority || 100,
        active: trigger.active !== false,
        x: trigger.x || 50,
        y: trigger.y || 100
      }))
    };

    const saveResponse = await axios.post('/externalContent/savePromptChain', copyData);
    if (saveResponse.data.success) {
      triggerNotification('Chain copied to template successfully', 'success');
      await loadChains();
      chainScope.value = 'template';
    } else {
      triggerNotification('Failed to copy chain', 'error');
    }
  } catch (error) {
    console.error('Error copying chain to template:', error);
    triggerNotification('Error copying chain to template', 'error');
  }
};

const openAddServiceDialog = () => {
  // Reset form
  editingServiceId.value = null;
  Object.assign(serviceForm, {
    service_name: '',
    service_type: 'rest_api',
    description: '',
    base_url: '',
    auth_type: 'none',
    auth_config: {
      headers: []
    },
    auth_config_key: '',
    healthcheck_endpoint: '',
    healthcheck_method: 'GET',
    healthcheck_interval: 300,
    timeout: 30,
    retry_attempts: 3,
    active: 1,
    is_system: false,
    endpoints: []
  });
  showAddServiceDialog.value = true;
};

const editService = async (service) => {
  try {
    editingServiceId.value = service.service_id;

    // Load full service details including endpoints
    const response = await axios.get(`/externalContent/services/${service.service_id}/endpoints`);
    const endpoints = response.data.success ? response.data.data : [];

    // Load service details and parse auth_config
    let authConfig = { headers: [] };
    try {
      if (service.auth_config) {
        authConfig = typeof service.auth_config === 'string'
          ? JSON.parse(service.auth_config)
          : service.auth_config;
      }
    } catch (e) {
      console.error('Error parsing auth_config:', e);
    }

    // Populate form
    Object.assign(serviceForm, {
      service_name: service.name || service.service_name,
      service_type: service.service_type || 'rest_api',
      description: service.description || '',
      base_url: service.base_url,
      auth_type: service.auth_type || 'none',
      auth_config: authConfig,
      auth_config_key: service.auth_config_key || '',
      healthcheck_endpoint: service.healthcheck_endpoint || '',
      healthcheck_method: service.healthcheck_method || 'GET',
      healthcheck_interval: service.healthcheck_interval || 300,
      timeout: service.timeout || 30,
      retry_attempts: service.retry_attempts || 3,
      active: service.active || 1,
      is_system: service.is_system || false,
      endpoints: endpoints.map(ep => ({
        service_endpoint_id: ep.service_endpoint_id,
        endpoint_name: ep.endpoint_name,
        endpoint_path: ep.endpoint_path,
        http_method: ep.http_method,
        description: ep.description || '',
        input_schema: ep.input_schema || {},
        output_schema: ep.output_schema || {},
        headers: ep.headers || [],
        query_params: ep.query_params || [],
        request_example: ep.request_example || {},
        response_example: ep.response_example || {},
        active: ep.active
      }))
    });

    showAddServiceDialog.value = true;
  } catch (error) {
    console.error('Error loading service for edit:', error);
    triggerNotification('Failed to load service details', 'error');
  }
};

const deleteServiceConfirm = async (service) => {
  if (!confirm(`Are you sure you want to delete the service "${service.name}"? This will also delete all associated endpoints.`)) {
    return;
  }

  try {
    const response = await axios.post(`/externalContent/services/${service.service_id}/delete`);
    if (response.data.success) {
      triggerNotification('Service deleted successfully', 'success');
      await loadServices();
    } else {
      triggerNotification('Failed to delete service', 'error');
    }
  } catch (error) {
    console.error('Error deleting service:', error);
    triggerNotification('Error deleting service', 'error');
  }
};

const addAuthHeader = () => {
  if (!serviceForm.auth_config.headers) {
    serviceForm.auth_config.headers = [];
  }
  serviceForm.auth_config.headers.push({ key: '', value: '' });
};

const removeAuthHeader = (index) => {
  serviceForm.auth_config.headers.splice(index, 1);
};

const openEndpointEditor = (index = -1) => {
  if (index >= 0) {
    const endpoint = serviceForm.endpoints[index];
    Object.assign(currentEndpoint, {
      endpoint_name: endpoint.endpoint_name,
      endpoint_path: endpoint.endpoint_path,
      http_method: endpoint.http_method,
      description: endpoint.description || '',
      input_schema: typeof endpoint.input_schema === 'string' ? endpoint.input_schema : JSON.stringify(endpoint.input_schema, null, 2),
      output_schema: typeof endpoint.output_schema === 'string' ? endpoint.output_schema : JSON.stringify(endpoint.output_schema, null, 2),
      headers: endpoint.headers || [],
      query_params: endpoint.query_params || [],
      request_example: typeof endpoint.request_example === 'string' ? endpoint.request_example : JSON.stringify(endpoint.request_example, null, 2),
      response_example: typeof endpoint.response_example === 'string' ? endpoint.response_example : JSON.stringify(endpoint.response_example, null, 2),
      active: endpoint.active
    });
    editingEndpointIndex.value = index;
  } else {
    Object.assign(currentEndpoint, {
      endpoint_name: '',
      endpoint_path: '',
      http_method: 'POST',
      description: '',
      input_schema: '{}',
      output_schema: '{}',
      headers: [],
      query_params: [],
      request_example: '{}',
      response_example: '{}',
      active: 1
    });
    editingEndpointIndex.value = -1;
  }
  showEndpointEditor.value = true;
};

const saveEndpoint = () => {
  try {
    const endpoint = {
      endpoint_name: currentEndpoint.endpoint_name,
      endpoint_path: currentEndpoint.endpoint_path,
      http_method: currentEndpoint.http_method,
      description: currentEndpoint.description,
      input_schema: JSON.parse(currentEndpoint.input_schema),
      output_schema: JSON.parse(currentEndpoint.output_schema),
      headers: currentEndpoint.headers,
      query_params: currentEndpoint.query_params,
      request_example: JSON.parse(currentEndpoint.request_example),
      response_example: JSON.parse(currentEndpoint.response_example),
      active: currentEndpoint.active
    };

    if (editingEndpointIndex.value >= 0) {
      serviceForm.endpoints[editingEndpointIndex.value] = endpoint;
    } else {
      serviceForm.endpoints.push(endpoint);
    }

    showEndpointEditor.value = false;
  } catch (error) {
    triggerNotification('Invalid JSON in schema fields', 'error');
  }
};

const removeEndpoint = (index) => {
  serviceForm.endpoints.splice(index, 1);
};

const saveService = async () => {
  if (!serviceFormValid.value) {
    return;
  }

  savingService.value = true;
  try {
    let response;
    if (editingServiceId.value) {
      // Update existing service
      response = await axios.post(`/externalContent/services/${editingServiceId.value}`, serviceForm);
      if (response.data.success) {
        triggerNotification('Service updated successfully', 'success');
        showAddServiceDialog.value = false;
        await loadServices();
      } else {
        triggerNotification('Failed to update service: ' + response.data.error, 'error');
      }
    } else {
      // Create new service
      response = await axios.post('/externalContent/services', serviceForm);
      if (response.data.success) {
        triggerNotification('Service created successfully', 'success');
        showAddServiceDialog.value = false;
        await loadServices();
      } else {
        triggerNotification('Failed to create service: ' + response.data.error, 'error');
      }
    }
  } catch (error) {
    console.error('Error saving service:', error);
    triggerNotification(editingServiceId.value ? 'Failed to update service' : 'Failed to create service', 'error');
  } finally {
    savingService.value = false;
  }
};

const loadHookFunctions = async () => {
  try {
    const response = await axios.get('/externalContent/hookFunctions');
    if (response.data.success) {
      hookFunctions.value = response.data.data || [];
    }
  } catch (error) {
    console.error('Error loading hook functions:', error);
  }
};

const loadServices = async () => {
  try {
    const response = await axios.get('/externalContent/services');
    if (response.data.success) {
      const services = response.data.data;

      // Create hierarchical structure with endpoints
      const serviceItems = [];

      for (const service of services) {
        // Load endpoints for this service
        const endpointsResponse = await axios.get(`/externalContent/services/${service.service_id}/endpoints`);
        const endpoints = endpointsResponse.data.success ? endpointsResponse.data.data : [];

        // Add service as parent item
        const serviceItem = {
          id: `service_${service.service_id}`,
          service_id: service.service_id,
          itemType: 'service_parent',
          name: service.service_name,
          service_name: service.service_name,
          description: service.description || '',
          service_type: service.service_type || 'rest_api',
          base_url: service.base_url,
          auth_type: service.auth_type || 'none',
          auth_config: service.auth_config,
          auth_config_key: service.auth_config_key,
          healthcheck_endpoint: service.healthcheck_endpoint,
          healthcheck_method: service.healthcheck_method,
          healthcheck_interval: service.healthcheck_interval,
          retry_attempts: service.retry_attempts,
          endpoint_count: endpoints.length,
          healthcheck_status: service.active ? 'healthy' : 'inactive',
          timeout: service.timeout || 30,
          active: service.active,
          is_system: service.client_id === null || service.client_id === undefined,
          endpoints: endpoints,
          isExpanded: false
        };

        serviceItems.push(serviceItem);

        // Add endpoints as child items
        if (endpoints.length > 0) {
          for (const endpoint of endpoints) {
            serviceItems.push({
              id: `endpoint_${endpoint.service_endpoint_id}`,
              service_endpoint_id: endpoint.service_endpoint_id,
              service_id: service.service_id,
              itemType: 'service',
              name: endpoint.endpoint_name,
              endpoint_name: endpoint.endpoint_name,
              endpoint_path: endpoint.endpoint_path,
              http_method: endpoint.http_method,
              description: endpoint.description || '',
              input_schema: endpoint.input_schema || {},
              output_schema: endpoint.output_schema || {},
              base_url: service.base_url,
              timeout: service.timeout || 30,
              retry_attempts: service.retry_attempts || 3,
              parentServiceId: `service_${service.service_id}`,
              isEndpoint: true
            });
          }
        }
      }

      const servicesCategory = promptCategories.value.find(c => c.type === 'services');
      if (servicesCategory) {
        servicesCategory.items = serviceItems;
      } else {
        promptCategories.value.push({
          type: 'services',
          name: 'External Services',
          expanded: true,
          items: serviceItems
        });
      }
    }
  } catch (error) {
    console.error('Error loading services:', error);
  }
};

const toggleServiceExpanded = (serviceItem) => {
  serviceItem.isExpanded = !serviceItem.isExpanded;
};

const isServiceExpanded = (parentServiceId) => {
  const servicesCategory = promptCategories.value.find(c => c.type === 'services');
  if (!servicesCategory) return false;

  const parentService = servicesCategory.items.find(item => item.id === parentServiceId);
  return parentService ? parentService.isExpanded : false;
};

const loadChainInputs = async (chainStep) => {
  try {
    const response = await axios.get(`/externalContent/promptChain/${chainStep.child_chain_id}`);

    if (response.data.success && response.data.data) {
      const childChain = response.data.data;
      const steps = childChain.steps || [];

      // Collect all unique inputs from the first step of the child chain
      // (Typically the first step defines what the chain needs as input)
      const chainInputs = [];
      const seenProperties = new Set();

      for (const step of steps) {
        const stepConfig = step.configuration_data || {};
        const inputs = stepConfig.input || [];

        for (const input of inputs) {
          // Only include inputs from non-chain-output sources (those need to come from parent)
          if (input.source !== 'chain_output' && !seenProperties.has(input.property)) {
            chainInputs.push({
              id: chainInputs.length + 1,
              property: input.property,
              prompt: input.prompt || `Input for ${input.property}`,
              source: 'chain_output',  // Will be mapped from parent chain outputs
              sourceField: input.property,
              outputProperty: '',  // User will select which output to map
              required: input.required || false,
              metadata: {
                from_child_chain: true,
                child_chain_input: input
              }
            });
            seenProperties.add(input.property);
          }
        }
      }

      // Update the step's config with the loaded inputs
      chainStep.config.input = chainInputs;
    }
  } catch (error) {
    console.error('Error loading chain inputs:', error);
    triggerNotification('Failed to load chain input requirements', 'error');
  }
};

const loadAvailableChains = async () => {
  try {
    // Load template chains and system chains in parallel
    const [templateResponse, systemResponse] = await Promise.all([
      axios.get(`/externalContent/promptChains/${props.value}`),
      axios.get('/externalContent/systemPromptChains')
    ]);

    const mapChainItem = (c, isSystem = false) => ({
      prompt_chain_id: c.prompt_chain_id,
      id: c.prompt_chain_id,
      itemType: 'chain',
      name: c.chain_name,
      description: c.description || `Chain with ${c.step_count} steps`,
      step_count: c.step_count,
      is_system: isSystem
    });

    const templateChains = (templateResponse.data.success ? templateResponse.data.data || [] : [])
      .filter(c => c.prompt_chain_id !== chain.prompt_chain_id)
      .map(c => mapChainItem(c, false));

    const sysChains = (systemResponse.data.success ? systemResponse.data.data || [] : [])
      .filter(c => c.prompt_chain_id !== chain.prompt_chain_id)
      .map(c => mapChainItem(c, true));

    // Template chains category
    const chainsCategory = promptCategories.value.find(c => c.type === 'chains');
    if (chainsCategory) {
      chainsCategory.items = templateChains;
    } else if (templateChains.length > 0) {
      promptCategories.value.push({
        type: 'chains',
        name: 'Chains',
        expanded: true,
        items: templateChains
      });
    }

    // System chains category
    const systemChainsCategory = promptCategories.value.find(c => c.type === 'system_chains');
    if (systemChainsCategory) {
      systemChainsCategory.items = sysChains;
    } else if (sysChains.length > 0) {
      promptCategories.value.push({
        type: 'system_chains',
        name: 'System Chains',
        expanded: true,
        items: sysChains
      });
    }
  } catch (error) {
    console.error('Error loading available chains:', error);
  }
};

const onBulkModeToggle = (enabled) => {
  if (!selectedStep.value || selectedStep.value.step_type !== 'chain') return;

  if (enabled) {
    // Initialize bulk_mode structure if it doesn't exist
    if (!selectedStep.value.config.chain_config.bulk_mode) {
      selectedStep.value.config.chain_config.bulk_mode = {
        enabled: true,
        source_output: '',
        array_path: '',
        item_property_mappings: {}
      };
    }
  }
};

const getAvailableOutputs = () => {
  if (!selectedStep.value) return [];

  const outputs = [];
  const currentStepIndex = chain.steps.findIndex(s => s.id === selectedStep.value.id);

  // Iterate through all previous steps
  for (let i = 0; i < currentStepIndex; i++) {
    const step = chain.steps[i];
    const stepOutputs = step.config.output || [];

    // Add each output from this step
    stepOutputs.forEach(output => {
      outputs.push({
        text: `${step.step_name}: ${output.property}`,
        value: output.property
      });
    });
  }

  return outputs;
};

async function viewChainHistory(chainItem) {
  selectedChainForHistory.value = chainItem;
  showHistoryView.value = true;
  await loadExecutionHistory(chainItem.prompt_chain_id || chainItem.id);
}

function getExecutionTypeColor(type) {
  const colors = {
    'full': 'success',
    'regeneration': '#ED742D',
    'partial': 'warning',
    'error': 'error'
  };
  return colors[type] || 'grey';
}

function getExecutionTypeIcon(type) {
  const icons = {
    'full': 'mdi-checkbox-marked-circle',
    'regeneration': 'mdi-refresh',
    'partial': 'mdi-alert-circle',
    'error': 'mdi-close-circle'
  };
  return icons[type] || 'mdi-help-circle';
}

function getExecutionTypeLabel(type) {
  const labels = {
    'full': 'Full',
    'regeneration': 'Regen',
    'partial': 'Partial',
    'error': 'Error'
  };
  return labels[type] || 'Unknown';
}

function getStepStatusIcon(status) {
  const icons = {
    'completed': 'mdi-check-circle',
    'failed': 'mdi-close-circle',
    'skipped': 'mdi-skip-forward',
    'running': 'mdi-loading',
    'queued': 'mdi-clock'
  };
  return icons[status] || 'mdi-help-circle';
}

function getStepStatusColor(status) {
  const colors = {
    'completed': 'success',
    'failed': 'error',
    'skipped': 'warning',
    'running': 'primary',
    'queued': 'grey'
  };
  return colors[status] || 'grey';
}

function getStepStatusLabel(status) {
  const labels = {
    'completed': 'Completed',
    'failed': 'Failed',
    'skipped': 'Skipped',
    'running': 'Running',
    'queued': 'Queued'
  };
  return labels[status] || status;
}

async function loadExecutionHistory(promptChainId) {
  if (loadingHistory.value) return;
  
  try {
    loadingHistory.value = true;
    executionHistory.value = [];
    
    const response = await axios.get(`/externalContent/promptChainExecutionHistory/${promptChainId}`);
    
    if (response.data && response.data.success) {
      executionHistory.value = response.data.data.map(execution => {
        // Determine execution type
        let executionType = 'full';
        if (execution.is_regeneration) {
          executionType = 'regeneration';
        } else if (execution.execution_status === 'failed') {
          executionType = 'error';
        } else if (execution.has_skipped_steps) {
          executionType = 'partial';
        }
        
        return {
          ...execution,
          execution_type: executionType,
          started_at: new Date(execution.started_at),
          completed_at: execution.completed_at ? new Date(execution.completed_at) : null,
          duration: execution.completed_at && execution.started_at 
            ? Math.round((new Date(execution.completed_at) - new Date(execution.started_at)) / 1000)
            : null
        };
      });
      
    } else {
      executionHistory.value = [];
    }
  } catch (error) {
    executionHistory.value = [];
    triggerNotification('Error loading execution history: ' + error.message, 'error');
  } finally {
    loadingHistory.value = false;
  }
}

async function viewExecutionDetails(execution) {
  selectedExecution.value = execution;
  showExecutionDetails.value = true;
  await loadExecutionSteps(execution.execution_id);
}

async function loadExecutionSteps(executionId) {
  if (loadingExecutionSteps.value) return;
  
  try {
    loadingExecutionSteps.value = true;
    executionSteps.value = [];
    
    const response = await axios.get(`/externalContent/promptChainExecutionSteps/${executionId}`);
        
    if (response.data && response.data.success) {
      executionSteps.value = response.data.data.map(step => {
        // The backend is returning input_data and output_data as the parsed field names
        // Use these instead of inputs/outputs
        let inputs = step.input_data || {};
        let outputs = step.output_data || {};

        // Handle other JSON fields
        let requestPayload = step.request_payload;
        let responsePayload = step.response_payload;
        let configurationData = step.configuration_data;

        const processedStep = reactive({
          ...step,
          started_at: step.started_at ? new Date(step.started_at) : null,
          completed_at: step.completed_at ? new Date(step.completed_at) : null,
          duration: step.duration || (step.completed_at && step.started_at
            ? Math.round((new Date(step.completed_at) - new Date(step.started_at)) / 1000)
            : null),
          inputs: inputs,
          outputs: outputs,
          request_payload: requestPayload,
          response_payload: responsePayload,
          configuration_data: configurationData,
          // Add raw_response for prompt steps - try different sources
          raw_response: step.step_type === 'prompt' ? (
            responsePayload ||
            outputs?.result ||
            outputs?.response ||
            step.response_payload
          ) : null,
          // Version and model tracking
          prompt_version: step.prompt_version,
          current_version: step.current_version,
          model_used: step.model_used,
          // Prompt content (may be historical)
          system_prompt: step.system_prompt,
          user_prompt: step.user_prompt,
          prompt_type: step.prompt_type,
          // Initialize activeTab for tabs
          activeTab: 0,
          // Initialize expansion states for inputs and outputs
          ...initializeExpansionStates(inputs, outputs)
        });

        return processedStep;
      });
      
    } else {
      executionSteps.value = [];
    }
  } catch (error) {
    executionSteps.value = [];
    triggerNotification('Error loading execution steps: ' + error.message, 'error');
  } finally {
    loadingExecutionSteps.value = false;
  }
}

const getChildTemplateParts = (templatePartId) => {
  if (!templatePartId || !availableTemplateParts.value) return [];

  const templatePart = availableTemplateParts.value.find(tp => tp.tp_id === templatePartId);
  return templatePart ? templatePart.child_parts : [];
};

// Helper function to initialize expansion states for inputs and outputs
function initializeExpansionStates(inputs, outputs) {
  const expansionStates = {};
  
  // Initialize expansion states for inputs
  if (inputs && typeof inputs === 'object') {
    Object.keys(inputs).forEach(key => {
      expansionStates[`showFullInput_${key}`] = false;
    });
  }
  
  // Initialize expansion states for outputs
  if (outputs && typeof outputs === 'object') {
    Object.keys(outputs).forEach(key => {
      expansionStates[`showFullOutput_${key}`] = false;
    });
  }
  
  return expansionStates;
}

function backToHistoryList() {
  showExecutionDetails.value = false;
  selectedExecution.value = null;
  executionSteps.value = [];
}

function closeHistoryView() {
  showHistoryView.value = false;
  selectedChainForHistory.value = null;
  executionHistory.value = [];
  selectedExecution.value = null;
  showExecutionDetails.value = false;
  executionSteps.value = [];
}

// Report view functions
async function viewChainReport(chainItem) {
  selectedChainForReport.value = chainItem;
  selectedExecutionForReport.value = null;
  showReportView.value = true;
  showHistoryView.value = false;
  showLandingPage.value = true; // Keep landing page state, just show report
  await loadChainReportData(chainItem.prompt_chain_id || chainItem.id);
}

async function viewExecutionReport(execution) {
  selectedExecutionForReport.value = execution;
  selectedChainForReport.value = selectedChainForHistory.value; // Keep reference to chain

  // First load the chain report data if not already loaded
  if (!reportData.value) {
    await loadChainReportData(execution.prompt_chain_id);
  }

  // Then load execution-specific data
  await loadExecutionReportData(execution.execution_id);

  // Switch to report view
  showReportView.value = true;
  showHistoryView.value = false;
  showExecutionDetails.value = false;
}

async function loadChainReportData(promptChainId) {
  if (loadingReportData.value) return;

  try {
    loadingReportData.value = true;
    reportData.value = null;

    const response = await axios.get(`/externalContent/promptChainReport/${promptChainId}`);

    if (response.data && response.data.success) {
      reportData.value = response.data.data;
    } else {
      reportData.value = null;
    }
  } catch (error) {
    console.error('Error loading chain report data:', error);
    reportData.value = null;
  } finally {
    loadingReportData.value = false;
  }
}

async function loadExecutionReportData(executionId) {
  if (loadingReportData.value) return;

  try {
    loadingReportData.value = true;

    const response = await axios.get(`/externalContent/executionReport/${executionId}`);

    if (response.data && response.data.success) {
      // Merge execution-specific report data into existing reportData
      if (!reportData.value) {
        reportData.value = {};
      }
      reportData.value.execution_specific = response.data.data;
    }
  } catch (error) {
    console.error('Error loading execution report data:', error);
  } finally {
    loadingReportData.value = false;
  }
}

// Watch for expanded trend rows to load failed execution details
watch(expandedTrendRows, async (newExpanded, oldExpanded) => {
  const newlyExpanded = newExpanded.filter(row => !oldExpanded.includes(row));

  for (const row of newlyExpanded) {
    const date = row.date;
    if (!trendDetails.value[date] && !loadingTrendDetails.value[date]) {
      // Set loading state with new object reference to trigger reactivity
      loadingTrendDetails.value = { ...loadingTrendDetails.value, [date]: true };

      try {
        const response = await axios.get(
          `/externalContent/promptChainReportFailedExecutions/${selectedChainForReport.value.prompt_chain_id || selectedChainForReport.value.id}`,
          { params: { date } }
        );

        if (response.data && response.data.success) {
          // Set data with new object reference to trigger reactivity
          trendDetails.value = { ...trendDetails.value, [date]: response.data.data };
        }
      } catch (error) {
        console.error('Error loading failed executions:', error);
        // Set empty array with new object reference
        trendDetails.value = { ...trendDetails.value, [date]: [] };
      } finally {
        // Clear loading state with new object reference
        loadingTrendDetails.value = { ...loadingTrendDetails.value, [date]: false };
      }
    }
  }
});

// Watch for expanded validation rows to load validation error details
watch(expandedValidationRows, async (newExpanded, oldExpanded) => {
  const newlyExpanded = newExpanded.filter(row => !oldExpanded.includes(row));

  for (const row of newlyExpanded) {
    const stepName = row.step_name;
    if (!validationDetails.value[stepName] && !loadingValidationDetails.value[stepName]) {
      // Initialize filter and grouping defaults for this step
      if (validationFilters.value[stepName] === undefined) {
        validationFilters.value = { ...validationFilters.value, [stepName]: 0 };
      }
      if (validationGrouping.value[stepName] === undefined) {
        validationGrouping.value = { ...validationGrouping.value, [stepName]: 0 };
      }

      // Set loading state with new object reference to trigger reactivity
      loadingValidationDetails.value = { ...loadingValidationDetails.value, [stepName]: true };

      try {
        const response = await axios.get(
          `/externalContent/promptChainReportValidationErrors/${selectedChainForReport.value.prompt_chain_id || selectedChainForReport.value.id}`,
          { params: { step_name: stepName } }
        );

        if (response.data && response.data.success) {
          // Set data with new object reference to trigger reactivity
          validationDetails.value = { ...validationDetails.value, [stepName]: response.data.data };
        }
      } catch (error) {
        console.error('Error loading validation errors:', error);
        // Set empty array with new object reference
        validationDetails.value = { ...validationDetails.value, [stepName]: [] };
      } finally {
        // Clear loading state with new object reference
        loadingValidationDetails.value = { ...loadingValidationDetails.value, [stepName]: false };
      }
    }
  }
});

function closeReportView() {
  showReportView.value = false;

  // If we have an execution report, go back to execution details
  if (selectedExecutionForReport.value && selectedExecution.value) {
    showHistoryView.value = true;
    showExecutionDetails.value = true;
  } else {
    // Otherwise go back to landing page
    showLandingPage.value = true;
  }

  selectedChainForReport.value = null;
  selectedExecutionForReport.value = null;
  reportData.value = null;
  reportTab.value = 0;

  // Clear drilldown data
  expandedTrendRows.value = [];
  expandedValidationRows.value = [];
  trendDetails.value = {};
  validationDetails.value = {};
  loadingTrendDetails.value = {};
  loadingValidationDetails.value = {};
  validationFilters.value = {};
  validationGrouping.value = {};
}

function drilldownPromptVersion(item) {
  // Future enhancement: Show detailed drilldown for specific prompt version
  console.log('Drilldown prompt version:', item);
}

function drilldownModel(item) {
  // Future enhancement: Show detailed drilldown for specific model
  console.log('Drilldown model:', item);
}

function getStatusColor(status) {
  const colors = {
    'completed': 'success',
    'failed': 'error',
    'running': 'primary',
    'queued': 'warning'
  };
  return colors[status] || 'grey';
}

function getStatusIcon(status) {
  const icons = {
    'completed': 'mdi-check-circle',
    'failed': 'mdi-alert-circle',
    'running': 'mdi-loading',
    'queued': 'mdi-clock'
  };
  return icons[status] || 'mdi-help-circle';
}

function formatDuration(seconds) {
  if (seconds === null || seconds === undefined) return 'N/A';
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}m ${remainingSeconds}s`;
}

function formatDateTime(dateString) {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleString();
  } catch (e) {
    return dateString;
  }
} 

function formatTime(dateString) {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleTimeString();
  } catch (e) {
    return dateString;
  }
}

function getFilteredValidationDetails(stepName) {
  const details = validationDetails.value[stepName] || [];
  const filterMode = validationFilters.value[stepName] || 0;

  if (filterMode === 1) {
    // Only show items with retry_count > 0
    return details.filter(d => d.retry_count > 0);
  }

  return details; // Show all
}

function getGroupedValidationDetails(stepName) {
  const filtered = getFilteredValidationDetails(stepName);
  const grouped = {};

  filtered.forEach(detail => {
    const date = formatDate(detail.execution_date);
    if (!grouped[date]) {
      grouped[date] = [];
    }
    grouped[date].push(detail);
  });

  // Sort groups by date descending
  const sortedGrouped = {};
  Object.keys(grouped)
    .sort((a, b) => new Date(b) - new Date(a))
    .forEach(key => {
      sortedGrouped[key] = grouped[key];
    });

  return sortedGrouped;
}

function truncateText(text, maxLength = 80) {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
}

const updateDocumentMapping = (index, field, value) => {
  if (field === 'stepDataId') {
    // Parse the stepDataId to populate other fields
    const stepData = getAvailableStepData().find(item => item.id === value);
    if (stepData) {
      documentFormatterMappings.value[index].stepId = stepData.stepId;
      documentFormatterMappings.value[index].stepName = stepData.stepName;
      documentFormatterMappings.value[index].property = stepData.property;
      documentFormatterMappings.value[index].type = stepData.type;
    }
  }
  
  if (field === 'templatePartId') {
    // Reset child part and delimiter fields when template part changes
    documentFormatterMappings.value[index].childTemplatePartId = '';
    documentFormatterMappings.value[index].childTemplatePart = null;
    documentFormatterMappings.value[index].attributeId = '';
    documentFormatterMappings.value[index].attr_list = [];
    
    // Set the template part object in the correct format
    if (value && typeof value === 'object') {
      documentFormatterMappings.value[index].templatePart = value;
      documentFormatterMappings.value[index].has_child_part = value.child_parts_count > 0;
      documentFormatterMappings.value[index].map_child_part = false;
      documentFormatterMappings.value[index].delimiter = value.cardinality === 0 ? '||' : '';
      
      // Set tag_type if template part is linked to a skills tag
      if (value.isTagPart && value.tag_type_id) {
        documentFormatterMappings.value[index].tag_type = value.tag_type_id;
        documentFormatterMappings.value[index].uses_tags = true;
      } else {
        documentFormatterMappings.value[index].tag_type = null;
        documentFormatterMappings.value[index].uses_tags = false;
      }
      
      // Set tmpl_part object in backend format
      documentFormatterMappings.value[index].tmpl_part = {
        label: value.name,
        text: value.name,
        value: value.name,
        tmpl_part_id: value.tp_id,
        tmpl_id: props.value,
        sequence: value.sequence
      };
      documentFormatterMappings.value[index].tmpl_part_id = value.tp_id;
      
      // Copy ALL attributes from this template part with full structure
      if (value.attributes && value.attributes.length > 0) {
        documentFormatterMappings.value[index].attr_list = value.attributes.map((attr, idx) => ({
          // Copy the full attribute structure from template part
          ...attr,
          // Add mapping-specific fields
          source_index: idx,
          mapped: false // Default to unmapped
        }));
      }
    }
  }
  
  if (field === 'childTemplatePartId') {
    // Reset attribute field when child template part changes
    documentFormatterMappings.value[index].attributeId = '';
    documentFormatterMappings.value[index].attr_list = [];
    
    // Set child part in the correct format
    if (value && typeof value === 'object') {
      documentFormatterMappings.value[index].childTemplatePart = value;
      documentFormatterMappings.value[index].map_child_part = true;
      documentFormatterMappings.value[index].child_part_delimiter = value.cardinality === 0 ? '|' : '';
      
      // Set tag_type if child template part is linked to a skills tag
      if (value.isTagPart && value.tag_type_id) {
        documentFormatterMappings.value[index].tag_type = value.tag_type_id;
        documentFormatterMappings.value[index].uses_tags = true;
      } else {
        documentFormatterMappings.value[index].tag_type = null;
        documentFormatterMappings.value[index].uses_tags = false;
      }
      
      // Set child_part object in backend format
      documentFormatterMappings.value[index].child_part = {
        tp_name: value.name,
        tp_id: value.tp_id
      };
      
      // Copy ALL attributes from this child template part with full structure
      if (value.attributes && value.attributes.length > 0) {
        documentFormatterMappings.value[index].attr_list = value.attributes.map((attr, idx) => ({
          // Copy the full attribute structure from template part
          ...attr,
          // Add mapping-specific fields
          source_index: idx,
          mapped: false // Default to unmapped
        }));
      }
    }
  }
  
  // Handle delimiter fields
  if (field === 'parentDelimiter') {
    documentFormatterMappings.value[index].delimiter = value;
  }
  
  if (field === 'parentSecondaryDelimiter') {
    documentFormatterMappings.value[index].parent_secondary_delimiter = value;
    // Also store in split_value for backend processing
    documentFormatterMappings.value[index].split_value = value;
  }
  
  if (field === 'childDelimiter') {
    documentFormatterMappings.value[index].child_part_delimiter = value;
  }
  
  if (field === 'childSecondaryDelimiter') {
    documentFormatterMappings.value[index].child_secondary_delimiter = value;
  }

  if (field === 'uses_tag_rating') {
    documentFormatterMappings.value[index].uses_tag_rating = value;
    // Clear the delimiter if unchecked
    if (!value) {
      documentFormatterMappings.value[index].tag_rating_delimiter = '';
    }
  }

  if (field === 'tag_rating_delimiter') {
    documentFormatterMappings.value[index].tag_rating_delimiter = value;
  }

  // Handle JSON mode fields
  if (field === 'use_json_object') {
    documentFormatterMappings.value[index].use_json_object = value;
  }

  if (field === 'json_primary_property') {
    documentFormatterMappings.value[index].json_primary_property = value;
  }

  if (field === 'json_child_property') {
    documentFormatterMappings.value[index].json_child_property = value;
  }

  if (field === 'json_tag_property') {
    documentFormatterMappings.value[index].json_tag_property = value;
  }

  if (field === 'json_rating_property') {
    documentFormatterMappings.value[index].json_rating_property = value;
  }

  if (field === 'json_mapping_mode') {
    documentFormatterMappings.value[index].json_mapping_mode = value;

    if (value === 'map_properties') {
      if (!documentFormatterMappings.value[index].json_property_mappings) {
        documentFormatterMappings.value[index].json_property_mappings = [
          { json_property: '', attribute_id: null }
        ];
      }
      documentFormatterMappings.value[index].json_target_attribute_id = null;
      documentFormatterMappings.value[index].attr_type = 'attr_split';
    } else if (value === 'full_object') {
      documentFormatterMappings.value[index].json_property_mappings = [];
      documentFormatterMappings.value[index].attr_type = 'attr_full';
    }
  }

  if (field === 'json_target_attribute_id') {
    documentFormatterMappings.value[index].json_target_attribute_id = value;
  }

  if (field === 'mappingTargetType') {
    documentFormatterMappings.value[index].mappingTargetType = value;
  }

  if (field === 'hierarchyValueId') {
    documentFormatterMappings.value[index].hierarchyValueId = value;
  }

  if (field === 'attributeId') {
    documentFormatterMappings.value[index].attributeId = value;
    
    // Update tpa_map for backend saving
    documentFormatterMappings.value[index].tpa_map = value;
    
    // Set attr_type to indicate full value mapping
    documentFormatterMappings.value[index].attr_type = 'attr_full';
  }
  
  if (field === 'parentMappingMode') {
    documentFormatterMappings.value[index].parentMappingMode = value;
    
    // Update attr_type and split_content based on mapping mode
    if (value === 'full_value') {
      documentFormatterMappings.value[index].attr_type = 'attr_full';
      documentFormatterMappings.value[index].split_content = false;
    } else if (value === 'map_parts') {
      documentFormatterMappings.value[index].attr_type = 'attr_split';
      documentFormatterMappings.value[index].split_content = true;
      // Clear single attribute selection when switching to map_parts
      documentFormatterMappings.value[index].attributeId = '';
      documentFormatterMappings.value[index].tpa_map = null;
    }
  }
  
  documentFormatterMappings.value[index][field] = value;
};

const loadTemplateParts = async () => {
  try {
    loadingTemplateParts.value = true;
    const response = await axios.get(`/templateBuilder/templateDetails/${props.value}`);
    
    if (response.data && response.data.Data) {
      const parts = response.data.Data.parts.filter(x => x.tp_active);
      
      // First pass: create the basic structure
      availableTemplateParts.value = parts.map((tp,idx) => ({
        tp_id: tp.tp_id,
        name: tp.tp_name,
        parent_tp_id: tp.parent_tp_id,
        cardinality: tp.cardinality,
        attributes: tp.attributes || [],
        child_parts: [], // Will be populated in second pass
        child_parts_count: 0, // Will be calculated in second pass
        isTagPart: tp.isTagPart,
        tag_type_id: tp.isTagPart ? tp.attributes[0]?.tag_type_id : null,
        sequence: idx + 1
      }));

      // Second pass: populate child parts and calculate counts
      availableTemplateParts.value.forEach(part => {
        // Find all child parts for this template part
        part.child_parts = availableTemplateParts.value.filter(p => p.parent_tp_id === part.tp_id);
        part.child_parts_count = part.child_parts.length;
      });
    }
  } catch (error) {
    console.error('Error loading template parts:', error);
    triggerNotification('Error loading template parts: ' + error.message, 'error');
  } finally {
    loadingTemplateParts.value = false;
  }
};

function computeMappingIdFor(mappingIndex, sourceRefType, sourceRefId, formatterRefType, formatterRefId) {
  // Prefer source reference (type:id), else fall back to formatter's ref so id is deterministic
  const baseRef = (sourceRefType && sourceRefId) ? `${sourceRefType}:${sourceRefId}` :
                  (formatterRefType && formatterRefId) ? `${formatterRefType}:${formatterRefId}` :
                  `local:${selectedStep.value?.id || 'unknown'}`;
  return `${baseRef}:map:${mappingIndex + 1}`;
}



const openDocumentFormatterDialog = async () => {
  if (!selectedStep.value) return;
  
  // Load template parts first
  await loadTemplateParts();

  // Initialize mappings from existing config
  const existingMappings = selectedStep.value.config.formatter_config?.document_mappings || [];
  
  // Reset collapsed mappings state
  collapsedMappings.value = new Set();
  
  // Determine formatter stable ref for fallback id generation
  const formatterRefType = selectedStep.value.step_type === 'output_formatter' ? 'formatter'
                         : selectedStep.value.step_type === 'prompt' ? 'prompt'
                         : selectedStep.value.step_type === 'service' ? 'service'
                         : 'step';
  const formatterRefId = selectedStep.value.output_formatter_id || selectedStep.value.tmpl_ai_prompt_id || selectedStep.value.service_id || selectedStep.value.id || `local_${Date.now()}`;
  
  // Wait for next tick to ensure getAvailableStepData has current step data
  nextTick(() => {
    try {
    const availableStepData = getAvailableStepData();

    documentFormatterMappings.value = existingMappings.map((mapping, index) => {
      // Generate a stable ID for each mapping using source ref if available, else formatter ref
      // If the mapping already has an id that follows expected pattern, use it
      let mappingId = mapping.id;
      if (!mappingId || mappingId.startsWith('mapping_')) {
        const srcRefType = mapping.stepRefType || null;
        const srcRefId = mapping.stepRefId || mapping.stepId || null;
        mappingId = computeMappingIdFor(index, srcRefType, srcRefId, formatterRefType, formatterRefId);
      }
      
      // Try to find match by stable refs or by UI step id + property
      // Priority: 1) exact stepDataId match (already encodes type+property), 2) type+property + ref match
      const matchingStepData = availableStepData.find(item => {
        const matchByStepDataId = mapping.stepDataId && item.id && String(item.id) === String(mapping.stepDataId);
        if (matchByStepDataId) return true;
        const sameTypeAndProp = item.type === mapping.type && item.property === mapping.property;
        const matchByStepId = mapping.stepId && item.stepId && String(item.stepId) === String(mapping.stepId);
        const matchByRef = mapping.stepRefId && item.stepRefId && String(item.stepRefId) === String(mapping.stepRefId);
        return sameTypeAndProp && (matchByStepId || matchByRef);
      });

      if (mapping.mappingTargetType === 'hierarchy_value') {
        // Hierarchy value mapping restoration
        return {
          id: mappingId,

          // Step data reference
          stepDataId: matchingStepData ? matchingStepData.id : (mapping.stepDataId || ''),
          stepId: matchingStepData ? matchingStepData.stepId : (mapping.stepId || ''),
          stepRefType: matchingStepData ? matchingStepData.stepRefType : (mapping.stepRefType || null),
          stepRefId: matchingStepData ? matchingStepData.stepRefId : (mapping.stepRefId || null),
          stepName: mapping.stepName,
          property: mapping.property,
          type: mapping.type,

          // Mapping target configuration
          mappingTargetType: 'hierarchy_value',
          hierarchyValueType: mapping.hierarchyValueType,
          hierarchyValueLevel: mapping.hierarchyValueLevel || 0,

          // Template part fields (empty for hierarchy mappings)
          templatePart: null,
          childTemplatePart: null,
          attributeId: '',
          parentMappingMode: 'full_value',
          childMappingMode: 'full_value',

          // Backend format fields (empty/default for hierarchy mappings)
          tmpl_part: null,
          delimiter: '',
          parent_secondary_delimiter: '',
          child_secondary_delimiter: '',
          uses_essential_flag: 0,
          default_essential_value: null,
          child_part: null,
          has_child_part: false,
          map_child_part: false,
          child_part_delimiter: '',
          uses_tags: false,
          tag_type: null,
          attr_list: [],
          attr_type: null,
          tpa_map: null,
          tmpl_part_id: null,
          parentSecondaryDelimiter: '',
          childSecondaryDelimiter: '',

          // JSON mode fields
          use_json_object: mapping.use_json_object || false,
          json_primary_property: mapping.json_primary_property || '',
          json_child_property: mapping.json_child_property || '',
          json_tag_property: mapping.json_tag_property || '',
          json_rating_property: mapping.json_rating_property || '',
          json_mapping_mode: mapping.json_mapping_mode || 'full_object',
          json_target_attribute_id: mapping.json_target_attribute_id || null,
          json_property_mappings: mapping.json_property_mappings || [],

          // Active/Inactive status
          is_active: mapping.is_active !== undefined ? mapping.is_active : true
        };
      } else {
        // Template part mapping restoration (existing logic)
        // Find template part from loaded data
        const templatePart = availableTemplateParts.value.find(tp => tp.tp_id === mapping.tmpl_part_id);
        let childTemplatePart = null;
        
        // Find child template part if it exists
        if (mapping.child_part && mapping.child_part.tp_id) {
          childTemplatePart = availableTemplateParts.value.find(tp => tp.tp_id === mapping.child_part.tp_id);
          
          // If not found in top level, look in child parts of template part
          if (!childTemplatePart && templatePart && templatePart.child_parts) {
            childTemplatePart = templatePart.child_parts.find(cp => cp.tp_id === mapping.child_part.tp_id);
          }
        }
        
        // Determine correct mapping modes based on saved data
        let parentMappingMode = 'full_value';
        let childMappingMode = 'full_value';
        let attributeId = '';
        
        if (mapping.attr_list && mapping.attr_list.length > 0) {
          if (!mapping.map_child_part) {
            if (mapping.attr_type === 'attr_full') {
              parentMappingMode = 'full_value';
              attributeId = mapping.tpa_map || '';
            } else if (mapping.attr_type === 'attr_split') {
              parentMappingMode = 'map_parts';
            }
          } else {
            if (mapping.attr_type === 'attr_full') {
              childMappingMode = 'full_value';
              attributeId = mapping.tpa_map || '';
            } else if (mapping.attr_type === 'attr_split') {
              childMappingMode = 'map_parts';
            }
          }
        } else if (mapping.tpa_map) {
          // Fallback: if no attr_list but tpa_map exists, restore it
          attributeId = mapping.tpa_map;
          parentMappingMode = 'full_value';
        }
        
        return {
          id: mappingId,

          // Backend format fields (required for saving)
          tmpl_part: mapping.tmpl_part || null,
          delimiter: mapping.delimiter || '',
          parent_secondary_delimiter: mapping.parent_secondary_delimiter || '',
          child_secondary_delimiter: mapping.child_secondary_delimiter || '',
          split_value: mapping.split_value || mapping.parent_secondary_delimiter || '',
          split_content: mapping.split_content !== undefined ? mapping.split_content : (parentMappingMode === 'map_parts'),
          uses_essential_flag: mapping.uses_essential_flag || 0,
          default_essential_value: mapping.default_essential_value || null,
          child_part: mapping.child_part || null,
          has_child_part: mapping.has_child_part || false,
          map_child_part: mapping.map_child_part || false,
          child_part_delimiter: mapping.child_part_delimiter || '',
          uses_tags: mapping.uses_tags || false,
          tag_type: mapping.tag_type || null,
          uses_tag_rating: mapping.uses_tag_rating || false,
          tag_rating_delimiter: mapping.tag_rating_delimiter || '',
          tag_limit: mapping.tag_limit || null,
          attr_list: mapping.attr_list || [],
          attr_type: mapping.attr_type || null,
          tpa_map: mapping.tpa_map || null,
          tmpl_part_id: mapping.tmpl_part_id,

          // JSON mode fields
          use_json_object: mapping.use_json_object || false,
          json_primary_property: mapping.json_primary_property || '',
          json_child_property: mapping.json_child_property || '',
          json_tag_property: mapping.json_tag_property || '',
          json_rating_property: mapping.json_rating_property || '',
          json_mapping_mode: mapping.json_mapping_mode || 'full_object',
          json_target_attribute_id: mapping.json_target_attribute_id || null,
          json_property_mappings: mapping.json_property_mappings || [],

          // Step data reference
          stepDataId: matchingStepData ? matchingStepData.id : (mapping.stepDataId || ''),
          stepId: matchingStepData ? matchingStepData.stepId : (mapping.stepId || ''),
          stepRefType: matchingStepData ? matchingStepData.stepRefType : (mapping.stepRefType || null),
          stepRefId: matchingStepData ? matchingStepData.stepRefId : (mapping.stepRefId || null),
          stepName: mapping.stepName,
          property: mapping.property,
          type: '' || (matchingStepData ? matchingStepData.type : ''), // Use type from step data if available

          // Mapping target configuration
          mappingTargetType: mapping.mappingTargetType || 'template_part',
          hierarchyValueType: mapping.hierarchyValueType || null,
          hierarchyValueLevel: mapping.hierarchyValueLevel || 0,

          // UI helper fields for editing
          templatePart: templatePart || null,
          childTemplatePart: childTemplatePart || null,
          attributeId: attributeId,

          // Correctly restored mapping modes
          parentMappingMode: parentMappingMode,
          childMappingMode: childMappingMode,

          // Restore delimiter fields
          parentSecondaryDelimiter: mapping.parent_secondary_delimiter || '',
          childSecondaryDelimiter: mapping.child_secondary_delimiter || '',

          // Active/Inactive status
          is_active: mapping.is_active !== undefined ? mapping.is_active : true,
          sequence: templatePart?.sequence || 0
        };
      }
    });
    } catch (error) {
      console.error('Error initializing document formatter mappings:', error);
    }
  });

  showDocumentFormatterDialog.value = true;
};

const saveDocumentMappings = () => {
  if (!selectedStep.value) return;

  const stepIndex = chain.steps.findIndex(s => s.id === selectedStep.value.id);
  if (stepIndex !== -1) {
    // Build helper map of available step data to resolve refs
    const availableStepData = getAvailableStepData();

    // Filter out incomplete mappings and save in exact backend format
    const validMappings = documentFormatterMappings.value
      .filter(mapping => mapping.stepDataId || mapping.stepId || mapping.stepRefId)
      .map((mapping, idx) => {
        // Find the selected stepData entry if user picked from UI list
        const selected = availableStepData.find(item => item.id === mapping.stepDataId);

        // Resolve step identifiers for saving: prefer stable refId when available
        const resolvedStepId = selected ? selected.stepId : (mapping.stepId || null);
        const resolvedRefType = selected ? selected.stepRefType : (mapping.stepRefType || null);
        const resolvedRefId = selected ? selected.stepRefId : (mapping.stepRefId || null);

        // Ensure tpa_map is set if attributeId is set
        const tpaMap = mapping.attributeId || mapping.tpa_map || mapping.json_target_attribute_id|| null;
        
        // Determine attr_type based on mapping mode and attribute selection
        let attrType = null;
        if (tpaMap && (mapping.parentMappingMode === 'full_value' || mapping.json_mapping_mode === 'full_object')) {
          attrType = 'attr_full';
        } else if (mapping.parentMappingMode === 'map_parts' || mapping.json_property_mappings.length > 0) {
          attrType = 'attr_split';
        } else if (tpaMap && (mapping.childMappingMode === 'full_value'|| mapping.json_mapping_mode === 'full_object')) {
          attrType = 'attr_full';
        } else if (mapping.childMappingMode === 'map_parts') {
          attrType = 'attr_split';
        }
        
        return {
          // Keep both UI stepId and stable ref so backend or later restore can use either
          stepId: resolvedStepId,
          stepName: mapping.stepName,
          property: mapping.property,
          type: mapping.type,
          stepDataId: mapping.stepDataId || (selected ? selected.id : null),
          stepRefType: resolvedRefType,
          stepRefId: resolvedRefId,

          // Backend format fields - include ALL fields including attr_type and tpa_map
          tmpl_part: mapping.tmpl_part,
          delimiter: mapping.delimiter || '',
          parent_secondary_delimiter: mapping.parent_secondary_delimiter || '',
          child_secondary_delimiter: mapping.child_secondary_delimiter || '',
          split_value: mapping.split_value || mapping.parent_secondary_delimiter || '',
          split_content: mapping.split_content !== undefined ? mapping.split_content : (mapping.parentMappingMode === 'map_parts'),
          uses_essential_flag: mapping.uses_essential_flag || 0,
          default_essential_value: mapping.default_essential_value || null,
          child_part: mapping.child_part || null,
          has_child_part: mapping.has_child_part || false,
          map_child_part: mapping.map_child_part || false,
          child_part_delimiter: mapping.child_part_delimiter || '',
          uses_tags: mapping.uses_tags || false,
          tag_type: mapping.tag_type || null,
          uses_tag_rating: mapping.uses_tag_rating || false,
          tag_rating_delimiter: mapping.tag_rating_delimiter || '',
          tag_limit: mapping.tag_limit || null,
          attr_list: mapping.attr_list || [],
          attr_type: attrType,
          tpa_map: tpaMap,
          tmpl_part_id: mapping.tmpl_part_id,
          mappingTargetType: mapping.mappingTargetType || null,
          hierarchyValueType: mapping.hierarchyValueType || null,
          hierarchyValueLevel: mapping.hierarchyValueLevel || 0,

          // JSON mode fields
          use_json_object: mapping.use_json_object || false,
          json_primary_property: mapping.json_primary_property || '',
          json_child_property: mapping.json_child_property || '',
          json_tag_property: mapping.json_tag_property || '',
          json_rating_property: mapping.json_rating_property || '',
          json_mapping_mode: mapping.json_mapping_mode || 'full_object',
          json_target_attribute_id: mapping.json_target_attribute_id || null,
          json_property_mappings: mapping.json_property_mappings || [],

          // Active/Inactive status
          is_active: mapping.is_active !== undefined ? mapping.is_active : true,
          sequence: mapping.sequence
        };
      });

    chain.steps[stepIndex].config.formatter_config.document_mappings = validMappings;

    // Force reactivity update
    selectedStep.value = { ...chain.steps[stepIndex] };
  }

  showDocumentFormatterDialog.value = false;
  triggerNotification('Document mappings saved successfully', 'success');
};

const getAvailableHrLevel = (ht_id) => { 
  if (!ht_id) return [];

  const hr = store.state.hierarchies?.hierarchies || [];
  let item = hr.find(x => x.ht_id === ht_id);
  if(item){
    return item.levels
  }
}


const getAvailableStepData = () => {
  if (!selectedStep.value) return [];

  const stepData = [];
  
  // Get all previous steps
  const previousSteps = chain.steps.filter(s => s.id !== selectedStep.value.id);

  previousSteps.forEach(step => {
    // Determine a stable reference for the step (prefer prompt id / formatter id / service id)
    const refType = step.step_type === 'prompt' ? 'prompt'
                  : step.step_type === 'output_formatter' ? 'formatter'
                  : step.step_type === 'service' ? 'service'
                  : 'step';
    const refId = step.tmpl_ai_prompt_id || step.output_formatter_id || step.service_id || step.id;

    // Get step order for display
    const stepOrder = step.step_order === 999 ? '?' : step.step_order;
    
    // Add inputs
    const inputs = step.config?.input || [];
    inputs.forEach(input => {
      if (input.property) {
        stepData.push({
          id: `${refType}:${refId}:input:${input.property}`, // stable id including ref
          type: 'input',
          stepId: step.id,                // UI id (may change after save)
          stepRefType: refType,           // stable ref type
          stepRefId: refId,               // stable ref id
          stepName: step.step_name,
          stepOrder: stepOrder,
          property: input.property,
          source: input.source,
          displayName: `Step ${stepOrder ? stepOrder : '-'}: ${step.step_name} → Input: ${input.property}`,
          shortName: `${input.property}`,
          category: `Step ${stepOrder ? stepOrder : '-'}: ${step.step_name}`
        });
      }
    });

    // Add outputs
    const outputs = step.outputs || ['result'];
    outputs.forEach(output => {
      stepData.push({
        id: `${refType}:${refId}:output:${output}`, // stable id including ref
        type: 'output',
        stepId: step.id,
        stepRefType: refType,
        stepRefId: refId,
        stepName: step.step_name,
        stepOrder: stepOrder,
        property: output,
        displayName: `Step ${stepOrder ? stepOrder : '-'}: ${step.step_name} → Output: ${output}`,
        shortName: `${output}`,
        category: `Step ${stepOrder ? stepOrder : '-'}: ${step.step_name}`
      });
    });
  });

  return stepData;
};

const updateTriggerConfig = (field, value) => {
  if (!currentTrigger.value.trigger_config) {
    currentTrigger.value.trigger_config = {};
  }
  
  // Convert to number if value is a number string, otherwise use as-is (for null/undefined)
  const numericValue = value === null || value === undefined || value === '' 
    ? null 
    : Number(value);
  
  currentTrigger.value.trigger_config[field] = numericValue;
  
  // If changing hierarchy type, reset level
  if (field === 'ht_id') {
    currentTrigger.value.trigger_config.level = null;
  }
};

const addDocumentMapping = () => {
  // derive formatter stable ref
  const formatterRefType = selectedStep.value?.step_type === 'output_formatter' ? 'formatter'
                         : selectedStep.value?.step_type === 'prompt' ? 'prompt'
                         : selectedStep.value?.step_type === 'service' ? 'service'
                         : 'step';
  const formatterRefId = selectedStep.value?.output_formatter_id || selectedStep.value?.tmpl_ai_prompt_id || selectedStep.value?.service_id || selectedStep.value?.id || `local_${Date.now()}`;

  const newMappingIndex = documentFormatterMappings.value.length;
  const newMappingId = computeMappingIdFor(newMappingIndex, null, null, formatterRefType, formatterRefId);

  documentFormatterMappings.value.push({
    id: newMappingId,

    // Backend format fields (required for saving)
    tmpl_part: null,
    delimiter: '',
    parent_secondary_delimiter: '',
    child_secondary_delimiter: '',
    split_value: '',
    split_content: false,
    uses_essential_flag: 0,
    default_essential_value: null,
    child_part: null,
    has_child_part: false,
    map_child_part: false,
    child_part_delimiter: '',
    uses_tags: false,
    tag_type: null,
    uses_tag_rating: false,
    tag_rating_delimiter: '',
    attr_list: [],
    tmpl_part_id: null,

    // JSON mode fields
    use_json_object: false,
    json_primary_property: '',
    json_child_property: '',
    json_tag_property: '',
    json_rating_property: '',
    json_mapping_mode: 'full_object',
    json_target_attribute_id: null,
    json_property_mappings: [],

    // Step data reference
    stepDataId: '',
    stepId: '',
    stepRefType: null,
    stepRefId: null,
    stepName: '',
    property: '',
    type: '', // 'input' or 'output'

    // UI helper fields for editing
    templatePart: null,
    childTemplatePart: null,
    attributeId: '',
    parentMappingMode: 'full_value',
    childMappingMode: 'full_value',
    parentSecondaryDelimiter: '',
    childSecondaryDelimiter: '',
    attributeMappings: [],
    mappingTargetType: 'template_part',
    hierarchyValueType: null,
    hierarchyValueLevel: 0,

    // Active/Inactive status
    is_active: true
  });
  
  // Start new mappings expanded (not collapsed)
  // No need to modify collapsedMappings since we want it expanded by default
};

const updateAttributeMapping = (mappingIndex, attrIndex, field, value) => {
  if (!documentFormatterMappings.value[mappingIndex] || !documentFormatterMappings.value[mappingIndex].attr_list) {
    return;
  }
  
  if (!documentFormatterMappings.value[mappingIndex].attr_list[attrIndex]) {
    return;
  }
  
  // Map the field correctly
  const fieldMap = {
    'sourceIndex': 'source_index'
  };
  
  const actualField = fieldMap[field] || field;
  documentFormatterMappings.value[mappingIndex].attr_list[attrIndex][actualField] = value;
};

const toggleAttributeMapping = (mappingIndex, attrIndex) => {
  if (!documentFormatterMappings.value[mappingIndex] || !documentFormatterMappings.value[mappingIndex].attr_list) {
    return;
  }

  if (!documentFormatterMappings.value[mappingIndex].attr_list[attrIndex]) {
    return;
  }

  // Toggle the mapped property
  documentFormatterMappings.value[mappingIndex].attr_list[attrIndex].mapped =
    !documentFormatterMappings.value[mappingIndex].attr_list[attrIndex].mapped;
};

const addJsonPropertyMapping = (mappingIndex) => {
  if (!documentFormatterMappings.value[mappingIndex]) {
    return;
  }

  if (!documentFormatterMappings.value[mappingIndex].json_property_mappings) {
    documentFormatterMappings.value[mappingIndex].json_property_mappings = [];
  }

  documentFormatterMappings.value[mappingIndex].json_property_mappings.push({
    json_property: '',
    attribute_id: null
  });
};

const removeJsonPropertyMapping = (mappingIndex, propIndex) => {
  if (!documentFormatterMappings.value[mappingIndex] ||
      !documentFormatterMappings.value[mappingIndex].json_property_mappings) {
    return;
  }

  documentFormatterMappings.value[mappingIndex].json_property_mappings.splice(propIndex, 1);
};

const updateJsonPropertyMapping = (mappingIndex, propIndex, field, value) => {
  if (!documentFormatterMappings.value[mappingIndex] ||
      !documentFormatterMappings.value[mappingIndex].json_property_mappings ||
      !documentFormatterMappings.value[mappingIndex].json_property_mappings[propIndex]) {
    return;
  }

  documentFormatterMappings.value[mappingIndex].json_property_mappings[propIndex][field] = value;
};

const cancelDocumentMappings = () => {
  showDocumentFormatterDialog.value = false;
  // Reset mappings to original state
  documentFormatterMappings.value = [];
};

// Add helper functions for empty value detection and messaging
const isEmptyOrNull = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string' && value.trim() === '') return true;
  if (Array.isArray(value) && value.length === 0) return true;
  if (typeof value === 'object' && Object.keys(value).length === 0) return true;
  return false;
};

const getEmptyValueType = (value) => {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (typeof value === 'string') return 'empty string';
  if (Array.isArray(value)) return 'empty array';
  if (typeof value === 'object') return 'empty object';
  return 'empty';
};

const getEmptyValueMessage = (value) => {
  if (value === null) return 'Input value is null';
  if (value === undefined) return 'Input value is undefined';
  if (typeof value === 'string' && value.trim() === '') return 'Input value is an empty string';
  if (Array.isArray(value) && value.length === 0) return 'Input array is empty';
  if (typeof value === 'object' && Object.keys(value).length === 0) return 'Input object is empty';
  return 'Input value is empty or missing';
};
//#endregion

//#region Component Lifecycle
watch(() => chain.steps, () => {
  if (selectedStep.value) {
    const updated = chain.steps.find(s => s.id === selectedStep.value.id);
    if (updated) selectedStep.value = updated;
  }
}, { deep: true });

// Watch for changes in step dependencies to recalculate connection positions
watch(() => chain.steps.map(s => ({ id: s.id, depCount: s.dependencies?.length || 0 })), async () => {
  // Wait for DOM to update with new dependency chips
  await nextTick();
  // Force connections to recalculate by creating a new array reference
  if (chain.connections.length > 0) {
    const temp = [...chain.connections];
    chain.connections = temp;
  }
}, { deep: true });

// Watch for changes in the tmpl_id prop and reinitialize
watch(() => props.value, async (newValue, oldValue) => {
  if (newValue !== oldValue && newValue != null) {
    
    // Reset component state
    chain.chain_name = 'New Prompt Chain';
    chain.prompt_chain_id = null;
    chain.description = '';
    chain.agent_exposure = null;
    chain.available_via = '';
    chain.steps = [];
    chain.connections = [];
    chain.triggers = [];
    chain.execution_config = null;
    chainExecutionConfig.execution_mode = 'sequence';
    chainExecutionConfig.context_mode = 'summary';
    chainExecutionConfig.max_context_rows = 20;
    selectedStep.value = null;
    savedChains.value = [];
    showLandingPage.value = true;
    
    // Reload data for new template
    await loadPrompts();
    await loadChains();
  }
});

onMounted(() => {
  document.addEventListener('mousemove', onStepDrag);
  document.addEventListener('mouseup', stopDragStep);
  document.addEventListener('mousemove', onTriggerDrag);
  document.addEventListener('mouseup', stopDragTrigger);
  loadPrompts();
  loadChains();
  loadSystemChains();
  loadServices();
  loadHookFunctions();
  loadAvailableChains();
});

onUnmounted(() => {
  document.removeEventListener('mousemove', onStepDrag);
  document.removeEventListener('mouseup', stopDragStep);
  document.removeEventListener('mousemove', onTriggerDrag);
  document.removeEventListener('mouseup', stopDragTrigger);

  // Clear polling interval if active
  if (testPollingInterval.value) {
    clearInterval(testPollingInterval.value);
    testPollingInterval.value = null;
  }
});
//#endregion
</script>

<style lang="scss" scoped>
/* ================================================
   BASE COMPONENT STYLES
   ================================================ */
.connection-actions {
  position: absolute;
  z-index: 100;
  display: flex;
  gap: 8px;
  align-items: center;
  pointer-events: auto;
  transition: opacity 0.15s;
  opacity: 1;
}

.connection-action-btn {
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  transition: all 0.2s;
  position: relative;
  outline: none;

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
  }
}

.connection-delete-btn {
  background: #F44336;
}

.connection-edit-btn {
  background: #9333EA;

  .mapping-count {
    position: absolute;
    top: -4px;
    right: -4px;
    background: #10B981;
    color: white;
    font-size: 10px;
    font-weight: 600;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px solid white;
  }
}

.chain-builder {
  height: calc(100vh - 120px);
  display: flex;
  flex-direction: column;
  background: #f9fafb;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

/* ================================================
   LANDING PAGE STYLES
   ================================================ */

.landing-page {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.landing-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 32px 48px;
  background: white;
  border-bottom: 1px solid #e5e7eb;
}

.landing-title-section {
  flex: 1;
}

.landing-title {
  font-size: 32px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 8px 0;
}

.landing-subtitle {
  font-size: 16px;
  color: #6b7280;
  margin: 0;
}

.landing-content {
  flex: 1;
  overflow-y: auto;
  padding: 32px 48px;
}

.search-field {
  max-width: 600px;
}

.chains-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  gap: 24px;
}

.chain-card {
  transition: all 0.2s;
  border: 2px solid transparent;

  &:hover {
    border-color: #EA3785;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
}

.chain-card-header {
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
}

.chain-icon {
  width: 56px;
  height: 56px;
  background: #50B3CB;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.chain-info {
  flex: 1;
  min-width: 0;
}

.chain-title {
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 6px 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chain-description {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.chain-stats {
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #6b7280;
}

.chain-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 24px;
  text-align: center;

  h3 {
    font-size: 20px;
    font-weight: 600;
    color: #4b5563;
    margin: 16px 0 8px 0;
  }

  p {
    font-size: 14px;
    color: #9ca3af;
    margin: 0;
  }
}

/* ================================================
   HISTORY VIEW BASE STYLES
   ================================================ */

.history-view {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #f9fafb;
}

.history-header {
  display: flex;
  align-items: center;
  padding: 20px 24px;
  background: white;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
  
  .history-title {
    font-size: 20px;
    font-weight: 600;
    color: #1f2937;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  
  .history-subtitle {
    font-size: 14px;
    color: #6b7280;
    margin: 4px 0 0 0;
  }
}

.history-content {
  flex: 1;
  overflow: hidden;
  padding: 24px;
  
  .v-data-table {
    background: white;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }
}

/* ================================================
   EXECUTION DETAILS LAYOUT
   ================================================ */

.execution-details-content {
  flex: 1;
  overflow: hidden;
  background: #f9fafb;
}

.execution-details-layout {
  display: flex;
  height: 100%;
  gap: 16px;
  background: #f9fafb;
  padding: 24px;
}

.execution-list-panel {
  width: 320px;
  background: white;
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  flex-shrink: 0;
  border: 1px solid #e5e7eb;
}

.execution-steps-panel {
  flex: 1;
  background: white;
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  min-width: 0;
  border: 1px solid #e5e7eb;
}

.panel-header {
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
  background: linear-gradient(to bottom, #ffffff, #f8fafc);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
  
  h3 {
    font-size: 17px;
    font-weight: 700;
    color: #111827;
    margin: 0;
    letter-spacing: -0.01em;
  }
}

.execution-list {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
}

.execution-item {
  padding: 14px 16px;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  margin-bottom: 10px;
  cursor: pointer;
  transition: all 0.15s ease;
  background: white;

  &:hover {
    border-color: #50B3CB;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    transform: translateY(-1px);
  }

  &.active {
    border-color: #50B3CB;
    background: linear-gradient(to bottom, rgba(80, 179, 203, 0.08), rgba(80, 179, 203, 0.04));
    box-shadow: 0 2px 12px rgba(80, 179, 203, 0.2);
  }

  &.has-issues {
    border-left: 4px solid #f59e0b;
    background: linear-gradient(to bottom, #fffbeb, #fefce8);
    
    &:hover {
      border-color: #f59e0b;
      box-shadow: 0 2px 8px rgba(245, 158, 11, 0.15);
    }
    
    &.active {
      border-color: #f59e0b;
      background: linear-gradient(to bottom, #fef3c7, #fef3c7);
      box-shadow: 0 2px 12px rgba(245, 158, 11, 0.25);
    }
  }

  &.is-partial {
    border-left: 4px solid #f59e0b;
    background: linear-gradient(to bottom, #fffbeb, #ffffff);
    
    &:hover {
      border-color: #f59e0b;
      box-shadow: 0 2px 8px rgba(245, 158, 11, 0.15);
    }
    
    &.active {
      border-color: #f59e0b;
      background: linear-gradient(to bottom, #fef3c7, #fef3c7);
      box-shadow: 0 2px 12px rgba(245, 158, 11, 0.25);
    }
  }

  &.is-regeneration {
    border-left: 4px solid #ED742D;
    background: linear-gradient(to bottom, #fff7ed, #fefce8);
    
    &:hover {
      border-color: #ED742D;
      box-shadow: 0 2px 8px rgba(237, 116, 45, 0.15);
    }
    
    &.active {
      border-color: #ED742D;
      background: linear-gradient(to bottom, #fed7aa, #fed7aa);
      box-shadow: 0 2px 12px rgba(237, 116, 45, 0.25);
    }
  }

  &:last-child {
    margin-bottom: 0;
  }
}

.execution-item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.execution-id-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.execution-id {
  font-weight: 700;
  color: #111827;
  font-size: 14px;
  letter-spacing: -0.01em;
}

.execution-item-meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.execution-time {
  font-size: 12px;
  color: #6b7280;
  font-weight: 500;
}

.execution-duration {
  font-size: 12px;
  color: #9ca3af;
  font-weight: 500;
}

.execution-warning {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 6px;
  margin-top: 6px;
  
  .warning-text {
    font-size: 11px;
    color: #92400e;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }
}

.execution-summary-detailed {
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
  background: linear-gradient(to bottom, #f8fafc, #ffffff);
  flex-shrink: 0;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 14px;
  margin-bottom: 16px;
}

.summary-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 14px 16px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  transition: all 0.15s ease;

  &:hover {
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
    transform: translateY(-1px);
  }
}

.summary-label {
  font-size: 11px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  display: block;
  margin-bottom: 6px;
  font-weight: 700;
}

.summary-value {
  font-size: 15px;
  color: #111827;
  font-weight: 700;
  letter-spacing: -0.01em;
}

.error-summary-detailed {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  background: #fef2f2;
  border-top: 1px solid #fecaca;
  
  .error-icon {
    flex-shrink: 0;
    margin-top: 1px;
  }
  
  .error-content {
    flex: 1;
    min-width: 0;
  }
  
  .error-title {
    font-size: 12px;
    font-weight: 700;
    color: #dc2626;
    margin-bottom: 4px;
    letter-spacing: -0.01em;
  }
  
  .error-text {
    font-size: 12px;
    color: #dc2626;
    line-height: 1.5;
    font-weight: 500;
  }
}
.panel-header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  
  h3 {
    font-size: 17px;
    font-weight: 700;
    color: #111827;
    margin: 0;
    letter-spacing: -0.01em;
  }
}

.execution-meta-chips {
  display: flex;
  gap: 10px;
  align-items: center;
}

.steps-container {
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px;
  background: #fafbfc;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  
  .loading-text {
    margin-top: 16px;
    font-size: 14px;
    color: #6b7280;
    font-weight: 500;
  }
}

.empty-steps-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 24px;
  text-align: center;
  
  .empty-title {
    font-size: 18px;
    font-weight: 600;
    color: #4b5563;
    margin: 16px 0 8px 0;
  }
  
  .empty-subtitle {
    font-size: 14px;
    color: #9ca3af;
    margin: 0;
  }
}

.steps-timeline {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.step-timeline-item {
  position: relative;
}

.step-timeline-connector {
  position: absolute;
  left: 20px;
  top: -16px;
  width: 2px;
  height: 16px;
  background: #e5e7eb;
}

.execution-item {
  padding: 14px 16px;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  margin-bottom: 10px;
  cursor: pointer;
  transition: all 0.15s ease;
  background: white;

  &:hover {
    border-color: #50B3CB;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    transform: translateY(-1px);
  }

  &.active {
    border-color: #50B3CB;
    background: linear-gradient(to bottom, rgba(80, 179, 203, 0.08), rgba(80, 179, 203, 0.04));
    box-shadow: 0 2px 12px rgba(80, 179, 203, 0.2);
  }

  &.has-issues {
    border-left: 4px solid #f59e0b;
    background: linear-gradient(to bottom, #fffbeb, #fefce8);
    
    &:hover {
      border-color: #f59e0b;
      box-shadow: 0 2px 8px rgba(245, 158, 11, 0.15);
    }
    
    &.active {
      border-color: #f59e0b;
      background: linear-gradient(to bottom, #fef3c7, #fef3c7);
      box-shadow: 0 2px 12px rgba(245, 158, 11, 0.25);
    }
  }

  &.is-regeneration {
    border-left: 4px solid #ED742D;
    background: linear-gradient(to bottom, #fff7ed, #fefce8);
    
    &:hover {
      border-color: #ED742D;
      box-shadow: 0 2px 8px rgba(237, 116, 45, 0.15);
    }
    
    &.active {
      border-color: #ED742D;
      background: linear-gradient(to bottom, #fed7aa, #fed7aa);
      box-shadow: 0 2px 12px rgba(237, 116, 45, 0.25);
    }
  }

  &:last-child {
    margin-bottom: 0;
  }
}

.execution-regen-info {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: #fff7ed;
  border: 1px solid #ED742D;
  border-radius: 6px;
  margin-top: 6px;
  
  .regen-text {
    font-size: 11px;
    color: #9a3412;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }
}

.step-detail-card-enhanced {
  border: 1.5px solid #e5e7eb;
  border-radius: 10px;
  background: white;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  transition: all 0.15s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transform: translateY(-1px);
  }
  
  &.step-skipped {
    border-left: 4px solid #f59e0b;
    background: linear-gradient(to bottom, #fffbeb, #ffffff);
    
    .step-header-enhanced {
      background: linear-gradient(to bottom, #fef3c7, #fffbeb);
    }
  }
}

.step-skipped-info {
  display: flex;
  align-items: center;
  padding: 12px 20px;
  background: #fef3c7;
  border-top: 1px solid #fbbf24;
  border-bottom: 1px solid #f3f4f6;
  
  .skipped-text {
    font-size: 13px;
    color: #92400e;
    font-weight: 600;
    letter-spacing: -0.01em;
  }
}

.issues-indicator {
  display: flex;
  gap: 4px;
  align-items: center;
  flex-wrap: wrap;
}

.step-header-enhanced {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  background: linear-gradient(to bottom, #f8fafc, #ffffff);
  border-bottom: 1px solid #e5e7eb;
}

.step-indicator {
  display: flex;
  align-items: center;
  gap: 12px;
}

.step-number-enhanced {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #50B3CB, #3b9ab0);
  color: white;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 800;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(80, 179, 203, 0.3);
  letter-spacing: -0.02em;
}

.step-status-indicator {
  flex-shrink: 0;
}

.step-info-enhanced {
  flex: 1;
  min-width: 0;
}

.step-title-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}

.step-name-enhanced {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  margin: 0;
  letter-spacing: -0.01em;
  flex: 1;
  min-width: 0;
}

.step-badges {
  display: flex;
  gap: 8px;
  align-items: center;
}

.status-chip {
  font-weight: 700 !important;
  letter-spacing: 0.02em !important;
  text-transform: uppercase !important;
  font-size: 10px !important;
  height: 22px !important;
}

.type-chip {
  font-weight: 700 !important;
  letter-spacing: 0.02em !important;
  text-transform: uppercase !important;
  font-size: 10px !important;
  height: 22px !important;
}

.step-metrics {
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
}

.metric-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #6b7280;
  background: rgba(255, 255, 255, 0.9);
  padding: 6px 12px;
  border-radius: 20px;
  border: 1px solid #e5e7eb;
  font-weight: 600;
  
  span {
    letter-spacing: -0.01em;
  }
}

.step-timing-detailed {
  padding: 12px 20px;
  background: #fafbfc;
  border-bottom: 1px solid #f3f4f6;
}

.timing-row {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
}

.timing-item-detailed {
  display: flex;
  flex-direction: column;
  gap: 4px;
  
  .timing-label-detailed {
    font-size: 10px;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    font-weight: 700;
  }
  
  .timing-value-detailed {
    font-size: 13px;
    color: #111827;
    font-weight: 600;
    letter-spacing: -0.01em;
  }
}

.step-error-detailed {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 20px;
  background: #fef2f2;
  border-top: 1px solid #fecaca;
  
  .error-icon {
    flex-shrink: 0;
    margin-top: 1px;
  }
  
  .error-content {
    flex: 1;
    min-width: 0;
  }
  
  .error-title {
    font-size: 12px;
    font-weight: 700;
    color: #dc2626;
    margin-bottom: 4px;
    letter-spacing: -0.01em;
  }
  
  .error-message {
    font-size: 12px;
    color: #dc2626;
    line-height: 1.5;
    font-weight: 500;
  }
}

/* ================================================
   TRIGGERS VIEW
   ================================================ */

.triggers-view {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
}

.triggers-container {
  max-width: 1200px;
  margin: 0 auto;
}

.triggers-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 32px;
  gap: 24px;
}

.triggers-title {
  font-size: 28px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 8px 0;
}

.triggers-subtitle {
  font-size: 16px;
  color: #6b7280;
  margin: 0;
}

.triggers-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(450px, 1fr));
  gap: 24px;
}

/* ================================================
   DOCUMENT FORMATTER DIALOG
   ================================================ */

.document-formatter-header {
  background: linear-gradient(to bottom, #ffffff, #f8fafc);
  border-bottom: 1px solid #e5e7eb;
  padding: 20px 24px;
}

.document-formatter-content {
  padding: 24px;
}

.formatter-description {
  margin-bottom: 24px;
  
  p {
    font-size: 14px;
    color: #6b7280;
    line-height: 1.6;
    margin: 0;
  }
}

.mappings-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  
  h3 {
    font-size: 18px;
    font-weight: 700;
    color: #1f2937;
    margin: 0;
  }
}

.empty-mappings {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 24px;
  text-align: center;
  
  h4 {
    font-size: 20px;
    font-weight: 600;
    color: #4b5563;
    margin: 16px 0 8px 0;
  }
  
  p {
    font-size: 14px;
    color: #9ca3af;
    margin: 0;
  }
}

.mapping-card {
  border: 1.5px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
  background: white;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }

  &.inactive-mapping {
    opacity: 0.6;
    background: #f9fafb;
    border-color: #d1d5db;
  }
}

.mapping-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: linear-gradient(to bottom, #f8fafc, #ffffff);
  border-bottom: 1px solid #e5e7eb;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: linear-gradient(to bottom, #f0f4f8, #f8fafc);
  }
}

.mapping-header-content {
  flex: 1;
  min-width: 0;
}
.dependency-label,
.output-label {
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 4px;
}


.step-endpoint{
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 8px;
  font-weight:bold;
}

.dependency-chips,
.output-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.mapping-title-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}

.mapping-title {
  font-size: 15px;
  font-weight: 700;
  color: #111827;
  letter-spacing: -0.01em;
}

.mapping-summary {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.mapping-header-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-shrink: 0;
}

.step-data-item-enhanced {
  padding: 8px 12px;
  width: 100%;
}

.step-data-main {
  display: flex;
  align-items: center;
  gap: 12px;
}

.step-indicator-small {
  width: 28px;
  height: 28px;
  background: linear-gradient(135deg, #50B3CB, #3b9ab0);
  color: white;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 800;
  flex-shrink: 0;
}

.step-data-content {
  flex: 1;
  min-width: 0;
}

.step-data-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.step-name-small {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
}

.property-name {
  font-size: 12px;
  color: #6b7280;
  font-weight: 500;
}

.attribute-mappings-section {
  padding: 16px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  
  .mappings-title {
    font-size: 14px;
    font-weight: 700;
    color: #111827;
    margin: 0 0 4px 0;
  }
  
  .mappings-subtitle {
    font-size: 12px;
    color: #6b7280;
    margin: 0 0 16px 0;
  }
}

.mapping-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 6px;

  .attribute-name {
    flex: 1;
    font-size: 13px;
    font-weight: 600;
    color: #374151;

    &.disabled-attribute {
      color: #9ca3af;
    }
  }

  .index-input {
    width: 100px;
    flex-shrink: 0;
  }
}

.json-property-mappings {
  display: flex;
  flex-direction: column;
  gap: 12px;

  .json-mapping-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 6px;

    .json-property-input {
      flex: 1;
      min-width: 200px;
    }

    .mapping-arrow {
      flex-shrink: 0;
      color: #9ca3af;
    }

    .attribute-select {
      flex: 1;
      min-width: 200px;
    }

    .remove-mapping-btn {
      flex-shrink: 0;
    }
  }
}

/* ================================================
   EXECUTION STEP TABS AND DATA DISPLAY
   ================================================ */

.step-tabs-enhanced {
  border-top: 1px solid #e5e7eb;

  ::v-deep .v-tab {
    text-transform: none;
    font-weight: 600;
    letter-spacing: -0.01em;
  }
}

.step-tab {
  font-size: 13px !important;
  min-width: auto !important;
  padding: 0 16px !important;
}

.tab-content-enhanced {
  padding: 20px;
  background: #fafbfc;
}

.no-data-enhanced {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;

  .no-data-text {
    font-size: 14px;
    color: #9ca3af;
    margin-top: 8px;
    font-weight: 500;
  }
}

.data-display {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.data-item {
  background: white;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  transition: all 0.15s ease;

  &:hover {
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
  }

  &.data-item-warning {
    border-left: 4px solid #f59e0b;
    background: linear-gradient(to bottom, #fffbeb, #fefce8);
  }
}

.data-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  gap: 12px;
  flex-wrap: wrap;
}

.data-key-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.data-key-enhanced {
  font-size: 14px;
  font-weight: 700;
  color: #111827;
  letter-spacing: -0.01em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.data-value-enhanced {
  background: #f8fafc;
  border-radius: 6px;
  overflow: hidden;
}

.empty-value-warning {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 6px;

  .empty-value-text {
    font-size: 13px;
    color: #92400e;
    font-weight: 600;
    letter-spacing: -0.01em;
  }
}

.expandable-content {
  .content-preview {
    padding: 12px 16px;
    font-family: 'Courier New', monospace;
    font-size: 12px;
    color: #374151;
    line-height: 1.6;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 6px 6px 0 0;
    overflow-wrap: break-word;
  }

  .expand-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    border-top: 1px solid #e5e7eb;
    background: white;
    border-radius: 0 0 6px 6px;
    padding: 8px;
    font-weight: 600;
    text-transform: none;
  }

  .full-content {
    border-top: 1px solid #e5e7eb;
  }
}

.simple-content {
  padding: 12px 16px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
}

.content-code {
  margin: 0;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  color: #374151;
  line-height: 1.6;
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.prompt-display {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.prompt-version-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
  border: 2px solid #10b981;
  border-radius: 8px;
  margin-bottom: 4px;

  &.historical-version {
    background: linear-gradient(135deg, #fff7ed 0%, #fed7aa 100%);
    border-color: #f59e0b;
  }

  .version-info {
    display: flex;
    align-items: center;
    gap: 4px;

    .version-text {
      font-size: 13px;
      color: #111827;

      strong {
        font-weight: 700;
      }

      .historical-label {
        color: #d97706;
        font-size: 12px;
        margin-left: 6px;
      }

      .current-label {
        color: #059669;
        font-size: 12px;
        margin-left: 6px;
      }
    }
  }

  .model-info {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    color: #6b7280;
    padding: 4px 10px;
    background: rgba(255, 255, 255, 0.6);
    border-radius: 6px;
  }
}

.prompt-section-enhanced {
  background: white;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.15s ease;

  &:hover {
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
  }
}

.prompt-section-header {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: linear-gradient(to bottom, #f8fafc, #ffffff);
  border-bottom: 1px solid #e5e7eb;
}

.prompt-section-title {
  font-size: 13px;
  font-weight: 700;
  color: #111827;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.prompt-content {
  margin: 0;
  padding: 16px;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  color: #374151;
  line-height: 1.6;
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.raw-response-display {
  background: white;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}

.response-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: linear-gradient(to bottom, #fff7ed, #ffffff);
  border-bottom: 1px solid #fed7aa;
}

.response-title {
  font-size: 13px;
  font-weight: 700;
  color: #111827;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  flex: 1;
}

.response-content {
  margin: 0;
  padding: 16px;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  color: #374151;
  line-height: 1.6;
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-height: 500px;
  overflow-y: auto;
}

.validation-history-display {
  background: white;
  border-radius: 8px;
}

.validation-history-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: #fef3c7;
  border-bottom: 1.5px solid #fbbf24;
}

.validation-attempts-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
}

.validation-attempt-card {
  background: white;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  padding: 14px;
  transition: all 0.2s;
}

.validation-attempt-card:hover {
  border-color: #d1d5db;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.attempt-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.attempt-number {
  display: flex;
  align-items: center;
}

.attempt-label {
  font-size: 13px;
  font-weight: 600;
  color: #111827;
}

.attempt-errors {
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  padding: 12px;
  margin-top: 10px;
}

.errors-header {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.errors-title {
  font-size: 12px;
  font-weight: 600;
  color: #991b1b;
}

.errors-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.error-item {
  display: flex;
  align-items: flex-start;
  font-size: 12px;
  color: #7f1d1d;
  line-height: 1.5;
}

.attempt-response {
  margin-top: 10px;
}

.response-toggle {
  min-height: 36px !important;
  padding: 8px 12px !important;
  background: #f9fafb;
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
}

.response-content-small {
  margin: 0;
  padding: 12px;
  font-family: 'Courier New', monospace;
  font-size: 11px;
  color: #374151;
  line-height: 1.5;
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-height: 300px;
  overflow-y: auto;
  background: #f9fafb;
  border-radius: 4px;
}

.trigger-name {
  display: flex;
  align-items: center;
  gap: 6px;
}

.execution-trigger {
  display: flex;
  align-items: center;
  font-size: 11px;
  color: #6b7280;
  margin-top: 4px;
  
  .trigger-text {
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.trigger-context {
  display: flex;
  align-items: center;
}

/* ================================================
   BUILDER VIEW STYLES
   ================================================ */

.builder-view {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.header-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 24px;
  background: white;
  border-bottom: 1px solid #e5e7eb;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  flex-shrink: 0;
}

.chain-name-input {
  max-width: 400px;

  ::v-deep input {
    font-size: 20px;
    font-weight: 600;
  }
}

.step-name-input {
  ::v-deep .v-input__control {
    min-height: auto !important;
    background: rgba(255, 255, 255, 0) !important;
  }
  
  ::v-deep .v-text-field__details {
    display: none;
  }
  
  ::v-deep .v-input__slot {
    background: rgba(255, 255, 255, 0) !important;
    border-radius: 4px;
    padding: 4px 8px !important;
  }
  
  ::v-deep input {
    font-size: 14px;
    font-weight: 600;
    background: rgba(255, 255, 255, 0) !important;
    
    &::placeholder {
      color: rgba(255, 255, 255, 0.7) !important;
    }
  }
  
  ::v-deep .v-input__slot:before,
  ::v-deep .v-input__slot:after {
    display: none !important;
  }
}

.main-content {
  display: flex;
  flex: 1;
  overflow: hidden;
}

.sidebar {
  width: 320px;
  background: white;
  border-right: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
}

.sidebar-header {
  padding: 24px;
  border-bottom: 1px solid #e5e7eb;
}

.sidebar-title {
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 12px;
}

.sidebar-content {
  flex: 1;
  overflow-y: auto;
}

.category-header {
  font-weight: 500;
  color: #374151;

  ::v-deep .v-expansion-panel-header {
    padding: 12px 24px;
  }
}

.category-name {
  font-weight: 500;
}

.category-content {
  background: #f9fafb;

  ::v-deep .v-expansion-panel-content__wrap {
    padding: 0;
  }
}

.service-parent-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  cursor: pointer;
  border-bottom: 1px solid #e5e7eb;
  transition: all 0.2s;
  user-select: none;
  background: #f9fafb;
  font-weight: 600;

  &:hover {
    background: #f3f4f6;
  }
}

.service-endpoint-item {
  padding-left: 56px !important;
  background: #fafbfc;
}

.draggable-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 24px;
  cursor: move;
  border-bottom: 1px solid #e5e7eb;
  transition: all 0.2s;
  user-select: none;

  &:hover {
    background: white;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }
}

.endpoint-path {
  font-size: 11px;
  color: #6b7280;
  font-family: monospace;
}

.item-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;

  &.service-icon {
    background: #e8d5f0;
  }

  &.formatter-icon {
    background: #ffecd9;
  }

  &.prompt-icon {
    background: #d4f0f7;
  }
}

.item-content {
  flex: 1;
  min-width: 0;
}

.item-name {
  font-weight: 500;
  font-size: 14px;
  color: #1f2937;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.item-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #6b7280;
  margin-top: 2px;
}

.health-status {
  display: flex;
  align-items: center;
  gap: 4px;
}

.health-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;

  &.health-green { background: #10b981; }
  &.health-yellow { background: #f59e0b; }
  &.health-red { background: #ef4444; }
  &.health-grey { background: #9ca3af; }
}

.health-text {
  text-transform: capitalize;
}

.canvas {
  flex: 1;
  position: relative;
  overflow: auto;
  background: #f9fafb;
  background-image: radial-gradient(circle, #e5e7eb 1px, transparent 1px);
  background-size: 20px 20px;
}

.start-node {
  position: absolute;
  left: 50px;
  top: 100px;
  width: 160px;
  height: 96px;
  background: #48A401;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;

  &:hover {
    box-shadow: 0 8px 12px rgba(0, 0, 0, 0.15);
  }
}

.start-text {
  margin-top: 4px;
}

.start-hint {
  font-size: 10px;
  opacity: 0.7;
  margin-top: 4px;
}

/* Trigger Nodes */
.trigger-node {
  position: absolute;
  width: 200px;
  min-height: 100px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  cursor: grab;
  transition: box-shadow 0.2s ease, border 0.2s ease;
  overflow: visible;
  user-select: none;

  &:hover {
    box-shadow: 0 8px 12px rgba(0, 0, 0, 0.15);
  }

  &.selected {
    box-shadow: 0 0 0 2px #3b82f6;
  }

  &.dragging {
    cursor: grabbing;
    transform: scale(1.02);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.2);
    z-index: 1000;
    transition: none;
  }
}

.trigger-header {
  padding: 10px 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: white;
  font-weight: 600;
  font-size: 13px;
  border-radius: 12px 12px 0 0;
}

.trigger-name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.trigger-body {
  padding: 10px 12px;
  font-size: 12px;
  background: #f9fafb;
  border-radius: 0 0 12px 12px;
}

.trigger-summary {
  .label {
    color: #6b7280;
    font-size: 11px;
    display: block;
    margin-bottom: 2px;
  }

  .schema-snippet {
    font-family: monospace;
    font-size: 11px;
    background: #e5e7eb;
    padding: 2px 6px;
    border-radius: 4px;
    display: inline-block;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .mapping-count, .chain-name, .hierarchy-name {
    font-weight: 500;
    color: #374151;
  }

  .schema-preview, .mappings-preview, .chain-preview, .hierarchy-preview {
    margin-bottom: 6px;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

/* Trigger type-specific header colors */
.trigger-type-api .trigger-header {
  background: linear-gradient(135deg, #6366f1, #4f46e5);
}

.trigger-type-chain_output .trigger-header {
  background: linear-gradient(135deg, #8b5cf6, #7c3aed);
}

.trigger-type-hierarchy_node_action .trigger-header {
  background: linear-gradient(135deg, #48A401, #3d8a01);
}

.trigger-type-document_action .trigger-header {
  background: linear-gradient(135deg, #0ea5e9, #0284c7);
}

.trigger-type-scheduled .trigger-header {
  background: linear-gradient(135deg, #f59e0b, #d97706);
}

.trigger-type-webhook .trigger-header {
  background: linear-gradient(135deg, #ec4899, #db2777);
}

.trigger-type-batch_operation .trigger-header {
  background: linear-gradient(135deg, #14b8a6, #0d9488);
}

.trigger-type-manual .trigger-header,
.trigger-type-doc_create .trigger-header {
  background: linear-gradient(135deg, #48A401, #3d8a01);
}

/* Trigger node connection points - color matches header */
.trigger-type-api .connection-point {
  background: #4f46e5;
  &:hover { background: #6366f1; }
}

.trigger-type-chain_output .connection-point {
  background: #7c3aed;
  &:hover { background: #8b5cf6; }
}

.trigger-type-hierarchy_node_action .connection-point {
  background: #3d8a01;
  &:hover { background: #48A401; }
}

.trigger-type-document_action .connection-point {
  background: #0284c7;
  &:hover { background: #0ea5e9; }
}

.trigger-type-scheduled .connection-point {
  background: #d97706;
  &:hover { background: #f59e0b; }
}

.trigger-type-webhook .connection-point {
  background: #db2777;
  &:hover { background: #ec4899; }
}

.trigger-type-batch_operation .connection-point {
  background: #0d9488;
  &:hover { background: #14b8a6; }
}

.trigger-type-manual .connection-point,
.trigger-type-doc_create .connection-point {
  background: #3d8a01;
  &:hover { background: #48A401; }
}

.step-node {
  position: absolute;
  width: 288px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  cursor: grab;
  transition: box-shadow 0.2s ease, border 0.2s ease, opacity 0.3s ease;

  &:hover {
    box-shadow: 0 8px 12px rgba(0, 0, 0, 0.15);
  }

  &.selected {
    box-shadow: 0 0 0 2px #3b82f6;
  }

  &.dragging {
    cursor: grabbing;
    transform: scale(1.05);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.2);
    transition: none;
    pointer-events: none;
  }

  &.inactive {
    opacity: 0.4;
    filter: grayscale(60%);
    pointer-events: auto;
    cursor: default;

    &:hover {
      opacity: 0.5;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    }

    .step-header {
      background: #9e9e9e !important;
    }

    .connection-point {
      display: none;
    }
  }
}

.step-header {
  padding: 12px 16px;
  border-radius: 12px 12px 0 0;
  display: flex;
  align-items: center;
  gap: 8px;
  color: white;

  &.service-header {
    background: #5E2872;
  }

  &.formatter-header {
    background: #ED742D;
  }

  &.chain-header {
    background: #4CAF50;
  }

  &.selection-header {
    background: #FF9800;
  }

  &.prompt-header {
    background: #50B3CB;
  }
}

.step-active-switch {
  margin: 0 !important;
  padding: 0 !important;
  height: auto !important;

  ::v-deep .v-input__slot {
    margin: 0 !important;
  }

  ::v-deep .v-input--selection-controls__input {
    margin: 0 !important;
  }
}

.step-body {
  padding: 16px;
}

.step-type {
  display: flex;
  align-items: center;
  gap:  8px;
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 12px;
  font-weight: 500;
}

.connection-point {
  position: absolute;
  width: 16px;
  height: 16px;
  background: #50B3CB;
  border: 3px solid white;
  border-radius: 50%;
  cursor: pointer;
  z-index: 10;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);

  &:hover {
    background: #EA3785;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
  }

  &.connection-service {
    background: #5E2872;
  }

  &.connection-formatter {
    background: #ED742D;
  }

  &.connection-chain {
    background: #4CAF50;
  }

  &.connection-selection {
    background: #FF9800;
  }

  &.connection-prompt {
    background: #50B3CB;
  }
}

.left-point {
  left: 0;
  top: 50%;
  transform: translate(-50%, -50%);
}

.right-point {
  right: 0;
  top: 50%;
  transform: translate(50%, -50%);
}

.top-point {
  left: 50%;
  top: 0;
  transform: translate(-50%, -50%);
}

.bottom-point {
  left: 50%;
  bottom: 0;
  transform: translate(-50%, 50%);
}

.connections-svg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;

  .connection-hover-area {
    cursor: pointer;
    pointer-events: stroke;
  }
}

.properties-panel {
  width: 384px;
  background: white;
  border-left: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.properties-header {
  padding: 16px 24px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;

  h3 {
    font-size: 18px;
    font-weight: 600;
    color: #1f2937;
    margin: 0;
  }
}

.properties-content {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
}

.property-group {
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }
}

.property-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
}

/* Input/Output Mapping Styles */
.input-mapping-header,
.output-mapping-header {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
}

.input-list,
.output-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.input-mapping-item,
.output-mapping-item {
  padding: 16px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.validation-config {
  background: #fef3c7;
  border: 1px solid #fbbf24;
  border-radius: 6px;
  padding: 12px;
  margin-top: 8px;
}

.input-mapping-row,
.output-mapping-row {
  display: flex;
  gap: 8px;
  align-items: flex-start;

  > *:first-child {
    flex: 1;
    min-width: 0;
  }

  .v-btn {
    flex-shrink: 0;
    margin-top: 4px;
  }
}

/* Prompt Preview Styles */
.prompt-preview-box {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  max-height: 400px;
  overflow-y: auto;
}

.prompt-section {
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
}

.prompt-section-label {
  font-size: 12px;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 8px;
  display: block;
}

.prompt-section pre {
  margin: 0;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  color: #374151;
  line-height: 1.6;
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow-wrap: break-word;
  background: white;
  padding: 12px;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
}

/* Service Info Box */
.service-info-box,
.formatter-info-box {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
}

.service-info-content,
.info-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.info-label {
  font-size: 11px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 700;
}

.info-value {
  font-size: 13px;
  color: #111827;
  font-weight: 600;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.schema-box {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 12px;
  max-height: 200px;
  overflow-y: auto;

  pre {
    margin: 0;
    font-family: 'Courier New', monospace;
    font-size: 11px;
    color: #374151;
    line-height: 1.5;
    white-space: pre-wrap;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }
}

/* Panel Header Styles */
.panel-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 600;
  color: #374151;
}

/* Document Mappings Summary */
.document-mappings-summary {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;

  .no-mappings,
  .mappings-count {
    font-size: 13px;
    color: #6b7280;
    margin-bottom: 8px;
  }

  .mappings-count {
    font-weight: 600;
    color: #374151;
  }
}

/* Text Template Editor */
.text-template-editor {
  ::v-deep .v-input__control {
    background: #f9fafb;
  }

  ::v-deep textarea {
    font-family: 'Courier New', monospace;
    font-size: 12px;
    line-height: 1.6;
  }
}

/* Available Variables */
.available-variables {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px;
  max-height: 200px;
  overflow-y: auto;
}

.no-variables {
 
  font-size: 13px;
  color: #9ca3af;
  text-align: center;
  padding: 16px;
}

.variables-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.variable-item {
  padding: 10px 12px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: #50B3CB;
    box-shadow: 0 2px 4px rgba(80, 179, 203, 0.15);
  }
}

.variable-syntax {
  font-family: 'Courier New', monospace;
  font-size: 12px;
  color: #50B3CB;
  font-weight: 600;
  margin-bottom: 4px;
}

.variable-description {
  font-size: 11px;
  color: #6b7280;
}

// Modal Styles
.trigger-modal-header {
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
}

.trigger-modal-content {
  padding: 24px !important;
}
.modal-section {
  margin-bottom: 32px;

  &:last-child {
    margin-bottom: 0;
  }
}

.section-header {
  display: flex;
  justify-content: space-between; justify-content: space-between;
  align-items: center;  align-items: center;
  margin-bottom: 16px;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

.section-subtitle {
  font-size: 14px;
  color: #6b7280;
  margin: 0 0 16px 0;argin: 0 0 16px 0;
}

.mappings-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.mapping-card {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
}

.mapping-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.mapping-title {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

.step-data-item {
  display: flex;
  align-items: center;
  padding: 8px 0;
}

.step-data-item-enhanced {
  padding: 12px 8px;
  border-radius: 6px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f8fafc;
  }
}

.step-data-main {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
}

.step-indicator-small {
  width: 24px;
  height: 24px;
  background: #50B3CB;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 700;
  flex-shrink: 0;
  margin-top: 2px;
}

.step-data-content {
  flex: 1;
  min-width: 0;
}

.step-data-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
  gap: 8px;
}

.step-name-small {
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.type-indicator {
  font-size: 9px;
  font-weight: 700;
  height: 18px;
  flex-shrink: 0;
}

.property-name {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 2px;
}

.source-info {
  font-size: 11px;
  color: #6b7280;
  font-style: italic;
}

.step-data-name {
  font-size: 14px;
  font-weight: 500;
  color: #1f2937;
}
delimiter-section-header {
  margin-bottom: 12px;
  
  .section-title {
    font-size: 14px;
    font-weight: 600;
    color: #374151;
    margin: 0 0 4px 0;
  }
  
  .section-subtitle {
    font-size: 12px;
    color: #6b7280;
    font-style: italic;
  }
}

.attribute-mappings-section {
  padding: 16px;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  
  .mappings-title {
    font-size: 13px;
    font-weight: 600;
    color: #374151;
    margin: 0 0 4px 0;
  }
  
  .mappings-subtitle {
    font-size: 11px;
    color: #6b7280;
    display: block;
    margin-bottom: 12px;
  }
}

.mappings-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.mapping-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  
  .attribute-name {
    flex: 1;
    font-size: 13px;
    font-weight: 500;
    color: #374151;
    transition: color 0.2s ease, opacity 0.2s ease;
  }
  
  .index-input {
    width: 80px;
    flex-shrink: 0;
  }
}

.data-display {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.data-item {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  background: #fafbfc;
}

.trigger-context {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.trigger-text {
  font-size: 11px;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.execution-trigger {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #6b7280;
  margin-top: 2px;
}

.trigger-name {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
}

.step-metrics {
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
}

.metric-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #6b7280;
  background: rgba(255, 255, 255, 0.7);
  padding: 4px 8px;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
}

.step-timing-detailed {
  padding: 12px 20px;
  background: #fafbfc;
  border-bottom: 1px solid #f3f4f6;
}

.timing-row {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
}

.mapping-card-header {
  padding: 16px 20px;
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: background-color 0.2s ease;

  &:hover {
    background: #f1f5f9;
  }
}

.mapping-header-content {
  flex: 1;
  min-width: 0;
}

.mapping-title-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 4px;
  flex-wrap: wrap;
}

.mapping-title {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  flex-shrink: 0;
}

.mapping-summary {
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1;
  min-width: 0;
  flex-wrap: wrap;
}

.mapping-incomplete {
  flex: 1;
}

.mapping-status-indicator {
  margin-top: 2px;
  
  small {
    font-size: 11px;
    color: #9ca3af;
    font-style: italic;
  }
}

.mapping-header-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.collapse-toggle {
  transition: transform 0.2s ease;
  
  &:hover {
    background: rgba(80, 179, 203, 0.1);
  }
}

.triggers-view {
  flex: 1;
 
  overflow-y: auto;
  padding: 24px;
}

.triggers-container {
  max-width: 1200px;
  margin: 0 auto;
}

.triggers-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 32px;
  gap: 24px;
}

.triggers-title {
  font-size: 28px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 8px 0;
}

.triggers-subtitle {
  font-size: 16px;
  color: #6b7280;
  margin: 0;
}

.triggers-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 24px;
  text-align: center;

  .empty-title {
    font-size: 24px;
    font-weight: 600;
    color: #4b5563;
    margin: 0 0 16px 0;
  }

  .empty-subtitle {
    font-size: 16px;
    color: #9ca3af;
    margin: 0;
  }
}

.triggers-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(450px, 1fr));
  gap: 24px;
}

.trigger-card {
  transition: all 0.2s ease;
  border: 2px solid transparent;

  &:hover {
    border-color: #5E2872;
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(94, 40, 114, 0.15);
  }
}

.trigger-card-content {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.trigger-card-main {
  flex: 1;
  min-width: 0;
  display: flex;
  gap: 16px;
  align-items: flex-start;
}

.trigger-icon {
  width: 56px;
  height: 56px;
  background: rgba(94, 40, 114, 0.1);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.trigger-info {
  flex: 1;
  min-width: 0;
}

.trigger-header-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
  gap: 12px;
  flex-wrap: wrap;
}

.trigger-name {
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
}

.trigger-description {
  font-size: 14px;
  color: #6b7280;
  line-height: 1.5;
  margin: 0 0 12px 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.trigger-meta {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}

.trigger-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex-shrink: 0;
}

.consolidation-view {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #f9fafb;
}

.consolidation-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 24px;
}

.consolidation-toolbar {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 24px;
  background: white;
  border-radius: 12px;
  margin-bottom: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  flex-wrap: wrap;
}

.toolbar-section {
  display: flex;
  align-items: center;
  gap: 12px;

  &:not(:last-child) {
    border-right: 1px solid #e5e7eb;
    padding-right: 16px;
  }
}

.search-input {
  min-width: 280px;
}

.suggestions-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  background: linear-gradient(to right, #fff7ed, #fffbeb);
  border: 1px solid #fed7aa;
  border-radius: 8px;
  margin-bottom: 16px;

  .suggestion-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 2px;

    strong {
      font-size: 14px;
      color: #9a3412;
    }

    span {
      font-size: 12px;
      color: #c2410c;
    }
  }
}

.consolidation-grid {
  flex: 1;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
}

.grid-header {
  display: flex;
  background: linear-gradient(to bottom, #f8fafc, #ffffff);
  border-bottom: 2px solid #e5e7eb;
  flex-shrink: 0;
  position: sticky;
  top: 0;
  z-index: 8;
}

.property-column-header {
  width: 320px;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  border-right: 1px solid #e5e7eb;
  flex-shrink: 0;

  .column-title {
    font-size: 14px;
    font-weight: 700;
    color: #374151;
    flex: 1;
  }

  .select-all-checkbox {
    margin: 0 !important;
    padding: 0 !important;
  }
}

.type-column-header {
  width: 100px;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-right: 1px solid #e5e7eb;
  flex-shrink: 0;

  .column-title {
    font-size: 14px;
    font-weight: 700;
    color: #374151;
  }
}

.steps-header {
  flex: 1;
  display: flex;
  overflow-x: auto;
  min-width: 0;
}

.step-column-header {
  padding: 12px;
  border-right: 1px solid #f3f4f6;
  flex-shrink: 0;

  &:last-child {
    border-right: none;
  }
}

.step-header-content {
  display: flex;
  align-items: center;
  gap: 8px;
}

.step-number-small {
  width: 24px;
  height: 24px;
  background: #50B3CB;
  color: white;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 800;
  flex-shrink: 0;
}

.step-info-compact {
  flex: 1;
  min-width: 0;
}

.step-name-compact {
  font-size: 13px;
  font-weight: 600;
  color: #111827;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-bottom: 4px;
}

.grid-body {
  flex: 1;
  overflow: auto;
}

.property-row {
  display: flex;
  border-bottom: 1px solid #f3f4f6;
  transition: all 0.15s ease;

  &:hover {
    background: #f9fafb;
  }

  &.selected-row {
    background: #eff6ff;
    border-left: 3px solid #3b82f6;
  }

  &.suggested-row {
    background: linear-gradient(to right, #fff7ed, transparent);
    border-left: 3px solid #f59e0b;
  }
}

.property-cell {
  width: 320px;
  padding: 12px 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  border-right: 1px solid #e5e7eb;
  flex-shrink: 0;

  .row-checkbox {
    margin: 0 !important;
    padding: 0 !important;
    flex-shrink: 0;
  }
}

.type-cell {
  width: 100px;
  padding: 12px 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-right: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.property-info {
  flex: 1;
  min-width: 0;
}

.property-name-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;

  .property-name-display-inline {
    font-size: 14px;
    font-weight: 600;
    color: #111827;
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .property-name-input {
    flex: 1;
    min-width: 0;

    ::v-deep .v-input__control {
      min-height: 28px !important;
    }

    ::v-deep .v-input__slot {
      padding: 0 8px !important;
      min-height: 28px !important;
    }

    ::v-deep input {
      font-size: 14px;
      font-weight: 600;
      padding: 4px 0 !important;
    }
  }
}

.property-meta {
  display: flex;
  gap: 8px;
  align-items: center;
}

.source-tag {
  font-size: 11px;
  color: #6b7280;
  background: #f3f4f6;
  padding: 2px 8px;
  border-radius: 12px;
  font-weight: 500;
}

.required-tag {
  font-size: 11px;
  color: #dc2626;
  background: #fee2e2;
  padding: 2px 8px;
  border-radius: 12px;
  font-weight: 600;
}

.steps-cells {
  flex: 1;
  display: flex;
  overflow-x: auto;
  min-width: 0;
}

.step-cell {
  padding: 12px;
  border-right: 1px solid #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s ease;
  flex-shrink: 0;

  &:last-child {
    border-right: none;
  }

  &:hover {
    background: #f9fafb;
  }

  &.has-property {
    background: #ecfdf5;

    &:hover {
      background: #d1fae5;
    }
  }

  &.is-required {
    background: #fef2f2;

    &:hover {
      background: #fee2e2;
    }
  }
}

.property-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: 100%;
}

.cell-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  width: 100%;
}

.property-details {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.property-source-mini {
  font-size: 10px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.property-output-mini {
  font-size: 10px;
  color: #3b82f6;
  font-weight: 600;
  margin-top: 2px;
}

.empty-indicator {
  opacity: 0.3;
  transition: opacity 0.15s ease;
  cursor: pointer;

  &:hover {
    opacity: 0.6;
  }
}

.cell-editor {
  min-width: 300px;

  .cell-editor-content {
    display: flex;
    flex-direction: column;
  }

  .property-name-display {
    padding: 12px;
    background: #f9fafb;
    border-radius: 8px;
    font-size: 14px;
    color: #374151;
  }
}

.consolidation-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 24px;
  text-align: center;

  h3 {
    font-size: 20px;
    font-weight: 600;
    color: #4b5563;
    margin: 16px 0 8px 0;
  }

  p {
    font-size: 14px;
    color: #9ca3af;
    margin: 0;
  }
}

.merge-dialog-header {
  background: linear-gradient(to bottom, #faf5ff, #ffffff);
  border-bottom: 1px solid #e9d5ff;
}

.merge-dialog-content {
  padding: 24px !important;
}

.merge-preview {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px;
  background: #f9fafb;
  border-radius: 8px;

  h4 {
    font-size: 14px;
    font-weight: 600;
    color: #374151;
    margin: 0 0 12px 0;
  }
}

.merge-arrow {
  display: flex;
  justify-content: center;
  padding: 8px 0;
}

.affected-steps {
  h4 {
    font-size: 14px;
    font-weight: 600;
    color: #374151;
    margin: 0 0 12px 0;
  }
}

.step-ref {
  display: flex;
  align-items: center;
  gap: 8px;
}

.step-number-mini {
  width: 20px;
  height: 20px;
  background: #50B3CB;
  color: white;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 800;
}

.suggestions-dialog-header {
  background: linear-gradient(to bottom, #fffbeb, #ffffff);
  border-bottom: 1px solid #fed7aa;
}

.suggestions-dialog-content {
  padding: 24px !important;
  max-height: 600px;
}

.import-dialog-header {
  background: linear-gradient(to bottom, #faf5ff, #ffffff);
  border-bottom: 1px solid #e9d5ff;
}

.import-wizard-card {
  display: flex;
  flex-direction: column;
  max-height: 85vh;
  overflow: hidden;
}

.import-wizard-step-label {
  font-size: 12px;
  font-weight: 400;
  color: #9e9e9e;
}

.import-wizard-stepper {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: transparent !important;
}

.import-stepper-header {
  flex-shrink: 0;
  box-shadow: none !important;
  border-bottom: 1px solid #e5e7eb;
}

.import-stepper-items {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.import-step-content {
  padding: 8px 0;
  min-height: 200px;
}

.import-wizard-actions {
  flex-shrink: 0;
  border-top: 1px solid #e5e7eb;
  padding: 12px 16px;
}

.import-preview-card {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 8px;
  padding: 16px;
}

.import-preview-header {
  display: flex;
  align-items: center;
  font-size: 16px;
  margin-bottom: 4px;
}

.import-preview-desc {
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 8px;
}

.import-preview-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 8px;
}

.import-preview-meta {
  font-size: 11px;
  color: #9ca3af;
}

.import-prompt-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.warning-header {
  background: linear-gradient(to bottom, #fffbeb, #ffffff);
  border-bottom: 1px solid #fbbf24;
}

.import-dialog-content {
  padding: 24px !important;
}

.upload-section {
  padding: 32px;
  text-align: center;
}

.remap-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.prompt-mapping-row {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0;
  padding: 12px 16px;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  transition: border-color 0.2s, background 0.2s;

  &.prompt-row--mapped {
    border-color: #d1fae5;
    background: #f0fdf4;
  }

  &.prompt-row--creating {
    border-color: #9333ea;
    border-width: 2px;
    background: #faf5ff;
  }

  .mapping-left {
    flex: 1;
    display: flex;
    align-items: flex-start;
    gap: 12px;
  }

  .step-indicator {
    flex-shrink: 0;
  }

  .imported-prompt-info {
    flex: 1;
    min-width: 0;
  }

  .imported-prompt-name {
    font-size: 14px;
    font-weight: 600;
    color: #111827;
  }

  .imported-prompt-meta {
    font-size: 12px;
    color: #6b7280;
    margin-top: 2px;
  }

  .mapping-right {
    flex: 1;
    min-width: 0;
  }
}

.prompt-row-main {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
}

.import-input-chips {
  display: flex;
  flex-wrap: wrap;
}

.create-from-import-hint {
  display: flex;
  align-items: center;
}

.create-mode-label {
  display: flex;
  align-items: center;
  font-size: 12px;
}

.create-remap-panel {
  background: #f5f3ff;
  border: 1px solid #e9d5ff;
  border-radius: 6px;
  padding: 12px 16px;
  width: 100%;
}

.create-remap-header {
  font-size: 12px;
  font-weight: 600;
  color: #7c3aed;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
}

.create-remap-input-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid #ede9fe;

  &:last-child {
    border-bottom: none;
  }
}

.create-remap-input-info {
  min-width: 200px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.create-remap-var {
  font-size: 12px;
  background: #ede9fe;
  padding: 2px 6px;
  border-radius: 4px;
  color: #7c3aed;
}

.create-remap-selects {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
}

.remap-select {
  flex: 1;
  min-width: 150px;
}

.create-remap-auto {
  display: flex;
  align-items: center;
}

.create-remap-actions {
  display: flex;
  align-items: center;
}

/* Document mapping wizard styles (step 3) */
.import-doc-mappings-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.import-doc-formatter-group {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}

.import-doc-group-header {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  background: #f3f4f6;
  font-weight: 600;
  font-size: 13px;
  border-bottom: 1px solid #e5e7eb;
}

.import-doc-mapping-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
}

.import-doc-mapping-left {
  flex: 1;
  display: flex;
  align-items: center;
  min-width: 0;
}

.import-doc-mapping-hint {
  min-width: 0;
}

.import-doc-mapping-name {
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.import-doc-mapping-meta {
  font-size: 11px;
  color: #9ca3af;
}

.import-doc-mapping-right {
  flex: 1;
  min-width: 0;
}

.import-doc-mapping-select {
  max-width: 100%;
}

/* Review step styles */
.import-review-section {
  padding: 10px 0;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
}

.import-review-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  color: #6b7280;
  margin-bottom: 4px;
}

.import-review-value {
  font-size: 14px;
}

.import-review-table {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.import-review-row {
  display: flex;
  align-items: center;
  font-size: 13px;
  padding: 4px 0;
}

.import-review-from {
  color: #6b7280;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.import-review-to {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 500;
}

.suggestion-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }

  &:last-child {
    margin-bottom: 0;
  }
}

.suggestion-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.suggestion-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

.suggestion-body {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.suggestion-properties {
  display: flex;
  flex-wrap: wrap;
  padding: 12px;
  background: #f9fafb;
  border-radius: 6px;
}

.suggestion-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.suggestion-input {
  flex: 1;
}

.tab-navigation {
  display: flex;
  gap: 4px;
}

.tab-btn {
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: -0.01em !important;

  &.tab-active {
    background: rgba(80, 179, 203, 0.1);
    color: #50B3CB;
  }
}

/* Test Chain Dialog Styles */
.test-chain-header {
  background: linear-gradient(135deg, #10B981 0%, #059669 100%);
  color: white;
  padding: 20px 24px;
  font-weight: 600;
}

.test-chain-content {
  padding: 24px;
  max-height: 70vh;
  overflow-y: auto;
}

.monospace-input {
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
  font-size: 13px;
}

.execution-status-container {
  .status-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;

    .status-title {
      font-size: 18px;
      font-weight: 600;
      color: #1F2937;
    }
  }

  .current-progress-box {
    display: flex;
    align-items: center;
    background: linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%);
    border: 2px solid #10B981;
    border-radius: 8px;
    padding: 12px 16px;

    .progress-text {
      flex: 1;

      .progress-step {
        font-size: 14px;
        font-weight: 600;
        color: #1F2937;
        margin-bottom: 2px;
      }

      .progress-count {
        font-size: 12px;
        color: #6B7280;
      }
    }
  }

  .status-messages-wrapper {
    margin-bottom: 16px;
  }

  .status-messages {
    background: #F9FAFB;
    border: 1px solid #E5E7EB;
    border-radius: 8px;
    padding: 16px;
    max-height: 200px;
    overflow-y: auto;

    .status-message {
      display: flex;
      align-items: flex-start;
      padding: 8px 0;
      font-size: 14px;
      color: #374151;

      &:not(:last-child) {
        border-bottom: 1px solid #E5E7EB;
      }

      &.status-message-final {
        font-weight: 600;
        padding: 12px;
        background: #ECFDF5;
        border-radius: 6px;
        margin-top: 8px;
        border: none !important;
      }
    }
  }

  .execution-result {
    .result-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 12px;

      .result-title {
        font-size: 16px;
        font-weight: 600;
        color: #1F2937;
      }
    }

    .result-content-wrapper {
      background: #1F2937;
      border-radius: 8px;
      max-height: 300px;
      overflow: auto;
    }

    .result-content {
      color: #10B981;
      padding: 16px;
      font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
      font-size: 13px;
      white-space: pre-wrap;
      word-wrap: break-word;
      margin: 0;
    }
  }
}

/* Connection Mapping Dialog Styles */
.connection-mapping-header {
  background: linear-gradient(135deg, #9333EA 0%, #7C3AED 100%);
  color: white;
  padding: 20px 24px;
  font-weight: 600;
}

.connection-mapping-content {
  padding: 24px;
}

.mapping-section {
  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;

    h4 {
      margin: 0;
      font-size: 16px;
      font-weight: 600;
      color: #1F2937;
    }
  }

  .empty-mappings {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    text-align: center;

    p {
      margin-top: 12px;
      font-size: 14px;
    }
  }

  .mappings-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .mapping-row-config {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 16px;
    background: #F9FAFB;
    border: 1px solid #E5E7EB;
    border-radius: 8px;

    .mapping-row-number {
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #9333EA;
      color: white;
      border-radius: 50%;
      font-weight: 600;
      font-size: 14px;
      flex-shrink: 0;
      margin-top: 8px;
    }

    .mapping-fields-container {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .mapping-fields-row {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .mapping-select {
      flex: 1;
    }

    .mapping-arrow {
      color: #9333EA;
      flex-shrink: 0;
    }

    .mapping-array-mode {
      padding-left: 4px;

      .v-radio-group {
        margin: 0;
      }

      .v-input--radio-group__input {
        flex-direction: row !important;
      }
    }
  }
}

.mapping-summary {
  h4 {
    font-size: 14px;
    font-weight: 600;
    color: #1F2937;
  }

  .summary-content {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .summary-item {
    display: flex;
    align-items: center;
    padding: 8px 12px;
    background: #F0FDF4;
    border: 1px solid #BBF7D0;
    border-radius: 6px;

    .summary-text {
      flex: 1;
      display: flex;
      align-items: center;
      gap: 12px;
      font-size: 13px;
      color: #166534;

      .mapping-path {
        flex: 1;
      }

      .mapping-mode-badge {
        flex-shrink: 0;
      }

      strong {
        font-weight: 600;
      }
    }
  }
}

/* ================================================
   REPORT VIEW STYLES
   ================================================ */

.report-view {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #f9fafb;
}

.report-header {
  display: flex;
  align-items: center;
  padding: 20px 24px;
  background: white;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
  gap: 16px;

  .report-title {
    font-size: 20px;
    font-weight: 600;
    color: #1f2937;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .report-subtitle {
    font-size: 14px;
    color: #6b7280;
    margin: 4px 0 0 0;
  }
}

.report-content {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
}

.report-dashboard {
  max-width: 1400px;
  margin: 0 auto;
}

.report-summary-card {
  height: 100%;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  .v-card__text {
    padding: 20px;
  }

  .summary-card-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 12px;

    .v-icon {
      opacity: 0.8;
    }

    .summary-card-label {
      font-size: 13px;
      font-weight: 500;
      color: #6b7280;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
  }

  .summary-card-value {
    font-size: 32px;
    font-weight: 700;
    color: #1f2937;
    line-height: 1;
  }
}

.v-tabs {
  .v-tab {
    text-transform: none;
    font-weight: 500;
    letter-spacing: 0;
  }
}

.v-data-table {
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  tbody tr {
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover {
      background-color: #f9fafb;
    }
  }
}

/* ================================================
   EXTRACTED INLINE STYLES
   ================================================ */

.report-expanded-content {
  background: #f9fafb;
  width: 100%;
  box-sizing: border-box;
  overflow-x: auto;
}

.report-date-group-header {
  background: white;
  padding: 8px 12px;
  border-radius: 4px;
  border-left: 4px solid #1976d2;
}

.reason-block {
  background: #f6f6fa;
  border-left: 4px solid #50B3CB;
  border-radius: 0 0 6px 6px;
  font-size: 14px;
  color: #333;
  font-style: italic;
  font-family: 'Georgia', 'Times New Roman', Times, serif;
  padding: 10px;
}

.select-header-item {
  display: flex;
  align-items: center;
  width: 100%;
  background-color: #f5f5f5;
  padding: 4px 0;
}

.output-mappings-container {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 16px;
  background: #fafafa;
}

.output-mapping-entry {
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e0e0e0;
}

.output-mapping-grid {
  display: grid;
  grid-template-columns: 1fr auto 1fr auto;
  gap: 12px;
  align-items: end;
}

.output-mapping-value {
  padding: 10px 12px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 13px;
  font-family: monospace;
}

.prompt-preview-panel {
  background-color: #f5f5f5;
  border-radius: 4px;
  max-height: 150px;
  overflow-y: auto;
}

.prompt-preview-pre {
  white-space: pre-wrap;
  margin: 0;
}

.prompt-result-panel {
  background-color: #e8f5e9;
  border-radius: 4px;
  max-height: 300px;
  overflow-y: auto;
}

.payload-example-pre {
  font-size: 11px;
  background: rgba(0, 0, 0, 0.05);
  padding: 8px;
  border-radius: 4px;
}

/* ================================================
   SCHEMA VIEW STYLES
   ================================================ */
.schema-view {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  background: #f9fafb;
}

.schema-container {
  max-width: 1200px;
  margin: 0 auto;
}

.schema-toolbar {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 24px;
  background: white;
  border-radius: 12px;
  margin-bottom: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.schema-section-title {
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

.schema-card {
  border-radius: 12px !important;
  overflow: hidden;
}

.schema-card-title {
  font-size: 16px !important;
  font-weight: 600;
  padding: 16px 20px !important;
  border-bottom: 1px solid #e5e7eb;
}

.schema-table {
  th {
    font-weight: 600 !important;
    font-size: 12px !important;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #6b7280 !important;
    white-space: nowrap;
  }

  td {
    vertical-align: middle !important;
  }

  tr.selected-row {
    background: #eff6ff !important;
    border-left: 3px solid #3b82f6;
  }

  tr.suggested-row {
    background: #fff7ed !important;
    border-left: 3px solid #f59e0b;
  }
}

.schema-key {
  font-size: 12px;
  padding: 2px 8px;
  background: #f3f4f6;
  border-radius: 4px;
  color: #374151;
  font-family: 'SF Mono', 'Consolas', monospace;
}

.schema-edit-field {
  font-size: 13px !important;

  .v-input__slot {
    min-height: 32px !important;
    padding: 0 8px !important;
    background: transparent !important;
    border: 1px solid transparent !important;
    border-radius: 6px;

    &:hover {
      border-color: #d1d5db !important;
      background: #f9fafb !important;
    }

    &:focus-within {
      border-color: #3b82f6 !important;
      background: white !important;
    }
  }
}

.api-schema-preview {
  font-size: 12px;
  font-family: 'SF Mono', 'Consolas', monospace;
  background: #1e293b;
  color: #e2e8f0;
  padding: 16px;
  border-radius: 8px;
  max-height: 400px;
  overflow-y: auto;
  white-space: pre-wrap;
  word-break: break-word;
}

.trigger-resolution-block {
  padding: 16px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}
</style>

<style lang="scss">
/* ================================================
   DARK THEME OVERRIDES (unscoped to match .v-application.theme--dark)
   ================================================ */

$dark-bg-primary: #1a1a2e;
$dark-bg-surface: #1e1e2e;
$dark-bg-elevated: #2d2d44;
$dark-bg-input: #252540;
$dark-border: #374151;
$dark-border-light: #4b5563;
$dark-text-primary: #e5e7eb;
$dark-text-secondary: #d1d5db;
$dark-text-muted: #9ca3af;
$dark-text-faint: #6b7280;
$dark-shadow: rgba(0, 0, 0, 0.4);
$dark-code-bg: #0d1117;
$dark-code-border: #30363d;
$dark-error-bg: #450a0a;
$dark-error-border: #991b1b;
$dark-warning-bg: #451a03;
$dark-warning-border: #78350f;
$dark-success-bg: #052e16;
$dark-success-border: #14532d;

.v-application.theme--dark {

  /* ================================================
     BASE COMPONENT
     ================================================ */
  .chain-builder {
    background: $dark-bg-primary;
  }

  /* ================================================
     LANDING PAGE
     ================================================ */
  .landing-header {
    background: $dark-bg-surface;
    border-bottom-color: $dark-border;
  }

  .landing-title {
    color: $dark-text-primary;
  }

  .landing-subtitle {
    color: $dark-text-muted;
  }

  .landing-content {
    background: $dark-bg-primary;
  }

  .chain-card {
    background: $dark-bg-surface !important;
    border-color: $dark-border !important;

    &:hover {
      border-color: #EA3785 !important;
      box-shadow: 0 4px 12px $dark-shadow;
    }

    .v-card__text {
      background: $dark-bg-surface;
    }
  }

  .chain-card-header .chain-icon {
    background: rgba(80, 179, 203, 0.2);
  }

  .chain-title {
    color: $dark-text-primary;
  }

  .chain-description {
    color: $dark-text-muted;
  }

  .stat-item {
    color: $dark-text-muted;
  }

  .empty-state {
    h3 {
      color: $dark-text-secondary;
    }
    p {
      color: $dark-text-muted;
    }
  }

  /* ================================================
     HISTORY VIEW
     ================================================ */
  .history-view {
    background: $dark-bg-primary;
  }

  .history-header {
    background: $dark-bg-surface;
    border-bottom-color: $dark-border;

    .history-title {
      color: $dark-text-primary;
    }

    .history-subtitle {
      color: $dark-text-muted;
    }
  }

  .history-content {
    .v-data-table {
      background: $dark-bg-surface;
      box-shadow: 0 1px 3px $dark-shadow;
    }
  }

  /* ================================================
     EXECUTION DETAILS
     ================================================ */
  .execution-details-content {
    background: $dark-bg-primary;
  }

  .execution-details-layout {
    background: $dark-bg-primary;
  }

  .execution-list-panel {
    background: $dark-bg-surface;
    box-shadow: 0 1px 3px $dark-shadow;
    border-color: $dark-border;
  }

  .execution-steps-panel {
    background: $dark-bg-surface;
    box-shadow: 0 1px 3px $dark-shadow;
    border-color: $dark-border;
  }

  .panel-header {
    border-bottom-color: $dark-border;
    background: linear-gradient(to bottom, $dark-bg-elevated, $dark-bg-surface);
    color: $dark-text-secondary;

    h3 {
      color: $dark-text-primary;
    }
  }

  .execution-list {
    background: $dark-bg-surface;
  }

  .execution-item {
    border-color: $dark-border;
    background: $dark-bg-surface;

    &:hover {
      border-color: #50B3CB;
      box-shadow: 0 2px 8px $dark-shadow;
    }

    &.active {
      border-color: #50B3CB;
      background: linear-gradient(to bottom, rgba(80, 179, 203, 0.15), rgba(80, 179, 203, 0.08));
      box-shadow: 0 2px 12px rgba(80, 179, 203, 0.3);
    }

    &.has-issues {
      border-left-color: #f59e0b;
      background: linear-gradient(to bottom, $dark-warning-bg, $dark-bg-surface);

      &:hover {
        border-color: #f59e0b;
        box-shadow: 0 2px 8px rgba(245, 158, 11, 0.2);
      }

      &.active {
        border-color: #f59e0b;
        background: linear-gradient(to bottom, rgba(120, 53, 15, 0.5), $dark-warning-bg);
        box-shadow: 0 2px 12px rgba(245, 158, 11, 0.3);
      }
    }

    &.is-partial {
      border-left-color: #f59e0b;
      background: linear-gradient(to bottom, $dark-warning-bg, $dark-bg-surface);

      &:hover {
        border-color: #f59e0b;
        box-shadow: 0 2px 8px rgba(245, 158, 11, 0.2);
      }

      &.active {
        border-color: #f59e0b;
        background: linear-gradient(to bottom, rgba(120, 53, 15, 0.5), $dark-warning-bg);
        box-shadow: 0 2px 12px rgba(245, 158, 11, 0.3);
      }
    }

    &.is-regeneration {
      border-left-color: #ED742D;
      background: linear-gradient(to bottom, rgba(154, 52, 18, 0.3), $dark-bg-surface);

      &:hover {
        border-color: #ED742D;
        box-shadow: 0 2px 8px rgba(237, 116, 45, 0.2);
      }

      &.active {
        border-color: #ED742D;
        background: linear-gradient(to bottom, rgba(154, 52, 18, 0.5), rgba(154, 52, 18, 0.3));
        box-shadow: 0 2px 12px rgba(237, 116, 45, 0.3);
      }
    }
  }

  .execution-id {
    color: $dark-text-primary;
  }

  .execution-time {
    color: $dark-text-muted;
  }

  .execution-duration {
    color: $dark-text-faint;
  }

  .execution-warning {
    background: $dark-warning-bg;
    border-color: $dark-warning-border;

    .warning-text {
      color: #fbbf24;
    }
  }

  .execution-regen-info {
    background: rgba(154, 52, 18, 0.3);
    border-color: #ED742D;

    .regen-text {
      color: #fdba74;
    }
  }

  .execution-summary-detailed {
    border-bottom-color: $dark-border;
    background: linear-gradient(to bottom, $dark-bg-elevated, $dark-bg-surface);
  }

  .summary-card {
    background: $dark-bg-surface;
    border-color: $dark-border;
    box-shadow: 0 1px 2px $dark-shadow;

    &:hover {
      box-shadow: 0 2px 6px $dark-shadow;
    }
  }

  .summary-label {
    color: $dark-text-muted;
  }

  .summary-value {
    color: $dark-text-primary;
  }

  .error-summary-detailed {
    background: $dark-error-bg;
    border-top-color: $dark-error-border;

    .error-title {
      color: #fca5a5;
    }

    .error-text {
      color: #fca5a5;
    }
  }

  .panel-header-content {
    h3 {
      color: $dark-text-primary;
    }
  }

  /* ================================================
     STEP TIMELINE & DETAIL CARDS
     ================================================ */
  .step-timeline-connector {
    background: $dark-border;
  }

  .steps-container {
    background: $dark-bg-primary;
  }

  .step-detail-card-enhanced {
    border-color: $dark-border;
    background: $dark-bg-surface;
    box-shadow: 0 1px 3px $dark-shadow;

    &:hover {
      box-shadow: 0 4px 12px $dark-shadow;
    }

    &.step-skipped {
      border-left-color: #f59e0b;
      background: linear-gradient(to bottom, $dark-warning-bg, $dark-bg-surface);

      .step-header-enhanced {
        background: linear-gradient(to bottom, rgba(120, 53, 15, 0.4), $dark-warning-bg);
      }
    }
  }

  .step-skipped-info {
    background: $dark-warning-bg;
    border-top-color: $dark-warning-border;
    border-bottom-color: $dark-border;

    .skipped-text {
      color: #fbbf24;
    }
  }

  .step-header-enhanced {
    background: linear-gradient(to bottom, $dark-bg-elevated, $dark-bg-surface);
    border-bottom-color: $dark-border;
  }

  .step-name-enhanced {
    color: $dark-text-primary;
  }

  .step-metrics .metric-item {
    color: $dark-text-muted;
    background: rgba(255, 255, 255, 0.05);
    border-color: $dark-border;
  }

  .step-timing-detailed {
    background: $dark-bg-elevated;
    border-bottom-color: $dark-border;

    .timing-label-detailed {
      color: $dark-text-muted;
    }

    .timing-value-detailed {
      color: $dark-text-primary;
    }
  }

  .step-error-detailed {
    background: $dark-error-bg;
    border-top-color: $dark-error-border;

    .error-title {
      color: #fca5a5;
    }

    .error-message {
      color: #fca5a5;
    }
  }

  .loading-state {
    .loading-text {
      color: $dark-text-muted;
    }
  }

  .empty-steps-state {
    .empty-title {
      color: $dark-text-secondary;
    }
    .empty-subtitle {
      color: $dark-text-muted;
    }
  }

  /* ================================================
     TRIGGERS VIEW
     ================================================ */
  .triggers-view {
    background: $dark-bg-primary;
  }

  .triggers-title {
    color: $dark-text-primary;
  }

  .triggers-subtitle {
    color: $dark-text-muted;
  }

  .trigger-card {
    background: $dark-bg-surface !important;

    &:hover {
      box-shadow: 0 8px 25px rgba(94, 40, 114, 0.3);
    }
  }

  .trigger-icon {
    background: rgba(94, 40, 114, 0.25);
  }

  .trigger-name {
    color: $dark-text-primary;
  }

  .trigger-description {
    color: $dark-text-muted;
  }

  .triggers-empty {
    .empty-title {
      color: $dark-text-secondary;
    }
    .empty-subtitle {
      color: $dark-text-muted;
    }
  }

  /* ================================================
     DOCUMENT FORMATTER / MAPPING DIALOGS
     ================================================ */
  .document-formatter-header {
    background: linear-gradient(to bottom, $dark-bg-elevated, $dark-bg-surface);
    border-bottom-color: $dark-border;
  }

  .document-formatter-content {
    background: $dark-bg-primary;
  }

  .formatter-description p {
    color: $dark-text-muted;
  }

  .mappings-header h3 {
    color: $dark-text-primary;
  }

  .empty-mappings {
    h4 {
      color: $dark-text-secondary;
    }
    p {
      color: $dark-text-muted;
    }
  }

  .mapping-card {
    border-color: $dark-border;
    background: $dark-bg-surface;

    &.inactive-mapping {
      background: $dark-bg-primary;
      border-color: $dark-border-light;
    }
  }

  .mapping-card-header {
    background: linear-gradient(to bottom, $dark-bg-elevated, $dark-bg-surface);
    border-bottom-color: $dark-border;

    &:hover {
      background: linear-gradient(to bottom, lighten($dark-bg-elevated, 3%), $dark-bg-elevated);
    }
  }

  .mapping-title {
    color: $dark-text-primary;
  }

  .mapping-status-indicator small {
    color: $dark-text-faint;
  }

  .dependency-label,
  .output-label {
    color: $dark-text-muted;
  }

  .step-endpoint {
    color: $dark-text-muted;
  }

  .attribute-mappings-section {
    background: $dark-bg-elevated;
    border-color: $dark-border;

    .mappings-title {
      color: $dark-text-primary;
    }

    .mappings-subtitle {
      color: $dark-text-muted;
    }
  }

  .mapping-row {
    background: $dark-bg-surface;
    border-color: $dark-border;

    .attribute-name {
      color: $dark-text-secondary;

      &.disabled-attribute {
        color: $dark-text-faint;
      }
    }
  }

  .json-property-mappings .json-mapping-row {
    background: $dark-bg-surface;
    border-color: $dark-border;

    .mapping-arrow {
      color: $dark-text-faint;
    }
  }

  /* ================================================
     EXECUTION STEP TABS & DATA DISPLAY
     ================================================ */
  .step-tabs-enhanced {
    border-top-color: $dark-border;
  }

  .tab-content-enhanced {
    background: $dark-bg-primary;
  }

  .no-data-enhanced {
    .no-data-text {
      color: $dark-text-muted;
    }
  }

  .data-item {
    background: $dark-bg-surface;
    border-color: $dark-border;

    &:hover {
      box-shadow: 0 2px 6px $dark-shadow;
    }

    &.data-item-warning {
      border-left-color: #f59e0b;
      background: linear-gradient(to bottom, $dark-warning-bg, $dark-bg-surface);
    }
  }

  .data-key-enhanced {
    color: $dark-text-primary;
  }

  .data-value-enhanced {
    background: $dark-bg-elevated;
  }

  .empty-value-warning {
    background: $dark-warning-bg;
    border-color: $dark-warning-border;

    .empty-value-text {
      color: #fbbf24;
    }
  }

  .expandable-content {
    .content-preview {
      background: $dark-code-bg;
      border-color: $dark-code-border;
      color: $dark-text-secondary;
    }

    .expand-btn {
      background: $dark-bg-surface;
      border-top-color: $dark-border;
    }

    .full-content {
      border-top-color: $dark-border;
    }
  }

  .simple-content {
    background: $dark-code-bg;
    border-color: $dark-code-border;
  }

  .content-code {
    color: $dark-text-secondary;
  }

  /* ================================================
     PROMPT DISPLAY
     ================================================ */
  .prompt-version-banner {
    background: linear-gradient(135deg, $dark-success-bg 0%, rgba(20, 83, 45, 0.6) 100%);
    border-color: #10b981;

    &.historical-version {
      background: linear-gradient(135deg, $dark-warning-bg 0%, rgba(120, 53, 15, 0.6) 100%);
      border-color: #f59e0b;
    }

    .version-info .version-text {
      color: $dark-text-primary;

      .historical-label {
        color: #fbbf24;
      }

      .current-label {
        color: #34d399;
      }
    }

    .model-info {
      color: $dark-text-muted;
      background: rgba(0, 0, 0, 0.3);
    }
  }

  .prompt-section-enhanced {
    background: $dark-bg-surface;
    border-color: $dark-border;
  }

  .prompt-section-header {
    background: linear-gradient(to bottom, $dark-bg-elevated, $dark-bg-surface);
    border-bottom-color: $dark-border;
  }

  .prompt-section-title {
    color: $dark-text-primary;
  }

  .prompt-content {
    color: $dark-text-secondary;
    background: $dark-code-bg;
  }

  .raw-response-display {
    background: $dark-bg-surface;
    border-color: $dark-border;
  }

  .response-header {
    background: linear-gradient(to bottom, rgba(154, 52, 18, 0.3), $dark-bg-surface);
    border-bottom-color: rgba(237, 116, 45, 0.3);
  }

  .response-title {
    color: $dark-text-primary;
  }

  .response-content {
    color: $dark-text-secondary;
    background: $dark-code-bg;
  }

  /* ================================================
     VALIDATION HISTORY
     ================================================ */
  .validation-history-display {
    background: $dark-bg-surface;
  }

  .validation-history-header {
    background: $dark-warning-bg;
    border-bottom-color: $dark-warning-border;
  }

  .validation-attempts-list {
    background: $dark-bg-surface;
  }

  .validation-attempt-card {
    background: $dark-bg-surface;
    border-color: $dark-border;

    &:hover {
      border-color: $dark-border-light;
      box-shadow: 0 2px 4px $dark-shadow;
    }
  }

  .attempt-label {
    color: $dark-text-primary;
  }

  .attempt-errors {
    background: $dark-error-bg;
    border-color: $dark-error-border;
  }

  .errors-title {
    color: #fca5a5;
  }

  .error-item {
    color: #fca5a5;
  }

  .response-toggle {
    background: $dark-bg-elevated;
    color: $dark-text-muted;
  }

  .response-content-small {
    color: $dark-text-secondary;
    background: $dark-code-bg;
  }

  .execution-trigger .trigger-text {
    color: $dark-text-muted;
  }

  /* ================================================
     BUILDER VIEW
     ================================================ */
  .builder-view {
    background: $dark-bg-primary;
  }

  .header-bar {
    background: $dark-bg-surface;
    border-bottom-color: $dark-border;
    box-shadow: 0 1px 2px $dark-shadow;
  }

  .sidebar {
    background: $dark-bg-surface;
    border-right-color: $dark-border;
  }

  .sidebar-header {
    border-bottom-color: $dark-border;
  }

  .sidebar-title {
    color: $dark-text-primary;
  }

  .category-header {
    color: $dark-text-secondary;
  }

  .category-name {
    color: $dark-text-secondary;
  }

  .category-content {
    background: $dark-bg-primary;
  }

  .service-parent-item {
    background: $dark-bg-elevated;
    border-bottom-color: $dark-border;

    &:hover {
      background: lighten($dark-bg-elevated, 5%);
    }
  }

  .service-endpoint-item {
    background: $dark-bg-primary;
  }

  .draggable-item {
    border-bottom-color: $dark-border;

    &:hover {
      background: $dark-bg-elevated;
      box-shadow: 0 1px 3px $dark-shadow;
    }
  }

  .endpoint-path {
    color: $dark-text-muted;
  }

  .item-icon {
    &.service-icon {
      background: rgba(94, 40, 114, 0.3);
    }
    &.formatter-icon {
      background: rgba(237, 116, 45, 0.25);
    }
    &.prompt-icon {
      background: rgba(80, 179, 203, 0.25);
    }
  }

  .item-name {
    color: $dark-text-primary;
  }

  .item-meta {
    color: $dark-text-muted;
  }

  /* Canvas */
  .canvas {
    background: $dark-bg-primary;
    background-image: radial-gradient(circle, $dark-border 1px, transparent 1px);
  }

  /* Step Nodes */
  .step-node {
    background: $dark-bg-surface;
    box-shadow: 0 4px 6px $dark-shadow;

    &:hover {
      box-shadow: 0 8px 12px rgba(0, 0, 0, 0.5);
    }

    &.selected {
      box-shadow: 0 0 0 2px #60a5fa;
    }

    &.dragging {
      box-shadow: 0 12px 24px rgba(0, 0, 0, 0.6);
    }

    &.inactive {
      .step-header {
        background: #555 !important;
      }
    }
  }

  .step-body {
    background: $dark-bg-surface;
  }

  .step-type {
    color: $dark-text-muted;
  }

  .connection-point {
    border-color: $dark-bg-surface;
  }

  /* Trigger Nodes */
  .trigger-node {
    background: $dark-bg-surface;
    box-shadow: 0 4px 6px $dark-shadow;

    &:hover {
      box-shadow: 0 8px 12px rgba(0, 0, 0, 0.5);
    }

    &.selected {
      box-shadow: 0 0 0 2px #60a5fa;
    }

    &.dragging {
      box-shadow: 0 12px 24px rgba(0, 0, 0, 0.6);
    }
  }

  .trigger-body {
    background: $dark-bg-elevated;
  }

  .trigger-summary {
    .label {
      color: $dark-text-muted;
    }

    .schema-snippet {
      background: $dark-bg-primary;
      color: $dark-text-secondary;
    }

    .mapping-count,
    .chain-name,
    .hierarchy-name {
      color: $dark-text-secondary;
    }
  }

  /* Start Node */
  .start-node {
    box-shadow: 0 4px 6px $dark-shadow;

    &:hover {
      box-shadow: 0 8px 12px rgba(0, 0, 0, 0.5);
    }
  }

  /* ================================================
     PROPERTIES PANEL
     ================================================ */
  .properties-panel {
    background: $dark-bg-surface;
    border-left-color: $dark-border;
  }

  .properties-header {
    border-bottom-color: $dark-border;

    h3 {
      color: $dark-text-primary;
    }
  }

  .properties-content {
    background: $dark-bg-surface;
  }

  .property-label {
    color: $dark-text-secondary;
  }

  .input-mapping-item,
  .output-mapping-item {
    background: $dark-bg-elevated;
    border-color: $dark-border;
  }

  .validation-config {
    background: $dark-warning-bg;
    border-color: $dark-warning-border;
  }

  .prompt-preview-box {
    background: $dark-bg-elevated;
    border-color: $dark-border;
  }

  .prompt-section-label {
    color: $dark-text-muted;
  }

  .prompt-section pre {
    background: $dark-code-bg;
    border-color: $dark-code-border;
    color: $dark-text-secondary;
  }

  .service-info-box,
  .formatter-info-box {
    background: $dark-bg-elevated;
    border-color: $dark-border;
  }

  .info-label {
    color: $dark-text-muted;
  }

  .info-value {
    color: $dark-text-primary;
  }

  .schema-box {
    background: $dark-code-bg;
    border-color: $dark-code-border;

    pre {
      color: $dark-text-secondary;
    }
  }

  /* ================================================
     DOCUMENT MAPPINGS & TEXT TEMPLATE
     ================================================ */
  .document-mappings-summary {
    background: $dark-bg-elevated;
    border-color: $dark-border;

    .no-mappings {
      color: $dark-text-muted;
    }

    .mappings-count {
      color: $dark-text-secondary;
    }
  }

  .text-template-editor {
    ::v-deep .v-input__control {
      background: $dark-bg-elevated;
    }
  }

  .available-variables {
    background: $dark-bg-elevated;
    border-color: $dark-border;
  }

  .no-variables {
    color: $dark-text-muted;
  }

  .variable-item {
    background: $dark-bg-surface;
    border-color: $dark-border;

    &:hover {
      border-color: #50B3CB;
      box-shadow: 0 2px 4px rgba(80, 179, 203, 0.2);
    }
  }

  .variable-syntax {
    color: #67d4e8;
  }

  .variable-description {
    color: $dark-text-muted;
  }

  /* ================================================
     MODAL / DIALOG STYLES
     ================================================ */
  .trigger-modal-header {
    background: $dark-bg-elevated;
    border-bottom-color: $dark-border;
  }

  .trigger-modal-content {
    background: $dark-bg-primary;
  }

  .section-title {
    color: $dark-text-primary;
  }

  .section-subtitle {
    color: $dark-text-muted;
  }

  .section-header {
    border-bottom-color: $dark-border;
  }

  /* ================================================
     CONSOLIDATION VIEW
     ================================================ */
  .consolidation-view {
    background: $dark-bg-primary;
  }

  .consolidation-toolbar {
    background: $dark-bg-surface;
    box-shadow: 0 1px 3px $dark-shadow;
  }

  .toolbar-section {
    &:not(:last-child) {
      border-right-color: $dark-border;
    }
  }

  .suggestions-banner {
    background: linear-gradient(to right, $dark-warning-bg, rgba(69, 26, 3, 0.5));
    border-color: rgba(237, 116, 45, 0.4);

    .suggestion-content {
      strong {
        color: #fdba74;
      }
      span {
        color: #fb923c;
      }
    }
  }

  .consolidation-grid {
    background: $dark-bg-surface;
    box-shadow: 0 1px 3px $dark-shadow;
  }

  .grid-header {
    background: linear-gradient(to bottom, $dark-bg-elevated, $dark-bg-surface);
    border-bottom-color: $dark-border;
  }

  .property-column-header {
    border-right-color: $dark-border;

    .column-title {
      color: $dark-text-secondary;
    }
  }

  .type-column-header {
    border-right-color: $dark-border;

    .column-title {
      color: $dark-text-secondary;
    }
  }

  .step-column-header {
    border-right-color: $dark-border;
  }

  .step-name-compact {
    color: $dark-text-primary;
  }

  .property-row {
    border-bottom-color: $dark-border;

    &:hover {
      background: $dark-bg-elevated;
    }

    &.selected-row {
      background: rgba(59, 130, 246, 0.15);
      border-left-color: #3b82f6;
    }

    &.suggested-row {
      background: linear-gradient(to right, $dark-warning-bg, transparent);
      border-left-color: #f59e0b;
    }
  }

  .property-cell {
    border-right-color: $dark-border;
  }

  .type-cell {
    border-right-color: $dark-border;
  }

  .property-name-row .property-name-display-inline {
    color: $dark-text-primary;
  }

  .source-tag {
    color: $dark-text-muted;
    background: $dark-bg-elevated;
  }

  .required-tag {
    color: #fca5a5;
    background: $dark-error-bg;
  }

  .step-cell {
    border-right-color: $dark-border;

    &:hover {
      background: $dark-bg-elevated;
    }

    &.has-property {
      background: $dark-success-bg;

      &:hover {
        background: rgba(5, 46, 22, 0.8);
      }
    }

    &.is-required {
      background: $dark-error-bg;

      &:hover {
        background: rgba(69, 10, 10, 0.8);
      }
    }
  }

  .property-source-mini {
    color: $dark-text-muted;
  }

  .property-output-mini {
    color: #60a5fa;
  }

  .cell-editor .property-name-display {
    background: $dark-bg-elevated;
    color: $dark-text-secondary;
  }

  .consolidation-empty {
    h3 {
      color: $dark-text-secondary;
    }
    p {
      color: $dark-text-muted;
    }
  }

  /* ================================================
     SCHEMA VIEW
     ================================================ */
  .schema-view {
    background: $dark-bg-primary;
  }

  .schema-toolbar {
    background: $dark-bg-surface;
  }

  .schema-section-title {
    color: $dark-text-primary;
  }

  .schema-card {
    background: $dark-bg-surface !important;
    border-color: $dark-border !important;
  }

  .schema-card-title {
    color: $dark-text-primary;
  }

  .schema-key {
    background: $dark-bg-elevated;
    color: #93c5fd;
  }

  .schema-table {
    tr.selected-row {
      background: rgba(59, 130, 246, 0.15) !important;
      border-left-color: #3b82f6;
    }

    tr.suggested-row {
      background: linear-gradient(to right, $dark-warning-bg, transparent) !important;
      border-left-color: #f59e0b;
    }
  }

  .api-schema-preview {
    background: $dark-code-bg;
    border-color: $dark-code-border;
    color: #93c5fd;
  }

  .api-schema-preview-code {
    background: $dark-code-bg !important;
    color: #93c5fd;
  }

  .trigger-resolution-block {
    border-color: $dark-border;
  }

  /* ================================================
     MERGE / IMPORT / SUGGESTIONS DIALOGS
     ================================================ */
  .merge-dialog-header {
    background: linear-gradient(to bottom, rgba(94, 40, 114, 0.3), $dark-bg-surface);
    border-bottom-color: rgba(233, 213, 255, 0.2);
  }

  .merge-dialog-content {
    background: $dark-bg-primary;
  }

  .merge-preview {
    background: $dark-bg-elevated;

    h4 {
      color: $dark-text-secondary;
    }
  }

  .affected-steps h4 {
    color: $dark-text-secondary;
  }

  .suggestions-dialog-header {
    background: linear-gradient(to bottom, $dark-warning-bg, $dark-bg-surface);
    border-bottom-color: rgba(253, 186, 116, 0.3);
  }

  .suggestions-dialog-content {
    background: $dark-bg-primary;
  }

  .import-dialog-header {
    background: linear-gradient(to bottom, rgba(94, 40, 114, 0.3), $dark-bg-surface);
    border-bottom-color: rgba(233, 213, 255, 0.2);
  }

  .import-wizard-card {
    background: $dark-bg-surface;
  }

  .import-stepper-header {
    border-bottom-color: $dark-border;
  }

  .import-wizard-actions {
    border-top-color: $dark-border;
  }

  .import-preview-card {
    background: rgba(34, 197, 94, 0.1);
    border-color: rgba(34, 197, 94, 0.3);
  }

  .import-preview-meta {
    color: $dark-text-muted;
  }

  .import-doc-formatter-group {
    border-color: $dark-border;
  }

  .import-doc-group-header {
    background: $dark-bg-elevated;
    border-bottom-color: $dark-border;
    color: $dark-text-secondary;
  }

  .import-doc-mapping-row {
    border-bottom-color: $dark-border;
  }

  .import-doc-mapping-name {
    color: $dark-text-primary;
  }

  .import-doc-mapping-meta {
    color: $dark-text-muted;
  }

  .import-review-section {
    border-bottom-color: $dark-border;
  }

  .import-review-label {
    color: $dark-text-muted;
  }

  .import-review-from {
    color: $dark-text-muted;
  }

  .import-review-to {
    color: $dark-text-primary;
  }

  .warning-header {
    background: linear-gradient(to bottom, $dark-warning-bg, $dark-bg-surface);
    border-bottom-color: $dark-warning-border;
  }

  .import-dialog-content {
    background: $dark-bg-primary;
  }

  .prompt-mapping-row {
    background: $dark-bg-elevated;
    border-color: $dark-border;

    &.prompt-row--creating {
      background: rgba(147, 51, 234, 0.08);
      border-color: rgba(147, 51, 234, 0.5);
    }

    &.prompt-row--mapped {
      background: rgba(34, 197, 94, 0.08);
      border-color: rgba(34, 197, 94, 0.3);
    }

    .imported-prompt-name {
      color: $dark-text-primary;
    }

    .imported-prompt-meta {
      color: $dark-text-muted;
    }
  }

  .create-remap-panel {
    background: rgba(147, 51, 234, 0.1);
    border-color: rgba(147, 51, 234, 0.3);
  }

  .create-remap-var {
    background: rgba(147, 51, 234, 0.2);
    color: #d8b4fe;
  }

  .create-remap-header {
    color: #c4b5fd;
  }

  .create-remap-input-row {
    border-bottom-color: rgba(147, 51, 234, 0.2);
  }

  .suggestion-card {
    background: $dark-bg-surface;
    border-color: $dark-border;

    &:hover {
      box-shadow: 0 4px 12px $dark-shadow;
    }
  }

  .suggestion-title {
    color: $dark-text-secondary;
  }

  .suggestion-properties {
    background: $dark-bg-elevated;
  }

  /* ================================================
     TAB NAVIGATION
     ================================================ */
  .tab-btn.tab-active {
    background: rgba(80, 179, 203, 0.15);
  }

  /* ================================================
     TEST CHAIN DIALOG
     ================================================ */
  .test-chain-content {
    background: $dark-bg-primary;
  }

  .execution-status-container {
    .status-title {
      color: $dark-text-primary;
    }

    .current-progress-box {
      background: linear-gradient(135deg, $dark-success-bg 0%, rgba(20, 83, 45, 0.6) 100%);
      border-color: #10B981;

      .progress-step {
        color: $dark-text-primary;
      }

      .progress-count {
        color: $dark-text-muted;
      }
    }

    .status-messages {
      background: $dark-bg-elevated;
      border-color: $dark-border;

      .status-message {
        color: $dark-text-secondary;

        &:not(:last-child) {
          border-bottom-color: $dark-border;
        }

        &.status-message-final {
          background: $dark-success-bg;
        }
      }
    }

    .execution-result {
      .result-title {
        color: $dark-text-primary;
      }

      .result-content-wrapper {
        background: $dark-code-bg;
      }
    }
  }

  /* ================================================
     CONNECTION MAPPING DIALOG
     ================================================ */
  .connection-mapping-content {
    background: $dark-bg-primary;
  }

  .mapping-section {
    .section-header h4 {
      color: $dark-text-primary;
    }

    .empty-mappings p {
      color: $dark-text-muted;
    }

    .mapping-row-config {
      background: $dark-bg-elevated;
      border-color: $dark-border;
    }

    .mapping-arrow {
      color: #a855f7;
    }
  }

  .mapping-summary {
    h4 {
      color: $dark-text-primary;
    }

    .summary-item {
      background: $dark-success-bg;
      border-color: $dark-success-border;

      .summary-text {
        color: #34d399;
      }
    }
  }

  /* ================================================
     REPORT VIEW
     ================================================ */
  .report-view {
    background: $dark-bg-primary;
  }

  .report-header {
    background: $dark-bg-surface;
    border-bottom-color: $dark-border;

    .report-title {
      color: $dark-text-primary;
    }

    .report-subtitle {
      color: $dark-text-muted;
    }
  }

  .report-content {
    background: $dark-bg-primary;
  }

  .report-summary-card {
    background: $dark-bg-surface !important;
    box-shadow: 0 1px 3px $dark-shadow;

    &:hover {
      box-shadow: 0 4px 12px $dark-shadow;
    }

    .summary-card-label {
      color: $dark-text-muted;
    }

    .summary-card-value {
      color: $dark-text-primary;
    }
  }

  /* ================================================
     DATA TABLES (scoped overrides)
     ================================================ */
  .chain-builder .v-data-table {
    background: $dark-bg-surface;
    box-shadow: 0 1px 3px $dark-shadow;

    tbody tr:hover {
      background-color: $dark-bg-elevated !important;
    }
  }

  /* ================================================
     SEARCH FIELD & GENERAL INPUT OVERRIDES
     ================================================ */
  .chain-builder .search-field {
    .v-input__slot {
      background: $dark-bg-input;
    }
  }

  .step-name-small {
    color: $dark-text-primary;
  }

  .property-name {
    color: $dark-text-secondary;
  }

  .source-info {
    color: $dark-text-muted;
  }

  .step-data-name {
    color: $dark-text-primary;
  }

  .step-data-item-enhanced:hover {
    background-color: $dark-bg-elevated;
  }

  .connection-action-btn {
    box-shadow: 0 2px 4px $dark-shadow;

    &:hover {
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.5);
    }
  }

  /* ================================================
     EXTRACTED INLINE STYLES - DARK OVERRIDES
     ================================================ */

  .report-expanded-content {
    background: $dark-bg-elevated;
  }

  .report-date-group-header {
    background: $dark-bg-surface;
    border-left-color: #60a5fa;
  }

  .reason-block {
    background: $dark-bg-elevated;
    color: $dark-text-secondary;
  }

  .select-header-item {
    background-color: $dark-bg-elevated;
  }

  .output-mappings-container {
    border-color: $dark-border;
    background: $dark-bg-elevated;
  }

  .output-mapping-entry {
    border-bottom-color: $dark-border;
  }

  .output-mapping-value {
    background: $dark-bg-surface;
    border-color: $dark-border;
    color: $dark-text-secondary;
  }

  .prompt-preview-panel {
    background-color: $dark-bg-elevated;
  }

  .prompt-result-panel {
    background-color: $dark-success-bg;
  }

  .payload-example-pre {
    background: rgba(255, 255, 255, 0.05);
  }

  /* ================================================
     REPORT VIEW - TABS & SUMMARY CARDS
     ================================================ */

  .report-view,
  .report-dashboard {
    .v-tabs-bar.grey.lighten-4 {
      background-color: $dark-bg-elevated !important;
    }

    .v-card.v-sheet {
      background-color: $dark-bg-surface !important;
    }

    .v-card__text {
      h3 {
        color: $dark-text-primary;
      }
    }

    .v-data-table__expanded__content {
      td {
        background: $dark-bg-elevated;
      }
    }
  }

  /* Schema View Dark Overrides */
  .schema-view {
    background: $dark-bg-primary;
  }

  .schema-toolbar {
    background: $dark-bg-surface;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  }

  .schema-section-title {
    color: $dark-text-primary;
  }

  .schema-key {
    background: $dark-bg-elevated;
    color: $dark-text-primary;
  }

  .schema-edit-field .v-input__slot {
    &:hover {
      border-color: $dark-border !important;
      background: $dark-bg-elevated !important;
    }
    &:focus-within {
      background: $dark-bg-surface !important;
    }
  }

  .trigger-resolution-block {
    background: $dark-bg-elevated;
    border-color: $dark-border;
  }
}
</style>

